// ============================================================
// core/physics/simulation.js
// EV Powertrain Simulator — 시뮬레이션 엔진
// WLTP/JC08/CLTC/FTP-75/RDE, 발진/재가속/코스팅/제동, runCoreSim/FullSim
// ============================================================

// ── simulation.js: zero-import 설계 유지 (앱 크래시 방지) ──
// motor.js·battery.js·tire.js 등 외부 함수는 Vite 번들 스코프에서 공유됨
// import 문 추가 시 Vite가 strict module scope 적용 → 공유 함수 undefined 오류

// [Fix-DI-warn] DI 미호출 감지 플래그
// setSSTConstants() / setPhysicsFunctions() 호출 여부를 추적.
// 모듈 로드 후 setTimeout(0)으로 미주입 상태를 경고 (→ Fix 참조).
let _sstInjected     = false;
let _physFnInjected  = false;

// ★ [Fix-C1 / SST-DI] ROAD_MU — zero-import 제약 대응: DI(setSSTConstants)로 주입 가능
// ★ 원본 SST: constants/physics_consts.js export const ROAD_MU
// ★ 앱 초기화 시 setSSTConstants()를 호출하면 SST 원본으로 자동 교체+검증됨.
// ★ 미호출 시 아래 기본값(fallback)을 사용 — 크래시 없음, SST 경고만 발생.
let ROAD_MU = { dry: 1.0, wet: 0.7, snow: 0.4, ice: 0.3 };

// [Fix-g-global] 함수 내 8곳 인라인 중복 선언 → 파일 상단 단일 선언으로 통합
// [SST-DI] setSSTConstants() 호출 시 G_GRAVITY SST 원본으로 교체됨
let g = 9.80665; // [SST] constants/physics_consts.js G_GRAVITY

// [P-3-Fix] 탄소 배출 계수 fallback — 한국 고정 459 → IEA 세계 평균 436
// [SST-DI] setSSTConstants() 로 physics_consts.js GRID_EF_DEFAULT 주입 시 교체됨
let GRID_EF_DEFAULT_SIM = 436; // [SST] constants/physics_consts.js GRID_EF_DEFAULT

// ══════════════════════════════════════════════════════════════════════
// §DRIVE_MODE_GAINS_INLINE
// ★ SST: core/control/drive_mode_config.js → DRIVE_MODE_GAIN_TABLE
// ★ simulation.js는 zero-import 설계이므로 직접 import 불가.
// ★ setSSTConstants() 호출 시 SST 원본 테이블로 자동 동기화됨.
// ══════════════════════════════════════════════════════════════════════
let DRIVE_MODE_GAIN_TABLE_SIM = {
  ECO:      { Kp_id:0.65, Ki_id:0.55, Kp_iq:0.65, Ki_iq:0.55, Kp_spd:0.60, Ki_spd:0.50 },
  NORMAL:   { Kp_id:1.00, Ki_id:1.00, Kp_iq:1.00, Ki_iq:1.00, Kp_spd:1.00, Ki_spd:1.00 },
  SPORT:    { Kp_id:1.45, Ki_id:1.30, Kp_iq:1.45, Ki_iq:1.30, Kp_spd:1.50, Ki_spd:1.40 },
  SNOW:     { Kp_id:0.50, Ki_id:0.40, Kp_iq:0.50, Ki_iq:0.40, Kp_spd:0.45, Ki_spd:0.35 },
  OFF_ROAD: { Kp_id:0.80, Ki_id:0.70, Kp_iq:0.80, Ki_iq:0.70, Kp_spd:0.75, Ki_spd:0.65 },
};

// ── [SST-DI] setSSTConstants — SST 원본 상수 주입 ───────────────────────────
/**
 * 앱 초기화 시 SST 원본 상수를 simulation.js 내부 변수로 주입한다.
 * zero-import 제약을 DI(의존성 역전)로 해소하는 패턴.
 *
 * 사용 (ev-sim_v2_0.jsx 또는 main.jsx 초기화 블록):
 *   import { ROAD_MU, G_GRAVITY }     from '../constants/physics_consts.js';
 *   import { DRIVE_MODE_GAIN_TABLE }  from '../core/control/drive_mode_config.js';
 *   import { setSSTConstants }        from '../core/physics/simulation.js';
 *   setSSTConstants(ROAD_MU, G_GRAVITY, DRIVE_MODE_GAIN_TABLE);
 *
 * @param {object} ext_ROAD_MU          physics_consts.js 의 ROAD_MU
 * @param {number} [ext_G_GRAVITY]      physics_consts.js 의 G_GRAVITY (선택)
 * @param {object} [ext_DRIVE_GAIN_TABLE] drive_mode_config.js 의 DRIVE_MODE_GAIN_TABLE (선택)
 * @returns {{ ok: boolean, errors: string[] }}  verifySSTConstants 결과
 */
function setSSTConstants(ext_ROAD_MU, ext_G_GRAVITY, ext_DRIVE_GAIN_TABLE, ext_GRID_EF) {
  // ① ROAD_MU 교체
  if (ext_ROAD_MU && typeof ext_ROAD_MU === 'object') {
    ROAD_MU = ext_ROAD_MU;
  }
  // ② G_GRAVITY 교체
  if (typeof ext_G_GRAVITY === 'number' && isFinite(ext_G_GRAVITY)) {
    g = ext_G_GRAVITY;
  }
  // ③ DRIVE_MODE_GAIN_TABLE 동기화 (게인 키 6종만 추출 — label/color 등 UI 필드 제외)
  if (ext_DRIVE_GAIN_TABLE && typeof ext_DRIVE_GAIN_TABLE === 'object') {
    const GAIN_KEYS = ['Kp_id', 'Ki_id', 'Kp_iq', 'Ki_iq', 'Kp_spd', 'Ki_spd'];
    for (const mode of Object.keys(ext_DRIVE_GAIN_TABLE)) {
      const src = ext_DRIVE_GAIN_TABLE[mode];
      if (!src) continue;
      const row = {};
      for (const k of GAIN_KEYS) if (src[k] != null) row[k] = src[k];
      DRIVE_MODE_GAIN_TABLE_SIM[mode] = row;
    }
  }
  // ④ [P-3-Fix] GRID_EF_DEFAULT 교체 — 한국 고정 459 → physics_consts SST 원본으로 갱신
  if (typeof ext_GRID_EF === 'number' && isFinite(ext_GRID_EF) && ext_GRID_EF > 0) {
    GRID_EF_DEFAULT_SIM = ext_GRID_EF;
  }
  // ⑤ 주입 직후 자동 검증 — 불일치 시 console.error
  _sstInjected = true;
  return verifySSTConstants(ext_ROAD_MU, ext_DRIVE_GAIN_TABLE);
}

/**
 * 드라이브 모드(ECO/NORMAL/SPORT/SNOW/OFF_ROAD)에 따라 FOC·속도 PI 게인을 적용한 params를 반환한다.
 * mode_gains 커스텀값 우선, 없으면 DRIVE_MODE_GAIN_TABLE_SIM 배율로 기본값 스케일.
 * zero-import 설계: motor_control.js DRIVE_MODE_GAIN_TABLE 대신 로컬 복사본 사용.
 *
 * @param {object} params  drive_mode, Kp_id, Ki_id, Kp_iq, Ki_iq, Kp_spd, Ki_spd 등
 * @returns {object}  적용된 게인을 포함한 새 params 객체 (_sim_dm_mode, _sim_dm_factors 포함)
 */
function _applyDriveModeGainsInline(params) {
  let mode    = ((params.drive_mode || 'NORMAL') + '').toUpperCase();
  let factors = DRIVE_MODE_GAIN_TABLE_SIM[mode] || DRIVE_MODE_GAIN_TABLE_SIM.NORMAL;
  let base_Kp_id  = params.Kp_id  != null ? params.Kp_id  : 1.51;
  let base_Ki_id  = params.Ki_id  != null ? params.Ki_id  : 75;
  let base_Kp_iq  = params.Kp_iq  != null ? params.Kp_iq  : 3.02;
  let base_Ki_iq  = params.Ki_iq  != null ? params.Ki_iq  : 75;
  let base_Kp_spd = params.Kp_spd != null ? params.Kp_spd : 143.4;
  let base_Ki_spd = params.Ki_spd != null ? params.Ki_spd : 3003;
  let custom = params.mode_gains && params.mode_gains[mode];
  let eff_Kp_id  = (custom && custom.Kp_id  != null) ? custom.Kp_id  : +(base_Kp_id  * factors.Kp_id ).toFixed(4);
  let eff_Ki_id  = (custom && custom.Ki_id  != null) ? custom.Ki_id  : +(base_Ki_id  * factors.Ki_id ).toFixed(3);
  let eff_Kp_iq  = (custom && custom.Kp_iq  != null) ? custom.Kp_iq  : +(base_Kp_iq  * factors.Kp_iq ).toFixed(4);
  let eff_Ki_iq  = (custom && custom.Ki_iq  != null) ? custom.Ki_iq  : +(base_Ki_iq  * factors.Ki_iq ).toFixed(3);
  let eff_Kp_spd = (custom && custom.Kp_spd != null) ? custom.Kp_spd : +(base_Kp_spd * factors.Kp_spd).toFixed(3);
  let eff_Ki_spd = (custom && custom.Ki_spd != null) ? custom.Ki_spd : +(base_Ki_spd * factors.Ki_spd).toFixed(2);
  return Object.assign({}, params, {
    Kp_id:  eff_Kp_id,  Ki_id:  eff_Ki_id,
    Kp_iq:  eff_Kp_iq,  Ki_iq:  eff_Ki_iq,
    Kp_spd: eff_Kp_spd, Ki_spd: eff_Ki_spd,
    _sim_dm_mode:    mode,
    _sim_dm_factors: factors,
    _sim_dm_applied: true,
  });
}

// ── init2RCBatteryState / initRLSMuState 인라인 스텁 ──────────────────────
// ★ SST: core/physics/_shared_stubs.js
// simulation.js는 zero-import 설계이므로 해당 파일을 직접 import할 수 없다.
// _shared_stubs.js의 구현과 반드시 동일하게 유지할 것.
// 시그니처·반환값 변경 시 _shared_stubs.js → battery.js · tire.js도 함께 수정.
//
// [Fix-StubVerify] 자동 동기화 감지:
//   앱 초기화 시 verifySSTConstants(ROAD_MU, DRIVE_MODE_GAIN_TABLE,
//     { init2RCBatteryState, initRLSMuState })를 호출하면
//   이 스텁의 반환 키 집합이 _shared_stubs.js 원본과 일치하는지 런타임 검증.
//   불일치 시 console.error로 보고. 빌드 타임 린트로 발전시키는 것이 장기 목표.
/**
 * 2RC 배터리 ECM 상태를 초기화한다 (_shared_stubs.js 인라인 복사).
 * zero-import 설계로 인해 simulation.js 내 복사본 유지 필요.
 *
 * @param {object} params  soc_init, T_amb
 * @returns {{ SOC: number, V1: number, V2: number, T_cell: number }}
 */
function init2RCBatteryState(params) {
  return { SOC: params.soc_init ?? 0.9, V1: 0, V2: 0, T_cell: params.T_amb ?? 25 };
}
/**
 * RLS µ 추정기 상태를 초기화한다 (_shared_stubs.js 인라인 복사).
 *
 * @param {object} params  mu, rls_P_init
 * @returns {{ mu_est: number, P: number }}
 */
function initRLSMuState(params) {
  return { mu_est: params.mu ?? 1.0, P: params.rls_P_init ?? 1.0 };
}

// ── [Fix-dmt-DI] driveModeThrottle 명시적 DI 패턴 ──────────────────────────
// simulation.js는 zero-import 설계이므로 driveModeThrottle을 직접 import할 수 없음.
// 대신 모듈 레벨 변수 + setDriveModeThrottle() 주입 함수로 의존성 역전(DI) 구현.
//   · 런타임(앱 실행): ev-sim_v2_0.jsx에서 import 직후 setDriveModeThrottle(driveModeThrottle) 호출
//   · 테스트/SSR 환경: _driveModeThrottleStub fallback 자동 사용 → 크래시 없음
/**
 * driveModeThrottle DI 미연결 시 폴백 스텁.
 * ECO: pedal × eco_max_torque_ratio (기본 0.7), SPORT: ×1.15, others: ×1.0.
 *
 * @param {number} pedal_pos  페달 입력 [0~1]
 * @param {object} params     drive_mode, eco_max_torque_ratio, sport_max_torque_ratio
 * @returns {number}  유효 스로틀 [0~1.15]
 */
function _driveModeThrottleStub(pedal_pos, params /*, v_kmh */) {
  // Minimal fallback: ECO=0.7×, SPORT=1.15×, others=1.0× of pedal_pos
  let mode = (params && params.drive_mode || 'NORMAL').toUpperCase();
  let k = mode === 'ECO' ? (params.eco_max_torque_ratio || 0.7)
        : mode === 'SPORT' ? (params.sport_max_torque_ratio || 1.15)
        : 1.0;
  return Math.min(1.15, Math.max(0, pedal_pos * k));
}
let _driveModeThrottle = _driveModeThrottleStub;
/**
 * driveModeThrottle 함수를 외부에서 주입한다 (DI).
 * ev-sim_v2_0.jsx 초기화 시 motor_control.js의 실제 구현을 주입.
 *
 * @param {Function} fn  (pedal_pos, params, v_kmh) → number
 */
function setDriveModeThrottle(fn) {
  if (typeof fn === 'function') _driveModeThrottle = fn;
}
// 내부 호출 래퍼: 파일 전체의 driveModeThrottle() 호출은 이 변수를 통해 라우팅
/**
 * 내부 driveModeThrottle 호출 래퍼 — 주입된 함수 또는 스텁으로 라우팅.
 *
 * @param {number} pedal_pos  페달 입력 [0~1]
 * @param {object} params     차량 파라미터
 * @param {number} v_kmh      현재 속도 [km/h]
 * @returns {number}  모드 보정된 스로틀 [0~1.15]
 */
function driveModeThrottle(pedal_pos, params, v_kmh) {
  return _driveModeThrottle(pedal_pos, params, v_kmh);
}

// ── [Fix-typeof-DI] 외부 물리함수 DI 패턴 ──────────────────────────────────
// motor.js / dynamics.js / vehicle.js의 함수를 번들 스코프 암묵 의존 없이
// 안전하게 호출하기 위한 명시적 DI. setPhysicsFunctions()로 주입.
// 미주입 시 null → 기존 폴백 분기(인라인 근사값)로 자동 전환 — 크래시 없음.
// ─────────────────────────────────────────────────────────────────────────────
// 출처별 분류:
//   motor.js   : _motorFOCStep, _sensorlessObserver, _initFOCState
//   dynamics.js: _steeringFeedbackModel, _initBetaEKF, _ekfUpdateBeta,
//                _initVDControlState, _vdControlStep, _computeVerticalLoad_v4
//   vehicle.js : _CrrDynamic
let _motorFOCStep          = null;
let _sensorlessObserver    = null;
let _initFOCState          = null;
let _steeringFeedbackModel = null;
let _initBetaEKF           = null;
let _ekfUpdateBeta         = null;
let _initVDControlState    = null;
let _vdControlStep         = null;
let _computeVerticalLoad_v4= null;
let _CrrDynamic            = null;

/**
 * 물리함수 DI 주입 — zero-import 설계 부채를 DI로 해소.
 *
 * 사용 (ev-sim_v2_0.jsx 또는 main.jsx 초기화 블록):
 *   import { motorFOCStep, sensorlessObserver, initFOCState } from './core/physics/motor.js';
 *   import { steeringFeedbackModel, initBetaEKF, ekfUpdateBeta,
 *            initVDControlState, vdControlStep, computeVerticalLoad_v4 }
 *            from './core/physics/dynamics.js';
 *   import { CrrDynamic } from './core/physics/vehicle.js';
 *   setPhysicsFunctions({ motorFOCStep, sensorlessObserver, initFOCState,
 *     steeringFeedbackModel, initBetaEKF, ekfUpdateBeta,
 *     initVDControlState, vdControlStep, computeVerticalLoad_v4, CrrDynamic });
 *
 * @param {{ [name: string]: Function }} fns  주입할 함수 맵
 */
function setPhysicsFunctions(fns) {
  if (!fns || typeof fns !== 'object') return;
  if (typeof fns.motorFOCStep           === 'function') _motorFOCStep          = fns.motorFOCStep;
  if (typeof fns.sensorlessObserver     === 'function') _sensorlessObserver    = fns.sensorlessObserver;
  if (typeof fns.initFOCState           === 'function') _initFOCState          = fns.initFOCState;
  if (typeof fns.steeringFeedbackModel  === 'function') _steeringFeedbackModel = fns.steeringFeedbackModel;
  if (typeof fns.initBetaEKF            === 'function') _initBetaEKF           = fns.initBetaEKF;
  if (typeof fns.ekfUpdateBeta          === 'function') _ekfUpdateBeta         = fns.ekfUpdateBeta;
  if (typeof fns.initVDControlState     === 'function') _initVDControlState    = fns.initVDControlState;
  if (typeof fns.vdControlStep          === 'function') _vdControlStep         = fns.vdControlStep;
  if (typeof fns.computeVerticalLoad_v4 === 'function') _computeVerticalLoad_v4= fns.computeVerticalLoad_v4;
  if (typeof fns.CrrDynamic             === 'function') _CrrDynamic            = fns.CrrDynamic;
  _physFnInjected = true;
}

// ── generateFTP75Profile ──
/**
 * FTP-75 표준 주행 사이클 속도 프로파일을 생성한다 (1874초, 1Hz).
 * Phase 1 Cold(0~505s) + Phase 2 Transient(506~1369s) + Phase 3 Hot(1370~1874s = Phase1 재현).
 * 키포인트 선형보간 후 1초 간격 결과 반환.
 *
 * @returns {{ t: number, v: number }[]}  { t[s], v[km/h] } 배열
 */
function generateFTP75Profile() {
  // FTP-75 speed trace (km/h) - representative approximation at 1Hz
  // Phase 1 (cold): 0-505s | Phase 2 (transient): 506-1369s | Phase 3 (hot): 1370-1874s (= Phase1 replay)
  const data = [];
  // [Fix-AP-1] 전역 스코프 오염 방지 — generateWLTPProfile의 addPhase와 이름 분리
  function _addPhaseFTP75(t_start, pts) {
    pts.forEach(([t, v]) => {
      data.push({ t: t_start + t, v: v });
    });
  }
  // Phase 1: Cold Start (0-505s) - simplified FTP-75 urban driving
  _addPhaseFTP75(0, [
    [0,0],[10,20],[20,35],[30,55],[40,65],[50,50],[55,30],[60,0],[70,0],
    [80,30],[90,50],[100,70],[110,75],[115,60],[120,40],[125,0],[130,0],
    [140,25],[150,45],[160,55],[165,65],[170,60],[175,45],[178,20],[180,0],
    [190,0],[200,35],[210,60],[220,70],[225,75],[230,80],[235,75],[240,65],
    [245,55],[250,40],[255,20],[258,0],[265,0],[275,30],[285,50],[295,55],
    [300,60],[305,65],[310,55],[315,40],[320,20],[325,0],[335,0],
    [345,25],[355,45],[360,55],[365,60],[370,55],[375,45],[380,30],[385,0],
    [390,0],[400,30],[410,50],[415,60],[420,65],[425,70],[430,65],[435,55],
    [440,45],[445,35],[450,20],[455,0],[465,0],[475,25],[480,35],[485,45],
    [490,50],[495,45],[498,30],[500,10],[505,0],
  ]);
  // Phase 2: Transient (506-1369s) - higher speed, more aggressive
  _addPhaseFTP75(506, [
    [0,0],[10,25],[20,50],[30,70],[40,80],[50,85],[60,80],[70,70],[80,55],
    [85,40],[90,20],[95,0],[105,0],[115,30],[125,55],[135,75],[145,85],
    [150,90],[155,85],[160,75],[165,60],[170,45],[175,25],[180,0],[190,0],
    [200,35],[210,60],[220,80],[230,90],[235,95],[240,90],[245,80],[250,65],
    [255,50],[260,30],[265,10],[270,0],[280,0],[290,30],[300,55],[310,75],
    [315,85],[320,90],[325,85],[330,75],[335,60],[340,45],[345,25],[350,0],
    [360,0],[370,30],[380,55],[390,75],[400,85],[405,90],[410,85],[415,70],
    [420,55],[425,40],[430,20],[435,0],[445,0],[455,35],[465,60],[475,80],
    [480,90],[485,95],[490,90],[495,80],[500,65],[505,50],[510,30],[515,10],
    [520,0],[530,0],[540,25],[550,50],[560,70],[570,80],[575,85],[580,80],
    [585,70],[590,55],[595,40],[600,20],[605,0],[615,0],[625,30],[635,55],
    [645,75],[650,85],[655,80],[660,65],[665,50],[670,35],[675,15],[680,0],
    [690,0],[700,35],[710,60],[720,80],[725,90],[730,85],[735,75],[740,60],
    [745,45],[750,25],[755,0],[763,0],
  ]);
  // Phase 3: Hot Start = Phase 1 replay (1370-1874s)
  const phase1 = data.filter(d => d.t <= 505);
  phase1.forEach(d => data.push({ t: 1370 + d.t, v: d.v }));
  // Fill gaps by linear interpolation
  const result = [];
  for (let t = 0; t <= 1874; t++) {
    const lo = data.filter(d => d.t <= t).pop() || data[0];
    const hi = data.find(d => d.t >= t) || data[data.length - 1];
    if (lo.t === hi.t) {
      result.push({ t, v: lo.v });
    } else {
      const frac = (t - lo.t) / (hi.t - lo.t);
      result.push({ t, v: Math.max(0, lo.v + frac * (hi.v - lo.v)) });
    }
  }
  return result;
}

// ============================================================
// NEW v3: EPA Highway Cycle (HWFET - 765s)
// ============================================================

// ── generateWLTPProfile ──
/**
 * WLTP Class 3b 공식 속도 프로파일을 생성한다 (1800초, 1Hz).
 * Low(0~588s) / Medium(589~1021s) / High(1022~1476s) / Extra-High(1477~1800s) 4구간.
 * ECE/TRANS/WP.29/GRPE 규정 기반 키포인트 선형보간.
 *
 * @returns {{ t: number, v: number }[]}  { t[s], v[km/h] } 배열
 */
function generateWLTPProfile() {
  // WLTP Class 3b speed trace [km/h] at 1Hz sampling
  // Approximated from official WLTP regulation ECE/TRANS/WP.29/GRPE
  // Low phase: avg 18.9 km/h, Med: 39.5, High: 56.7, ExH: 92.0
  const profile = [];

  // ── LOW phase (0–589 s) ────────────────────────────────────
  // Low phase key waypoints [duration_s, v_end_kmh]
  let t = 0;
  const lowPts = [
    [11,0],[3,15],[11,15],[4,0],[12,0],[4,32],[12,32],[4,0],[11,0],
    [4,0],[3,15],[3,32],[8,32],[5,0],[11,0],[3,35],[10,35],[3,0],
    [11,0],[4,50],[12,50],[4,0],[11,0],[4,35],[12,35],[4,0],
    [11,0],[3,26],[9,26],[3,0],[12,0],[4,50],[15,50],[4,0],[11,0],
    [4,38],[9,38],[4,0],[3,0],[4,50],[14,50],[4,0],[10,0],[3,16],
    [8,16],[3,0],[11,0],[4,35],[10,35],[4,0],[11,0],
  ];
  for (const [dur, ve] of lowPts) {
    const v0 = profile.length > 0 ? profile[profile.length-1].v : 0;
    for (let i = 0; i < dur; i++) {
      profile.push({ t, v: +(v0 + (ve - v0) * (dur > 1 ? i/(dur-1) : 0)).toFixed(1) });
      t++;
    }
  }
  // Pad to 589s
  while (t < 589) { profile.push({ t, v: 0 }); t++; }

  // Medium phase (589–1022 s): key acceleration events up to ~76 km/h
  const medTargets = [
    [20,54],[13,54],[4,0],[13,0],[4,56],[25,56],[8,0],[12,0],[4,76],[24,76],
    [8,47],[5,47],[4,0],[13,0],[4,54],[24,54],[11,0],[12,0],[4,69],[22,69],
    [6,0],[4,0],[3,58],[21,58],[8,0],[11,0],[4,68],[25,68],[7,0],[10,0],
  ];
  for (const [dur, ve] of medTargets) {
    const v0 = profile.length > 0 ? profile[profile.length-1].v : 0;
    for (let i = 0; i < dur; i++) {
      profile.push({ t, v: +(v0 + (ve - v0) * (dur > 1 ? i/(dur-1) : 0)).toFixed(1) });
      t++;
    }
  }
  while (t < 1022) { profile.push({ t, v: 0 }); t++; }

  // High phase (1022–1477 s): up to ~97 km/h
  const hiTargets = [
    [15,0],[5,80],[30,80],[10,60],[12,60],[5,0],[11,0],[5,85],[30,85],
    [10,65],[12,65],[5,0],[10,0],[5,90],[25,90],[8,70],[10,70],[5,0],
    [12,0],[5,97],[28,97],[10,75],[12,75],[5,0],[11,0],[5,88],[30,88],
    [10,68],[12,68],[5,0],[10,0],[5,93],[25,93],[8,72],[10,72],[5,0],
  ];
  for (const [dur, ve] of hiTargets) {
    const v0 = profile.length > 0 ? profile[profile.length-1].v : 0;
    for (let i = 0; i < dur; i++) {
      profile.push({ t, v: +(v0 + (ve - v0) * (dur > 1 ? i/(dur-1) : 0)).toFixed(1) });
      t++;
    }
  }
  while (t < 1477) { profile.push({ t, v: 0 }); t++; }

  // Extra-High phase (1477–1800 s): up to ~131 km/h
  const exhTargets = [
    [10,0],[6,100],[30,100],[12,90],[10,90],[8,110],[25,110],[8,100],
    [12,100],[5,80],[8,80],[8,131],[20,131],[10,115],[15,115],[8,100],
    [12,100],[6,90],[10,90],[8,120],[22,120],[10,105],[15,105],[8,95],
    [10,95],[5,80],[8,80],[5,0],
  ];
  for (const [dur, ve] of exhTargets) {
    const v0 = profile.length > 0 ? profile[profile.length-1].v : 0;
    for (let i = 0; i < dur; i++) {
      profile.push({ t, v: +(v0 + (ve - v0) * (dur > 1 ? i/(dur-1) : 0)).toFixed(1) });
      t++;
    }
  }
  while (t < 1800) { profile.push({ t, v: profile[profile.length-1]?.v || 0 }); t++; }

  // [P-1-Fix] WLTP Class 3b 공식 위상 기준 거리로 속도 스케일 정규화 ──────────
  // 문제: waypoint 선형보간은 구간 내 속도 변화를 trapezoidal로 근사하므로
  //       위상별 주행 거리가 규정값(ECE/TRANS/WP.29/GRPE/2021/2 Annex 1)과
  //       최대 ±5% 편차 → 전비(kWh/100 km) 분모 직접 오차로 이어짐.
  // 수정: 각 위상의 생성 거리(사다리꼴 적분)를 규정 거리로 스케일링하여
  //       위상 내 모든 속도값에 동일 배율 k = ref_m / gen_m 를 곱함.
  //       결과: 위상 거리가 규정값에 수렴 → 전비 분모 오차 제거.
  //
  // WLTP Class 3b 공식 위상 기준값 [start_s, end_s, ref_distance_m]
  // 출처: UN Regulation No. 154 Annex B4, Table A4/2
  const WLTP_PHASE_REF = [
    //  [start_s, end_s, ref_dist_m,  phase_name]
    [    0,   589,  3_095,  'Low'       ],
    [  589,  1022,  4_756,  'Medium'    ],
    [ 1022,  1477,  7_158,  'High'      ],
    [ 1477,  1800,  8_254,  'Extra-High'],
  ];
  for (const [s, e, ref_m] of WLTP_PHASE_REF) {
    // 사다리꼴 적분으로 위상 생성 거리 계산 (km/h → m/s: ÷ 3.6, Δt = 1 s)
    let gen_m = 0;
    for (let i = s; i < e - 1; i++) {
      gen_m += (profile[i].v + profile[i + 1].v) / 2 / 3.6; // [m]
    }
    const k = gen_m > 1 ? ref_m / gen_m : 1; // 배율 (gen_m ≈ 0이면 스킵)
    if (Math.abs(k - 1) < 1e-6) continue;    // 이미 일치하면 스킵
    for (let i = s; i < e; i++) {
      // 속도 스케일 후 물리적 상한 200 km/h 클램프
      profile[i] = { t: profile[i].t, v: +Math.min(profile[i].v * k, 200).toFixed(2) };
    }
  }
  // ─────────────────────────────────────────────────────────────────────────────

  return profile.slice(0, 1800);
}

// Memoized WLTP profile (computed once)
let _wltpProfile = null;
// [Fix-③] 공식 1Hz 프로파일 DI 수신용 — injectWLTPOfficialProfile() 호출 후 교체
let _wltpOfficialProfile = null;

/**
 * [Fix-③] WLTP 공식 1Hz 프로파일 주입 (wltp_official.js → ev-sim_v2_2.jsx 에서 호출)
 * generateWLTPProfile() 선형근사 대신 UN R154 Annex B4 공식 데이터 사용
 * @param {Array<{t:number,v:number}>} officialProfile  getWLTPOfficialProfile() 반환값
 */
function injectWLTPOfficialProfile(officialProfile) {
  if (Array.isArray(officialProfile) && officialProfile.length >= 1800) {
    _wltpOfficialProfile = officialProfile;
    _wltpProfile = officialProfile; // 메모화 캐시도 교체
  }
}

// ── generateJC08Profile ──
/**
 * JC08 일본 공인 연비 측정 사이클 속도 프로파일을 생성한다 (1204초, 8.171 km).
 * 저속 도심 위주, 평균 24.4 km/h, 최고 81.6 km/h. 키포인트 선형보간.
 *
 * @returns {{ t: number, v: number }[]}  { t[s], v[km/h] } 배열
 */
function generateJC08Profile() {
  // 대표 속도 프로파일 (keypoints 기반 선형보간)
  const keypoints = [
    [0,0],[10,0],[27,15],[40,0],[50,0],[62,20],[75,40],[90,60],[100,65],
    [110,50],[120,0],[130,0],[148,25],[160,40],[175,50],[185,50],[200,0],
    [220,0],[235,30],[250,50],[270,70],[285,81.6],[300,75],[320,60],
    [340,0],[360,0],[380,40],[400,60],[420,70],[435,60],[450,40],
    [465,0],[480,0],[500,25],[515,40],[530,55],[545,55],[560,30],[575,0],
    [600,0],[620,30],[640,50],[660,60],[680,65],[700,50],[715,0],
    [730,0],[750,20],[765,35],[780,50],[800,60],[820,50],[840,30],
    [855,0],[870,0],[900,30],[920,45],[940,55],[960,65],[975,55],
    [990,40],[1010,0],[1030,0],[1060,35],[1080,50],[1100,65],[1120,55],
    [1140,30],[1160,0],[1180,0],[1195,15],[1204,0],
  ];
  const profile = [];
  for (let i = 0; i < keypoints.length - 1; i++) {
    const [t0, v0] = keypoints[i];
    const [t1, v1] = keypoints[i + 1];
    const steps = t1 - t0;
    for (let s = 0; s < steps; s++) {
      profile.push({ t: t0 + s, v: v0 + (v1 - v0) * s / steps });
    }
  }
  profile.push({ t: 1204, v: 0 });
  return profile;
}

// ──────────────────────────────────────────────────────────────
// CLTC — 중국 경량 차량 시험 사이클 (총 1800초, 14.48 km)
//   도심(0~674s) + 교외(674~1494s) + 고속(1494~1800s)
//   평균 속도 28.96 km/h, 최고 속도 114 km/h
// ──────────────────────────────────────────────────────────────

// ── generateCLTCProfile ──
/**
 * CLTC 중국 경량 차량 시험 사이클 속도 프로파일을 생성한다 (1800초, 14.48 km).
 * 도심(0~674s) + 교외(674~1494s) + 고속(1494~1800s), 평균 28.96 km/h.
 *
 * @returns {{ t: number, v: number }[]}  { t[s], v[km/h] } 배열
 */
function generateCLTCProfile() {
  // Phase 1: 도심 (0~674s) — 저속 반복 정지
  const urban_kp = [
    [0,0],[15,20],[30,35],[45,25],[55,0],[65,0],[80,30],[95,45],
    [110,35],[125,0],[140,0],[158,25],[175,40],[190,30],[205,0],
    [220,0],[238,35],[255,50],[270,40],[285,0],[300,0],[320,40],
    [340,55],[355,45],[375,0],[395,0],[415,35],[435,50],[455,40],
    [470,0],[490,0],[510,30],[525,45],[545,35],[560,0],[580,0],
    [600,40],[620,55],[640,45],[655,0],[674,0],
  ];
  // Phase 2: 교외 (674~1494s) — 중속
  const suburban_kp = [
    [674,0],[700,40],[725,60],[755,70],[785,65],[810,50],[830,0],
    [850,0],[875,45],[900,65],[925,75],[955,70],[985,55],[1005,0],
    [1025,0],[1055,50],[1085,70],[1110,80],[1140,75],[1170,60],
    [1195,0],[1215,0],[1245,55],[1275,72],[1305,80],[1335,70],
    [1365,55],[1390,0],[1410,0],[1440,60],[1470,75],[1494,0],
  ];
  // Phase 3: 고속 (1494~1800s)
  const express_kp = [
    [1494,0],[1515,40],[1535,70],[1555,90],[1575,100],[1600,108],
    [1625,114],[1650,108],[1670,100],[1695,90],[1715,80],[1740,65],
    [1760,50],[1780,30],[1800,0],
  ];
  const allKp = [...urban_kp, ...suburban_kp, ...express_kp];
  const profile = [];
  for (let i = 0; i < allKp.length - 1; i++) {
    const [t0, v0] = allKp[i];
    const [t1, v1] = allKp[i + 1];
    const steps = Math.max(1, Math.round(t1 - t0));
    for (let s = 0; s < steps; s++) {
      profile.push({ t: t0 + s, v: v0 + (v1 - v0) * s / steps, phase: t0 < 674 ? 'Urban' : t0 < 1494 ? 'Suburban' : 'Expressway' });
    }
  }
  profile.push({ t: 1800, v: 0, phase: 'Expressway' });
  return profile;
}

// ──────────────────────────────────────────────────────────────
// RDE — 실도로 배출(Real Driving Emissions) 사이클
//   UN ECE Reg.168 기반 확률적 합성: 도심/농촌/고속도로 세그먼트
//   각 세그먼트: 랜덤 가속·감속·크루즈 이벤트 조합
//   mode: 'urban' | 'rural' | 'motorway' | 'full'
// ──────────────────────────────────────────────────────────────

// ── generateRDEProfile ──
/**
 * RDE(Real Driving Emissions) 주행 사이클 속도 프로파일을 생성한다.
 * mode: 'urban'(0~899s) / 'rural'(900~1499s) / 'motorway'(1500~1799s) / 'full'(1800s).
 * 각 구간은 EU RDE 규정 비율(도심 29~44%, 농촌 23~43%, 고속 23~43%)을 근사.
 *
 * @param {'full'|'urban'|'rural'|'motorway'} [mode='full']  출력 구간
 * @returns {{ t: number, v: number, phase: string }[]}
 */
function generateRDEProfile(mode = 'full') {
  // 시드 기반 유사난수 (재현성 확보)
  let seed = 20240101;
  function lcg() { seed = (seed * 1664525 + 1013904223) & 0xffffffff; return (seed >>> 0) / 0xffffffff; }

  // 세그먼트 정의 (duration_s, v_target, accel_type)
  function makeSegment(v_lo, v_hi, t_seg) {
    const segs = [];
    let t = 0;
    while (t < t_seg) {
      const v_tgt = v_lo + lcg() * (v_hi - v_lo);
      const idle_t = lcg() < 0.25 ? Math.floor(10 + lcg() * 30) : 0;
      const accel_t = Math.max(5, Math.floor(5 + lcg() * 15));
      const cruise_t = Math.floor(10 + lcg() * 40);
      const decel_t = Math.max(4, Math.floor(4 + lcg() * 10));
      segs.push({ idle: idle_t, v: v_tgt, accel: accel_t, cruise: cruise_t, decel: decel_t });
      t += idle_t + accel_t + cruise_t + decel_t;
    }
    return segs;
  }

  // UN RDE 구성: 도심(≤60 km/h, ≥29%), 농촌(60~90, ≥23%), 고속(>90, ≥23%)
  const configs = {
    urban:    { dur: 900,  v_lo: 0,  v_hi: 60  },
    rural:    { dur: 600,  v_lo: 60, v_hi: 90  },
    motorway: { dur: 600,  v_lo: 90, v_hi: 130 },
    full:     null,
  };

  let profile = [];
  let t_cur = 0;

  function buildFromSegs(segs, v_from) {
    let v_cur = v_from;
    for (const seg of segs) {
      // 정지
      for (let i = 0; i < seg.idle; i++) { profile.push({ t: t_cur++, v: 0 }); }
      // 가속
      for (let i = 0; i < seg.accel; i++) {
        v_cur += (seg.v - v_cur) / Math.max(1, seg.accel - i);
        profile.push({ t: t_cur++, v: Math.max(0, v_cur) });
      }
      v_cur = seg.v;
      // 크루즈 (±5% 변동)
      for (let i = 0; i < seg.cruise; i++) {
        const v_noise = seg.v * (0.95 + lcg() * 0.10);
        profile.push({ t: t_cur++, v: v_noise });
      }
      // 감속
      for (let i = 0; i < seg.decel; i++) {
        v_cur = Math.max(0, v_cur * (1 - (i + 1) / seg.decel));
        profile.push({ t: t_cur++, v: v_cur });
      }
      v_cur = 0;
    }
  }

  if (mode === 'full') {
    // 순서: 도심 → 농촌 → 고속
    buildFromSegs(makeSegment(0, 60, 900), 0);
    buildFromSegs(makeSegment(60, 90, 600), 0);
    buildFromSegs(makeSegment(90, 130, 600), 0);
    // phase 태그 추가
    profile = profile.map((d, i) => ({
      ...d,
      phase: i < 900 ? 'Urban' : i < 1500 ? 'Rural' : 'Motorway'
    }));
  } else {
    const c = configs[mode];
    buildFromSegs(makeSegment(c.v_lo, c.v_hi, c.dur), 0);
    const pLabel = mode === 'urban' ? 'Urban' : mode === 'rural' ? 'Rural' : 'Motorway';
    profile = profile.map(d => ({ ...d, phase: pLabel }));
  }

  return profile;
}

// ──────────────────────────────────────────────────────────────
// §RDE_EMISSIONS  RDE 사이클 오염물질(NOx/PM) 배출 계산 체인  [Verification_41 신규]
// 기반: EMEP/EEA Air Pollutant Emission Inventory Guidebook 2023
//       UN GTR19 (타이어 마모 PM 기준)  /  ECE/TRANS/WP.29/2021 RDE 규정
// EV 특성: 배기 NOx = 0, 비배기 PM (브레이크·타이어·도로재부유) + 간접 NOx(전력망)
// ──────────────────────────────────────────────────────────────

// ── generateEPA_CITY_Profile ──
/**
 * EPA 도심 사이클(FTP-72/UDDS) 속도 프로파일을 생성한다 (1369초).
 * 평균 속도 34.1 km/h, 23회 정지. 키포인트 선형보간.
 *
 * @returns {{ t: number, v: number }[]}
 */
function generateEPA_CITY_Profile() {
  // UDDS (FTP-72) 대표 keypoints [시간(s), 속도(km/h)]
  // 출처: EPA 40 CFR Part 86 Appendix I — 1Hz 근사 keypoints
  const keypoints = [
    [0,0],[9,0],[12,16],[18,32],[25,45],[32,56],[40,48],[47,39],
    [54,26],[60,0],[68,0],[72,19],[79,37],[87,48],[97,62],[106,72],
    [113,64],[119,48],[124,35],[128,19],[132,0],[140,0],[145,22],
    [151,36],[158,48],[167,60],[177,74],[188,80],[199,74],[211,64],
    [222,52],[232,38],[241,22],[248,0],[258,0],[263,24],[270,40],
    [278,53],[287,64],[296,56],[303,40],[310,24],[315,0],[323,0],
    [328,21],[335,39],[343,52],[354,65],[367,79],[380,88],[393,91.2],
    [406,85],[418,74],[428,60],[437,45],[444,28],[450,0],[460,0],
    [466,25],[474,42],[483,56],[493,67],[504,76],[514,68],[522,55],
    [529,40],[535,22],[541,0],[550,0],[556,19],[562,35],[568,50],
    [576,60],[585,52],[592,38],[598,22],[603,0],[611,0],[617,24],
    [625,41],[634,55],[644,65],[655,72],[665,62],[673,48],[680,32],
    [686,15],[691,0],[700,0],[706,22],[714,39],[723,52],[732,62],
    [742,54],[750,40],[757,25],[763,10],[767,0],[776,0],[782,20],
    [789,36],[797,48],[808,59],[819,68],[828,76],[838,82],[847,76],
    [856,65],[863,50],[869,33],[874,15],[878,0],[886,0],[892,21],
    [899,38],[908,52],[918,63],[928,72],[937,62],[944,48],[950,32],
    [955,15],[959,0],[967,0],[973,22],[981,39],[990,53],[1001,64],
    [1013,72],[1023,63],[1030,48],[1036,32],[1041,15],[1045,0],
    [1053,0],[1060,21],[1068,38],[1078,52],[1088,63],[1098,56],
    [1106,42],[1113,28],[1119,13],[1123,0],[1130,0],[1136,20],
    [1143,35],[1151,48],[1160,58],[1170,68],[1179,60],[1186,45],
    [1192,30],[1198,15],[1202,0],[1209,0],[1215,22],[1222,38],
    [1230,52],[1241,65],[1252,73],[1261,64],[1268,50],[1274,35],
    [1280,18],[1284,0],[1292,0],[1298,21],[1306,38],[1315,52],
    [1326,64],[1336,56],[1343,42],[1349,27],[1354,12],[1358,0],
    [1366,0],[1369,0],
  ];
  const profile = [];
  for (let i = 0; i < keypoints.length - 1; i++) {
    const [t0, v0] = keypoints[i];
    const [t1, v1] = keypoints[i + 1];
    const steps = Math.max(1, Math.round(t1 - t0));
    for (let s = 0; s < steps; s++) {
      profile.push({ t: t0 + s, v: Math.max(0, v0 + (v1 - v0) * s / steps), phase: 'City' });
    }
  }
  profile.push({ t: 1369, v: 0, phase: 'City' });
  return profile;
}

// Memoized cycle profiles
const _cycleCache = {};

// ── generateEPA_HWY_Profile ──
/**
 * EPA 고속도로 사이클(HWFET) 속도 프로파일을 생성한다 (765초).
 * 평균 속도 77.7 km/h, 최고 96.5 km/h. 키포인트 선형보간.
 *
 * @returns {{ t: number, v: number }[]}
 */
function generateEPA_HWY_Profile() {
  const pts = [
    [0,0],[10,30],[20,55],[30,75],[40,90],[50,100],[60,100],[80,105],
    [100,100],[110,95],[120,100],[140,100],[160,95],[170,90],[180,100],
    [200,100],[210,105],[220,100],[230,95],[240,90],[250,100],[270,100],
    [280,95],[290,100],[310,100],[320,105],[340,100],[350,95],[360,90],
    [380,95],[390,100],[410,100],[420,95],[440,100],[450,105],[460,100],
    [480,95],[500,100],[520,100],[540,95],[550,90],[570,100],[580,100],
    [590,95],[600,90],[620,95],[630,100],[640,100],[650,95],[660,90],
    [680,95],[700,100],[720,95],[740,90],[750,80],[760,50],[765,0],
  ];
  const result = [];
  for (let t = 0; t <= 765; t++) {
    const lo = pts.filter(d => d[0] <= t).pop() || pts[0];
    const hi = pts.find(d => d[0] >= t) || pts[pts.length - 1];
    if (lo[0] === hi[0]) {
      result.push({ t, v: lo[1] });
    } else {
      const frac = (t - lo[0]) / (hi[0] - lo[0]);
      result.push({ t, v: Math.max(0, lo[1] + frac * (hi[1] - lo[1])) });
    }
  }
  return result;
}

// [A23-FIX-3] EPA City (UDDS — Urban Dynamometer Driving Schedule, 1369s, 12.07km)
//   수정 전: FTP75 앞 1369초를 단순 슬라이스 → phase 태깅 없음, 프로파일 부정확
//   수정 후: UDDS 공식 대표 keypoint 기반 선형보간 프로파일 + phase:'City' 내장
//   특성: 평균 속도 31.5 km/h, 최고 속도 91.2 km/h, 23회 정지

// ── getCycleProfile ──
/**
 * 사이클 유형 문자열로 속도 프로파일을 반환한다 (캐시 적용).
 * 지원: FTP75, EPA_HWY, EPA_CITY, JC08, CLTC, RDE_URBAN/RURAL/MOTORWAY/FULL, NEDC, RDE_STOCHASTIC.
 * WLTP는 별도 getWLTPProfile()로 처리; 미지원 사이클은 null 반환.
 *
 * @param {string} cycle_type
 * @returns {{ t: number, v: number }[]|null}
 */
function getCycleProfile(cycle_type) {
  if (_cycleCache[cycle_type]) return _cycleCache[cycle_type];
  let profile;
  if (cycle_type === 'FTP75') profile = generateFTP75Profile();
  else if (cycle_type === 'EPA_HWY') profile = generateEPA_HWY_Profile();
  else if (cycle_type === 'EPA_CITY') profile = generateEPA_CITY_Profile();
  else if (cycle_type === 'JC08')    profile = generateJC08Profile();
  else if (cycle_type === 'CLTC')    profile = generateCLTCProfile();
  else if (cycle_type === 'RDE_URBAN') profile = generateRDEProfile('urban');
  else if (cycle_type === 'RDE_RURAL') profile = generateRDEProfile('rural');
  else if (cycle_type === 'RDE_MOTORWAY') profile = generateRDEProfile('motorway');
  else if (cycle_type === 'RDE_FULL')  profile = generateRDEProfile('full');
  else if (cycle_type === 'NEDC')      profile = generateNEDCProfile();          // ✅ RevB
  else if (cycle_type === 'RDE_STOCHASTIC') profile = generateRDEProfileStochastic(); // ✅ RevB
  else profile = null; // WLTP handled separately
  _cycleCache[cycle_type] = profile;
  return profile;
}

// ════════════════════════════════════════════════════════════════
// §  [R-14] 신규 주행 사이클 프로파일 생성기
// ════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────
// JC08 — 일본 공인 연비 측정 사이클 (총 1204초, 8.171 km)
//   저속 도심 위주, 평균 속도 24.4 km/h, 최고 속도 81.6 km/h
// ──────────────────────────────────────────────────────────────

// ── getWLTPProfile ──
/**
 * WLTP 프로파일을 캐시에서 반환하거나 최초 생성 후 캐시한다.
 *
 * @returns {{ t: number, v: number }[]}
 */
function getWLTPProfile() {
  if (!_wltpProfile) _wltpProfile = generateWLTPProfile();
  return _wltpProfile;
}

// Phase tag for WLTP time index

// ── wltpPhaseTag ──
/**
 * WLTP 시각 t[s]에 해당하는 구간 레이블을 반환한다.
 * Low(<589) / Medium(<1022) / High(<1477) / Extra-High.
 *
 * @param {number} t  시뮬레이션 시각 [s]
 * @returns {string}
 */
function wltpPhaseTag(t) {
  if (t < 589) return 'Low';
  if (t < 1022) return 'Medium';
  if (t < 1477) return 'High';
  return 'Extra-High';
}

// ============================================================
// ① WLTP 사이클 시뮬레이션 고도화
// ────────────────────────────────────────────────────────────
// WLTP Class-3 속도 프로파일 기반 완전 에너지 적분:
//
//  속도 추종 가속도 명령:
//    a(t) = (v_WLTP(t+dt) − v_sim(t)) / dt  [m/s²]
//    → PI 제어: demand = Kp·e_v + Ki·∫e_v dt  [−1, +1]
//
//  에너지 소비 (전 사이클 적분):
//    E_drive = ∫ P_batt(t) dt    [J]   (구동 방전, aux 포함)
//    E_regen = ∫ P_chg(t)  dt    [J]   (회생 충전)
//    E_net   = E_drive − E_regen  [J]
//    E_WLTP  = E_net / 3,600,000  [kWh]
//
//  전비 (Specific Energy Consumption):
//    e = E_WLTP / d_WLTP × 100   [kWh/100 km]
//    d_WLTP ≈ 23.27 km (Class 3, 1800 s)
//
//  CO₂ 환산 (Well-to-Wheel 전력 배출계수 기반):
//    CO2_eq = e × EF_grid / 100  [g CO₂/km]
//    EF_grid : 전력망 탄소 배출 계수 [g CO₂/kWh]
//             한국 2024 ≈ 415, EU 평균 ≈ 280, 재생에너지 ≈ 50
//
//  항속거리 추정:
//    range = battery_energy × SOC_usable / e × 100  [km]
//
//  Phase별 분해: Low / Medium / High / Extra-High
//  열 디레이팅: T_batt, T_inv, T_motor → 출력 제한 곡선
// ============================================================

// ── jc08PhaseTag ──
/**
 * JC08 시각 t[s]에 해당하는 구간 레이블 반환.
 *
 * @param {number} t  [s]
 * @returns {'Low'|'Medium'|'High'}
 */
function jc08PhaseTag(t) {
  if (t < 400) return 'Low';
  if (t < 800) return 'Medium';
  return 'High';
}

// Generic phase tag: uses profile's embedded .phase field if available
// [A23-FIX-2] EPA_HWY / EPA_CITY / JC08 / RDE_URBAN / RDE_RURAL / RDE_MOTORWAY 누락 케이스 추가
//   수정 전: 이 6개 사이클은 모두 'Main'으로만 태깅 → DC3 Phase 막대 전부 0
//   수정 후: 각 사이클 특성에 맞는 Phase명 반환

// ── cltcPhaseTag ──
/**
 * CLTC 시각 t[s]에 해당하는 구간 레이블 반환.
 *
 * @param {number} t  [s]
 * @returns {'Urban'|'Suburban'|'Expressway'}
 */
function cltcPhaseTag(t) {
  if (t < 674) return 'Urban';
  if (t < 1494) return 'Suburban';
  return 'Expressway';
}

// Phase tag for RDE Full

// ── ftp75PhaseTag ──
/**
 * FTP-75 시각 t[s]에 해당하는 구간 레이블 반환.
 *
 * @param {number} t  [s]
 * @returns {'Cold-Start'|'Transient'|'Hot-Start'}
 */
function ftp75PhaseTag(t) {
  if (t < 505) return 'Cold-Start';
  if (t < 1370) return 'Transient';
  return 'Hot-Start';
}

// Phase tag for CLTC

// ── rdePhaseTag ──
/**
 * RDE Full 시각 t[s]에 해당하는 구간 레이블 반환.
 *
 * @param {number} t  [s]
 * @returns {'Urban'|'Rural'|'Motorway'}
 */
function rdePhaseTag(t) {
  if (t < 900) return 'Urban';
  if (t < 1500) return 'Rural';
  return 'Motorway';
}

// [A23-FIX-2] Phase tag for JC08 (일본 1204s — 시간 기반 3구간 분할)
//   Low: 0~399s (저속 도심), Medium: 400~799s (중속), High: 800~1204s (고속)

// ── genericPhaseTag ──
/**
 * 사이클 유형·시각·프로파일 포인트로 구간 레이블을 반환한다.
 * profile_point.phase 우선; 없으면 사이클별 PhaseTag 함수 위임.
 * [A23-FIX-2] EPA_HWY/CITY, RDE_URBAN/RURAL/MOTORWAY 단일 구간 지원 추가.
 *
 * @param {string} cycle_type
 * @param {number} t                시각 [s]
 * @param {object} [profile_point]  { phase? }
 * @returns {string}  구간 레이블
 */
function genericPhaseTag(cycle_type, t, profile_point) {
  // profile에 .phase가 내장된 경우 우선 사용 (CLTC, RDE_URBAN/RURAL/MOTORWAY/FULL 등)
  if (profile_point && profile_point.phase) return profile_point.phase;
  if (cycle_type === 'FTP75')        return ftp75PhaseTag(t);
  if (cycle_type === 'CLTC')         return cltcPhaseTag(t);
  if (cycle_type === 'RDE_FULL')     return rdePhaseTag(t);
  if (cycle_type === 'JC08')         return jc08PhaseTag(t);    // [A23-FIX-2]
  if (cycle_type === 'EPA_HWY')      return 'Highway';          // [A23-FIX-2] 단일 고속 구간
  if (cycle_type === 'EPA_CITY')     return 'City';             // [A23-FIX-2] 단일 도심 구간
  if (cycle_type === 'RDE_URBAN')    return 'Urban';            // [A23-FIX-2] profile.phase 백업
  if (cycle_type === 'RDE_RURAL')    return 'Rural';            // [A23-FIX-2]
  if (cycle_type === 'RDE_MOTORWAY') return 'Motorway';         // [A23-FIX-2]
  return 'Main';
}

// ============================================================
// NEW v3 + [R-14]: Generic Driving Cycle Simulation Kernel
// Wraps WLTP + FTP-75 + EPA + JC08 + CLTC + RDE under uniform interface
// ============================================================

// ── [Step5] initDriverMPCState ──────────────────────────────────────────────
/**
 * initDriverMPCState — 드라이버 MPC 제어기 초기 상태
 *
 * 상태: 종방향 속도 v [m/s]
 * 입력: 구동력 F [N] (throttle 변환은 호출 측에서 처리)
 * 예측 지평 N 스텝, 샘플링 시간 dt
 */
// ── [CascadePID] initCascadeSpeedState ──────────────────────────────────────
/**
 * initCascadeSpeedState — Cascade 속도 PI 루프 상태 초기화
 *
 * Cascade 3단 구조:
 *   L3 드라이버(gs_kp/gs_ki, 외부) → L2 속도 PI(Kp_spd/Ki_spd, 중간) → L1 FOC 전류 PI(내부)
 *
 * L2 속도 PI 상태:
 *   - integral: T_cmd 적분 누산 [Nm·s]
 *   - prev_err:  이전 v_err (미분 필터용)
 *
 * DriveMode 배율(ECO 0.6× ~ SPORT 1.5×)은 _applyDriveModeGainsInline()이
 * 이미 Kp_spd/Ki_spd 에 반영한 뒤 전달되므로 별도 모드 인스턴스 불필요.
 */
function initCascadeSpeedState() {
  return {
    integral:  0,   // 적분 누산 [Nm·s]
    prev_err:  0,   // 이전 속도 오차 [m/s]
  };
}

/**
 * cascadeSpeedLoopStep — L2 속도 PI 1스텝
 *
 * v_target → T_cmd [Nm] 변환.
 * T_cmd는 T_max_motor 범위로 클램프 후 throttle 등가값으로 반환.
 *
 * @param {number} v_target  목표 속도 [m/s]
 * @param {number} v_actual  현재 속도 [m/s]
 * @param {object} spd_state initCascadeSpeedState 반환값
 * @param {object} params    effectiveParams (Kp_spd/Ki_spd 이미 드라이브모드 배율 적용됨)
 * @param {number} dt        시뮬 스텝 [s]
 * @returns {{ T_cmd, pedal_pos, brake_demand, new_state, bw_sep_warn }}
 *   - T_cmd        : 속도 PI 출력 토크 지령 [Nm]
 *   - pedal_pos    : T_cmd를 [0,1] 정규화한 throttle 등가값
 *   - brake_demand : 음의 T_cmd → 제동 요구 [0,1]
 *   - new_state    : 갱신된 상태 객체
 *   - bw_sep_warn  : BW 분리비 < 5 경고 플래그 (진단용)
 */
function cascadeSpeedLoopStep(v_target, v_actual, spd_state, params, dt) {
  const Kp_s  = params.Kp_spd || 143.4;
  const Ki_s  = params.Ki_spd || 3003;
  const i_lim = params.cascade_spd_i_limit || 400;  // anti-windup [Nm]
  const T_max = params.T_max_motor || 450;

  const err   = v_target - v_actual;   // [m/s]

  // anti-windup 클램프 적분
  const integral_new = Math.max(-i_lim, Math.min(i_lim,
    spd_state.integral + err * dt
  ));

  // PI 출력 → T_cmd [Nm]
  let T_cmd = Kp_s * err + Ki_s * integral_new;

  // 토크 포화 시 적분 back-calculation (조건부 적분 중지)
  const T_cmd_raw = T_cmd;
  T_cmd = Math.max(-T_max, Math.min(T_max, T_cmd));
  // 포화 중이면 적분 누산 억제 (windup 방지)
  const integral_final = (T_cmd_raw !== T_cmd && Math.sign(err) === Math.sign(T_cmd_raw))
    ? spd_state.integral   // 포화 & 같은 방향 → 적분 동결
    : integral_new;

  const new_state = {
    integral: integral_final,
    prev_err: err,
  };

  // throttle 등가 변환 [0,1] + 제동 분리
  const pedal_raw    = T_cmd / Math.max(T_max, 1);
  const pedal_pos    = Math.max(0, Math.min(1.0, pedal_raw));
  const brake_demand = Math.max(0, Math.min(1.0, -pedal_raw * 0.6));

  // ── BW 분리비 진단 (계산 비용 낮음, 매 스텝 경량 체크) ──
  // wci = Kp_id / Ld,  wcs = sqrt(Kp_spd * wci / J_eq)
  // sep_si = wci / wcs  → 권장 ≥ 5
  const Ld    = params.Ld    || 0.0003;
  const Kp_i  = params.Kp_id || 1.51;
  const m     = params.m     || 1850;
  const gr    = params.gear_ratio || 9.5;
  const rw    = params.r_wheel   || 0.33;
  const Jm    = params.J_motor   || 0.05;
  const J_eq  = Jm + m * rw * rw / (gr * gr);
  const wci   = Kp_i / Ld;
  const wcs   = Math.sqrt(Kp_s * wci / J_eq);
  const bw_sep_warn = (wci / Math.max(wcs, 1)) < 5;

  return { T_cmd, pedal_pos, brake_demand, new_state, bw_sep_warn };
}

/**
 * 드라이버 MPC 제어기 초기 상태를 생성한다.
 *
 * @returns {{ u_prev: number, pedal_prev: number, t_filter: number }}
 */
function initDriverMPCState() {
  return {
    u_prev:   0,     // 이전 최적 입력 [N] (warm-start)
    pedal_prev: 0,   // 이전 페달 출력 (Jerk 제한용)
    t_filter: 0,     // 1차 지연 필터 상태
  };
}

/**
 * driverMPCController — receding horizon MPC 드라이버 제어기
 *
 * 모델: 종방향 단일 적분기 (마찰/공기저항 비선형 포함)
 *   v[k+1] = v[k] + dt/m · (F[k] - F_res(v[k]))
 *   F_res(v) = 0.5·ρ·CdA·v² + Crr·m·g
 *
 * 비용함수 (예측 지평 N):
 *   J = Σ q·(v_ref - v[k])² + r·(ΔF[k])²
 *
 * QP 최적화: 후퇴 지평 gradient descent (10회 반복)
 *   — 전체 지평 ΔF 벡터를 gradient로 업데이트
 *   — ΔF 제약: |ΔF[k]| ≤ dF_max
 *   — F 제약:  |F[k]| ≤ F_max
 *
 * @param {number} v_target   — 목표 속도 [m/s]
 * @param {number} v_actual   — 현재 속도 [m/s]
 * @param {object} mpc_state  — initDriverMPCState 반환값
 * @param {object} params
 * @param {number} dt         — 시뮬레이션 스텝 [s]
 * @returns {{ pedal_pos, brake_demand, new_state, cost, u_opt, n_iter }}
 */
function driverMPCController(v_target, v_actual, mpc_state, params, dt) {
  const N      = Math.min(params.mpc_N      || 10, 30);   // 예측 지평
  const q      = params.mpc_q_pos           || 100;        // 속도 오차 가중치
  const r_u    = params.mpc_r_u             || 1.0;        // 입력 변화율 가중치
  const dF_max = (params.mpc_dU_max         || 50) * (params.gear_ratio || 9.5) / (params.r_wheel || 0.33);  // [N]
  const F_max  = (params.T_max_motor        || 450) * (params.gear_ratio || 9.5) / (params.r_wheel || 0.33); // [N]

  const m   = params.m    || 2000;
  const CdA = params.CdA  || 0.65;
  const Crr = params.Crr  || 0.010;
  const rho = params.rho  || 1.2;

  // 저속 안전장치: v=0 근처 공기저항 폭발 방지
  const F_res = (v) => 0.5 * rho * CdA * v * v + Crr * m * g;

  // ── gradient descent QP (후퇴 지평) ──────────────────────────────────────
  // ΔF 시퀀스 초기화 (warm-start: 이전 최적 입력 기반)
  let dF = new Array(N).fill(0);
  const alpha_gd = 1e-5;   // gradient step 크기
  const n_iter   = 10;      // 반복 횟수

  let u_opt = mpc_state.u_prev;
  let cost_final = 0;

  for (let iter = 0; iter < n_iter; iter++) {
    // ① Forward pass: ΔF → F → v 예측
    const F_seq = new Array(N);
    const v_seq = new Array(N + 1);
    v_seq[0] = v_actual;
    let F_k = u_opt;
    for (let k = 0; k < N; k++) {
      F_k = Math.max(-F_max, Math.min(F_max, F_k + dF[k]));
      F_seq[k] = F_k;
      const a_k = (F_k - F_res(Math.max(0, v_seq[k]))) / m;
      v_seq[k + 1] = Math.max(0, v_seq[k] + a_k * dt);
    }

    // ② 비용 계산
    let cost = 0;
    for (let k = 0; k < N; k++) {
      const e_v = v_target - v_seq[k + 1];
      cost += q * e_v * e_v + r_u * dF[k] * dF[k];
    }
    cost_final = cost;

    // ③ gradient 계산 (수치 미분: 중앙차분)
    const eps = dF_max * 0.01;
    for (let k = 0; k < N; k++) {
      const dF_plus = dF.slice();
      dF_plus[k] += eps;
      const dF_minus = dF.slice();
      dF_minus[k] -= eps;

      // cost(dF + eps_k) 계산
      let cost_p = 0, F_p = u_opt;
      const v_p = [v_actual];
      for (let j = 0; j < N; j++) {
        const f = Math.max(-F_max, Math.min(F_max, F_p + dF_plus[j]));
        F_p = f;
        const a = (f - F_res(Math.max(0, v_p[j]))) / m;
        v_p.push(Math.max(0, v_p[j] + a * dt));
        cost_p += q * (v_target - v_p[j + 1]) ** 2 + r_u * dF_plus[j] ** 2;
      }

      // cost(dF - eps_k) 계산
      let cost_m = 0, F_m = u_opt;
      const v_m = [v_actual];
      for (let j = 0; j < N; j++) {
        const f = Math.max(-F_max, Math.min(F_max, F_m + dF_minus[j]));
        F_m = f;
        const a = (f - F_res(Math.max(0, v_m[j]))) / m;
        v_m.push(Math.max(0, v_m[j] + a * dt));
        cost_m += q * (v_target - v_m[j + 1]) ** 2 + r_u * dF_minus[j] ** 2;
      }

      const grad_k = (cost_p - cost_m) / (2 * eps);
      // gradient descent 업데이트 + ΔF 제약 클램프
      dF[k] = Math.max(-dF_max, Math.min(dF_max, dF[k] - alpha_gd * grad_k));
    }
  }

  // ── receding horizon: 첫 번째 입력만 적용 ────────────────────────────────
  u_opt = Math.max(-F_max, Math.min(F_max, u_opt + dF[0]));

  // ── 페달 변환 (F → [0,1] throttle 또는 [-1,0] 제동) ─────────────────────
  const tau        = params.drv_pedal_lag  || 0.12;
  const max_jerk   = params.drv_max_jerk   ? params.drv_max_jerk * dt / g : 0.08;
  const raw_pedal  = u_opt / Math.max(F_max, 1);   // [-1, 1] 정규화

  const alpha      = dt / (tau + dt);
  const t_filter_new = mpc_state.t_filter + alpha * (raw_pedal - mpc_state.t_filter);
  let pedal_clamped  = Math.max(-1.0, Math.min(1.0, t_filter_new));

  const dpedal = pedal_clamped - mpc_state.pedal_prev;
  if (Math.abs(dpedal) > max_jerk) {
    pedal_clamped = mpc_state.pedal_prev + Math.sign(dpedal) * max_jerk;
  }

  const new_state = {
    u_prev:     u_opt,
    pedal_prev: pedal_clamped,
    t_filter:   t_filter_new,
  };

  const pedal_pos    = Math.max(0, Math.min(1.0, pedal_clamped));
  const brake_demand = Math.max(0, Math.min(1.0, -pedal_clamped * 0.6));

  return { pedal_pos, brake_demand, new_state, cost: +cost_final.toFixed(1), u_opt, n_iter };
}

// ── initDriverPIDState ──
/**
 * 드라이버 PID 제어기 초기 상태를 생성한다.
 *
 * @returns {{ integral: number, prev_error: number, pedal_prev: number, t_filter: number }}
 */
function initDriverPIDState() {
  return {
    integral:    0,      // 적분 누산
    prev_error:  0,      // 이전 오차 (미분용)
    pedal_prev:  0,      // 이전 페달 값 (Jerk 제한용)
    t_filter:    0,      // 1차 지연 필터 상태
  };
}

/**
 * driverPIDController(v_target, v_actual, drv_state, params, dt)
 * PID 기반 드라이버 페달 모델
 *   - 출력: pedal_pos [0~1] (가속), brake_demand [0~1] (제동)
 *   - Jerk 제한(drv_max_jerk), 드라이버 지연(drv_pedal_lag), anti-windup 포함
 */

// ── driverPIDController ──
function driverPIDController(v_target, v_actual, drv_state, params, dt) {
  const Kp = params.Kp_drv  || 0.8;
  const Ki = params.Ki_drv  || 0.15;
  const Kd = params.Kd_drv  || 0.05;
  const i_lim = params.drv_int_limit || 1.5;
  const tau   = params.drv_pedal_lag  || 0.12;
  const max_jerk_pct = params.drv_max_jerk ? params.drv_max_jerk * dt / (params.g || 9.81) : 0.08;

  const err  = v_target - v_actual;        // [m/s]
  const derr = (err - drv_state.prev_error) / Math.max(dt, 1e-4);

  // anti-windup: 적분 항 제한
  const integral_new = Math.max(-i_lim, Math.min(i_lim,
    drv_state.integral + err * dt
  ));

  const raw_pedal = Kp * err + Ki * integral_new + Kd * derr;

  // 1차 지연 필터 (드라이버 근육 반응 지연)
  const alpha = dt / (tau + dt);
  const t_filter_new = drv_state.t_filter + alpha * (raw_pedal - drv_state.t_filter);

  // Jerk 제한: 페달 변화율 제한
  let pedal_clamped = Math.max(-1.0, Math.min(1.5, t_filter_new));
  const dpedal = pedal_clamped - drv_state.pedal_prev;
  if (Math.abs(dpedal) > max_jerk_pct) {
    pedal_clamped = drv_state.pedal_prev + Math.sign(dpedal) * max_jerk_pct;
  }

  const new_state = {
    integral:   err > 0 && integral_new >= i_lim  ? i_lim  :
                err < 0 && integral_new <= -i_lim ? -i_lim : integral_new,
    prev_error: err,
    pedal_prev: pedal_clamped,
    t_filter:   t_filter_new,
  };

  // 가속/제동 분리
  const pedal_pos    = Math.max(0, Math.min(1.0, pedal_clamped));
  const brake_demand = Math.max(0, Math.min(1.0, -pedal_clamped * 0.6));

  return { pedal_pos, brake_demand, new_state, raw_pedal };
}

// ============================================================
// [R-10-02] GAP-10: 다단 기어 변속 모델 (2-speed / AMT)
// ============================================================
/**
 * initGearState(params)
 * 변속기 상태 초기화
 */

// ============================================================
// PRIORITY 1 ENHANCEMENTS: MTPV (Maximum Torque Per Voltage)
// ============================================================
// [Fix-2] init2RCBatteryState / initRLSMuState 스텁 제거
// → battery.js / tire.js 정규 구현을 import해 사용 (파일 상단 import 참조)

// ── simMonteCarloAsync ──
function simMonteCarloAsync(params, options = {}) {
  const nRuns      = options.nRuns      ?? (typeof options === 'number' ? options : 300);
  const onProgress = options.onProgress ?? (typeof arguments[2] === 'function' ? arguments[2] : null);
  const lightweight = options.lightweight ?? true; // 기본: proxy 계산 (빠름)
  const chunkSize  = options.chunkSize  ?? (lightweight ? 50 : 10);
  const cycle_type = options.cycle_type ?? (params.cycle_type || 'WLTP');

  // 불확실 파라미터 σ값 (사이클별 조정)
  const RDE_EXTRA = cycle_type.startsWith('RDE') ? { grade_deg: 0.5, mu: 0.05 } : {};
  const UNCERTAIN = {
    CdA:             0.03,   // ±3%  공력 계수
    Crr:             0.05,   // ±5%  구름 저항
    m:               0.02,   // ±2%  질량
    eta_motor_inv:   0.015,  // ±1.5% 모터 효율
    R_batt:          0.10,   // ±10% 배터리 내부저항
    aux_power:       0.15,   // ±15% 보조기기 소비
    T_amb:           0.08,   // ±8%  환경 온도 변동
    ...RDE_EXTRA,
  };

  // Box-Muller 정규 난수
  const randn = () => {
    let u, v;
    do { u = Math.random(); v = Math.random(); } while (u === 0);
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  // 경량 proxy 계산 (단일 스텝 추정)
  // [Fix-PR-1] 전역 proxyRun(p, cycle_type)과 이름 충돌 방지 → _proxyRunLightweight로 명확화
  function _proxyRunLightweight(p) {
    const v_avg_ms = (
      cycle_type === 'JC08'       ? 24.4 :
      cycle_type === 'CLTC'       ? 28.96 :
      cycle_type.startsWith('RDE_URBAN') ? 20 :
      cycle_type.startsWith('RDE_RURAL') ? 67 :
      cycle_type.startsWith('RDE_MOTORWAY') ? 100 :
      cycle_type.startsWith('RDE') ? 42 :
      cycle_type === 'EPA_HWY'    ? 77.7 :
      cycle_type === 'EPA_CITY'   ? 34.1 :
      46.5  // WLTP
    ) / 3.6;

    // [ENV-FIX-03] rho=1.225 하드코딩 → p.rho 사용 (UI에서 T_amb·altitude로 이미 갱신된 값)
    const rho = p.rho || 1.225;
    const F_aero = 0.5 * rho * p.CdA * v_avg_ms * v_avg_ms;
    const F_roll = p.Crr * p.m * g;
    const F_road = F_aero + F_roll;
    const P_wheel = F_road * v_avg_ms;
    const eta_chain = (p.eta_driveline || 0.95) * (p.eta_motor_inv || 0.91) * 0.97;
    const P_elec = P_wheel / eta_chain + (p.aux_power || 1500);
    const kWh_100km = P_elec / 1000 / (v_avg_ms * 3.6) * 100;
    const _soc_rng1 = (p.wltp_soc_usable_max || 0.90) - (p.wltp_soc_usable_min || 0.10); // [B-2 Fix]
    const range_km = p.battery_energy > 0 ? p.battery_energy * _soc_rng1 / kWh_100km * 100 : 0;
    return { e_wltp: +kWh_100km.toFixed(2), range_km: +range_km.toFixed(0) };
  }

  return new Promise((resolve) => {
    const results = [];
    let i = 0;

    // 중간 통계 계산 (onProgress에 전달)
    function partialStats(arr) {
      if (arr.length < 2) return { mean: 0, sigma: 0 };
      const vals = arr.map(r => r.e_wltp);
      const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
      const sigma = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length);
      return { mean: +mean.toFixed(3), sigma: +sigma.toFixed(3) };
    }

    function runChunk() {
      const end = Math.min(i + chunkSize, nRuns);
      for (; i < end; i++) {
        const p = { ...params };
        for (const [key, sigmaRatio] of Object.entries(UNCERTAIN)) {
          if (p[key] !== undefined) {
            // 절대값 파라미터(T_amb, grade_deg)는 덧셈 노이즈
            if (key === 'T_amb' || key === 'grade_deg') {
              p[key] = p[key] + (p[key] || 0) * sigmaRatio * randn();
            } else {
              p[key] = p[key] * (1 + sigmaRatio * randn());
            }
          }
        }
        try {
          // 드라이브 모드 게인 적용 후 시뮬레이션 (노이즈 변동 후에도 모드 게인 보존)
          const r = lightweight ? _proxyRunLightweight(p) : simWLTP({ ..._applyDriveModeGainsInline(p), cycle_type });
          if (r && isFinite(r.e_wltp) && r.e_wltp > 0) {
            results.push({ e_wltp: r.e_wltp, range_km: r.range_km || 0 });
          }
        } catch (_) { /* skip failed runs silently */ }
      }

      // [R-14] onProgress: ratio + 부분 통계 전달
      if (onProgress) {
        const ps = partialStats(results);
        onProgress({ ratio: i / nRuns, ...ps, done: false, n_done: i });
      }

      if (i < nRuns) {
        setTimeout(runChunk, 0); // UI에 제어권 반환 (non-blocking)
      } else {
        // 최종 통계
        const vals = results.map(r => r.e_wltp).sort((a, b) => a - b);
        const n    = vals.length;
        if (n === 0) { resolve({ results:[], stats:{mean:0,sigma:0,p5:0,p95:0,n:0}, histogram:[] }); return; }
        const mean = vals.reduce((s, v) => s + v, 0) / n;
        const sigma = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / n);
        const p5   = vals[Math.max(0, Math.floor(n * 0.05))];
        const p95  = vals[Math.min(n - 1, Math.floor(n * 0.95))];
        // 히스토그램 (20 bins)
        const lo = vals[0], hi = vals[n - 1];
        const binW = (hi - lo) / 20 || 0.1;
        const histogram = Array.from({ length: 20 }, (_, b) => {
          const lo_b = lo + b * binW, hi_b = lo_b + binW;
          return { x: +((lo_b + hi_b) / 2).toFixed(2), count: vals.filter(v => v >= lo_b && v < hi_b).length };
        });
        // 범위별 카운트
        const range_vals = results.map(r => r.range_km).sort((a, b) => a - b);
        const range_mean = range_vals.reduce((s, v) => s + v, 0) / n;

        const finalStats = {
          mean: +mean.toFixed(3), sigma: +sigma.toFixed(3),
          p5: +p5.toFixed(3), p95: +p95.toFixed(3), n,
          range_mean: +range_mean.toFixed(0),
          range_p5:   +range_vals[Math.floor(n * 0.05)].toFixed(0),
          range_p95:  +range_vals[Math.floor(n * 0.95)].toFixed(0),
          cycle_type,
        };
        if (onProgress) onProgress({ ratio: 1.0, ...partialStats(results), done: true, n_done: n });
        resolve({ results, stats: finalStats, histogram });
      }
    }

    setTimeout(runChunk, 0);
  });
}








// Thermal state update (4-node: batt, inv, motor, coolant)
// ── [R-13] thermalStep v04: coolantFlowControl.phi_flow → UA_rad 방열 가중치 반영 ──
// phi_flow 없을 시 기존 속도 의존 phi만 사용 (하위 호환성 유지)

// ── simLaunch ──
/**
 * 발진 시뮬레이션을 실행한다 (dt=5 ms, v=0 → v_target).
 * FOC 전류 제어, 2RC 배터리, 열 모델, EKF/UKF SOC, 타이어 Pacejka, ESC/TCS,
 * AWD 토크 벡터링, 7DOF 동역학, 서스펜션, β EKF, SOH 등 전체 파이프라인 포함.
 * [Fix-L1] road_condition → mu_eff 초기화로 노면별 발진 성능 정확 반영.
 *
 * @param {object} params       차량 파라미터
 * @param {number} throttle     스로틀 개도 [0~1]
 * @param {number} v_target     목표 속도 [km/h]
 * @param {number} grade_deg    경사도 [°]
 * @returns {{ data, time, distance, E_net_kWh, ... }}
 */
function simLaunch(params, throttle, v_target, grade_deg) {
  const dt = 0.005, dt_th = 0.05;
  const grade_rad = (grade_deg * Math.PI) / 180;
  // [Fix-L1] road_condition µ → mu_eff: 습윤/빙설 노면에서 접지한계 실제 적용
  //   수정 전: mu_eff 계산 후 미사용 → mu_hat = params.mu_ref_nominal (=1.0 dry) 고정
  //            → wet/ice/snow 노면 선택해도 traction clamp에 반영 안 됨
  //   수정 후: mu_hat 초기값을 mu_eff (road_condition 반영)로 시드
  //            RLS 추정기도 road_condition 기반 초기값으로 출발 → 노면별 발진 성능 차이 정확 반영
  const mu_eff = ROAD_MU[params.road_condition] || params.mu || 1.0; // [Fix-L1]
  let v = 0.1, s = 0, soc = params.soc_init, t = 0;
  let Id = 0, Iq = 0;
  // ── [Step1] motorFOCStep 상태 (initFOCState 폴백: motor.js 미연결 환경 대비) ──
  let foc_state_sl = (_initFOCState !== null) ? _initFOCState() : { Id:0, Iq:0, int_d:0, int_q:0, rep_d_state:null, rep_q_state:null };
  // [D3-DTC] deadtime 보상 전압 손실 추적 (foc_current_pi_enable 블록 스코프 밖 접근용)
  let dtc_V_loss_sl = 0;
  // [D6-STR] 조향 피드백 모델 상태 — dynamics.js steeringFeedbackModel 패리티
  let steer_state_sl = { delta_sw: 0, delta_sw_dot: 0, T_driver: 0 };
  let thState = { T_batt: params.T_amb, T_inv: params.T_amb, T_motor: params.T_amb, T_cool: params.T_amb };
  
  // Priority 2/3 상태 초기화
  let battery_2rc = params.battery_model === '2RC' ? init2RCBatteryState(params) : null;
  let tire_RL = params.tire_pacejka_enable ? initTireState(params) : null;  // [R-06-C] 실제 루프 연동
  let soh_state = params.soh_enable ? initSOHState(params) : null;
  let sensorless_state = params.sensorless_enable ? initSensorlessState(params) : null;
  let thermal_3d = params.thermal_3d_enable ? init3DThermalState(params) : null;
  // v3: ESC/TCS state
  let esc_state = initESCState();
  // [P3-대책A] 통합 VD 제어 파이프라인 상태 (vdControlStep 6단계)
  // vd_control_enable=true 시 escIntervention 개별 호출을 vdControlStep으로 교체
  let vd_state_sl = (_initVDControlState !== null)
    ? _initVDControlState(params) : null;
  // ── [Step3] lateral_state: 2DOF(beta, psi_dot) + 7DOF 확장(phi, z_cg, Fz 4륜) ──
  let lateral_state = {
    beta: 0, psi_dot: 0, psi: 0, ay: 0,
    // 7DOF 상태 (seven_dof_enable 시 사용)
    phi: 0, phi_dot: 0, z_cg: 0, z_dot: 0,
    // 4륜 Fz (fourWheelSuspensionStep 결과 전달용)
    Fz_FL: null, Fz_FR: null, Fz_RL: null, Fz_RR: null,
    // Combined Slip 종방향 슬립 연속성
    lambda_f: 0, lambda_r: 0,
  };
  // ── [Step3] 4륜 서스펜션 상태 (seven_dof_enable 시 fw_susp 결과를 lateral에 전달) ──
  let fw_susp_state_sl = (params.seven_dof_enable && params.suspension_enable)
    ? initFourWheelSuspension(params) : null;
  // ── [Step4] β EKF 상태 초기화 ──
  let beta_ekf_sl = (params.beta_ekf_enable && params.lateral_enable && _initBetaEKF !== null)
    ? _initBetaEKF(params) : null;
  // [TV-7] v4: Torque Vectoring state (always init, gated by params inside function)
  let tv_state = initTorqueVectoringState();
  let mu_hat = mu_eff;  // [Fix-L1] road_condition 기반 초기값 (기존: params.mu_ref_nominal || 1.0)
  let a_prev = 0;  // previous acceleration for mu_hat estimation
  // [v7] 신규 상태 초기화
  let sei_state       = params.sei_enable ? initSEIState(params) : null;
  // [Parity-SL-RF] Rainflow SOH 루프 — battery.js 구현 완료, simulation.js 커널 미연결 해소
  // rainflow_enable=true 시 updateSOHWithRainflow로 교체, SOC 반전점 자동 누산
  let rf_state_sl     = (params.soh_enable && params.rainflow_enable) ? initRainflowState(params) : null;
  // [DC-Dyn] DC 링크 동적 전압 추적 상태 — V_terminal 피드백용
  // 초기값: params.V_dc (정격), 2RC 모델 활성화 시 매 스텝 V_terminal로 갱신
  let batt2rc_state_sl = params.V_dc || 400;
  let rls_state       = initRLSMuState(params);
  let cool_state      = initCoolantFlowState(params);
  let T_gear          = params.gear_T_oil || 80;  // 기어박스 오일 온도 (°C)
  // [R-14] Kalman SOC 상태 초기화 (EKF/UKF/Linear 자동 선택)
  let kf_state        = params.kalman_enable !== false ? initKalmanByType(params.soc_init, params) : null;
  let soc_kalman      = params.soc_init;  // Kalman 추정 SOC (초기값 = 쿨롱)
  // [R-15] BMS 셀 어레이 상태 초기화
  let bms_state       = params.bms_detailed_enable ? initBMSCells(params) : null;
  // [R-10-01] GAP-06: PID 드라이버 상태 초기화
  let drv_state       = params.driver_pid_enable !== false ? initDriverPIDState() : null;
  // ── [Step5] MPC 드라이버 상태 (driver_mpc_enable 시 PID보다 우선) ──
  let mpc_drv_state_sl = params.driver_mpc_enable ? initDriverMPCState() : null;
  // ── [CascadePID] Cascade 속도 PI 루프 상태 (cascade_speed_enable 시 최우선) ──
  let cascade_spd_state_sl = params.cascade_speed_enable ? initCascadeSpeedState() : null;
  // [R-10-02] GAP-10: 다단 기어 변속기 상태 초기화
  let gear_state      = params.multi_gear_enable ? initGearState(params) : null;
  // [R-12-02] Launch Control 상태 초기화
  let lc_state = { int_slip: 0, T_cmd_prev: 0, lc_active: false, slip: 0, e_slip: 0 };
  // [Advance02] 토크 응답 필터 상태 초기화
  let T_mot_filtered_sl = 0;  // 1차 저역통과 필터 상태값 (N·m)
  let T_slew_sl = 0;          // [A43-Fix-1] 슬루레이트 제한 후 목표 토크 (N·m)
  // [v7] 타이어 공기압·IRI 보정: resistanceForcesV3 내부 통합 처리
  // [v7] SOH 프로파일 적용 (화학계별 Arrhenius 계수 반영)
  const soh_profile = getSOHProfile(params);
  if (params.soh_enable && soh_state) {
    soh_state.profile = soh_profile;
  }

  // [R-05] 반복제어(RC) 토크리플 보상 상태 초기화 (simLaunch)
  let rep_d_state = params.rc_ripple_enable ? initRepetitiveControllerState(params) : null;
  let rep_q_state = params.rc_ripple_enable ? initRepetitiveControllerState(params) : null;

  // NEW v3: Drive mode effective mass
  const m_eff = effectiveMass(params);
  // [R-10-01] GAP-06: 루프 외부 고정 throttle → PID 드라이버로 교체
  // mode_throttle은 PID 비활성 시 fallback으로만 사용
  const mode_throttle_fixed = driveModeThrottle(throttle, params, 0); // v=0 초기값, 루프 내에서 갱신
  // [R-06-D] 서스펜션 상태 초기화 (simLaunch 독립 루프)
  let susp_state_sl = params.suspension_enable ? initSuspensionState(params) : null;
  
  let E_batt = 0, E_wheel = 0, E_loss = 0, th_accum = 0;
  let Q_b_acc = 0, Q_i_acc = 0, Q_m_acc = 0;
  const data = [], v_tgt = v_target / 3.6;
  let t_reach = null;   // v_target 최초 도달 시간
  // [A68-01-Fix-L3] v_target 도달 시점 s/E 스냅샷 추가:
  //   기존: distance/E_batt_Wh/eta_cycle = 30초 전체 누적값 → 0→v_target 구간 값으로 잘못 표시됨
  //   수정: v >= v_tgt 최초 조건에서 s/E_batt/E_wheel 즉시 캡처 → 정확한 가속 구간 KPI 제공
  let s_at_tgt = null, E_batt_at_tgt = null, E_wheel_at_tgt = null;
  let lastRes = null;   // [v7] 마지막 저항력 결과 (return 블록에서 사용)

  while (t < 30) {   // 항상 30초까지 연산 (v_target 도달 여부 무관)
    // [R-10-01] GAP-06: 드라이버 모델 → 목표 속도 추종
    // [CascadePID] 우선순위: Cascade 속도PI > MPC > PID > throttle 고정
    let mode_throttle, brake_demand_drv = 0;
    let mpc_cost_sl = 0;
    let cascade_bw_warn_sl = false;
    if (params.cascade_speed_enable && cascade_spd_state_sl) {
      // ── [CascadePID] L2 속도 PI → T_cmd → throttle 등가 ──────────────
      // Kp_spd/Ki_spd는 _applyDriveModeGainsInline()에 의해 드라이브모드 배율 이미 반영됨
      const casc_out = cascadeSpeedLoopStep(v_tgt, v, cascade_spd_state_sl, params, dt);
      cascade_spd_state_sl = casc_out.new_state;
      cascade_bw_warn_sl   = casc_out.bw_sep_warn;
      mode_throttle    = driveModeThrottle(Math.min(casc_out.pedal_pos, throttle), params, v * 3.6);
      brake_demand_drv = casc_out.brake_demand;
    } else if (params.driver_mpc_enable && mpc_drv_state_sl) {
      // ── [Step5] MPC 드라이버 (receding horizon) ────────────────────────
      const mpc_out = driverMPCController(v_tgt, v, mpc_drv_state_sl, params, dt);
      mpc_drv_state_sl = mpc_out.new_state;
      mode_throttle    = driveModeThrottle(Math.min(mpc_out.pedal_pos, throttle), params, v * 3.6);
      brake_demand_drv = mpc_out.brake_demand;
      mpc_cost_sl      = mpc_out.cost;
    } else if (params.driver_pid_enable !== false && drv_state) {
      const pid_out = driverPIDController(v_tgt, v, drv_state, params, dt);
      drv_state = pid_out.new_state;
      // [BugFix-A04-01] v_kmh 전달: LUT 2D 보간이 현재 차속 기준으로 작동하도록 수정
      // [A49-LaunchThr-Fix] PID pedal_pos를 throttle 상한으로 클램프 → 개도 슬라이더 그래프 반영
      mode_throttle   = driveModeThrottle(Math.min(pid_out.pedal_pos, throttle), params, v * 3.6);
      brake_demand_drv = pid_out.brake_demand;
    } else {
      // [BugFix-A04-01] fallback도 현재 v_kmh로 갱신
      mode_throttle = driveModeThrottle(throttle, params, v * 3.6);
    }

    // [R-10-02] GAP-10: 다단 기어 변속 로직
    let gear_ratio_eff = params.gear_ratio || 9.5;
    let torque_scale   = 1.0;
    let current_gear   = 1;
    if (params.multi_gear_enable && gear_state) {
      const gearOut = getActiveGear(v, params.T_max_motor * mode_throttle, soc, gear_state, params, dt, t);
      gear_ratio_eff = gearOut.gear_ratio_eff;
      torque_scale   = gearOut.torque_scale;
      current_gear   = gearOut.gear;
      gear_state     = gearOut.gear_state_new;
    }

    const omega_w = v / params.r_wheel;
    const omega_m = omega_w * gear_ratio_eff;
    const omega_e = omega_m * (params.poles / 2);

    // ── [v5-fix3] MTPV/FW 연동: 전압·전류 한계 기반 Id*/Iq* ──────────────
    // ── [DC-Dyn] DC 링크 전압 (동적 모드: V_terminal 피드백, 정적: params.V_dc 고정) ──
    // dc_link_dynamics_enable=true && battery_model=2RC 일 때 V_terminal 반영
    const V_dc_eff = (params.dc_link_dynamics_enable && batt2rc_state_sl)
      ? Math.min(
          Math.max(batt2rc_state_sl, params.V_dc_min || 250),
          params.V_dc_max || (params.V_dc || 400) * 1.1
        )
      : (params.V_dc || 400);
    const curRef = computeCurrentReferenceWithMTPV(omega_e, params.T_max_motor, V_dc_eff, params, thState.T_motor);

    // ── [R-06-E] 열적 디레이팅 계수 계산 ──────────────────────────────────
    const thermDerate = thermalDeratingFactor(thState, params);
    // [R-10-03] GAP-05: 3D 열 디레이팅 적용
    const derate3d = deratingFactor3D(thermal_3d, params);
    const total_derate = thermDerate.f_total * derate3d;
    // ── [R-06-D] 서스펜션 갱신 (simLaunch 루프) ─────────────────────────
    if (susp_state_sl && params.suspension_enable) {
      const z_road_sl = params.road_roughness_enable
        ? (params.IRI || 2.5) * 0.0001 * Math.sin(2 * Math.PI * v * t / 8) : 0;
      susp_state_sl = suspensionStep(susp_state_sl, z_road_sl, v, a_prev, 0, params, dt);
    }
    // ── 구동 토크 계산 (TCS 적용 + 열적 디레이팅 + 변속 토크스케일) ──────
    const T_mot_ideal = motorTorqueSpeed(omega_m, mode_throttle, params, thState.T_motor, thState.T_inv, thState.T_batt)
      * total_derate * torque_scale;  // [R-06-E][R-10-02][R-10-03] 복합 디레이팅
    // [Advance02] 토크 응답 1차 저역통과 필터 + 슬루레이트 제한
    const _mode_sl = params.drive_mode || 'NORMAL';
    const tau_sl   = { ECO: params.tau_torque_eco||0.08, NORMAL: params.tau_torque_normal||0.04,
                       SPORT: params.tau_torque_sport||0.02, SNOW: params.tau_torque_snow||0.12,
                       OFF_ROAD: params.tau_torque_offroad||0.06 }[_mode_sl] || 0.04;
    const slew_sl  = { ECO: params.torque_slew_rate_eco||500, NORMAL: params.torque_slew_rate_normal||1500,
                       SPORT: params.torque_slew_rate_sport||3000, SNOW: params.torque_slew_rate_snow||300,
                       OFF_ROAD: params.torque_slew_rate_offroad||800 }[_mode_sl] || 1500;
    const alpha_sl = dt / (tau_sl + dt);
    // [A43-Fix-1] ✅ 토크 필터 순서 수정: 슬루레이트 먼저 → LPF 추종
    //   수정 전: LPF 후 이중 클램프 → SPORT 모드 정상상태 ±15 Nm 진동 발생
    //   수정 후: T_ideal → 슬루 제한 T_slew 생성 → LPF가 T_slew 추종 → 단조 수렴
    const dT_max_sl = slew_sl * dt;
    // Step 1: 슬루레이트로 목표값 제한 (T_slew_sl 은 루프 상단에서 선언)
    if (T_mot_ideal > T_slew_sl + dT_max_sl) T_slew_sl += dT_max_sl;
    else if (T_mot_ideal < T_slew_sl - dT_max_sl) T_slew_sl -= dT_max_sl;
    else T_slew_sl = T_mot_ideal;
    // Step 2: LPF는 슬루 제한된 목표를 추종 (진동 없이 부드럽게 수렴)
    T_mot_filtered_sl += alpha_sl * (T_slew_sl - T_mot_filtered_sl);
    const T_mot_raw = T_mot_filtered_sl;
    const v_wheel_fwd = omega_w * params.r_wheel;
    const slip_ratio_est = v > 1 ? (v_wheel_fwd - v) / Math.max(v, 0.1) : 0;
    const tcs_result = tcsIntervention(slip_ratio_est, T_mot_raw, params);
    const T_mot_tcs = tcs_result.T_tcs;

    // [Fix-CS-1] lambda_f/r → lateral_state 갱신
    // combined_slip_enable=true 시 pacejkaCombinedSlipDugoff가 λ를 사용하는데
    // 기존 코드는 lambda_f/r=0 초기값 고정 → Dugoff 계산이 항상 무의미했음
    // AWD: 전륜 lambda는 토크 배분 비율로 보정
    if (params.combined_slip_enable) {
      const lambda_r_new = slip_ratio_est;
      const lambda_f_new = slip_ratio_est * (params.torque_split_front || 0);
      lateral_state = { ...lateral_state, lambda_f: lambda_f_new, lambda_r: lambda_r_new };
    }

    // ── [Step3] 4륜 서스펜션 업데이트 → Fz를 lateral_state에 주입 ──────────
    if (fw_susp_state_sl && params.seven_dof_enable) {
      const z_road_fw = params.road_roughness_enable
        ? (params.IRI || 2.5) * 0.0001 * Math.sin(2 * Math.PI * v * t / 8) : 0;
      const z_roads = { FL: z_road_fw, FR: z_road_fw, RL: z_road_fw, RR: z_road_fw };
      const fw_result = fourWheelSuspensionStep(
        fw_susp_state_sl, z_roads, v, a_prev, lateral_state.ay || 0, params, dt
      );
      fw_susp_state_sl = fw_result.corners;
      // 4륜 Fz를 lateral_state에 주입 → lateralDynamicsStep에서 자동 활용
      lateral_state = {
        ...lateral_state,
        Fz_FL: fw_result.Fz_FL,
        Fz_FR: fw_result.Fz_FR,
        Fz_RL: fw_result.Fz_RL,
        Fz_RR: fw_result.Fz_RR,
      };
    }

    // ── [v5-fix5] 횡방향 동역학 업데이트 (lateral_state 실제 적분) ─────────
    if (params.lateral_enable) {
      lateral_state = lateralDynamicsStep(
        lateral_state,
        params.delta_steer_rad || 0,
        Math.max(v, 0.1),
        null, null, params, dt   // Fz는 latState.Fz_FL/FR/RL/RR 경로로 전달
      );
      // [D6-STR] 조향 피드백 모델 — simLaunch 루프 연결 (구현됨·미연결 해소)
      // Fy_front = F_yf (lateralDynamicsStep 반환), alpha_f = 슬립각
      if (_steeringFeedbackModel !== null) {
        const steerOut_sl = _steeringFeedbackModel(
          steer_state_sl,
          lateral_state.F_yf || 0,
          lateral_state.alpha_f || 0,
          Math.max(v, 0.5), params, dt
        );
        steer_state_sl = {
          delta_sw:     steerOut_sl.delta_sw,
          delta_sw_dot: steerOut_sl.delta_sw_dot,
          T_driver:     steer_state_sl.T_driver,
        };
        steer_state_sl._last = steerOut_sl; // T_feedback / SAT / steer_angle_wheel 로깅용
      }
    }

    // ── [Step4] β EKF 업데이트 ─────────────────────────────────────────────
    let beta_ekf_result_sl = null;
    if (beta_ekf_sl && params.beta_ekf_enable && params.lateral_enable && _ekfUpdateBeta !== null) {
      // 측정값: ay는 물리 모델 직접값, psi_dot은 lateral_state 물리 모델값 사용
      // (sensor_noise_enable 시 노이즈 추가)
      const ay_meas_sl  = params.sensor_noise_enable
        ? sensorNoise(lateral_state.ay || 0, params.R_ay_ekf  ? Math.sqrt(params.R_ay_ekf)  : 0.7,  true, params.K_sensor_noise || 1.0)
        : (lateral_state.ay || 0);
      const psi_meas_sl = params.sensor_noise_enable
        ? sensorNoise(lateral_state.psi_dot || 0, params.R_psi_ekf ? Math.sqrt(params.R_psi_ekf) : 0.14, true, params.K_sensor_noise || 1.0)
        : (lateral_state.psi_dot || 0);

      beta_ekf_result_sl = _ekfUpdateBeta(
        beta_ekf_sl,
        ay_meas_sl, psi_meas_sl,
        params.delta_steer_rad || 0,
        Math.max(v, 1.0), params, dt
      );
      beta_ekf_sl = beta_ekf_result_sl.ekf;

      // EKF 추정값과 물리 모델값 융합 (beta_ekf_blend: 1=완전 EKF, 0=물리 직접)
      const blend = Math.min(1, Math.max(0, params.beta_ekf_blend ?? 0.7));
      lateral_state = {
        ...lateral_state,
        beta:    blend * beta_ekf_result_sl.beta_hat    + (1 - blend) * lateral_state.beta,
        psi_dot: blend * beta_ekf_result_sl.psi_dot_hat + (1 - blend) * lateral_state.psi_dot,
      };
    }

    // ── [P3-대책A] ESC/TV 통합 VD 제어 파이프라인 ───────────────────────────
    // vd_control_enable=true → vdControlStep 6단계 통합 파이프라인 (대책A)
    // vd_control_enable=false or 미설정 → 기존 escIntervention 개별 호출 (대책B 호환)
    let T_mot_after_esc;
    // esc_result를 블록 밖에 선언 → data.push 에서 참조 가능 (대책B 경로에서 할당)
    let esc_result = { active: false, torque_cut: 1.0, M_esc: 0 };
    if (params.vd_control_enable && vd_state_sl && _vdControlStep !== null) {
      // [대책A] vdControlStep — 2DOF 기준모델 → EKF → β판단 → 요 모멘트 → QP 배분
      const sensor_vd = {
        v,
        ay_meas:      lateral_state.ay      || 0,
        psi_dot_meas: lateral_state.psi_dot || 0,
        ax:           a_prev,
        mu_hat,
      };
      const loads_vd = _computeVerticalLoad_v4 !== null
        ? _computeVerticalLoad_v4(v, a_prev, lateral_state.ay || 0, params)
        : { Fz_FL: params.m * 9.81 * 0.25, Fz_FR: params.m * 9.81 * 0.25,
            Fz_RL: params.m * 9.81 * 0.25, Fz_RR: params.m * 9.81 * 0.25 };
      const vd_out = _vdControlStep(
        vd_state_sl, T_mot_tcs, params.delta_steer_rad || 0,
        sensor_vd, loads_vd, params, dt
      );
      vd_state_sl = vd_out.vd_state;
      // QP 배분 토크 합산 (4륜) → 총 구동 토크
      T_mot_after_esc = (vd_out.T_FL || 0) + (vd_out.T_FR || 0)
                      + (vd_out.T_RL || 0) + (vd_out.T_RR || 0);
      // esc_result 호환 구조 채움 (data.push 필드 통일)
      esc_result = {
        active:     vd_out.diagnostics?.stability?.level !== 'STABLE',
        torque_cut: 1.0,
        M_esc:      vd_out.diagnostics?.M_z_cmd || 0,
      };
      esc_state._vd_active = esc_result.active;
    } else {
      // [대책B] 기존 escIntervention 개별 호출 (하위 호환)
      const psi_ref_esc = computeYawRateRef_v4(params.delta_steer_rad || 0, v, mu_hat, params).psi_ref;
      esc_result = escIntervention(
        lateral_state.psi_dot || 0,
        psi_ref_esc,
        v, params, esc_state, dt
      );
      T_mot_after_esc = esc_result.active
        ? T_mot_tcs * (esc_result.torque_cut || 1.0)
        : T_mot_tcs;
    }

    // ── [TV-7] AWD / Torque Vectoring ──────────────────────────────────────
    // [P3-대책A] vdControlStep 경로는 QP에서 4륜 배분이 이미 완료 → TV 스킵
    const vs_tv = {
      v, ax: a_prev, ay: lateral_state.ay || 0,
      yaw_rate: lateral_state.psi_dot || 0,
      delta_steer: params.delta_steer_rad || 0,
      mu_hat,
      slip_FL: slip_ratio_est, slip_FR: slip_ratio_est,
      slip_RL: slip_ratio_est, slip_RR: slip_ratio_est,
    };
    // [A21-15-03] TV 삼항 데드코드 수정: TV+AWD 둘 다 활성 시만 torqueVectoring 호출
    //   기존: 양쪽 모두 torqueVectoring 호출 → TV 비활성 경로 없음 (완전한 데드코드)
    // [BugFix-A04-03] awd_calib_enable=true 시에도 torqueVectoring 진입하도록 조건 확장
    // [P3-대책A] vd_control_enable 시 TV는 이미 QP에서 처리됨 → torqueVectoring 바이패스
    const tv_result = (!params.vd_control_enable && (params.awd_calib_enable || params.torque_vectoring_enable) && params.awd_enable)
      ? torqueVectoring(T_mot_after_esc, vs_tv, params, tv_state)
      : { T_front: 0, T_rear: T_mot_after_esc, awd_active: params.awd_enable || false };

    // [A21-15-04] AWD 시 전·후륜 토크 합산 (T_rear만 사용하던 버그 수정)
    //   기존: T_mot = tv_result.T_rear → AWD 시 전륜 토크 누락 → 에너지/SOC 최대 50% 과소
    //   수정: T_mot = T_front + T_rear (전체 구동 토크 기준)
    let T_mot_awd2 = params.awd_enable
      ? ((tv_result.T_front || 0) + (tv_result.T_rear || 0)) || T_mot_after_esc
      : T_mot_after_esc;
    // [A10] BMS 콘택터 차단 → 구동 토크 강제 0
    let T_mot = (bms_state && bms_state.contactor_on === false) ? 0 : T_mot_awd2;

    // ── [R-12-02] Launch Control 연동 ──────────────────────────────────────
    const is_regen_pre = mode_throttle < (params.regen_threshold || 0.02) && v > 0.5;
    let lc_result = null;
    if (params.launch_control_enable && !is_regen_pre) {
      lc_result = launchControlTorque(v, omega_m, mu_hat, soc, params, lc_state, dt);
      lc_state  = lc_result.new_state;
      if (lc_result.T_lc !== null) T_mot = Math.min(T_mot, lc_result.T_lc);
    }

    // ── [R-12-01] 가변 CdA simLaunch 루프 연동: resistanceForcesV3 내부 variableCdA 매 스텝 갱신 ──
    // [R-12-01] 주석: resistanceForcesV3 → 내부 variableCdA(v, params, T_motor) 호출 시
    //            params.CdA_v3가 갱신되므로 이 아래 res는 최신 그릴셔터 상태를 반영
    // ── [v7] 저항력 산출 (공기압·IRI·CdA·측풍 통합) ─────────────────
    const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
    lastRes = res;  // [v7] 루프 외부 return 블록에서 사용 위해 저장
    const F_res_total = res.F_total;  // F_rough now included in F_total
    const F_drv_est = T_mot * gear_ratio_eff * params.eta_driveline / params.r_wheel;
    // ── [v7] RLS μ 추정 업데이트 ──────────────────────────────────────
    const F_normal = params.m * g * Math.cos(grade_rad);
    rls_state = rlsMuUpdate(rls_state, F_drv_est, F_normal, a_prev, params.m, params);
    mu_hat = rls_state.mu_est;

    // ── [v5-fix4] 회생제동: throttle<regen_threshold 시 regenTorqueEnhanced ──
    let F_drv, regen_result = null;
    const is_regen = mode_throttle < (params.regen_threshold || 0.02) && v > 0.5;
    if (is_regen) {
      // [BugFix-A04-02] regen_calib_enable 시 regenMapDemand LUT 우선 적용
      const brake_demand_base = Math.min(1.0, (1.0 - mode_throttle) * driveModeRegenStrength(params));
      const brake_demand = params.regen_calib_enable
        ? Math.max(regenMapDemand(v, 0, params), brake_demand_base)
        : brake_demand_base;
      // [R-12-04] 회생 에너지 최적화 연동
      if (params.regen_optimizer_enable) {
        const opt = regenEnergyOptimizer(omega_m, v, soc, thState.T_batt, thState.T_motor, params, brake_demand);
        regen_result = { T_regen: opt.T_regen, T_friction: opt.T_friction,
                         P_regen_kW: opt.E_recover_rate, regen_ratio: opt.regen_ratio };
      } else {
        regen_result = regenTorqueEnhanced(omega_m, brake_demand, params,
                                           soc, thState.T_batt, thState.T_motor, thState.T_inv);
      }
      // [v7] 비선형 regen/마찰 블렌딩 (S-커브 + 저속 fade-out)
      const blend = regenFrictionBlend(
        regen_result.T_regen, regen_result.T_friction,
        v, soc, omega_m, params, dt
      );
      regen_result.T_regen    = blend.T_regen;
      regen_result.T_friction = blend.T_friction;
      regen_result._blend     = blend;
      // [Fix-RB-2] 저크 제한용 이전 스텝 T_regen 갱신
      params._regen_T_prev = blend.T_regen;
      F_drv = -(regen_result.T_regen + regen_result.T_friction) * gear_ratio_eff * params.eta_driveline / params.r_wheel;
    } else {
      F_drv = T_mot * gear_ratio_eff * params.eta_driveline / params.r_wheel;
      F_drv = Math.min(F_drv, rls_state.mu_est * params.m * g * Math.cos(grade_rad));
    }

    // ── [v5-fix3] Iq_ref: MTPV/FW 기반 ──────────────────────────────────
    const lpm = lambda_pm_T(thState.T_motor, params);
    let Iq_ref, Id_ref;
    if (is_regen && regen_result) {
      Iq_ref = -(regen_result.T_regen / Math.max(0.01, 1.5 * (params.poles / 2) * lpm));
      Id_ref = 0;
    } else {
      Iq_ref = curRef.Iq || (T_mot > 0 ? T_mot / (1.5 * (params.poles / 2) * lpm) : 0);
      Id_ref = curRef.Id || 0;
    }

    // ── [Step1] dq 전류 제어: motorFOCStep (PI 또는 1차 지연) ───────────
    if (_motorFOCStep !== null) {
      foc_state_sl = _motorFOCStep(
        { ...foc_state_sl, Id, Iq },  // theta_e는 foc_state_sl에 누적 전달됨
        Id_ref, Iq_ref, omega_e, lpm,
        thState.T_motor, thState.T_inv, params, dt
      );
      Id = foc_state_sl.Id;
      Iq = foc_state_sl.Iq;
      // ── [Sensorless-Fix] 센서리스 위치/속도 추정기 — simLaunch 루프 연결 ──
      // sensorless_state는 init만 되고 루프 미사용 상태였음 (P1 이슈)
      // FOC 완료 후 Vd/Vq를 관측기에 피드 → theta_est/omega_est 갱신
      if (sensorless_state && params.sensorless_enable && foc_state_sl.Vd_cmd != null
          && _sensorlessObserver !== null) {
        sensorless_state = _sensorlessObserver(
          Id, Iq,
          foc_state_sl.Vd_cmd, foc_state_sl.Vq_cmd,
          omega_m, params, dt, sensorless_state
        );
        // 추정 전기각 → FOC theta_e 보정 피드백 (신뢰도 0.5 이상일 때만 적용)
        if (sensorless_state.confidence > 0.5) {
          foc_state_sl.theta_e = sensorless_state.theta_est;
        }
      }
    } else {
      // ── [v9-fix5] 폴백: 인라인 dq 전류 제어 ─────────────────────────
      if (params.foc_current_pi_enable) {
        const e_d = Id_ref - Id;
        const e_q = Iq_ref - Iq;
        if (typeof foc_int_d === 'undefined') { let foc_int_d = 0, foc_int_q = 0; }
        foc_int_d += e_d * dt;
        foc_int_q += e_q * dt;
        const V_max_foc = (params.V_dc || 400) * (params.fw_v_margin || 0.95) / Math.sqrt(3);
        const int_limit = V_max_foc / Math.max(params.Ki_id, 1);
        foc_int_d = Math.max(-int_limit, Math.min(int_limit, foc_int_d));
        foc_int_q = Math.max(-int_limit, Math.min(int_limit, foc_int_q));
        let Vd_cmd = params.Kp_id * e_d + params.Ki_id * foc_int_d - omega_e * (params.Lq || 0.0006) * Iq;
        let Vq_cmd = params.Kp_iq * e_q + params.Ki_iq * foc_int_q + omega_e * ((params.Ld || 0.0003) * Id + lpm);
        const theta_e_sl = Math.atan2(Iq, Id);
        const dtc = deadtimeCompensation(Id, Iq, theta_e_sl, params);
        Vd_cmd += dtc.V_d_comp;
        Vq_cmd += dtc.V_q_comp;
        // [D3-DTC] deadtime 보상 전압 손실 크기 추적 (로깅용)
        dtc_V_loss_sl = +Math.sqrt(dtc.V_d_comp * dtc.V_d_comp + dtc.V_q_comp * dtc.V_q_comp).toFixed(3);
        if (params.rc_ripple_enable) {
          if (!rep_d_state) rep_d_state = initRepetitiveControllerState(params);
          if (!rep_q_state) rep_q_state = initRepetitiveControllerState(params);
          rep_d_state.omega_e = omega_e;
          rep_q_state.omega_e = omega_e;
          const rc_V_d_rep = repetitiveController(e_d, params, rep_d_state);
          const rc_V_q_rep = repetitiveController(e_q, params, rep_q_state);
          const harm_comp = harmonicCompensation(omega_e, omega_e, Id, Iq, params);
          Vd_cmd += rc_V_d_rep + harm_comp.V_d_comp;
          Vq_cmd += rc_V_q_rep + harm_comp.V_q_comp;
        }
        const V_mag = Math.sqrt(Vd_cmd * Vd_cmd + Vq_cmd * Vq_cmd);
        if (V_mag > V_max_foc) {
          const vsc = V_max_foc / V_mag;
          Vd_cmd *= vsc; Vq_cmd *= vsc;
          foc_int_d -= e_d * dt;
          foc_int_q -= e_q * dt;
        }
        const Rs_eff = Rs_T(thState.T_motor, params);
        const dId_dt = (Vd_cmd - Rs_eff * Id + omega_e * (params.Lq || 0.0006) * Iq) / (params.Ld || 0.0003);
        const dIq_dt = (Vq_cmd - Rs_eff * Iq - omega_e * ((params.Ld || 0.0003) * Id + lpm)) / (params.Lq || 0.0006);
        Id += dId_dt * dt;
        Iq += dIq_dt * dt;
        const I_mag_pi = Math.sqrt(Id * Id + Iq * Iq);
        const I_max_pi = I_max_T(thState.T_inv, params) || params.I_max_rms;
        if (I_mag_pi > I_max_pi) { const sc = I_max_pi / I_mag_pi; Id *= sc; Iq *= sc; }
      } else {
        const tau_d = (params.Ld || 0.0003) / (params.Rs || 0.015);
        const tau_q = (params.Lq || 0.0006) / (params.Rs || 0.015);
        const alpha_d = Math.min(0.99, dt / (tau_d + dt));
        const alpha_q = Math.min(0.99, dt / (tau_q + dt));
        Id = Id + alpha_d * (Id_ref - Id);
        Iq = Iq + alpha_q * (Iq_ref - Iq);
        const I_mag_dq = Math.sqrt(Id * Id + Iq * Iq);
        const I_max_eff = I_max_T(thState.T_inv, params) || params.I_max_rms;
        if (I_mag_dq > I_max_eff) { const sc = I_max_eff / I_mag_dq; Id *= sc; Iq *= sc; }
      }
    }
    const steinmetz = steinmetzIronLoss_v3(omega_e, params);

    // ── [v7] 운동방정식 (effective mass + IRI 거칠기 저항 포함) ──────────
    const a = (F_drv - F_res_total) / m_eff;

    // ── 에너지 체인 계산 ──────────────────────────────────────────────────
    let chain, P_elec;
    if (is_regen && regen_result) {
      chain = computeRegenChain(omega_m, regen_result.T_regen, Id, Iq, soc, params, thState.T_batt, thState.T_motor);
      // [R-05] HVAC P_aux 회생 경로 연동: 회생 중에도 HVAC 보조부하는 배터리 소비
      // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
      const _hvac1 = hvacDetailedPower(params.T_amb, v, soc, params);
      params._hvac_prev_mode = _hvac1.new_hvac_mode;
      const P_hvac_regen = _hvac1.P;
      P_elec = P_hvac_regen - Math.max(0, (chain.P_batt_charge || 0) * 1000);  // 충전(음수) + 보조부하(양수)
    } else {
      chain = computeEnergyChain(v, omega_m, T_mot, Id, Iq, soc, params, grade_rad, thState);
      P_elec = chain.P_batt_chem * 1000;
    }

    // ── [v5-fix2] 2RC 배터리 모델 연동 ──────────────────────────────────
    let batt2rc_result = null;
    if (battery_2rc && params.battery_model === '2RC') {
      const I_batt_est = P_elec / Math.max(1, params.V_dc || 400);
      batt2rc_result = battery2RCModelWithSEI(battery_2rc, I_batt_est, dt, params, null);
      battery_2rc.SOC = soc;  // keep in sync
      battery_2rc.T_cell = thState.T_batt;
      // [DC-Dyn] V_terminal → batt2rc_state_sl 갱신 (다음 스텝 V_dc_eff에 사용)
      if (params.dc_link_dynamics_enable && batt2rc_result?.V_terminal) {
        // C_dc 커패시터 다이나믹스: dV/dt = (I_batt - I_inv) / C_dc
        // 근사: V_terminal ≈ V_dc_eff (커패시터 충방전 1차 저역통과)
        const C_dc = params.C_dc || 1500e-6;
        const tau_dc = C_dc * (params.R_batt || 0.08);   // RC 시정수 [s]
        const alpha_dc = Math.min(0.99, dt / (tau_dc + dt));
        batt2rc_state_sl = batt2rc_state_sl
          + alpha_dc * (batt2rc_result.V_terminal - batt2rc_state_sl);
      }
    }

    // ── SOC 업데이트 ──────────────────────────────────────────────────────
    soc = socUpdate(soc, P_elec, dt, params);

    // ── [R-14] Kalman SOC 업데이트: 2RC V_terminal 기반 EKF/UKF 추정 ─────
    // batt2rc_result.V_terminal 없으면 OCV 추정값으로 fallback
    if (kf_state && params.kalman_enable !== false) {
      const V_meas    = batt2rc_result
        ? batt2rc_result.V_terminal
        : batteryOCV_smart(soc, params, thState.T_batt);
      const I_batt_kf = P_elec / Math.max(1, params.V_dc || 400);
      const soc_coulomb = soc;
      const soc_measured_v = batteryOCV_smart(soc, params, thState.T_batt);  // OCV 역산 근사
      kf_state    = kalmanSOCUpdate(kf_state, V_meas, I_batt_kf, soc_coulomb, soc_measured_v, params);
      soc_kalman  = kf_state.soc_est !== undefined ? kf_state.soc_est : soc;
      // Kalman 추정 SOC가 유효하면 회생·디레이팅 제어에 피드백 (완전 연동)
      if (Math.abs(soc_kalman - soc) < 0.15) soc = soc_kalman;
    }

    // ── [R-15] BMS 셀 스텝: 셀 전압 편차 추적 + 밸런싱 에너지 계상 ──────
    let bms_max_dv = 0;
    if (bms_state && params.bms_detailed_enable) {
      const I_batt_bms = P_elec / Math.max(1, params.V_dc || 400);
      bms_state = bmsStep(bms_state, I_batt_bms, thState.T_batt, dt, params);
      bms_max_dv = bms_state.max_dV || 0;
      // BMS 셀 편차 → SOC 추정 보정 (편차 클 수록 낮은 셀 기준으로 보수적 추정)
      if (bms_max_dv > 0.05) {
        const soc_correction = -bms_max_dv * (params.bms_soc_corr_gain || 0.3);
        soc = Math.max(0.01, soc + soc_correction);
      }
    }

    // ── [R-06-G] Li-Plating 감지 (회생 구간) ────────────────────────────
    let li_plating_risk = 'NONE';
    if (is_regen && params.li_plating_enable) {
      const batt_snap_lp = {
        V_terminal: batt2rc_result ? batt2rc_result.V_terminal : (params.V_dc || 400),
      };
      const lp = liPlatingDetect(batt_snap_lp, P_elec / Math.max(1, params.V_dc || 400), params);
      li_plating_risk = lp.risk_level;
      // 위험도가 HIGH이면 SOC 보정 (추가 충전 억제)
      if (lp.plating && lp.risk_level === 'HIGH') {
        soc = Math.min(soc, 0.97);  // 최대 SOC 클램프
      }
    }
    // ── [v5-fix6] SOH 업데이트 + 출력 디레이팅 피드백 ────────────────────
    const I_batt_step = Math.abs(P_elec) / Math.max(1, params.V_dc || 400);
    if (soh_state && params.soh_enable) {
      const batt_snap = { SOC: soc, T_cell: thState.T_batt };
      // [Parity-SL-RF] rainflow_enable 시 updateSOHWithRainflow 사용 (rf_state에 SOC 반전점 누산)
      // rainflow_enable=false 이면 기존 updateSOH 경로 유지 (하위 호환)
      soh_state = rf_state_sl
        ? updateSOHWithRainflow(soh_state, rf_state_sl, batt_snap, I_batt_step, dt, params)
        : updateSOH(soh_state, batt_snap, I_batt_step, dt, params);
      if (t % 1.0 < dt) { Object.assign(params, applySOHDegradation(params, soh_state)); }
    }
    // ── [v7] SEI 성장 모델 업데이트 ────────────────────────────────────
    if (sei_state) {
      const batt_v7 = {
        T_cell: thState.T_batt,
        V_terminal: batt2rc_result ? batt2rc_result.V_terminal : params.V_dc,
      };
      sei_state = updateSEI(sei_state, batt_v7, I_batt_step, dt, params);
    }
    // ── [v7] 기어박스 온도 갱신 ──────────────────────────────────────
    const gear_loss = gearLossTempEnhanced(omega_m, T_gear, T_mot, params); // [R-06-A] unified
    T_gear = gearThermalStep(T_gear, gear_loss.P_loss, v, params, dt);
    // ── [R-06-C] 타이어 열 상태 갱신 ─────────────────────────────────
    if (tire_RL && params.tire_pacejka_enable) {
      updateTireTemperatureEnhanced(tire_RL, F_drv, is_regen ? -(regen_result?.T_regen||0)*0.1 : 0,
        v, is_regen ? 0 : (v_tgt > 0 ? Math.min(0.1, (v_tgt - v) / Math.max(v, 1)) : 0), dt, params);
    }
    // ── [v7] 냉각 유량 최적화 제어 ───────────────────────────────────
    if (th_accum >= dt_th - dt) {  // 열 모델 업데이트 직후
      cool_state = coolantFlowControl(cool_state, thState.T_cool, params, dt_th);
    }
    if (is_regen) {
      E_batt -= P_elec * dt;  // 충전 회수 에너지 (양수)
    } else {
      E_batt += P_elec * dt;
      E_wheel += (chain.P_wheel || 0) * 1000 * dt;
    }
    E_loss += ((chain.P_loss_batt || 0) + (chain.P_loss_inv || 0) +
               (chain.P_loss_motor || 0) + (chain.P_loss_gear || 0)) * 1000 * dt;

    Q_b_acc += (chain.Q_batt || 0) * dt;
    Q_i_acc += (chain.Q_inv  || 0) * dt;
    Q_m_acc += (chain.Q_motor || 0) * dt;
    th_accum += dt;
    if (th_accum >= dt_th) {
      // [R-13] cool_state.phi_flow → thermalStep UA_rad 가중치 반영
      thState = thermalStep(thState, Q_b_acc / th_accum, Q_i_acc / th_accum, Q_m_acc / th_accum,
                            v, params, th_accum, cool_state.phi_flow);
      // [R-10-03] GAP-05: 3D 열 분포 실시간 업데이트 (thermal_3d_enable 게이팅)
      if (params.thermal_3d_enable && thermal_3d) {
        const Q_total_3d = (Q_b_acc + Q_i_acc + Q_m_acc);
        thermal_3d = update3DThermalStep(thermal_3d, Q_total_3d, cool_state.phi_flow, thState.T_cool, th_accum, params);
      }
      Q_b_acc = 0; Q_i_acc = 0; Q_m_acc = 0; th_accum = 0;
    }

    // ── 데이터 로깅 (50ms 간격) ──────────────────────────────────────────
    if (t % 0.05 < dt) {
      data.push({
        t: +t.toFixed(3), v_kmh: +(v * 3.6).toFixed(2), v_ms: +v.toFixed(3),
        a: +a.toFixed(3), s: +s.toFixed(2), F_drv: +F_drv.toFixed(1),
        F_aero: +res.F_aero.toFixed(1), F_roll: +res.F_roll.toFixed(1), F_grade: +res.F_grade.toFixed(1),
        P_mech: +(F_drv * v / 1000).toFixed(2), P_elec: +(P_elec / 1000).toFixed(2),
        eta_motor: +(chain.eta_motor || 0), soc: +(soc * 100).toFixed(2),
        Id: +Id.toFixed(2), Iq: +Iq.toFixed(2), T_motor_torque: +T_mot.toFixed(1),
        T_motor_cmd: +T_mot_ideal.toFixed(1), // [RA1-Fix] 필터링 전 요구 토크 — 응답 지연 산출 기준
        omega_motor: +omega_m.toFixed(1),
        T_batt: +thState.T_batt.toFixed(1), T_inv: +thState.T_inv.toFixed(1),
        T_motor_temp: +thState.T_motor.toFixed(1), T_cool: +thState.T_cool.toFixed(1),
        derate_batt: +(chain.derate_batt || 100), derate_inv: +(chain.derate_inv || 100), derate_motor: +(chain.derate_motor || 100),
        // [v7] 신규 필드
        F_rough: +(res.F_rough || 0).toFixed(1),
        delta_Crr_p: +((res.delta_Crr_pressure || 0) * 1000).toFixed(2),  // ×10³
        T_gear: +T_gear.toFixed(1),
        eta_gear_v7: +(gear_loss?.eta_gear || params.eta_driveline).toFixed(4),
        mu_rls: +rls_state.mu_est.toFixed(3),
        regen_blend: regen_result?._blend?.blend_factor ?? 0,
        sei_R: +(sei_state?.R_sei || 0).toFixed(5),
        li_plating: sei_state?.li_plating_count || 0,
        pump_power: +(cool_state.pump_power || 0).toFixed(1),
        coolant_flow: +(cool_state.flow_rate || 0).toFixed(2),
        drive_mode: params.drive_mode || 'NORMAL', throttle_eff: +mode_throttle.toFixed(3),
        foc_mode: foc_state_sl ? (foc_state_sl.foc_mode || (params.foc_current_pi_enable ? 'PI' : 'LPF')) : (params.foc_current_pi_enable ? 'PI' : 'LPF'),
        // [SVPWM-Log] SVPWM 변조 결과 — MotorGroupContent SVPWM 탭 시각화용
        svpwm_m_a:     foc_state_sl?.svpwm?.m_a          ?? null,
        svpwm_sector:  foc_state_sl?.svpwm?.sector       ?? null,
        svpwm_mode:    foc_state_sl?.svpwm?.pwm_mode_active ?? null,
        svpwm_sixstep: foc_state_sl?.svpwm?.is_sixstep   ?? false,
        svpwm_duty_A:  foc_state_sl?.svpwm?.duty_A       ?? null,
        svpwm_duty_B:  foc_state_sl?.svpwm?.duty_B       ?? null,
        svpwm_duty_C:  foc_state_sl?.svpwm?.duty_C       ?? null,
        // [Sensorless-Fix] 센서리스 관측기 추정 결과 로깅
        sensorless_theta: sensorless_state ? +(sensorless_state.theta_est || 0).toFixed(4) : null,
        sensorless_omega: sensorless_state ? +(sensorless_state.omega_est || 0).toFixed(2) : null,
        sensorless_conf:  sensorless_state ? +(sensorless_state.confidence || 0).toFixed(3) : null,
        sensorless_method: sensorless_state ? (sensorless_state.method || null) : null,
        m_eff: +m_eff.toFixed(0),
        // v3 additions
        tcs_active: tcs_result.active, tcs_T_cut: tcs_result.active ? +(T_mot_raw - T_mot_tcs).toFixed(1) : 0,
        P_iron_steinmetz: +steinmetz.P_iron_v3.toFixed(1), P_hys: +steinmetz.P_hys.toFixed(1), P_eddy: +steinmetz.P_eddy.toFixed(1),
        CdA_v3: +(res.CdA_v3 || params.CdA).toFixed(4), shutter_open: res.shutter_open,
        delta_F_wind: +(res.delta_F_wind || 0).toFixed(1), crosswind_on: res.crosswind_active,
        // [R-12-02] Launch Control 데이터
        lc_active: lc_result ? lc_result.lc_active : false,
        lc_slip: lc_result ? +lc_result.slip.toFixed(4) : 0,
        lc_e_slip: lc_result ? +lc_result.e_slip.toFixed(4) : 0,
        // v4 AWD/TV
        awd_mode: tv_result.mode || 'RWD',
        lambda_fr: tv_result.lambda_fr !== undefined ? +tv_result.lambda_fr.toFixed(3) : 0,
        TV_active: tv_result.TV_active || false,
        yaw_rate_ref: +(tv_result.yaw_rate_ref || 0).toFixed(4),
        yaw_error: +(tv_result.yaw_error || 0).toFixed(4),
        Ku: +(tv_result.Ku || 0).toFixed(6),
        M_z_required: +(tv_result.M_z_required || 0).toFixed(1),
        friction_scale: tv_result.friction_scale !== undefined ? +tv_result.friction_scale.toFixed(4) : 1,
        mu_hat: +mu_hat.toFixed(3),
        Fz_FL: tv_result.loads ? +tv_result.loads.Fz_FL.toFixed(0) : 0,
        Fz_FR: tv_result.loads ? +tv_result.loads.Fz_FR.toFixed(0) : 0,
        Fz_RL: tv_result.loads ? +tv_result.loads.Fz_RL.toFixed(0) : 0,
        Fz_RR: tv_result.loads ? +tv_result.loads.Fz_RR.toFixed(0) : 0,
        // [R-07] per-wheel 토크 분배 (TV-7 완성)
        T_FL: tv_result.T_FL !== undefined ? +tv_result.T_FL.toFixed(1) : 0,
        T_FR: tv_result.T_FR !== undefined ? +tv_result.T_FR.toFixed(1) : 0,
        T_RL: tv_result.T_RL !== undefined ? +tv_result.T_RL.toFixed(1) : 0,
        T_RR: tv_result.T_RR !== undefined ? +tv_result.T_RR.toFixed(1) : 0,
        T_front_total: tv_result.T_front !== undefined ? +tv_result.T_front.toFixed(1) : 0,
        T_rear_total:  tv_result.T_rear  !== undefined ? +tv_result.T_rear.toFixed(1)  : +T_mot.toFixed(1),
        // v5 new fields
        fw_regime: curRef.regime || 'mtpa',
        is_regen: is_regen,
        T_regen: regen_result ? +regen_result.T_regen.toFixed(1) : 0,
        T_friction_brake: regen_result ? +regen_result.T_friction.toFixed(1) : 0,
        P_regen: regen_result ? +(regen_result.P_regen / 1000).toFixed(2) : 0,
        esc_active: esc_result.active || false,
        esc_M_z: +(esc_result.M_esc || 0).toFixed(1),
        vd_stability: vd_state_sl?.stability?.level || 'N/A',   // [P3-대책A]
        vd_beta_hat:  +(vd_state_sl?.beta_hat   || 0).toFixed(4), // [P3-대책A]
        psi_dot: +(lateral_state.psi_dot || 0).toFixed(4),
        beta: +(lateral_state.beta || 0).toFixed(4),
        ay_lat: +(lateral_state.ay || 0).toFixed(3),
        // ── [Step3] 7DOF 출력 필드 ──
        roll_angle_deg: +(lateral_state.roll_angle_deg || 0).toFixed(3),
        phi:   +(lateral_state.phi    || 0).toFixed(5),
        z_cg:  +(lateral_state.z_cg   || 0).toFixed(5),
        Fz_f_lat: +(lateral_state.Fz_f || 0).toFixed(0),
        Fz_r_lat: +(lateral_state.Fz_r || 0).toFixed(0),
        // ── [Step4] β EKF 출력 필드 ──
        beta_hat:    beta_ekf_result_sl ? +beta_ekf_result_sl.beta_hat.toFixed(5)    : +(lateral_state.beta || 0).toFixed(5),
        psi_dot_hat: beta_ekf_result_sl ? +beta_ekf_result_sl.psi_dot_hat.toFixed(5) : +(lateral_state.psi_dot || 0).toFixed(5),
        ekf_innov_ay:  beta_ekf_result_sl ? +beta_ekf_result_sl.innov_ay.toFixed(4)  : 0,
        ekf_innov_psi: beta_ekf_result_sl ? +beta_ekf_result_sl.innov_psi.toFixed(4) : 0,
        beta_ekf_active: !!(beta_ekf_sl && params.beta_ekf_enable),
        V_terminal: batt2rc_result ? +batt2rc_result.V_terminal.toFixed(2) : +(params.V_dc || 400),
        V_dc_eff: +V_dc_eff.toFixed(2),  // [DC-Dyn] 실효 DC 링크 전압
        V_dc_sag: +(( params.V_dc || 400) - V_dc_eff).toFixed(2),  // 전압 새그
        R0_batt: batt2rc_result ? +batt2rc_result.R0.toFixed(4) : 0,
        soh_pct: soh_state ? +(soh_state.SOH * 100).toFixed(1) : 100,
        li_plating_events: soh_state ? (soh_state.li_plating_events || 0) : 0,
        rainflow_damage: rf_state_sl ? +(rf_state_sl.total_damage || 0).toFixed(5) : 0,       // [Parity-SL-RF]
        rainflow_cycles: rf_state_sl ? +(rf_state_sl.cycle_count_rf || 0).toFixed(1) : 0,     // [Parity-SL-RF]
        V_dt_loss: +dtc_V_loss_sl.toFixed(3),                                                  // [D3-DTC] deadtime 보상 전압 손실 [V]
        steer_T_feedback: steer_state_sl._last ? +steer_state_sl._last.T_feedback.toFixed(3) : 0, // [D6-STR]
        steer_SAT:        steer_state_sl._last ? +steer_state_sl._last.SAT.toFixed(3)        : 0, // [D6-STR]
        steer_angle_wheel: steer_state_sl._last ? +steer_state_sl._last.steer_angle_wheel.toFixed(4) : 0, // [D6-STR]
        // [R-17] v04 신규 필드
        kalman_soc: +(soc_kalman * 100).toFixed(2),
        bms_max_dv: +(bms_max_dv * 1000).toFixed(1),  // [mV]
        cool_phi_flow: +(cool_state.phi_flow || 1.0).toFixed(3),
        li_plating_risk: li_plating_risk,  // [R-06-G]
        // [R-10-01] GAP-06: PID 드라이버 필드
        driver_pedal: params.driver_mpc_enable && mpc_drv_state_sl
          ? +mpc_drv_state_sl.pedal_prev.toFixed(3)
          : (drv_state ? +drv_state.pedal_prev.toFixed(3) : +mode_throttle.toFixed(3)),
        driver_pid_active: !!(params.driver_pid_enable !== false && drv_state && !params.driver_mpc_enable && !params.cascade_speed_enable),
        // ── [Step5] MPC 드라이버 필드 ──
        driver_mpc_active: !!(params.driver_mpc_enable && mpc_drv_state_sl && !params.cascade_speed_enable),
        mpc_cost: mpc_cost_sl,
        // ── [CascadePID] Cascade 속도 PI 필드 ──
        cascade_active: !!(params.cascade_speed_enable && cascade_spd_state_sl),
        cascade_T_cmd: params.cascade_speed_enable && cascade_spd_state_sl
          ? +(cascade_spd_state_sl.integral * (params.Ki_spd || 3003) / 1000 + 0).toFixed(1)
          : 0,
        cascade_bw_warn: cascade_bw_warn_sl,
        // [R-10-02] GAP-10: 다단 기어 필드
        gear_num: current_gear,
        gear_ratio_eff: +gear_ratio_eff.toFixed(3),
        gear_shifting: !!(gear_state && gear_state.is_shifting),
        torque_scale_gear: +torque_scale.toFixed(3),
        // [R-10-03] GAP-05: 3D 열 분포 필드
        hotspot_T: thermal_3d ? +thermal_3d.T_max.toFixed(1) : +thState.T_batt.toFixed(1),
        T_delta_3d: thermal_3d ? +thermal_3d.T_delta.toFixed(2) : 0,
        derate_3d: +derate3d.toFixed(3),
        ...chain,
      });
    }

    a_prev = a;
    v += a * dt; if (v < 0) v = 0;
    if (t_reach === null && v >= v_tgt) {
      t_reach = t;
      s_at_tgt      = s;        // [A68-01-Fix-L3] 도달 시점 거리 스냅샷
      E_batt_at_tgt  = E_batt;  // [A68-01-Fix-L3] 도달 시점 배터리 에너지
      E_wheel_at_tgt = E_wheel; // [A68-01-Fix-L3] 도달 시점 휠 에너지
    }
    s += v * dt; t += dt;
  }
  return {
    data,
    time: t_reach !== null ? +t_reach.toFixed(3) : 999,
    time_s: t_reach !== null ? +t_reach.toFixed(3) : 999,
    distance: +s.toFixed(2),
    dist_m: +s.toFixed(2),
    // [A68-01-Fix-L3] v_target 도달 시점 KPI (가속 구간 전용)
    //   수정 전: distance/E_batt_Wh/eta_cycle = 30초 전체 누적 → 잘못된 0→v_target 가속 KPI
    //   수정 후: dist_at_target / E_batt_at_target_Wh / eta_at_target 로 0→v_target 구간만 정확 산출
    dist_at_target:       s_at_tgt     != null ? +s_at_tgt.toFixed(2)                          : +s.toFixed(2),
    E_batt_at_target_Wh: E_batt_at_tgt != null ? +(E_batt_at_tgt / 3600).toFixed(1)             : +(E_batt / 3600).toFixed(1),
    eta_at_target:        (E_batt_at_tgt && E_batt_at_tgt > 0) ? +(E_wheel_at_tgt / E_batt_at_tgt * 100).toFixed(1) : 0,
    E_batt_Wh: +(E_batt / 3600).toFixed(1), E_wheel_Wh: +(E_wheel / 3600).toFixed(1),
    E_loss_Wh: +(E_loss / 3600).toFixed(1),
    eta_cycle: E_batt > 0 ? +(E_wheel / E_batt * 100).toFixed(1) : 0,
    thFinal: { ...thState },
    drive_mode: params.drive_mode || 'NORMAL',
    m_eff,
    soh_state: soh_state ? { soh: soh_state.SOH, soh_pct: +(soh_state.SOH * 100).toFixed(1) } : null, // [N5]
    rainflow_damage: rf_state_sl ? +(rf_state_sl.total_damage || 0).toFixed(5) : 0,  // [N5]
    rainflow_cycles: rf_state_sl ? +(rf_state_sl.cycle_count_rf || 0).toFixed(1) : 0, // [N5]
    // [v7] 신규 요약 지표
    v7: {
      sei_R_final:       sei_state ? +(sei_state.R_sei).toFixed(5) : 0,
      li_plating_events: sei_state ? sei_state.li_plating_count : 0,
      T_gear_final:      +T_gear.toFixed(1),
      mu_rls_final:      +rls_state.mu_est.toFixed(3),
      pump_energy_Wh:    +(cool_state.pump_power * t / 3600).toFixed(2),
      IRI_used:          params.IRI || 2.5,
      delta_Crr_pressure: +((lastRes?.delta_Crr_pressure) || 0).toFixed(5),
      profile_soh:       soh_profile.label || 'NMC 622',
    },
  };
}

// ── simCoast ──
/**
 * 코스팅(관성 주행) 시뮬레이션을 실행한다 (dt=50 ms, v>0.5 m/s until t<300 s).
 * [BugFix6] dt 0.005→0.05 (10배 속도), [Fix-CT1] effectiveMass 적용.
 * 2RC 배터리, 3D 열, 회생제동, RLS µ, SOH, 기어 등 simLaunch 패리티.
 *
 * @param {object} params       차량 파라미터
 * @param {number} v_init_kmh   초기 속도 [km/h]
 * @param {number} grade_deg    경사도 [°]
 * @returns {{ data, time, distance, E_aux_kWh, ... }}
 */
function simCoast(params, v_init_kmh, grade_deg) {
  const dt = 0.05, dt_th = 0.5; // [BugFix6] 성능 개선: dt 0.005→0.05 (10배 빠름, 코스팅 정확도 충분)
  const grade_rad = (grade_deg * Math.PI) / 180;
  let v = v_init_kmh / 3.6, s = 0, t = 0, soc = params.soc_init;
  let thState = { T_batt: params.T_amb, T_inv: params.T_amb, T_motor: params.T_amb, T_cool: params.T_amb };
  // [Fix-CT1] effectiveMass: simLaunch/simReaccel 패리티
  const m_eff_coast = effectiveMass(params);
  let E_aux = 0, E_KE_init = 0.5 * m_eff_coast * v * v;
  let E_aero = 0, E_roll = 0, E_grade = 0, E_drivetrain = 0;
  let th_accum = 0, Q_m_acc = 0;
  // [Parity-C1] Kalman SOC 추정 — simLaunch 패리티
  let kf_state_coast   = params.kalman_enable !== false ? initKalmanByType(soc, params) : null;
  let soc_kalman_coast = soc;
  // [Parity-C2] 2RC 배터리 모델 — simLaunch/simReaccel 패리티
  let battery_2rc_c   = params.battery_model === '2RC' ? init2RCBatteryState(params) : null;
  let batt2rc_state_c = params.V_dc || 400;
  let batt2rc_result_c = null;
  // [Parity-C3] 3D 열분포 — simLaunch/simReaccel 패리티
  let thermal_3d_c = params.thermal_3d_enable ? init3DThermalState(params) : null;
  // [Parity-C4] 냉각 유량 제어 — thermalStep phi_flow 반영
  let cool_state_c = initCoolantFlowState(params);
  // [Parity-C5] RLS µ 추정 — simLaunch/simReaccel 패리티
  let rls_state_c = initRLSMuState(params);
  let a_prev_c = 0;
  // [D13-CT] 다단 기어 상태 — simCoast getActiveGear 연결
  let gear_state_c = params.multi_gear_enable ? initGearState(params) : null;
  let T_gear_c     = params.gear_T_oil || 80;   // [P3] 기어박스 온도 추적
  // [P4] SOH + Rainflow — simLaunch 패리티
  let soh_state_c = params.soh_enable ? initSOHState(params) : null;
  let rf_state_c  = (params.soh_enable && params.rainflow_enable) ? initRainflowState(params) : null;
  const data = [];

  while (v > 0.5 && t < 300) {
    // [D13-CT] 다단 기어: 코스팅 구간 실효 기어비 (구동 토크=0으로 근사)
    let gear_ratio_eff_c = params.gear_ratio || 9.5;
    if (params.multi_gear_enable && gear_state_c) {
      const gearOut_c = getActiveGear(v, 0, soc, gear_state_c, params, dt, t);
      gear_ratio_eff_c = gearOut_c.gear_ratio_eff;
      gear_state_c     = gearOut_c.gear_state_new;
    }
    const omega_m = v / params.r_wheel * gear_ratio_eff_c;
    // [v7] 저항력 통합 산출 (공기압·IRI 보정 포함)
    const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
    // Drivetrain drag (gear + motor mech + iron loss)
    const gear = gearLossTempEnhanced(omega_m, T_gear_c, 0, params);  // [P3] T_gear_c 반영
    T_gear_c   = gearThermalStep(T_gear_c, gear.P_loss, v, params, dt); // [P3]
    const tf_m = thState.T_motor < 0 ? 1.3 : thState.T_motor < 10 ? 1.15 : 1.0;
    const P_mech_drag = (params.k_m0 + params.k_m1 * omega_m + params.k_m2 * omega_m * omega_m) * tf_m;
    const omega_e = omega_m * (params.poles / 2);
    const f_e = omega_e / (2 * Math.PI);
    const lpm = lambda_pm_T(thState.T_motor, params);
    const B_est = Math.min(1.5, lpm * 2);
    const P_fe = (params.k_h * f_e * Math.pow(Math.max(B_est, 0.01), 1.6) + params.k_e * f_e * f_e * B_est * B_est) * 50;
    const P_dt_drag = gear.P_loss + P_mech_drag + P_fe;
    const F_dt_drag = v > 0.1 ? P_dt_drag / v : 0;
    // [v5-fix4] Coast 중 회생제동
    let F_regen_coast = 0;
    const regen_coast_on = params.regen_coast_enable !== false && driveModeRegenStrength(params) > 0.05; // [BugFix-A05-04] normal 하드코딩 → 현재 모드 기준
    if (regen_coast_on && omega_m > (params.regen_blend_low_speed || 5)) {
      const brake_d = driveModeRegenStrength(params) * 0.5;
      const regen_c_raw = regenTorqueEnhanced(omega_m, brake_d, params, soc, thState.T_batt, thState.T_motor, thState.T_inv);
      // [Fix-RB-1] S-커브 블렌딩 적용 — 저속 fade-out, SOC 상한 보호
      const blend_c = regenFrictionBlend(regen_c_raw.T_regen, regen_c_raw.T_friction, v, soc, omega_m, params, dt);
      const regen_c = { ...regen_c_raw, T_regen: blend_c.T_regen, T_friction: blend_c.T_friction };
      // [Fix-RB-2] 저크 제한용 T_regen_prev 갱신
      params._regen_T_prev = blend_c.T_regen;
      if (regen_c.T_regen > 0) {
        F_regen_coast = regen_c.T_regen * gear_ratio_eff_c / params.r_wheel;
        const P_chg_coast = regen_c.P_regen * 0.85;
        soc = socUpdate(soc, -P_chg_coast, dt, params);
      }
    }
    const F_total = res.F_total + F_dt_drag + F_regen_coast;
    const a = -F_total / m_eff_coast; // [Fix-CT1] effectiveMass
    // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
    const _hvac2 = hvacDetailedPower(params.T_amb, v, params.soc_init || params.soc_current || 0.8, params);
    params._hvac_prev_mode = _hvac2.new_hvac_mode;
    const P_aux = _hvac2.P;
    soc = socUpdate(soc, P_aux, dt, params);
    // [Parity-C2] 2RC 배터리 모델 스텝 — V_terminal 추적
    if (battery_2rc_c && params.battery_model === '2RC') {
      const I_batt_c = P_aux / Math.max(1, batt2rc_state_c);
      batt2rc_result_c = battery2RCModelWithSEI(battery_2rc_c, I_batt_c, dt, params, null);
      battery_2rc_c.SOC    = soc;
      battery_2rc_c.T_cell = thState.T_batt;
      if (batt2rc_result_c?.V_terminal) {
        batt2rc_state_c = batt2rc_state_c * 0.9 + batt2rc_result_c.V_terminal * 0.1;
      }
    }
    // [Parity-C1] Kalman SOC 업데이트 — 2RC V_terminal 기반
    if (kf_state_coast && params.kalman_enable !== false) {
      const V_meas_c = batt2rc_result_c?.V_terminal ?? batteryOCV_smart(soc, params, thState.T_batt);
      const I_c      = P_aux / Math.max(1, batt2rc_state_c);
      kf_state_coast   = kalmanSOCUpdate(kf_state_coast, V_meas_c, I_c, soc, V_meas_c, params);
      soc_kalman_coast = kf_state_coast.soc_est !== undefined ? kf_state_coast.soc_est : soc;
      if (Math.abs(soc_kalman_coast - soc) < 0.15) soc = soc_kalman_coast;
    }
    // [Parity-C5] RLS µ 추정 — 코스팅 구간: F_drv=0 (회생력만 관여)
    const F_normal_c = params.m * g * Math.cos(grade_rad);
    rls_state_c = rlsMuUpdate(rls_state_c, -F_regen_coast, F_normal_c, a_prev_c, params.m, params);
    a_prev_c = a;
    E_aux += P_aux * dt;
    E_aero += res.F_aero * v * dt; E_roll += res.F_roll * v * dt;
    E_grade += res.F_grade * v * dt; E_drivetrain += P_dt_drag * dt;
    Q_m_acc += (P_mech_drag + P_fe) * dt; th_accum += dt;
    if (th_accum >= dt_th) {
      // [Parity-C4] coolantFlowControl → phi_flow → thermalStep
      cool_state_c = coolantFlowControl(cool_state_c, thState.T_cool, params, th_accum);
      thState = thermalStep(thState, 0, 0, Q_m_acc / th_accum, v, params, th_accum, cool_state_c.phi_flow);
      // [Parity-C3] 3D 열분포 업데이트
      if (params.thermal_3d_enable && thermal_3d_c) {
        const Q_3d_c = Q_m_acc; // motor/iron 열만 반영
        thermal_3d_c = update3DThermalStep(thermal_3d_c, Q_3d_c, cool_state_c.phi_flow, thState.T_cool, th_accum, params);
      }
      Q_m_acc = 0; th_accum = 0;
    }
    // [P4] SOH + Rainflow — simLaunch 패리티 (코스팅: 회생 소전류 구간)
    if (soh_state_c && params.soh_enable) {
      const batt_snap_c = { SOC: soc, T_cell: thState.T_batt };
      soh_state_c = rf_state_c
        ? updateSOHWithRainflow(soh_state_c, rf_state_c, batt_snap_c, 0, dt, params)
        : updateSOH(soh_state_c, batt_snap_c, 0, dt, params);
    }
    if (t % 0.1 < dt) {
      data.push({
        t: +t.toFixed(2), v_kmh: +(v * 3.6).toFixed(2), s: +s.toFixed(1),
        a: +a.toFixed(4), F_aero: +res.F_aero.toFixed(1), F_roll: +res.F_roll.toFixed(1),
        F_grade: +res.F_grade.toFixed(1), F_total: +F_total.toFixed(1),
        F_dt_drag: +F_dt_drag.toFixed(2),
        P_gear_drag: +(gear.P_loss / 1000).toFixed(3), P_mech_drag: +(P_mech_drag / 1000).toFixed(3),
        P_fe_coast: +(P_fe / 1000).toFixed(3), P_dt_drag: +(P_dt_drag / 1000).toFixed(3),
        soc: +(soc * 100).toFixed(2), kalman_soc: +(soc_kalman_coast * 100).toFixed(2), // [Parity-C1]
        rho_eff: +(res.rho_eff).toFixed(3), Crr_eff: +(res.Crr_eff).toFixed(5),
        T_motor_temp: +thState.T_motor.toFixed(1),
        F_regen_coast: +F_regen_coast.toFixed(2), regen_coast_active: F_regen_coast > 0,
        V_terminal: batt2rc_result_c ? +batt2rc_result_c.V_terminal.toFixed(2) : +(params.V_dc || 400), // [Parity-C2]
        hotspot_T:  thermal_3d_c ? +thermal_3d_c.T_max.toFixed(1) : +thState.T_batt.toFixed(1),        // [Parity-C3]
        T_delta_3d: thermal_3d_c ? +thermal_3d_c.T_delta.toFixed(2) : 0,                               // [Parity-C3]
        mu_rls: +rls_state_c.mu_est.toFixed(3),                                                         // [Parity-C5]
        gear_ratio_eff: +gear_ratio_eff_c.toFixed(3),                                                   // [D13-CT]
      });
    }
    v += a * dt; if (v < 0) v = 0; s += v * dt; t += dt;
  }
  const E_KE_final = 0.5 * m_eff_coast * v * v; // [Fix-CT1]
  return {
    data, time: +t.toFixed(2), time_s: +t.toFixed(2), // [v6-fix4] time_s 별칭
    distance: +s.toFixed(1), dist_m: +s.toFixed(1),
    soc_final:        +(soc * 100).toFixed(2),          // [Parity-C1]
    soc_kalman_final: +(soc_kalman_coast * 100).toFixed(2),
    V_terminal_final: batt2rc_result_c ? +batt2rc_result_c.V_terminal.toFixed(2) : +(params.V_dc || 400), // [Parity-C2]
    hotspot_T:  thermal_3d_c ? +thermal_3d_c.T_max.toFixed(1) : +thState.T_batt.toFixed(1),              // [Parity-C3]
    mu_rls_final: +rls_state_c.mu_est.toFixed(3),                                                         // [Parity-C5]
    E_aux_Wh: +(E_aux / 3600).toFixed(1),
    E_KE_init_Wh: +(E_KE_init / 3600).toFixed(1), E_KE_final_Wh: +(E_KE_final / 3600).toFixed(1),
    E_aero_Wh: +(E_aero / 3600).toFixed(1), E_roll_Wh: +(E_roll / 3600).toFixed(1),
    E_grade_Wh: +(E_grade / 3600).toFixed(1), E_dt_Wh: +(E_drivetrain / 3600).toFixed(1),
    T_gear_final: +T_gear_c.toFixed(1),  // [P3]
    rainflow_damage: rf_state_c ? +(rf_state_c.total_damage || 0).toFixed(5) : 0,   // [P4]
    rainflow_cycles: rf_state_c ? +(rf_state_c.cycle_count_rf || 0).toFixed(1) : 0, // [P4]
    soh_final: soh_state_c ? +(soh_state_c.soh * 100).toFixed(2) : null,            // [P4]
  };
}


// ══════════════════════════════════════════════════════════════════════════
// [enhanced-6] simReaccel — 실차 CSV 자동 비교
// ══════════════════════════════════════════════════════════════════════════
// [R-06-H deleted] compareReaccelWithCSV

// ── simReaccel ──
/**
 * 재가속(재출력) 시뮬레이션을 실행한다 (dt=5 ms, v0 → v1).
 * simLaunch와 동일 물리 파이프라인(FOC, 2RC, 7DOF, ESC 등). [Fix-L1] 노면 µ 반영.
 *
 * @param {object} params       차량 파라미터
 * @param {number} v0_kmh       시작 속도 [km/h]
 * @param {number} v1_kmh       목표 속도 [km/h]
 * @param {number} throttle     스로틀 개도 [0~1]
 * @param {number} grade_deg    경사도 [°]
 * @returns {{ data, time, distance, E_net_kWh, ... }}
 */
function simReaccel(params, v0_kmh, v1_kmh, throttle, grade_deg) {
  const dt = 0.005, dt_th = 0.05;
  const grade_rad = (grade_deg * Math.PI) / 180;
  // [Fix-L1] simLaunch와 동일: road_condition µ → mu_eff로 초기화
  const mu_eff = ROAD_MU[params.road_condition] || params.mu || 1.0; // [Fix-L1]
  let v = v0_kmh / 3.6, s = 0, t = 0, soc = params.soc_init;
  let Id = 0, Iq = 0, a_prev = 0;
  // ── [Step1] motorFOCStep 상태 ──
  let foc_state_ra = (_initFOCState !== null) ? _initFOCState() : { Id:0, Iq:0, int_d:0, int_q:0, rep_d_state:null, rep_q_state:null };
  // [DC-Dyn] simReaccel DC 링크 상태
  let batt2rc_state_ra = params.V_dc || 400;
  let thState = { T_batt: params.T_amb, T_inv: params.T_amb, T_motor: params.T_amb, T_cool: params.T_amb };
  // [v5] parity with simLaunch
  let battery_2rc = params.battery_model === '2RC' ? init2RCBatteryState(params) : null;
  let soh_state   = params.soh_enable ? initSOHState(params) : null;
  let rf_state_ra = (params.soh_enable && params.rainflow_enable) ? initRainflowState(params) : null; // [P4]
  let tv_state    = initTorqueVectoringState();
  let esc_state   = initESCState();
  // [D6-STR] 조향 피드백 모델 상태 — simReaccel (simLaunch 패리티)
  let steer_state_ra = { delta_sw: 0, delta_sw_dot: 0, T_driver: 0 };
  // ── [Step3] lateral_state 7DOF 확장 ──
  let lateral_state = {
    beta: 0, psi_dot: 0, psi: 0, ay: 0,
    phi: 0, phi_dot: 0, z_cg: 0, z_dot: 0,
    Fz_FL: null, Fz_FR: null, Fz_RL: null, Fz_RR: null,
    lambda_f: 0, lambda_r: 0,
  };
  // ── [Step3] 4륜 서스펜션 상태 (simReaccel 패리티) ──
  let fw_susp_state_ra = (params.seven_dof_enable && params.suspension_enable)
    ? initFourWheelSuspension(params) : null;
  // ── [Step4] β EKF 상태 (simReaccel 패리티) ──
  let beta_ekf_ra = (params.beta_ekf_enable && params.lateral_enable && _initBetaEKF !== null)
    ? _initBetaEKF(params) : null;
  let mu_hat = mu_eff;  // [Fix-L1] road_condition 기반 초기값 (기존: params.mu_ref_nominal || 1.0)
  // [v7] 신규 상태
  let rls_state_ra = initRLSMuState(params);
  // [R-16] simReaccel 완전 패리티: v04 신규 상태 추가
  let sei_state_ra  = params.sei_enable ? initSEIState(params) : null;
  let cool_state_ra = initCoolantFlowState(params);
  let kf_state_ra   = params.kalman_enable !== false ? initKalmanByType(params.soc_init, params) : null;
  let soc_kalman_ra = params.soc_init;
  // [R-05] 반복제어(RC) 토크리플 보상 상태 초기화 (simReaccel 패리티)
  let rep_d_state_r = params.rc_ripple_enable ? initRepetitiveControllerState(params) : null;
  let rep_q_state_r = params.rc_ripple_enable ? initRepetitiveControllerState(params) : null;
  let bms_state_ra  = params.bms_detailed_enable ? initBMSCells(params) : null;
  // tirePressureCrr 통합: resistanceForcesV3 내부 처리
  let T_gear_ra = params.gear_T_oil || 80;
  const m_eff = effectiveMass(params);
  // [R-10-01] GAP-06: PID 드라이버 상태 초기화 (simReaccel)
  let drv_state_ra   = params.driver_pid_enable !== false ? initDriverPIDState() : null;
  // ── [Step5] MPC 드라이버 상태 (simReaccel 패리티) ──
  let mpc_drv_state_ra = params.driver_mpc_enable ? initDriverMPCState() : null;
  // ── [CascadePID] Cascade 속도 PI 루프 상태 (simReaccel 패리티) ──
  let cascade_spd_state_ra = params.cascade_speed_enable ? initCascadeSpeedState() : null;
  const mode_throttle_fixed_ra = driveModeThrottle(throttle, params, 0); // v=0 초기값, 루프 내에서 갱신
  // [R-10-02] GAP-10: 다단 기어 변속기 상태 초기화 (simReaccel)
  let gear_state_ra  = params.multi_gear_enable ? initGearState(params) : null;
  // [BugFix] thermal_3d 초기화 추가 — simLaunch 패리티 확보
  // 원인: deratingFactor3D(thermal_3d, params) 호출 시 ReferenceError "thermal_3d is not defined"
  // 수정: simLaunch와 동일하게 thermal_3d_enable 게이팅 초기화
  let thermal_3d = params.thermal_3d_enable ? init3DThermalState(params) : null;
  let E_batt = 0, E_wheel = 0, th_accum = 0, Q_b_acc = 0, Q_i_acc = 0, Q_m_acc = 0;
  const data = [], v_tgt = v1_kmh / 3.6;
  let t_reach = null;
  // [A68-02-Fix-RA1] v_target 도달 시점 스냅샷: distance/E는 30초 전체 누적이 아닌 v0→v1 구간만
  let s_at_tgt = null, E_batt_at_tgt = null, E_wheel_at_tgt = null;

  // [Advance02] simReaccel 토크 응답 필터 상태
  let T_mot_filtered_ra = 0;
  let T_slew_ra = 0;  // [Fix-RA2] 슬루레이트 필터 상태값 (simLaunch T_slew_sl과 동일)

  while (t < 30) {
    // [R-10-01] GAP-06: 드라이버 모델 → 목표 속도 추종 (simReaccel)
    // [CascadePID] 우선순위: Cascade 속도PI > MPC > PID > throttle 고정
    let mode_throttle;
    let mpc_cost_ra = 0;
    let cascade_bw_warn_ra = false;
    if (params.cascade_speed_enable && cascade_spd_state_ra) {
      // ── [CascadePID] L2 속도 PI (simReaccel 패리티) ──────────────────
      const casc_out_ra = cascadeSpeedLoopStep(v_tgt, v, cascade_spd_state_ra, params, dt);
      cascade_spd_state_ra = casc_out_ra.new_state;
      cascade_bw_warn_ra   = casc_out_ra.bw_sep_warn;
      mode_throttle    = driveModeThrottle(Math.min(casc_out_ra.pedal_pos, throttle), params, v * 3.6);
    } else if (params.driver_mpc_enable && mpc_drv_state_ra) {
      // ── [Step5] MPC 드라이버 (simReaccel 패리티) ──────────────────────
      const mpc_out_ra = driverMPCController(v_tgt, v, mpc_drv_state_ra, params, dt);
      mpc_drv_state_ra = mpc_out_ra.new_state;
      mode_throttle    = driveModeThrottle(Math.min(mpc_out_ra.pedal_pos, throttle), params, v * 3.6);
      mpc_cost_ra      = mpc_out_ra.cost;
    } else if (params.driver_pid_enable !== false && drv_state_ra) {
      const pid_out_ra = driverPIDController(v_tgt, v, drv_state_ra, params, dt);
      drv_state_ra   = pid_out_ra.new_state;
      // [Fix-RA3] throttle 상한 클램프 누락 수정
      mode_throttle  = driveModeThrottle(Math.min(pid_out_ra.pedal_pos, throttle), params, v * 3.6);
    } else {
      // [BugFix-A04-01] fallback도 현재 v_kmh로 갱신
      mode_throttle = driveModeThrottle(throttle, params, v * 3.6);
    }
    // [R-10-02] GAP-10: 다단 기어 (simReaccel)
    let gear_ratio_eff_ra = params.gear_ratio || 9.5;
    let torque_scale_ra   = 1.0;
    let current_gear_ra   = 1;
    if (params.multi_gear_enable && gear_state_ra) {
      const gearOut_ra = getActiveGear(v, params.T_max_motor * mode_throttle, soc, gear_state_ra, params, dt, t);
      gear_ratio_eff_ra = gearOut_ra.gear_ratio_eff;
      torque_scale_ra   = gearOut_ra.torque_scale;
      current_gear_ra   = gearOut_ra.gear;
      gear_state_ra     = gearOut_ra.gear_state_new;
    }
    const omega_m = v / params.r_wheel * gear_ratio_eff_ra;
    const omega_e = omega_m * (params.poles / 2);
    // [DC-Dyn] simReaccel DC 링크 전압
    const V_dc_eff = (params.dc_link_dynamics_enable && batt2rc_state_ra)
      ? Math.min(
          Math.max(batt2rc_state_ra, params.V_dc_min || 250),
          params.V_dc_max || (params.V_dc || 400) * 1.1
        )
      : (params.V_dc || 400);

    // [v5] MTPV/FW 기반 전류 지령
    const curRef = computeCurrentReferenceWithMTPV(omega_e, params.T_max_motor, V_dc_eff, params, thState.T_motor);

    // TCS
    const thermDerate_ra = thermalDeratingFactor(thState, params);
    const derate3d_ra    = deratingFactor3D(thermal_3d, params);
    const T_mot_ideal_ra = motorTorqueSpeed(omega_m, mode_throttle, params, thState.T_motor, thState.T_inv, thState.T_batt)
      * thermDerate_ra.f_total * derate3d_ra * torque_scale_ra;
    // [Advance02] 토크 응답 필터 (simReaccel)
    // [Fix-RA2] ✅ 토크 필터 순서 수정: 슬루레이트 먼저 → LPF 추종 (simLaunch A43-Fix-1 패리티)
    //   수정 전: LPF(T_filtered += α×(T_ideal−T_filtered)) 먼저 → 이후 슬루레이트 클램프
    //            → LPF가 이미 스무딩한 값에 슬루 제한 → SPORT 모드 정상상태 ±진동 발생 가능
    //   수정 후: T_ideal → 슬루 제한(T_slew_ra) 생성 → LPF가 T_slew_ra 추종 → 단조 수렴
    const _mode_ra = params.drive_mode || 'NORMAL';
    const tau_ra   = { ECO: params.tau_torque_eco||0.08, NORMAL: params.tau_torque_normal||0.04,
                       SPORT: params.tau_torque_sport||0.02, SNOW: params.tau_torque_snow||0.12,
                       OFF_ROAD: params.tau_torque_offroad||0.06 }[_mode_ra] || 0.04;
    const slew_ra  = { ECO: params.torque_slew_rate_eco||500, NORMAL: params.torque_slew_rate_normal||1500,
                       SPORT: params.torque_slew_rate_sport||3000, SNOW: params.torque_slew_rate_snow||300,
                       OFF_ROAD: params.torque_slew_rate_offroad||800 }[_mode_ra] || 1500;
    const alpha_ra = dt / (tau_ra + dt);
    const dT_max_ra = slew_ra * dt;
    // [Fix-RA2] Step 1: 슬루레이트로 목표값 제한 (T_slew_ra 상태값 필요 — 루프 상단 초기화됨)
    if (T_mot_ideal_ra > T_slew_ra + dT_max_ra) T_slew_ra += dT_max_ra;
    else if (T_mot_ideal_ra < T_slew_ra - dT_max_ra) T_slew_ra -= dT_max_ra;
    else T_slew_ra = T_mot_ideal_ra;
    // [Fix-RA2] Step 2: LPF는 슬루 제한된 목표를 추종 (진동 없이 단조 수렴)
    T_mot_filtered_ra += alpha_ra * (T_slew_ra - T_mot_filtered_ra);
    const T_mot_raw = T_mot_filtered_ra;
    // [A43-Fix-2] ✅ simReaccel TCS 슬립률 계산 수정
    //   수정 전: v > 1 ? 0 : 0 (항상 0) → TCS 완전 비활성화
    //   수정 후: simLaunch와 동일한 슬립 계산 적용 → SNOW 고토크 구간 TCS 정상 작동
    const omega_w_ra  = v / (params.r_wheel || 0.33);
    const v_wheel_ra  = omega_w_ra * (params.r_wheel || 0.33);
    const slip_est    = v > 1 ? (v_wheel_ra - v) / Math.max(v, 0.1) : 0;
    const tcs_result = tcsIntervention(slip_est, T_mot_raw, params);
    const T_mot_tcs  = tcs_result.T_tcs;

    // [Fix-CS-1] lambda_f/r → lateral_state 갱신 (simReaccel 패리티)
    if (params.combined_slip_enable) {
      const lambda_r_ra = slip_est;
      const lambda_f_ra = slip_est * (params.torque_split_front || 0);
      lateral_state = { ...lateral_state, lambda_f: lambda_f_ra, lambda_r: lambda_r_ra };
    }

    // [v5] 횡방향 + ESC
    // ── [Step3] 4륜 서스펜션 → Fz 주입 (simReaccel 패리티) ──────────────
    if (fw_susp_state_ra && params.seven_dof_enable) {
      const z_road_fw = params.road_roughness_enable
        ? (params.IRI || 2.5) * 0.0001 * Math.sin(2 * Math.PI * v * t / 8) : 0;
      const z_roads = { FL: z_road_fw, FR: z_road_fw, RL: z_road_fw, RR: z_road_fw };
      const fw_result = fourWheelSuspensionStep(
        fw_susp_state_ra, z_roads, v, a_prev, lateral_state.ay || 0, params, dt
      );
      fw_susp_state_ra = fw_result.corners;
      lateral_state = {
        ...lateral_state,
        Fz_FL: fw_result.Fz_FL,
        Fz_FR: fw_result.Fz_FR,
        Fz_RL: fw_result.Fz_RL,
        Fz_RR: fw_result.Fz_RR,
      };
    }
    if (params.lateral_enable) {
      lateral_state = lateralDynamicsStep(lateral_state, params.delta_steer_rad || 0, Math.max(v,0.1), null, null, params, dt);
      // [D6-STR] 조향 피드백 모델 — simReaccel 루프 연결 (simLaunch 패리티)
      if (_steeringFeedbackModel !== null) {
        const steerOut_ra = _steeringFeedbackModel(
          steer_state_ra,
          lateral_state.F_yf || 0,
          lateral_state.alpha_f || 0,
          Math.max(v, 0.5), params, dt
        );
        steer_state_ra = {
          delta_sw:     steerOut_ra.delta_sw,
          delta_sw_dot: steerOut_ra.delta_sw_dot,
          T_driver:     steer_state_ra.T_driver,
        };
        steer_state_ra._last = steerOut_ra;
      }
    }

    // ── [Step4] β EKF 업데이트 (simReaccel 패리티) ────────────────────────
    let beta_ekf_result_ra = null;
    if (beta_ekf_ra && params.beta_ekf_enable && params.lateral_enable && _ekfUpdateBeta !== null) {
      const ay_meas_ra  = params.sensor_noise_enable
        ? sensorNoise(lateral_state.ay || 0, params.R_ay_ekf  ? Math.sqrt(params.R_ay_ekf)  : 0.7,  true, params.K_sensor_noise || 1.0)
        : (lateral_state.ay || 0);
      const psi_meas_ra = params.sensor_noise_enable
        ? sensorNoise(lateral_state.psi_dot || 0, params.R_psi_ekf ? Math.sqrt(params.R_psi_ekf) : 0.14, true, params.K_sensor_noise || 1.0)
        : (lateral_state.psi_dot || 0);
      beta_ekf_result_ra = _ekfUpdateBeta(
        beta_ekf_ra,
        ay_meas_ra, psi_meas_ra,
        params.delta_steer_rad || 0,
        Math.max(v, 1.0), params, dt
      );
      beta_ekf_ra = beta_ekf_result_ra.ekf;
      const blend = Math.min(1, Math.max(0, params.beta_ekf_blend ?? 0.7));
      lateral_state = {
        ...lateral_state,
        beta:    blend * beta_ekf_result_ra.beta_hat    + (1 - blend) * lateral_state.beta,
        psi_dot: blend * beta_ekf_result_ra.psi_dot_hat + (1 - blend) * lateral_state.psi_dot,
      };
    }

    const psi_ref_esc = computeYawRateRef_v4(params.delta_steer_rad || 0, v, mu_hat, params).psi_ref;
    const esc_result  = escIntervention(lateral_state.psi_dot || 0, psi_ref_esc, v, params, esc_state, dt);
    const T_mot_esc   = esc_result.active ? T_mot_tcs * (esc_result.torque_cut || 1.0) : T_mot_tcs;

    // [v5] AWD/TV
    const vs_tv = { v, ax: a_prev, ay: lateral_state.ay||0, yaw_rate: lateral_state.psi_dot||0,
                    delta_steer: params.delta_steer_rad||0, mu_hat,
                    slip_FL:0, slip_FR:0, slip_RL:0, slip_RR:0 };
    // [A21-15-03] TV 삼항 데드코드 수정 (simReaccel 패리티)
    // [BugFix-A04-03] awd_calib_enable=true 시에도 torqueVectoring 진입하도록 조건 확장
    const tv_result = ((params.awd_calib_enable || params.torque_vectoring_enable) && params.awd_enable)
      ? torqueVectoring(T_mot_esc, vs_tv, params, tv_state)
      : { T_front: 0, T_rear: T_mot_esc, awd_active: params.awd_enable || false };
    // [A21-15-04] AWD 시 전·후륜 토크 합산 (simReaccel 패리티)
    const T_mot = params.awd_enable
      ? (((tv_result.T_front || 0) + (tv_result.T_rear || 0)) || T_mot_esc)
      : T_mot_esc;

    let F_drv = T_mot * gear_ratio_eff_ra * params.eta_driveline / params.r_wheel;
    // [Fix-RA1] traction clamp: mu_eff(고정 road_condition) → rls_state_ra.mu_est(동적 RLS 추정값)
    //   수정 전: Math.min(F_drv, mu_eff × m × g) — 루프 내 rls_state_ra 업데이트 후에도 mu_eff 고정
    //            simLaunch는 rls_state.mu_est 사용하나 simReaccel은 초기 mu_eff 그대로 → 패리티 불일치
    //   수정 후: Math.min(F_drv, rls_state_ra.mu_est × m × g) — RLS 실시간 추정 반영
    //            (저항력 산출 전 클램프이므로 mu_est는 a_prev 기반 이전 스텝 값 사용 — 인과 관계 정상)
    const F_normal_ra = params.m * g * Math.cos(grade_rad);
    F_drv = Math.min(F_drv, rls_state_ra.mu_est * params.m * g * Math.cos(grade_rad)); // [Fix-RA1]
    // [v7] 저항력 통합 산출 (공기압·IRI 보정 포함)
    const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
    const F_res_total_ra = res.F_total;
    rls_state_ra = rlsMuUpdate(rls_state_ra, F_drv, F_normal_ra, a_prev, params.m, params);
    mu_hat = rls_state_ra.mu_est;
    const a = (F_drv - F_res_total_ra) / m_eff;

    // [v5] MTPV 기반 Iq_ref
    const lpm   = lambda_pm_T(thState.T_motor, params);
    const Iq_ref = curRef.Iq || (T_mot > 0 ? T_mot / (1.5 * (params.poles / 2) * lpm) : 0);
    const Id_ref = curRef.Id || 0;
    // ── [Step1] dq 전류 제어: motorFOCStep (PI 또는 1차 지연) ───────────
    if (_motorFOCStep !== null) {
      foc_state_ra = _motorFOCStep(
        { ...foc_state_ra, Id, Iq },  // theta_e는 foc_state_ra에 누적 전달됨
        Id_ref, Iq_ref, omega_e, lpm,
        thState.T_motor, thState.T_inv, params, dt
      );
      Id = foc_state_ra.Id;
      Iq = foc_state_ra.Iq;
    } else {
      // ── [v9-fix5] 폴백: 인라인 dq 전류 제어 ─────────────────────────
      if (params.foc_current_pi_enable) {
        const e_d = Id_ref - Id, e_q = Iq_ref - Iq;
        if (typeof foc_int_d_r === 'undefined') { let foc_int_d_r = 0, foc_int_q_r = 0; }
        foc_int_d_r += e_d * dt; foc_int_q_r += e_q * dt;
        const V_max_r = (V_dc_eff || 400) * (params.fw_v_margin || 0.95) / Math.sqrt(3);
        const il_r = V_max_r / Math.max(params.Ki_id, 1);
        foc_int_d_r = Math.max(-il_r, Math.min(il_r, foc_int_d_r));
        foc_int_q_r = Math.max(-il_r, Math.min(il_r, foc_int_q_r));
        const lpm_r2 = lambda_pm_T(thState.T_motor, params);
        let Vd_r = params.Kp_id * e_d + params.Ki_id * foc_int_d_r - omega_e * (params.Lq || 0.0006) * Iq;
        let Vq_r = params.Kp_iq * e_q + params.Ki_iq * foc_int_q_r + omega_e * ((params.Ld || 0.0003) * Id + lpm_r2);
        if (params.rc_ripple_enable) {
          if (!rep_d_state_r) rep_d_state_r = initRepetitiveControllerState(params);
          if (!rep_q_state_r) rep_q_state_r = initRepetitiveControllerState(params);
          rep_d_state_r.omega_e = omega_e; rep_q_state_r.omega_e = omega_e;
          Vd_r += repetitiveController(e_d, params, rep_d_state_r) + harmonicCompensation(omega_e, omega_e, Id, Iq, params).V_d_comp;
          Vq_r += repetitiveController(e_q, params, rep_q_state_r) + harmonicCompensation(omega_e, omega_e, Id, Iq, params).V_q_comp;
        }
        const Vm_r = Math.sqrt(Vd_r * Vd_r + Vq_r * Vq_r);
        if (Vm_r > V_max_r) { const vs = V_max_r / Vm_r; Vd_r *= vs; Vq_r *= vs; foc_int_d_r -= e_d * dt; foc_int_q_r -= e_q * dt; }
        const Rs_r = Rs_T(thState.T_motor, params);
        Id += ((Vd_r - Rs_r * Id + omega_e * (params.Lq || 0.0006) * Iq) / (params.Ld || 0.0003)) * dt;
        Iq += ((Vq_r - Rs_r * Iq - omega_e * ((params.Ld || 0.0003) * Id + lpm_r2)) / (params.Lq || 0.0006)) * dt;
        const Im_r = Math.sqrt(Id * Id + Iq * Iq);
        const Ix_r = I_max_T(thState.T_inv, params) || params.I_max_rms;
        if (Im_r > Ix_r) { const sc = Ix_r / Im_r; Id *= sc; Iq *= sc; }
      } else {
        const tau_q = (params.Lq || 0.0006) / (params.Rs || 0.015);
        const alpha_q = Math.min(0.99, dt / (tau_q + dt));
        const tau_d   = (params.Ld || 0.0003) / (params.Rs || 0.015);
        const alpha_d = Math.min(0.99, dt / (tau_d + dt));
        Id = Id + alpha_d * (Id_ref - Id);
        Iq = Iq + alpha_q * (Iq_ref - Iq);
        const I_mag_dq = Math.sqrt(Id * Id + Iq * Iq);
        const I_max_eff = I_max_T(thState.T_inv, params) || params.I_max_rms;
        if (I_mag_dq > I_max_eff) { const sc = I_max_eff / I_mag_dq; Id *= sc; Iq *= sc; }
      }
    }
    const chain  = computeEnergyChain(v, omega_m, T_mot, Id, Iq, soc, params, grade_rad, thState);
    const P_elec = chain.P_batt_chem * 1000;

    // [v5] 2RC 배터리
    let batt2rc_result = null;
    if (battery_2rc && params.battery_model === '2RC') {
      batt2rc_result = battery2RCModelWithSEI(battery_2rc, P_elec / Math.max(1, V_dc_eff), dt, params, null);
      battery_2rc.SOC = soc; battery_2rc.T_cell = thState.T_batt;
      // [DC-Dyn] V_terminal → batt2rc_state_ra 1차 저역통과 갱신
      if (params.dc_link_dynamics_enable && batt2rc_result?.V_terminal) {
        const C_dc_ra  = params.C_dc || 1500e-6;
        const tau_dc_ra = C_dc_ra * (params.R_batt || 0.08);
        const alpha_ra  = Math.min(0.99, dt / (tau_dc_ra + dt));
        batt2rc_state_ra = batt2rc_state_ra
          + alpha_ra * (batt2rc_result.V_terminal - batt2rc_state_ra);
      }
    }

    soc = socUpdate(soc, P_elec, dt, params);

    // [R-16] Kalman SOC 추정 (simLaunch 패리티)
    let bms_max_dv_ra = 0;
    if (kf_state_ra && params.kalman_enable !== false) {
      const V_meas_ra = batt2rc_result
        ? batt2rc_result.V_terminal
        : batteryOCV_smart(soc, params, thState.T_batt);
      const I_kf_ra = P_elec / Math.max(1, V_dc_eff);
      kf_state_ra   = kalmanSOCUpdate(kf_state_ra, V_meas_ra, I_kf_ra, soc,
                        batteryOCV_smart(soc, params, thState.T_batt), params);
      soc_kalman_ra = kf_state_ra.soc_est !== undefined ? kf_state_ra.soc_est : soc;
      if (Math.abs(soc_kalman_ra - soc) < 0.15) soc = soc_kalman_ra;
    }
    // [R-16] SEI 성장 모델 (simLaunch 패리티)
    if (sei_state_ra) {
      const batt_snap_ra = {
        T_cell: thState.T_batt,
        V_terminal: batt2rc_result ? batt2rc_result.V_terminal : params.V_dc,
      };
      sei_state_ra = updateSEI(sei_state_ra, batt_snap_ra,
                       Math.abs(P_elec) / Math.max(1, V_dc_eff), dt, params);
    }
    // [R-16] BMS 셀 스텝 (simLaunch 패리티)
    if (bms_state_ra && params.bms_detailed_enable) {
      bms_state_ra = bmsStep(bms_state_ra, P_elec / Math.max(1, V_dc_eff), thState.T_batt, dt, params);
      bms_max_dv_ra = bms_state_ra.max_dV || 0;
      if (bms_max_dv_ra > 0.05) {
        soc = Math.max(0.01, soc - bms_max_dv_ra * (params.bms_soc_corr_gain || 0.3));
      }
      // [A10] 콘택터 차단 시 다음 스텝 토크 차단을 위해 bms_state 공유
      if (bms_state_ra.contactor_on === false) {
        if (!bms_state) bms_state = bms_state_ra;
        else bms_state.contactor_on = false;
      }
    }

    // [v5] SOH — [P4] rainflow 패리티
    if (soh_state && params.soh_enable) {
      const batt_snap = { SOC: soc, T_cell: thState.T_batt };
      soh_state = rf_state_ra
        ? updateSOHWithRainflow(soh_state, rf_state_ra, batt_snap, Math.abs(P_elec)/Math.max(1,V_dc_eff), dt, params)
        : updateSOH(soh_state, batt_snap, Math.abs(P_elec)/Math.max(1,V_dc_eff), dt, params);
      if (t % 1.0 < dt) { Object.assign(params, applySOHDegradation(params, soh_state)); } // [v5-bugfix3]
    }
    // [Parity-RA-GT] 기어박스 오일 온도 갱신 — simLaunch 패리티 (D13·D7)
    //   수정 전: T_gear_ra 고정값(80°C) 사용 — gearThermalStep 호출 없음
    //   수정 후: 매 스텝 gearLossTempEnhanced → gearThermalStep 연동 (simLaunch와 동일 경로)
    {
      const gear_loss_ra = gearLossTempEnhanced(omega_m, T_gear_ra, thState.T_motor, params);
      T_gear_ra = gearThermalStep(T_gear_ra, gear_loss_ra.P_loss, v, params, dt);
    }

    E_batt += P_elec * dt; E_wheel += (chain.P_wheel||0) * 1000 * dt;
    Q_b_acc += (chain.Q_batt||0)*dt; Q_i_acc += (chain.Q_inv||0)*dt; Q_m_acc += (chain.Q_motor||0)*dt;
    th_accum += dt;
    if (th_accum >= dt_th) {
      // [R-16] 냉각 유량 제어 업데이트 → [R-13] phi_flow → thermalStep 반영
      cool_state_ra = coolantFlowControl(cool_state_ra, thState.T_cool, params, th_accum);
      thState = thermalStep(thState, Q_b_acc/th_accum, Q_i_acc/th_accum, Q_m_acc/th_accum,
                            v, params, th_accum, cool_state_ra.phi_flow);
      // [BugFix / R-10-03] GAP-05: 3D 열 분포 실시간 업데이트 (simReaccel — simLaunch 패리티)
      if (params.thermal_3d_enable && thermal_3d) {
        const Q_total_3d = (Q_b_acc + Q_i_acc + Q_m_acc);
        thermal_3d = update3DThermalStep(thermal_3d, Q_total_3d, cool_state_ra.phi_flow, thState.T_cool, th_accum, params);
      }
      Q_b_acc=0; Q_i_acc=0; Q_m_acc=0; th_accum=0;
    }
    if (t % 0.05 < dt) {
      data.push({
        t:+t.toFixed(3), v_kmh:+(v*3.6).toFixed(2), a:+a.toFixed(3), s:+s.toFixed(2),
        F_drv:+F_drv.toFixed(1), F_aero:+res.F_aero.toFixed(1), F_roll:+res.F_roll.toFixed(1),
        P_elec:+chain.P_batt_chem, soc:+(soc*100).toFixed(2), T_motor_torque:+T_mot.toFixed(1),
        T_motor_cmd:+T_mot_ideal_ra.toFixed(1), // [RA1-Fix] 필터링 전 요구 토크
        Id:+Id.toFixed(2), Iq:+Iq.toFixed(2), omega_motor:+omega_m.toFixed(1),
        T_batt:+thState.T_batt.toFixed(1), T_inv:+thState.T_inv.toFixed(1),
        T_motor_temp:+thState.T_motor.toFixed(1), T_cool:+thState.T_cool.toFixed(1),
        fw_regime: curRef.regime||'mtpa',
        awd_mode: tv_result.mode||'RWD', lambda_fr: tv_result.lambda_fr!==undefined?+tv_result.lambda_fr.toFixed(3):0,
        esc_active: esc_result.active, psi_dot:+(lateral_state.psi_dot||0).toFixed(4),
        beta:+(lateral_state.beta||0).toFixed(4), ay_lat:+(lateral_state.ay||0).toFixed(3),
        // ── [Step3] 7DOF 출력 필드 ──
        roll_angle_deg:+(lateral_state.roll_angle_deg||0).toFixed(3),
        phi:+(lateral_state.phi||0).toFixed(5), z_cg:+(lateral_state.z_cg||0).toFixed(5),
        Fz_f_lat:+(lateral_state.Fz_f||0).toFixed(0), Fz_r_lat:+(lateral_state.Fz_r||0).toFixed(0),
        // ── [Step4] β EKF 출력 필드 ──
        beta_hat:    beta_ekf_result_ra ? +beta_ekf_result_ra.beta_hat.toFixed(5)    : +(lateral_state.beta||0).toFixed(5),
        psi_dot_hat: beta_ekf_result_ra ? +beta_ekf_result_ra.psi_dot_hat.toFixed(5) : +(lateral_state.psi_dot||0).toFixed(5),
        ekf_innov_ay:  beta_ekf_result_ra ? +beta_ekf_result_ra.innov_ay.toFixed(4)  : 0,
        ekf_innov_psi: beta_ekf_result_ra ? +beta_ekf_result_ra.innov_psi.toFixed(4) : 0,
        beta_ekf_active: !!(beta_ekf_ra && params.beta_ekf_enable),
        V_terminal: batt2rc_result?+batt2rc_result.V_terminal.toFixed(2):+(params.V_dc||400),
        V_dc_eff: +V_dc_eff.toFixed(2),  // [DC-Dyn]
        V_dc_sag: +((params.V_dc||400) - V_dc_eff).toFixed(2),
        soh_pct: soh_state?+(soh_state.SOH*100).toFixed(1):100,
        tcs_active: tcs_result.active,
        derate_motor:+(chain.derate_motor||100), derate_batt:+(chain.derate_batt||100),
        // ── [Step5] MPC 드라이버 필드 (simReaccel 패리티) ──
        driver_mpc_active: !!(params.driver_mpc_enable && mpc_drv_state_ra && !params.cascade_speed_enable),
        driver_pid_active: !!(params.driver_pid_enable !== false && drv_state_ra && !params.driver_mpc_enable && !params.cascade_speed_enable),
        driver_pedal: params.driver_mpc_enable && mpc_drv_state_ra && !params.cascade_speed_enable
          ? +mpc_drv_state_ra.pedal_prev.toFixed(3)
          : (drv_state_ra ? +drv_state_ra.pedal_prev.toFixed(3) : +mode_throttle.toFixed(3)),
        mpc_cost: mpc_cost_ra,
        // ── [CascadePID] Cascade 속도 PI 필드 (simReaccel 패리티) ──
        cascade_active: !!(params.cascade_speed_enable && cascade_spd_state_ra),
        cascade_T_cmd: params.cascade_speed_enable && cascade_spd_state_ra
          ? +(cascade_spd_state_ra.integral * (params.Ki_spd || 3003) / 1000 + 0).toFixed(1)
          : 0,
        cascade_bw_warn: cascade_bw_warn_ra,
        // [R-17] v04 신규 필드 (simReaccel)
        kalman_soc: +(soc_kalman_ra * 100).toFixed(2),
        bms_max_dv: +(bms_max_dv_ra * 1000).toFixed(1),
        sei_R: +(sei_state_ra?.R_sei || 0).toFixed(5),
        cool_phi_flow: +(cool_state_ra.phi_flow || 1.0).toFixed(3),
        T_gear: +T_gear_ra.toFixed(1),                                                    // [Parity-RA-GT]
        steer_T_feedback: steer_state_ra._last ? +steer_state_ra._last.T_feedback.toFixed(3) : 0, // [D6-STR]
        steer_SAT:        steer_state_ra._last ? +steer_state_ra._last.SAT.toFixed(3)        : 0, // [D6-STR]
        steer_angle_wheel: steer_state_ra._last ? +steer_state_ra._last.steer_angle_wheel.toFixed(4) : 0, // [D6-STR]
        // [BugFix] hotspot_T / T_delta_3d 추가 (simLaunch 패리티)
        hotspot_T: thermal_3d ? +thermal_3d.T_max.toFixed(1) : +thState.T_batt.toFixed(1),
        T_delta_3d: thermal_3d ? +thermal_3d.T_delta.toFixed(2) : 0,
        ...chain,
      });
    }
    a_prev=a; v+=a*dt; if(v<0)v=0;
    if(t_reach===null && v>=v_tgt) {
      t_reach=t;
      s_at_tgt      = s;       // [A68-02-Fix-RA1] v0→v1 구간 거리 스냅샷
      E_batt_at_tgt  = E_batt; // [A68-02-Fix-RA1] v0→v1 구간 배터리 에너지 스냅샷
      E_wheel_at_tgt = E_wheel;// [A68-02-Fix-RA1] v0→v1 구간 휠 에너지 스냅샷
    }
    s+=v*dt; t+=dt;
  }
  return {
    data,
    time: t_reach!==null?+t_reach.toFixed(3):999, time_s: t_reach!==null?+t_reach.toFixed(3):999,
    distance: +s.toFixed(2),
    // [A68-02-Fix-RA1] v_target 도달 시점 KPI (재가속 구간 전용)
    //   수정 전: distance/E_batt_Wh = 30초 전체 누적 → v0→v1 구간 KPI로 잘못 표시
    //   수정 후: v_target 최초 도달 순간의 s/E 스냅샷으로 정확한 v0→v1 구간 값 산출
    dist_at_target:       s_at_tgt     != null ? +s_at_tgt.toFixed(2)                           : +s.toFixed(2),
    E_batt_at_target_Wh: E_batt_at_tgt != null ? +(E_batt_at_tgt/3600).toFixed(1)               : +(E_batt/3600).toFixed(1),
    eta_at_target:        (E_batt_at_tgt && E_batt_at_tgt>0) ? +(E_wheel_at_tgt/E_batt_at_tgt*100).toFixed(1) : 0,
    E_batt_Wh: +(E_batt/3600).toFixed(1), E_wheel_Wh: +(E_wheel/3600).toFixed(1),
    eta_cycle: E_batt>0?+(E_wheel/E_batt*100).toFixed(1):0,
    thFinal: {...thState},
    rainflow_damage: rf_state_ra ? +(rf_state_ra.total_damage || 0).toFixed(5) : 0,  // [P4]
    rainflow_cycles: rf_state_ra ? +(rf_state_ra.cycle_count_rf || 0).toFixed(1) : 0, // [P4]
  };
}



// 액티브 밸런싱 계산

// 통합 BMS 밸런싱 분석 (셀 전압 배열 입력)
// [R-06-H deleted] analyzeBMSBalance// ═══════════════════════════════════════════════════════
// PHASE 4: ABS, Brake Thermal, Fade, Sensor/Kalman Models
// ═══════════════════════════════════════════════════════

// ── ABS 슬립 제어 ──

// ── simBraking ──
/**
 * 제동 시뮬레이션을 실행한다 (dt=20 ms, v → 0).
 * 회생·마찰 협조 제동, ABS, 브레이크 디스크 열, 2RC 배터리, RLS µ.
 * [BugFix6] dt 0.005→0.02 (4배 속도).
 * [Fix-RB1] E_KE_init = m_eff 기반으로 에너지 수지 일관성 확보.
 *
 * @param {object} params         차량 파라미터
 * @param {number} v_init_kmh     초기 속도 [km/h]
 * @param {number} brake_demand   제동 요구 [0~1]
 * @param {number} grade_deg      경사도 [°]
 * @returns {{ data, time, distance, E_regen_Wh, eta_regen_total, ... }}
 */
function simBraking(params, v_init_kmh, brake_demand, grade_deg) {
  const dt = 0.02, dt_th = 0.2; // [BugFix6] 성능 개선: dt 0.005→0.02 (4배 빠름)
  const grade_rad = (grade_deg * Math.PI) / 180;
  let v = v_init_kmh / 3.6, s = 0, t = 0, soc = params.soc_init;
  let thState = { T_batt: params.T_amb, T_inv: params.T_amb, T_motor: params.T_amb, T_cool: params.T_amb };
  let T_disc = params.T_brake_init || 25;             // 브레이크 디스크 온도
  let kf = initKalmanByType(soc, params);            // 칼만 필터 초기화 (Linear/EKF/UKF)
  let omega_wheel = v / params.r_wheel;              // 초기 휠 각속도
  // [Parity-B1] 2RC 배터리 모델
  let battery_2rc_br   = params.battery_model === '2RC' ? init2RCBatteryState(params) : null;
  let batt2rc_state_br = params.V_dc || 400;
  // [Parity-B2] 3D 열 분포 모델
  let thermal_3d_br = params.thermal_3d_enable ? init3DThermalState(params) : null;
  // [Parity-B3] RLS µ 추정 — simLaunch/simReaccel/simCoast/simWLTP/simDC 패리티 완성
  // 제동 구간: 총 제동력(회생+마찰)을 종방향 타이어 힘으로 사용하여 µ 역산
  let rls_state_br = initRLSMuState(params);
  let a_prev_br    = 0;
  const m_eff = effectiveMass(params);               // 회전 관성 포함 유효 질량
  // [Fix-RB1] E_KE_init: params.m → m_eff (동역학 a = -F/m_eff 와 KE 수지 일관성 확보)
  //   수정 전: E_KE_init = 0.5 * params.m * v²  — 차체 질량만, 회전 관성 미포함
  //             동역학은 m_eff 사용 → KE_init과 실제 감속 에너지 기준 불일치
  //             → eta_regen_total = E_regen / (KE_init - KE_final) 분모 과소 → 효율 과대 표시
  //   수정 후: m_eff 기반으로 E_KE_init/final 통일 (simCoast Fix-CT1과 동일 방향)
  const E_KE_init = 0.5 * m_eff * v * v; // [Fix-RB1]
  let E_regen_batt = 0, E_friction = 0, E_regen_loss = 0;
  let th_accum = 0, Q_b_acc = 0, Q_i_acc = 0, Q_m_acc = 0;
  let abs_trigger_count = 0;
  const data = [];
  // [Fix-RB-3] 유압 브레이크 지연 모델 상태 초기화
  let brakeState_sb = initBrakeSystemState(params);
  // [Parity-BR-C] 냉각 유량 제어 — simLaunch/simReaccel/simWLTP/simDC 패리티
  // simBraking 구간에서도 coolantFlowControl → phi_flow → thermalStep UA_rad 반영
  let cool_state_br = initCoolantFlowState(params);
  // [D13-BR] 다단 기어 상태 — simBraking getActiveGear 연결 (simWLTP/DC 패턴 이식)
  //   수정 전: omega_m = v / r_wheel * params.gear_ratio (고정 비율)
  //   수정 후: multi_gear_enable 시 getActiveGear → gear_ratio_eff_br 동적 산출
  let gear_state_br = params.multi_gear_enable ? initGearState(params) : null;
  let T_gear_br     = params.gear_T_oil || 80;  // [P2] 기어박스 온도 추적
  // [P4] SOH + Rainflow — simLaunch 패리티
  let soh_state_br = params.soh_enable ? initSOHState(params) : null;
  let rf_state_br  = (params.soh_enable && params.rainflow_enable) ? initRainflowState(params) : null;
  const use_regen_map = params.regen_map_enable !== false;

  while (v > 0.3 && t < 60) {
    // [D13-BR] 다단 기어: 제동 구간 실효 기어비 산출 (회생 토크는 0으로 근사)
    let gear_ratio_eff_br = params.gear_ratio || 9.5;
    if (params.multi_gear_enable && gear_state_br) {
      const gearOut_br = getActiveGear(v, 0, soc, gear_state_br, params, dt, t);
      gear_ratio_eff_br = gearOut_br.gear_ratio_eff;
      gear_state_br     = gearOut_br.gear_state_new;
    }
    const omega_m = v / params.r_wheel * gear_ratio_eff_br;
    // NEW v3: effective brake demand from regen map
    const eff_brake_demand = use_regen_map
      ? Math.max(brake_demand, regenMapDemand(v, 0, params))
      : brake_demand;

    // [Fix-RB-3] 유압 브레이크 지연 모델 적용
    // regen 가능 비율: SOC < 0.95이면 regen_max_ratio까지 허용
    const p_regen_cmd_sb = soc < 0.95
      ? Math.min(eff_brake_demand * (params.regen_max_ratio || 0.6), eff_brake_demand)
      : 0;
    const brakeOut_sb = brakeSystemDynamics(brakeState_sb, eff_brake_demand, p_regen_cmd_sb, params, dt);
    brakeState_sb = brakeOut_sb.state;
    // 유압 지연 반영 실효 제동 요구값
    const eff_brake_delayed = brakeOut_sb.F_brake_total / Math.max(params.m * g, 1);

    // ── 회생 제동 토크 계산 ──
    const regen_raw = regenTorqueEnhanced(omega_m, eff_brake_delayed, params, soc, thState.T_batt, thState.T_motor, thState.T_inv);
    // [Fix-RB-1] S-커브 블렌딩 — 저속/고SOC 구간 마찰 비율 증가
    const blend_b = regenFrictionBlend(regen_raw.T_regen, regen_raw.T_friction, v, soc, omega_m, params, dt);
    const regen = { ...regen_raw, T_regen: blend_b.T_regen, T_friction: blend_b.T_friction, _blend: blend_b };
    // [Fix-RB-2] 저크 제한 T_regen_prev 갱신
    params._regen_T_prev = blend_b.T_regen;
    const regenChain = regen.T_regen > 0
      ? computeRegenChain(omega_m, regen.T_regen, 0, regen.T_regen / (1.5 * (params.poles / 2) * params.lambda_pm), soc, params, thState.T_batt, thState.T_motor)
      : null;
    const F_regen = regen.T_regen * gear_ratio_eff_br / params.r_wheel;

    // ── 브레이크 페이드 계수 ──
    const fade_f = brakeFadeFactor(T_disc, params);

    // ── 기계 마찰 제동력 (페이드 반영) ──
    let F_friction_raw = regen.T_friction * gear_ratio_eff_br / params.r_wheel * fade_f;

    // ── ABS 제어 ──
    const absOut = absControl(v, omega_wheel, params.r_wheel, F_friction_raw + F_regen, params);
    const F_friction = Math.max(0, absOut.F_brake - F_regen);
    abs_trigger_count += absOut.abs_active ? 1 : 0;

    // ── 휠 슬립 동역학 (간략화된 모델) ──
    const F_brake_total = F_regen + F_friction;
    const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
    // NEW v3: use effective mass
    const a = -(F_brake_total + res.F_total) / m_eff;

    // [Parity-B3] RLS µ 추정 — 제동력 = 회생+마찰 합산 (부호: 감속 방향 = 음수)
    // ABS 개입 구간은 absOut.lambda 기반 실제 마찰 제한이 반영된 F_brake_total 사용
    {
      const F_normal_br = params.m * g * Math.cos(grade_rad);
      rls_state_br = rlsMuUpdate(rls_state_br, -F_brake_total, F_normal_br, a_prev_br, params.m, params);
      a_prev_br = a;
    }

    // 휠 각속도 업데이트 (ABS 없을 때 잠금 방지)
    const T_brake_wheel = F_friction * params.r_wheel;
    const I_wheel_val = (params.I_wheel || 1.5) * 2; // 휠 관성 [kg·m²] (2 driven wheels)
    const alpha_wheel = -(T_brake_wheel) / I_wheel_val;
    omega_wheel = Math.max(0, omega_wheel + alpha_wheel * dt);
    if (!params.abs_enable) omega_wheel = Math.max(0, v / params.r_wheel * (1 - absOut.lambda));

    // ── 브레이크 디스크 열 ──
    const Q_fric_heat = F_friction * v * dt;         // 마찰열 [J]
    T_disc = brakeDiscThermal(T_disc, Q_fric_heat / dt, v, params, dt);

    // ── 에너지 계산 ──
    if (regenChain) {
      const P_charge = regenChain.P_batt_charge * 1000;
      const soc_raw = socUpdate(soc, -P_charge, dt, params);
      // 센서 노이즈 + 칼만 필터 SOC (NEW v3: EKF/UKF dispatch)
      const soc_meas = sensorNoise(soc_raw, params.sigma_soc || 0.005, params.sensor_noise_enable, params.K_sensor_noise || 1.0);
      const V_t_est = params.V_dc * (0.8 + 0.2 * soc_raw); // Approx terminal voltage
      const I_batt_est = P_charge / Math.max(1, params.V_dc);
      const kf_out = kalmanSOCUpdate(kf, V_t_est, I_batt_est, soc_raw, soc_meas, params);
      soc = kf_out.soc; kf.P = kf_out.P;
      E_regen_batt += P_charge * dt;
      E_regen_loss += (regenChain.Q_motor_regen + regenChain.Q_inv_regen + regenChain.Q_batt_regen) * dt;
      Q_b_acc += regenChain.Q_batt_regen * dt;
      Q_i_acc += regenChain.Q_inv_regen * dt;
      Q_m_acc += regenChain.Q_motor_regen * dt;
    }
    E_friction += F_friction * v * dt;

    // ── 열 관리 ──
    th_accum += dt;
    if (th_accum >= dt_th) {
      // [Parity-BR-C] coolantFlowControl → phi_flow → thermalStep UA_rad 가중치 반영
      cool_state_br = coolantFlowControl(cool_state_br, thState.T_cool, params, th_accum);
      // [P2] 기어박스 열 모델 — simLaunch 패턴 이식
      const gear_loss_br = gearLossTempEnhanced(Math.max(0.5, v / params.r_wheel * gear_ratio_eff_br), T_gear_br, thState.T_motor, params);
      T_gear_br = gearThermalStep(T_gear_br, gear_loss_br.P_loss, v, params, th_accum);
      thState = thermalStep(thState, Q_b_acc / th_accum, Q_i_acc / th_accum, Q_m_acc / th_accum, v, params, th_accum, cool_state_br.phi_flow);
      // [Parity-B2] 3D 열 분포 업데이트
      if (params.thermal_3d_enable && thermal_3d_br) {
        const Q_3d_br = Q_b_acc + Q_i_acc + Q_m_acc;
        thermal_3d_br = update3DThermalStep(thermal_3d_br, Q_3d_br, cool_state_br.phi_flow, thState.T_cool, th_accum, params);
      }
      Q_b_acc = 0; Q_i_acc = 0; Q_m_acc = 0; th_accum = 0;
    }
    // [P4] SOH + Rainflow 업데이트 — simLaunch 패리티
    if (soh_state_br && params.soh_enable) {
      const batt_snap_br = { SOC: soc, T_cell: thState.T_batt };
      const I_br = E_regen_batt > 0 ? Math.abs(E_regen_batt / Math.max(0.001, t) / Math.max(1, params.V_dc || 400)) : 0;
      soh_state_br = rf_state_br
        ? updateSOHWithRainflow(soh_state_br, rf_state_br, batt_snap_br, I_br, dt, params)
        : updateSOH(soh_state_br, batt_snap_br, I_br, dt, params);
    }
    if (t % 0.05 < dt) {
      const v_kmh_true = v * 3.6;
      const v_kmh_meas = sensorNoise(v_kmh_true, (params.sigma_v || 0.05) * 3.6, params.sensor_noise_enable, params.K_sensor_noise || 1.0);
      const a_meas = sensorNoise(a, params.sigma_a || 0.1, params.sensor_noise_enable, params.K_sensor_noise || 1.0);
      data.push({
        t: +t.toFixed(3),
        v_kmh: +v_kmh_true.toFixed(2),
        v_kmh_sensor: +v_kmh_meas.toFixed(2),           // 센서 측정값
        a: +a.toFixed(3),
        a_sensor: +a_meas.toFixed(3),                   // 센서 측정값
        s: +s.toFixed(2),
        lambda: +absOut.lambda.toFixed(4),              // 슬립비
        abs_active: absOut.abs_active ? 1 : 0,          // ABS 개입 여부
        fade_factor: +fade_f.toFixed(3),                // 페이드 계수
        T_disc: +T_disc.toFixed(1),                     // 디스크 온도
        F_regen: +F_regen.toFixed(1),
        F_friction: +F_friction.toFixed(1),
        F_brake: +F_brake_total.toFixed(1),
        F_hyd: +brakeOut_sb.F_hyd.toFixed(1),          // [Fix-RB-3] 유압 제동력
        blend_ratio: +brakeOut_sb.blend_ratio.toFixed(3), // 회생 기여 비율
        jerk_brake: +brakeOut_sb.jerk_brake.toFixed(3),   // 제동 저크
        regen_blend_factor: +(regen._blend?.blend_factor ?? 1).toFixed(3), // [Fix-RB-1] 블렌딩 계수
        F_aero: +res.F_aero.toFixed(1),
        F_roll: +res.F_roll.toFixed(1),
        T_regen: +regen.T_regen.toFixed(1),
        P_batt_charge: regenChain ? +regenChain.P_batt_charge.toFixed(2) : 0,
        eta_regen: regenChain ? +regenChain.eta_regen : 0,
        soc: +(soc * 100).toFixed(2),
        T_batt: +thState.T_batt.toFixed(1),
        T_inv: +thState.T_inv.toFixed(1),
        T_motor_temp: +thState.T_motor.toFixed(1),
        hotspot_T:  thermal_3d_br ? +thermal_3d_br.T_max.toFixed(1) : +thState.T_batt.toFixed(1), // [Parity-B2]
        T_delta_3d: thermal_3d_br ? +thermal_3d_br.T_delta.toFixed(2) : 0,
        E_regen_cum: +(E_regen_batt / 3600).toFixed(2),
        mu_rls: +rls_state_br.mu_est.toFixed(3),                                                   // [Parity-B3]
        cool_phi_flow: +(cool_state_br.phi_flow || 1.0).toFixed(3),                               // [Parity-BR-C]
        gear_ratio_eff: +gear_ratio_eff_br.toFixed(3),                                            // [D13-BR]
      });
    }
    v += a * dt; if (v < 0) v = 0; s += v * dt; t += dt;
  }
  const E_KE_final = 0.5 * m_eff * v * v; // [Fix-RB1]
  const E_KE_dissipated = E_KE_init - E_KE_final;
  // NEW v3: Enhanced regen metrics
  const regen_recovery_pct = E_KE_dissipated > 0 ? +(E_regen_batt / E_KE_dissipated * 100).toFixed(1) : 0;
  const regen_of_total_brake = (E_regen_batt + E_friction) > 0
    ? +(E_regen_batt / (E_regen_batt + E_friction) * 100).toFixed(1) : 0;
  return {
    data, time: +t.toFixed(3), distance: +s.toFixed(2),
    stopping_distance_m: +s.toFixed(1),               // NEW v3: explicit stopping distance
    decel_avg_ms2: v > 0 ? 0 : +(v_init_kmh / 3.6 / Math.max(0.01, t)).toFixed(2),
    E_KE_init_Wh: +(E_KE_init / 3600).toFixed(1),
    E_regen_Wh: +(E_regen_batt / 3600).toFixed(1),
    E_friction_Wh: +(E_friction / 3600).toFixed(1),
    E_regen_loss_Wh: +(E_regen_loss / 3600).toFixed(1),
    eta_regen_total: regen_recovery_pct,              // % of KE recovered
    regen_of_total_brake,                              // NEW v3: regen share of total braking
    regen_energy_recovered_Wh: +(E_regen_batt / 3600).toFixed(1),
    soc_delta_pct: +((E_regen_batt / 3600) / (params.battery_energy || 75) * 100).toFixed(2),
    soc_final: +(soc * 100).toFixed(2),
    T_disc_final: +T_disc.toFixed(1),
    abs_interventions: abs_trigger_count,
    fade_factor_final: +brakeFadeFactor(T_disc, params).toFixed(3),
    drive_mode: params.drive_mode || 'NORMAL',
    kalman_type: params.kalman_type || 'linear',
    m_eff: +effectiveMass(params).toFixed(0),
    mu_rls_final: +rls_state_br.mu_est.toFixed(3),      // [Parity-B3]
    T_gear_final: +T_gear_br.toFixed(1),                 // [P2]
    rainflow_damage: rf_state_br ? +(rf_state_br.total_damage || 0).toFixed(5) : 0,   // [P4]
    rainflow_cycles: rf_state_br ? +(rf_state_br.cycle_count_rf || 0).toFixed(1) : 0, // [P4]
    soh_final: soh_state_br ? +(soh_state_br.soh * 100).toFixed(2) : null,            // [P4]
  };
}

// ── 긴급 제동 시나리오 (ABS 유무 비교, 페이드 반복 시험) ──

// ════════════════════════════════════════════════════════════════════════════
// [PHASE 4] 승차감(Drivability) 저크 분석 엔진
// Tip-in / Tip-out 토크 필터링에 따른 종방향 저크(m/s³) 계산
// 유압 제동 지연 연계 저크 분석
// ════════════════════════════════════════════════════════════════════════════

/**
 * computeDrivabilityJerk(data, dt)
 * 시뮬레이션 데이터 배열로부터 저크 시계열 및 통계 계산
 * @param {Array}  data  시뮬레이션 스텝 데이터 배열 (각 요소에 .a 필드 필요)
 * @param {number} dt    타임스텝 간격 [s]
 * @returns {{ jerk_series, jerk_max, jerk_rms, jerk_95pct, tip_in_events, tip_out_events, jerk_grade }}
 */
function computeDrivabilityJerk(data, dt) {
  if (!data || data.length < 2) {
    return { jerk_series: [], jerk_max: 0, jerk_rms: 0, jerk_95pct: 0,
             tip_in_events: 0, tip_out_events: 0, jerk_grade: 'A' };
  }
  const _dt = dt || 0.05;
  const jerk_series = [];
  let sum_sq = 0;
  let tip_in  = 0;
  let tip_out = 0;
  const abs_jerks = [];

  for (let i = 1; i < data.length; i++) {
    const a_cur  = data[i].a   || 0;
    const a_prev = data[i-1].a || 0;
    const t      = data[i].t   || (i * _dt);
    const jerk   = (a_cur - a_prev) / _dt;
    // [Fix-JERK-SPD] v_kmh 필드 추가:
    //   수정 전: { t, jerk } 만 포함 → JK2 차트 "속도 오버레이" subtitle 미실현
    //   수정 후: v_kmh 포함 → JK2 ComposedChart에서 속도 Line 렌더링 가능
    jerk_series.push({ t: +t.toFixed(3), jerk: +jerk.toFixed(4), v_kmh: data[i].v_kmh ?? null });
    abs_jerks.push(Math.abs(jerk));
    sum_sq += jerk * jerk;
    // 팁인/팁아웃 임계값: UI 전체(KPI 배지 formula, GB8 ReferenceLine y=±5, 노하우 formula)와 통일
    const JERK_EVENT_THR = 5;  // [m/s³] — ±5 기준, UI 레이블·GB8 기준선과 단일 기준
    if (jerk >  JERK_EVENT_THR && (i < 2 || (data[i-1].a - data[i-2].a) / _dt <=  JERK_EVENT_THR)) tip_in++;
    if (jerk < -JERK_EVENT_THR && (i < 2 || (data[i-1].a - data[i-2].a) / _dt >= -JERK_EVENT_THR)) tip_out++;
  }

  const n        = jerk_series.length;
  const jerk_rms = n > 0 ? +Math.sqrt(sum_sq / n).toFixed(3) : 0;
  const jerk_max = n > 0 ? +Math.max(...abs_jerks).toFixed(3) : 0;

  // 95th percentile
  const sorted   = [...abs_jerks].sort((a, b) => a - b);
  const p95_idx  = Math.min(Math.floor(sorted.length * 0.95), sorted.length - 1);
  const jerk_95pct = n > 0 ? +sorted[p95_idx].toFixed(3) : 0;

  // ISO Grade: UI 전체(KPI 배지·노하우·파이프라인 설명)와 계산 기준 통일
  // A: RMS < 3 / B: < 6 / C: < 10 / D: ≥ 10  [m/s³]
  // (ISO 2631-1은 저크 등급표 미제공 — 본 시뮬 자체 기준, UI 레이블과 단일 기준 유지)
  const jerk_grade = jerk_rms < 3  ? 'A'
                   : jerk_rms < 6  ? 'B'
                   : jerk_rms < 10 ? 'C' : 'D';

  return { jerk_series, jerk_max, jerk_rms, jerk_95pct,
           tip_in_events: tip_in, tip_out_events: tip_out, jerk_grade };
}

// ── simDrivingCycle ──
/**
 * 주행 사이클 시뮬레이션 디스패처: WLTP는 simWLTP, 나머지는 제네릭 커널로 실행.
 * [A44-BugFix] TDZ 오류 수정: T_amb 선언을 p 생성보다 앞으로 이동.
 * [A44-Fix-1] effectiveMass 적용으로 비-WLTP 사이클 에너지 ~2~4% 정확도 향상.
 *
 * @param {object} params          차량 파라미터 (cycle_type 포함)
 * @param {number} [T_amb_override] 환경 온도 재정의 [°C]
 * @returns {object|null}  시뮬레이션 결과 (사이클 미지원 시 null)
 */
function simDrivingCycle(params, T_amb_override) {
  const cycle_type = params.cycle_type || 'WLTP';
  if (cycle_type === 'WLTP') {
    return simWLTP(params, T_amb_override);
  }
  // Generic kernel for all other cycles
  const profile = getCycleProfile(cycle_type);
  if (!profile || profile.length === 0) return null;
  // [A44-BugFix] ✅ TDZ(Temporal Dead Zone) 오류 수정 — 🔴 CRITICAL
  //   수정 전: const p = { ...params, T_amb };   ← T_amb 미선언 상태에서 참조
  //            const T_amb = ... ;               ← 선언은 이 줄에서
  //            → ReferenceError: Cannot access 'T_amb' before initialization
  //            → 모든 비-WLTP 사이클에서 simDrivingCycle가 크래시 → return null
  //            → wdata=[] → DC1~DC6 전 그래프 "데이터 없음" 표시
  //   수정 후: T_amb 먼저 선언 → p 생성 (simWLTP와 동일 순서)
  const T_amb = T_amb_override !== undefined ? T_amb_override : (params.T_amb || 25);
  // ── 드라이브 모드 게인 적용 (simWLTP와 동일) ──────────────────────────
  const p = _applyDriveModeGainsInline({ ...params, T_amb });
  const dt_sim = 0.5;
  // [A44-Fix-1] ✅ simDrivingCycle 유효 질량 적용 (A43-Fix-4의 비-WLTP 커널 버전)
  //   수정 전: a = F / p.m — 회전관성 미포함 → 비-WLTP 사이클 에너지 ~2~4% 오차
  //   수정 후: effectiveMass(p) 사용, 구동·회생 양쪽 a_vehicle 계산에 적용
  const m_eff_dc = effectiveMass(p);
  let v = 0, s = 0, soc = p.soc_init;
  let thState = { T_batt: T_amb, T_inv: T_amb, T_motor: T_amb, T_cool: T_amb };
  let E_drive_J = 0, E_regen_J = 0;
  let E_aux_drive_J = 0;  // [BUG-1-FIX] 구동 구간 aux — E_drive_J에 이미 포함, 보고용 전용
  let E_aux_regen_J = 0;  // [BUG-1-FIX] 회생+코스팅 구간 aux — E_net에 별도 가산
  let d_total = 0, t_total_s = 0;
  let track_err_i = 0;
  let Q_b_acc = 0, Q_i_acc = 0, Q_m_acc = 0, th_accum = 0;
  let maxT_motor = T_amb, maxT_batt = T_amb, maxT_inv = T_amb;
  let derate_ev_b = 0, derate_ev_i = 0, derate_ev_m = 0;
  let t_stop_dc = 0, t_accel_dc = 0, t_cruise_dc = 0, t_decel_dc = 0; // [A21-DC7]
  let _P_kW_last = 0; // [A22] 배터리 전력 트래킹
  let _phase_last = 'Main'; // [A22] 현재 phase 트래킹
  let cool_state_dc = initCoolantFlowState(p);      // [A25-Fix2] 냉각 유량 PI 상태
  let T_tire_sim = T_amb;                            // [A25-Fix5] 간이 타이어 온도 (°C)
  // [Parity-DC1] 2RC 배터리 모델
  let battery_2rc_dc   = p.battery_model === '2RC' ? init2RCBatteryState(p) : null;
  let batt2rc_state_dc = p.V_dc || 400;
  // [Parity-DC2] Kalman SOC 추정 (EKF/UKF/Linear)
  let kf_state_dc   = p.kalman_enable !== false ? initKalmanByType(p.soc_init, p) : null;
  let soc_kalman_dc = p.soc_init;
  // [Parity-DC3] 3D 열 분포 모델
  let thermal_3d_dc = p.thermal_3d_enable ? init3DThermalState(p) : null;
  // [Parity-DC4] RLS µ 추정 — simLaunch/simReaccel/simWLTP 패리티
  let rls_state_dc = initRLSMuState(p);
  let a_prev_dc    = 0;
  const data_out = [];
  const grade_rad = (p.grade_deg || 0) * Math.PI / 180;
  const phaseData = {};

  // [A24-FIX-A] mid_chain: 고정비율 근사 → 실제 computeEnergyChain 3구간 평균
  //   수정 전: data_out 중간 인덱스 P_kW × 고정계수(4%/3%/2%..) → 사이클과 무관한 가짜 손실 비중
  //   수정 후: 사이클 길이 25%·50%·75% 지점 구동 구간 chain 누산 평균
  let _dc_mc_acc = null;
  let _dc_mc_cnt = 0;
  const _DC_MC_FRACS = [0.25, 0.50, 0.75];  // 사이클 전체 길이 대비 비율
  const _dc_total_t = profile.length > 0 ? profile[profile.length - 1].t : 1800;

  // [A23-FIX-1] _kp_eff/_ki_eff: drive_mode는 시뮬 루프 중 불변 → outer loop 전으로 호이스팅
  //   수정 전: inner for(si) 블록 안 const 선언 → outer loop에서 ReferenceError (블록 스코프 버그)
  //   수정 후: outer loop 진입 전 한 번만 계산, inner/outer 모두 안전하게 참조
  const _dm_dc = (p.drive_mode || 'NORMAL').toLowerCase().replace('-','').replace('_road','road');
  const _gs_kp_key_dc = `gs_kp_${_dm_dc === 'off_road' ? 'offroad' : _dm_dc}`;
  const _gs_ki_key_dc = `gs_ki_${_dm_dc === 'off_road' ? 'offroad' : _dm_dc}`;
  const _kp_eff = p[_gs_kp_key_dc] || p.wltp_tracking_kp || 3.0;
  const _ki_eff = p[_gs_ki_key_dc] || p.wltp_tracking_ki || 0.1;
  // [Parity-DC5] MPC/PID 드라이버 상태 — simWLTP·simLaunch 패턴 이식 (driver_mpc_enable 시 PI보다 우선)
  let drv_state_dc   = p.driver_pid_enable !== false ? initDriverPIDState() : null;
  let mpc_drv_state_dc = p.driver_mpc_enable ? initDriverMPCState() : null;

  for (let ti = 0; ti < profile.length - 1; ti++) {
    const t_s = profile[ti].t;
    const v_ref_kmh = profile[ti].v;
    const v_ref = v_ref_kmh / 3.6;
    const v_ref_next = profile[Math.min(ti + 1, profile.length - 1)].v / 3.6; // [A17] look-ahead
    const dt_step = Math.min(profile[ti + 1].t - t_s, 1.0);
    const steps = Math.round(dt_step / dt_sim);
    for (let si = 0; si < Math.max(1, steps); si++) {
      const dt = dt_step / Math.max(1, steps);
      const v_err = v_ref - v;
      track_err_i += v_err * dt;
      // [A17] look-ahead feedforward: lookahead_s × (v_ref_next - v_ref)
      const lookahead_ff_dc = (v_ref_next - v_ref) * (p.wltp_lookahead_s || 0);
      // [Parity-DC5] MPC → PID → PI 우선순위 분기 (simWLTP·simLaunch 패턴 통일)
      let demand;
      if (p.driver_mpc_enable && mpc_drv_state_dc) {
        const mpc_out_dc = driverMPCController(v_ref, v, mpc_drv_state_dc, p, dt);
        mpc_drv_state_dc = mpc_out_dc.new_state;
        demand = mpc_out_dc.pedal_pos > 0 ? mpc_out_dc.pedal_pos : -mpc_out_dc.brake_demand;
      } else if (p.driver_pid_enable !== false && drv_state_dc) {
        const pid_dc = driverPIDController(v_ref, v, drv_state_dc, p, dt);
        drv_state_dc = pid_dc.new_state;
        demand = pid_dc.pedal_pos > 0 ? pid_dc.pedal_pos : -pid_dc.brake_demand;
      } else {
        // [A23-FIX-1] 기존 PI fallback (하위 호환)
        demand = _kp_eff * v_err + _ki_eff * track_err_i + lookahead_ff_dc;
      }
      const omega_m = Math.max(0.1, v / p.r_wheel * p.gear_ratio);
      const lpm_r = lambda_pm_T(thState.T_motor, p);
      const res = resistanceForcesV3(v, p, grade_rad, T_amb, T_tire_sim, thState.T_motor); // [A25-Fix5]
      // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
      const _hvac3 = hvacDetailedPower(T_amb, v, soc, p); // [A25-Fix4] 현재 soc 전달
      p._hvac_prev_mode = _hvac3.new_hvac_mode;
      const P_aux = _hvac3.P;

      let phase = 'Main';
      phase = genericPhaseTag(cycle_type, t_s, profile[ti]);
      _phase_last = phase;
      if (!phaseData[phase]) phaseData[phase] = { E_drive_J: 0, E_regen_J: 0, d_m: 0, t_s: 0 };

      if (demand > 0) {
        // [BugFix-A04-01] v_kmh 전달: LUT 2D 보간이 현재 차속 기준으로 작동하도록 수정
        const mode_throttle = driveModeThrottle(Math.min(1, demand / 10), p, v * 3.6);
        const T_mot = motorTorqueSpeed(omega_m, mode_throttle, p, thState.T_motor, thState.T_inv, thState.T_batt);
        let F_drv = T_mot * p.gear_ratio * p.eta_driveline / p.r_wheel;
        const mu_eff = ROAD_MU[p.road_condition] || p.mu;
        F_drv = Math.min(F_drv, mu_eff * p.m * g);
        const Id_est = 0, Iq_est = T_mot > 0 ? T_mot / (1.5 * (p.poles / 2) * lpm_r) : 0;
        const chain = computeEnergyChain(v, omega_m, T_mot, Id_est, Iq_est, soc, p, grade_rad, thState);
        const P_elec_W = chain.P_batt_chem * 1000;
        _P_kW_last = +(chain.P_batt_chem).toFixed(2); // [A22]
        // [A24-FIX-A] 3구간 mid_chain 누산: 사이클 25%/50%/75% 지점 구동 포인트 수집
        if (_dc_mc_cnt < 3 && v > 5 / 3.6 && _DC_MC_FRACS.some(function(f) {
              let tgt = _dc_total_t * f;
              return t_s >= tgt && t_s < tgt + 10; })) {
          if (_dc_mc_acc === null) {
            _dc_mc_acc = { ...chain };
          } else {
            Object.keys(_dc_mc_acc).forEach(function(k) {
              if (typeof _dc_mc_acc[k] === 'number') _dc_mc_acc[k] += (chain[k] || 0);
            });
          }
          _dc_mc_cnt++;
        }
        soc = socUpdate(soc, P_elec_W, dt, p);
        // [Parity-DC1] 2RC 배터리 모델 스텝
        if (battery_2rc_dc) {
          const I_dc = P_elec_W / Math.max(1, batt2rc_state_dc);
          const r2 = battery2RCModelWithSEI(battery_2rc_dc, I_dc, dt, p, null);
          if (r2?.V_terminal) batt2rc_state_dc = batt2rc_state_dc * 0.9 + r2.V_terminal * 0.1;
        }
        // [Parity-DC2] Kalman SOC 업데이트
        if (kf_state_dc && p.kalman_enable !== false) {
          const V_m_dc = batteryOCV_smart(soc, p, thState.T_batt);
          const I_kf_dc = P_elec_W / Math.max(1, p.V_dc || 400);
          kf_state_dc   = kalmanSOCUpdate(kf_state_dc, V_m_dc, I_kf_dc, soc, V_m_dc, p);
          soc_kalman_dc = kf_state_dc.soc_est !== undefined ? kf_state_dc.soc_est : soc;
          if (Math.abs(soc_kalman_dc - soc) < 0.15) soc = soc_kalman_dc;
        }
        const dE = P_elec_W * dt;
        E_drive_J += dE; E_aux_drive_J += (chain.P_aux || 0) * 1000 * dt; // [B-3 Fix] chain.P_aux(kW)→J 단일 출처, 구 외부 P_aux*dt 중복 제거
        phaseData[phase].E_drive_J += dE; phaseData[phase].d_m += v * dt; phaseData[phase].t_s += dt;
        // [A25-Fix7] HVAC 전류 → 배터리 내부저항 발열 환류
        const I_hvac_dc = P_aux / Math.max(1, p.V_dc || 400);
        const Q_hvac_batt_dc = I_hvac_dc * I_hvac_dc * R_int_T(soc, thState.T_batt, p);
        Q_b_acc += (chain.Q_batt + Q_hvac_batt_dc) * dt;
        Q_i_acc += chain.Q_inv * dt; Q_m_acc += chain.Q_motor * dt; th_accum += dt;
        const a = (F_drv - res.F_total) / m_eff_dc; // [A44-Fix-1] p.m → m_eff_dc
        // [Parity-DC4] RLS µ 추정 업데이트 — 구동 모드
        rls_state_dc = rlsMuUpdate(rls_state_dc, F_drv, p.m * g * Math.cos(grade_rad), a_prev_dc, p.m, p);
        a_prev_dc = a;
        v = Math.max(0, v + a * dt);
      } else if (demand < -0.05) {
        // [A17] BPP 데드존 + 감지 임계값 적용
        const bpp_dz_dc = Math.max((p.bpp_deadzone_pct || 0), (p.brake_detect_threshold || 0)) / 100;
        const demand_eff_dc = Math.max(0, -demand - bpp_dz_dc);
        // [BugFix-A04-02] regen_calib_enable 시 regenMapDemand LUT 우선 적용
        // [A44-Fix-2] ✅ simDrivingCycle 회생 강도 드라이브 모드 연동 (A43-Fix-5의 비-WLTP 버전)
        //   수정 전: wltp_regen_aggressiveness 고정 0.8 → 비-WLTP 사이클 모드별 차이 미반영
        //   수정 후: driveModeRegenStrength(p) × aggressiveness → ECO/SPORT JC08/CLTC 전비 차이 반영
        const _regen_mode_k_dc = driveModeRegenStrength(p);
        const _regen_aggr_dc   = (p.wltp_regen_aggressiveness || 0.8) * _regen_mode_k_dc;
        const brake_d = p.regen_calib_enable
          ? Math.max(regenMapDemand(v, 0, p), Math.min(1, demand_eff_dc * _regen_aggr_dc))
          : Math.min(1, demand_eff_dc * _regen_aggr_dc);
        const regen_raw_dc = regenTorqueEnhanced(omega_m, brake_d, p, soc, thState.T_batt, thState.T_motor, thState.T_inv);
        // [Fix-RB-1] S-커브 블렌딩 — 주행사이클 저속 감속 구간 마찰 비율 보정
        const blend_dc = regenFrictionBlend(regen_raw_dc.T_regen, regen_raw_dc.T_friction, v, soc, omega_m, p, dt);
        const regen = { ...regen_raw_dc, T_regen: blend_dc.T_regen, T_friction: blend_dc.T_friction };
        // [Fix-RB-2] 저크 제한 T_regen_prev 갱신
        p._regen_T_prev = blend_dc.T_regen;
        if (regen.T_regen > 0 && v > 0.5) {
          const Iq_r = regen.T_regen / Math.max(0.01, 1.5 * (p.poles / 2) * lpm_r);
          const regenChain = computeRegenChain(omega_m, regen.T_regen, 0, Iq_r, soc, p, thState.T_batt, thState.T_motor);
          const P_chg_W = Math.max(0, (regenChain.P_batt_charge || 0) * 1000);
          const dE_regen = P_chg_W * dt;
          E_regen_J += dE_regen; phaseData[phase].E_regen_J += dE_regen;
          soc = Math.min(p.wltp_soc_usable_max || 0.95, soc + dE_regen / (p.battery_energy * 3600000));
          Q_b_acc += (regenChain.Q_batt_regen || 0) * dt; Q_i_acc += (regenChain.Q_inv_regen || 0) * dt; Q_m_acc += (regenChain.Q_motor_regen || 0) * dt; th_accum += dt;
        }
        // [BugFix-v17-3] 회생 구간 HVAC SOC 차감 누락 수정
        //   기존: 회생 중 P_aux(HVAC) 에너지가 E_aux_J에도 미추가, SOC에도 미반영
        //   수정: socUpdate로 HVAC 소비 SOC 차감 + E_aux_regen_J 누산
        soc = socUpdate(soc, P_aux, dt, p);
        E_aux_regen_J += P_aux * dt; // [BUG-1-FIX] 회생 구간 aux는 chain 밖이므로 별도 누산
        const F_r = regen.T_regen * p.gear_ratio / p.r_wheel;
        const a_regen_dc = (-(F_r + res.F_total)) / m_eff_dc;
        // [Parity-DC4] RLS µ 추정 업데이트 — 회생 모드
        rls_state_dc = rlsMuUpdate(rls_state_dc, -F_r, p.m * g * Math.cos(grade_rad), a_prev_dc, p.m, p);
        a_prev_dc = a_regen_dc;
        v = Math.max(0, v + a_regen_dc * dt); // [A44-Fix-1] p.m → m_eff_dc
      } else {
        // Coast
        const a = -res.F_total / p.m;
        v = Math.max(0, v + a * dt);
        // [BugFix-v17-3] 코스팅 구간 HVAC SOC 차감 누락 수정
        //   기존: E_aux_J 누산만 되고 soc 업데이트 없음 → 코스팅 중 HVAC 전력 SOC 미반영
        //   수정: socUpdate 추가
        soc = socUpdate(soc, P_aux, dt, p);
        E_aux_regen_J += P_aux * dt; // [BUG-1-FIX] 코스팅 구간 aux도 E_net에 별도 가산
      }
      if (th_accum >= 0.5) {
        // [A25-Fix2] coolantFlowControl → phi_flow → thermalStep 연동
        cool_state_dc = coolantFlowControl(cool_state_dc, thState.T_cool, p, th_accum);
        thState = thermalStep(thState, Q_b_acc / th_accum, Q_i_acc / th_accum, Q_m_acc / th_accum, v, p, th_accum, cool_state_dc.phi_flow);
        // [Parity-DC3] 3D 열 분포 업데이트
        if (p.thermal_3d_enable && thermal_3d_dc) {
          const Q_3d_dc = Q_b_acc + Q_i_acc + Q_m_acc;
          thermal_3d_dc = update3DThermalStep(thermal_3d_dc, Q_3d_dc, cool_state_dc.phi_flow, thState.T_cool, th_accum, p);
        }
        Q_b_acc = 0; Q_i_acc = 0; Q_m_acc = 0; th_accum = 0;
      }
      // [A25-Fix5] 간이 타이어 온도 갱신: 구름 마찰열 - 대류 냉각
      {
        const Q_tire_heat = res.F_roll * v * (p.tire_heat_fraction || 0.015);
        const h_conv_tire = 10 * (1 + Math.min(v / 30, 2));
        const A_tire = 0.05; // 방열 면적 근사 [m²]
        const Q_tire_cool = h_conv_tire * A_tire * Math.max(0, T_tire_sim - T_amb);
        T_tire_sim = Math.max(T_amb, T_tire_sim + (Q_tire_heat - Q_tire_cool) / (p.C_th_tire || 8000) * dt);
      }
      maxT_motor = Math.max(maxT_motor, thState.T_motor);
      maxT_batt = Math.max(maxT_batt, thState.T_batt);
      maxT_inv = Math.max(maxT_inv, thState.T_inv);
    }
    d_total += v * dt_step;
    t_total_s = t_s;
    soc = Math.max(p.wltp_soc_usable_min || 0.05, soc);
    // [A21-DC7] 주행 모드 분류 — v_sim + demand 기반 (드라이빙 모드 감응)
    const _outer_demand = _kp_eff * (v_ref - v) + _ki_eff * track_err_i;
    if      (v * 3.6 < 0.5)          t_stop_dc   += dt_step;
    else if (_outer_demand >  0.05)   t_accel_dc  += dt_step;
    else if (_outer_demand < -0.05)   t_decel_dc  += dt_step;
    else                              t_cruise_dc += dt_step;
    // [A23-FIX-4] 샘플링 해상도 개선: ti%10(10s) → ti%5(5s) — simWLTP(5s)와 동일 해상도
    //   수정 전: EPA_HWY(765s)→76포인트, JC08(1204s)→120포인트 (그래프 거칠게 표시됨)
    //   수정 후: EPA_HWY→153포인트, JC08→240포인트 (DC1 속도추종 곡선 부드럽게 개선)
    if (ti % 5 === 0) {
      const E_net_J = E_drive_J - E_regen_J + E_aux_regen_J; // [BUG-1-FIX]
      const d_km = d_total / 1000;
      const _v_kmh = +(v * 3.6).toFixed(1);
      const _d_cum = +d_km.toFixed(3);
      data_out.push({
        t: t_s,
        v_ref: v_ref_kmh, v_sim: _v_kmh, v_kmh: _v_kmh,
        err: +(_v_kmh - v_ref_kmh).toFixed(2),
        phase: _phase_last,
        P_kW: _P_kW_last,
        soc: +(soc * 100).toFixed(2),
        kalman_soc: +(soc_kalman_dc * 100).toFixed(2),                                    // [Parity-DC2]
        // 에너지 누적 — simWLTP 호환 필드명
        E_cum_kWh:     +(E_drive_J / 3600000).toFixed(4),
        E_drive_cum:   +(E_drive_J / 3600000).toFixed(4),
        E_regen_cum:   +(E_regen_J / 3600000).toFixed(4),
        E_net_cum_kWh: +(E_net_J   / 3600000).toFixed(4),
        d_cum:         _d_cum,
        kWh_100km_cum: d_km > 0.5 ? +((E_net_J / 3600000) / d_km * 100).toFixed(2) : 0,
        // 온도
        T_batt:  +thState.T_batt.toFixed(1),
        T_motor: +thState.T_motor.toFixed(1),
        T_inv:   +thState.T_inv.toFixed(1),
        T_cool:  +thState.T_cool.toFixed(1),
        hotspot_T:  thermal_3d_dc ? +thermal_3d_dc.T_max.toFixed(1) : +thState.T_batt.toFixed(1), // [Parity-DC3]
        T_delta_3d: thermal_3d_dc ? +thermal_3d_dc.T_delta.toFixed(2) : 0,                        // [Parity-DC3]
        V_terminal: +batt2rc_state_dc.toFixed(2),                                                  // [Parity-DC1]
        mu_rls: +rls_state_dc.mu_est.toFixed(3),                                                   // [Parity-DC4]
        // 디레이팅 — simWLTP 호환
        derate_batt:  Math.min(100, +(P_dis_max_T(thState.T_batt, p) / (p.P_dis_ref || 150000) * 100).toFixed(0)),
        derate_inv:   Math.min(100, +(I_max_T(thState.T_inv,   p) / (p.I_max_rms  || 400)    * 100).toFixed(0)),
        derate_motor: Math.min(100, +(T_max_motor_T(thState.T_motor, p) / (p.T_max_motor || 400) * 100).toFixed(0)),
        driver_mpc_active: !!(p.driver_mpc_enable && mpc_drv_state_dc),               // [Parity-DC5]
        driver_pedal: p.driver_mpc_enable && mpc_drv_state_dc
          ? +mpc_drv_state_dc.pedal_prev.toFixed(3) : null,                            // [Parity-DC5]
      });
    }
  }
  const d_km = d_total / 1000;
  const E_drive_kWh = E_drive_J / 3600000;
  const E_regen_kWh = E_regen_J / 3600000;
  const E_aux_regen_kWh = E_aux_regen_J / 3600000; // [BUG-1-FIX] 회생+코스팅 구간 aux
  const E_aux_kWh = (E_aux_drive_J + E_aux_regen_J) / 3600000; // [BUG-1-FIX] 표시용 총 aux
  const E_net_kWh = E_drive_kWh - E_regen_kWh + E_aux_regen_kWh; // [BUG-1-FIX] simWLTP와 동일 구조
  const e_cycle = d_km > 0.1 ? +(E_net_kWh / d_km * 100).toFixed(2) : 0;
  const co2_gkm = +(e_cycle * (p.wltp_ef_grid || GRID_EF_DEFAULT_SIM) / 100).toFixed(1); // [BUG-2-FIX][P-3-Fix]
  const soc_range = (p.wltp_soc_usable_max || 0.90) - (p.wltp_soc_usable_min || 0.10);
  const range_km = e_cycle > 0 ? +((p.battery_energy * soc_range) / e_cycle * 100).toFixed(0) : 0;
  const eta_regen_pct = E_drive_kWh > 0 ? +(E_regen_kWh / E_drive_kWh * 100).toFixed(1) : 0;
  // Phase summary
  const phaseSummary = Object.entries(phaseData).map(([ph, pd]) => {
    const d_ph_km = pd.d_m / 1000;
    const E_net_ph = (pd.E_drive_J - pd.E_regen_J) / 3600000;
    const _eta_regen = pd.E_drive_J > 0 ? +(pd.E_regen_J / pd.E_drive_J * 100).toFixed(1) : 0;
    return {
      phase: ph, d_km: +d_ph_km.toFixed(3), t_s: +pd.t_s.toFixed(0),
      E_kWh: +E_net_ph.toFixed(4),                          // [A22] DCTabContent DC3 호환
      e_kWh100: d_ph_km > 0.01 ? +(E_net_ph / d_ph_km * 100).toFixed(2) : 0,
      eta_regen_pct: _eta_regen,
      regen_pct: _eta_regen,                                 // [A22] DCTabContent alias
      avg_v: pd.t_s > 0 ? +(pd.d_m / pd.t_s * 3.6).toFixed(1) : 0, // [A22] avg speed
    };
  });
  // [A21-DC7] maneuverSplit 계산
  const _t_ms_total = t_stop_dc + t_accel_dc + t_cruise_dc + t_decel_dc || 1;
  const maneuverSplit_dc = {
    stop:   +(t_stop_dc   / _t_ms_total * 100).toFixed(1),
    accel:  +(t_accel_dc  / _t_ms_total * 100).toFixed(1),
    cruise: +(t_cruise_dc / _t_ms_total * 100).toFixed(1),
    decel:  +(t_decel_dc  / _t_ms_total * 100).toFixed(1),
  };
  return {
    cycle_type, data: data_out, phaseSummary,
    d_km: +d_km.toFixed(3), t_total_s,
    E_drive_kWh: +E_drive_kWh.toFixed(4), E_regen_kWh: +E_regen_kWh.toFixed(4),
    E_aux_kWh:   +E_aux_kWh.toFixed(4),
    E_net_kWh:   +E_net_kWh.toFixed(4),   // [A22] EnergyTab 호환
    e_wltp: e_cycle, consumption_kWh_100km: e_cycle,
    co2_gkm, range_km, eta_regen_pct,
    soc_final:        +(soc * 100).toFixed(1),
    soc_kalman_final: +(soc_kalman_dc * 100).toFixed(2),                                 // [Parity-DC2]
    V_terminal_final: +batt2rc_state_dc.toFixed(2),                                      // [Parity-DC1]
    hotspot_T:  thermal_3d_dc ? +thermal_3d_dc.T_max.toFixed(1)   : +thState.T_batt.toFixed(1), // [Parity-DC3]
    T_delta_3d: thermal_3d_dc ? +thermal_3d_dc.T_delta.toFixed(2) : 0,                          // [Parity-DC3]
    mu_rls_final: +rls_state_dc.mu_est.toFixed(3),                                               // [Parity-DC4]
    maneuverSplit: maneuverSplit_dc,
    thermalStats: { T_motor_max: maxT_motor, T_batt_max: maxT_batt, T_inv_max: maxT_inv,
      derate_events_batt: derate_ev_b, derate_events_inv: derate_ev_i, derate_events_motor: derate_ev_m },
    thFinal: { ...thState },               // [A22] ThermalTab 호환
    T_amb_used: T_amb,                     // [A22] ThermalTab 호환
    // ── 드라이브 모드 메타 ────────────────────────────────────────────
    drive_mode_applied: p._sim_dm_mode || 'NORMAL',
    drive_mode_factors: p._sim_dm_factors || null,
    drive_mode_custom:  !!(params.mode_gains && params.mode_gains[p._sim_dm_mode || 'NORMAL']),
    // [V41] RDE 오염물질 배출 체인
    rdeEmissions: calcRDEEmissions(phaseSummary, p, cycle_type),
    // [A24-FIX-A] 3구간 평균 mid_chain 확정
    mid_chain: (function() {
      if (_dc_mc_acc && _dc_mc_cnt > 0) {
        let avg = {};
        Object.keys(_dc_mc_acc).forEach(function(k) {
          avg[k] = typeof _dc_mc_acc[k] === 'number'
            ? +(_dc_mc_acc[k] / _dc_mc_cnt).toFixed(4)
            : _dc_mc_acc[k];
        });
        return avg;
      }
      // fallback: data_out 중간 P_kW 기반 (구동 구간이 없는 극단적 경우)
      let mid = data_out[Math.floor(data_out.length / 2)] || data_out[0];
      let pk = (mid && mid.P_kW) ? mid.P_kW : 1;
      return {
        P_batt_chem: pk, P_loss_batt: pk * 0.04, P_loss_inv: pk * 0.03,
        P_cu: pk * 0.02, P_fe: pk * 0.015, P_loss_gear: pk * 0.01,
        P_wheel: pk * 0.875, P_aero: pk * 0.05, P_roll: pk * 0.02,
      };
    })(),
  };
}

// ============================================================
// ④ OCV-SOC 모델 — 단일 소스: battery.js
// ────────────────────────────────────────────────────────────
// [OCV-Dedup] OCV_LUT_SOC / OCV_LUT_V / OCV_TEMP_* 상수는
//   battery.js에 단일 정의됨 (batteryOCV_smart 내부 사용).
//   simulation.js에서의 중복 선언을 제거하여 불일치 위험 해소.
//   모든 OCV 계산은 공유 스코프의 batteryOCV_smart() 를 통해 수행.
// ============================================================

// ── simWLTP ──
/**
 * WLTP Class-3 주행 사이클 완전 시뮬레이션 커널을 실행한다 (1800초, dt=0.1 s).
 * 드라이버 PI/MPC 속도 추종, FOC, 2RC 배터리, 열 모델, EKF/UKF SOC, 회생제동,
 * HVAC, 기어, AWD, ESC/TCS, SOH 등 전체 파이프라인.
 * [A43-Fix-4] effectiveMass 적용. 단위 규약: P[W], E_J[J], E_kWh[kWh], Q[W], soc[0~1].
 *
 * @param {object} params            차량 파라미터
 * @param {number} [T_amb_override]  환경 온도 재정의 [°C]
 * @returns {{ e_wltp, range_km, eta_regen_pct, thermalStats, phaseSummary, ... }}
 */
function simWLTP(params, T_amb_override) {
  // ══════════════════════════════════════════════════════════
  // WLTP Class-3 시뮬레이션 커널
  // 단위 규약:
  //   P_xxx [W]  ·  E_xxx_J [J]  ·  E_xxx_kWh [kWh]
  //   socUpdate(soc, P_W, dt_s, params) → 새 SOC
  //   chain.P_batt_chem [kW] = 배터리에서 나가는 총 전력(aux 포함)
  //   regenChain.P_batt_charge [kW] = 배터리로 들어오는 전력
  //   Q_xxx [W] = 열 발생량 (thermalStep 입력)
  // ══════════════════════════════════════════════════════════
  const profile   = getWLTPProfile();          // [{t, v[km/h]}, ...] 1Hz
  const dt_sim    = 0.1;                        // 내부 물리 스텝 [s]
  const n_inner   = Math.round(1.0 / dt_sim);  // 스텝/초 = 10
  const grade_rad = (params.grade_deg || 0) * Math.PI / 180;
  const T_amb     = T_amb_override !== undefined ? T_amb_override : (params.T_amb || 25);
  // 보조기기 파워를 computeEnergyChain이 내부적으로 포함하므로
  // 시뮬레이션용 params의 T_amb를 설정해 heatPumpPower가 올바른 값을 쓰게 함
  // ── 드라이브 모드 게인 적용 ──────────────────────────────────────────
  // drive_mode (ECO/NORMAL/SPORT/SNOW/OFF_ROAD) 에 따라
  // Kp_id/Ki_id/Kp_iq/Ki_iq/Kp_spd/Ki_spd 를 결정.
  // mode_gains 커스텀값 우선, 없으면 motor_control.js DRIVE_MODE_GAIN_TABLE 배율 적용.
  const p         = _applyDriveModeGainsInline({ ...params, T_amb });
  const mu_eff    = ROAD_MU[p.road_condition] || p.mu || 0.8;
  // [A43-Fix-4] ✅ simWLTP 유효 질량 적용: 회전관성 포함 m_eff (~2~4% 정확도 향상)
  //   수정 전: a = F/p.m — 회전관성 미반영 → WLTP 에너지 ~2~4% 과소 추정
  //   수정 후: effectiveMass(p) 사용 → 고회전관성 파라미터 세트에서 효과 큼
  const m_eff_wltp = effectiveMass(p);

  // ── 상태 초기화 ──
  let v   = 0;           // 차속 [m/s]
  let soc = p.soc_init;  // 배터리 SOC [0~1]
  let thState = { T_batt: T_amb, T_inv: T_amb, T_motor: T_amb, T_cool: T_amb };
  let track_err_i = 0;   // PI 적분 오차
  let prev_demand_w = 0; // [A17] ROC 제한용 직전 demand
  // [R-10-01] GAP-06: WLTP PID 드라이버 상태 초기화
  let drv_state_w   = p.driver_pid_enable !== false ? initDriverPIDState() : null;
  // [Parity-W5] MPC 드라이버 상태 — simLaunch/simReaccel 패턴 이식 (driver_mpc_enable 시 PID보다 우선)
  let mpc_drv_state_w = p.driver_mpc_enable ? initDriverMPCState() : null;
  // [R-10-02] GAP-10: WLTP 다단 기어 상태 초기화
  let gear_state_w  = p.multi_gear_enable ? initGearState(p) : null;
  let T_gear_w      = p.gear_T_oil || 80;           // [P1] 기어박스 오일 온도 추적 (simLaunch 패턴)
  let t_wltp = 0; // WLTP 내부 절대 시간 (기어 변속 타이밍용)
  let cool_state_w  = initCoolantFlowState(p);      // [A25-Fix2] 냉각 유량 PI 상태
  let T_tire_sim    = T_amb;                         // [A25-Fix5] 간이 타이어 온도 (°C)
  // [Parity-W1] 2RC 배터리 모델 — simLaunch 패리티
  let battery_2rc_w   = p.battery_model === '2RC' ? init2RCBatteryState(p) : null;
  let batt2rc_state_w = p.V_dc || 400; // DC 링크 전압 추적 상태
  // [Parity-W2] Kalman SOC 추정 (EKF/UKF/Linear) — simLaunch 패리티
  let kf_state_w   = p.kalman_enable !== false ? initKalmanByType(p.soc_init, p) : null;
  let soc_kalman_w = p.soc_init;
  // [Parity-W3] 3D 열 분포 모델 — simLaunch 패리티
  let thermal_3d_w = p.thermal_3d_enable ? init3DThermalState(p) : null;
  // [Parity-W4] RLS µ 추정 — simLaunch/simReaccel/simFullSpecCycle 패리티
  let rls_state_w = initRLSMuState(p);
  let a_prev_w    = 0;
  // [P4] SOH + Rainflow — simLaunch 패리티
  let soh_state_w  = p.soh_enable ? initSOHState(p) : null;
  let rf_state_w   = (p.soh_enable && p.rainflow_enable) ? initRainflowState(p) : null;
  // [P5] 조향 피드백 모델 — simLaunch/simReaccel 패리티
  let steer_state_w = { delta_sw: 0, delta_sw_dot: 0, T_driver: 0 };

  // 열 누산기 (0.5s마다 thermalStep 호출)
  let Q_b_acc = 0, Q_i_acc = 0, Q_m_acc = 0, th_accum = 0;

  // ── 에너지 누산기 [J] ──
  // E_drive_J  : 구동 구간 배터리 방전 에너지 (aux 포함, chain.P_batt_chem 기반)
  // E_regen_J  : 회생 구간 배터리 충전 에너지 (regenChain.P_batt_charge 기반)
  // 보조기기는 chain.P_batt_chem 안에 이미 포함돼 있으므로 별도 차감 불필요
  // 회생 구간에서는 aux를 별도로 SOC에서 차감
  let E_drive_J = 0;   // 구동 방전 [J] (aux 포함)
  let E_regen_J = 0;   // 회생 충전 [J]
  let E_aux_regen_J = 0; // 회생 구간 보조기기 소비 [J] (별도 누산)
  // [A44-Fix-3] ✅ 구동 구간 보조기기(HVAC) 에너지 실제 누산
  //   수정 전: 반환값에서 hvacDetailedPower(soc_init)×t_total×0.6 단일점 추정 → soc·속도 변화 무시
  //   수정 후: 드라이브 루프에서 chain.P_aux×dt 직접 적산 → E_aux 정확 계산
  let E_aux_drive_J = 0; // 구동 구간 보조기기 소비 [J]
  let d_m = 0;         // 누적 주행거리 [m]

  // [A24-FIX-A] mid_chain: 단일 스냅샷(t≈900s) → 3구간 대표점 가중평균으로 개선
  //   수정 전: t≥850 첫 구동 포인트 1개 → 해당 순간 부하만 반영, E1 손실 비중 왜곡
  //   수정 후: Low(t=300s), Medium(t=900s), High(t=1500s) 3개 체인 합산 → 전 사이클 손실 구조 반영
  let mid_chain = null;
  let _mc_acc = null;   // 3구간 누산 체인 합 (P_* 필드)
  let _mc_cnt = 0;      // 누산 횟수
  const _MC_TARGETS = [300, 900, 1500]; // Low·Medium·ExtraHigh 대표 시각(s)

  let t_stop = 0, t_accel = 0, t_cruise = 0, t_decel = 0;

  const phases = {
    Low:           { E_drive_J: 0, E_regen_J: 0, d_m: 0, t_s: 0 },
    Medium:        { E_drive_J: 0, E_regen_J: 0, d_m: 0, t_s: 0 },
    High:          { E_drive_J: 0, E_regen_J: 0, d_m: 0, t_s: 0 },
    'Extra-High':  { E_drive_J: 0, E_regen_J: 0, d_m: 0, t_s: 0 },
  };
  const data = [];  // 로그 (5초마다)

  const n_steps = profile.length - 1;

  for (let step = 0; step < n_steps; step++) {
    const t_s    = profile[step].t;
    const v_ref  = profile[step].v / 3.6;                              // [m/s]
    const v_ref2 = profile[Math.min(step + 1, n_steps)].v / 3.6;
    const phase  = wltpPhaseTag(t_s);

    // [M4-Fix] Catmull-Rom 큐빅 스플라인 계수 사전 계산
    //   제어점: [step−1, step, step+1, step+2] — 경계 클램프 포함
    //   α = k/n_inner ∈ [0,1) → v_ref_k: 가속도 연속, jerk 불연속 제거
    //   MPC는 계산 비용으로 1Hz 유지, PID/PI는 내부 루프 0.1s마다 갱신
    const _cr_v0 = profile[Math.max(step - 1, 0)].v / 3.6;
    const _cr_v1 = v_ref;   // profile[step]
    const _cr_v2 = v_ref2;  // profile[step+1]
    const _cr_v3 = profile[Math.min(step + 2, n_steps)].v / 3.6;

    // ── [R-10-01] GAP-06: PID 드라이버 모델 (WLTP) ──────────────────────
    // MPC: 1Hz 계산 유지. PID/PI: 내부 루프 0.1s 재계산 (M4-Fix 참조)
    let demand;
    if (p.driver_mpc_enable && mpc_drv_state_w) {
      // [Parity-W5] MPC 드라이버 — simLaunch 패턴 이식 (PID보다 우선), 1Hz 유지
      const mpc_out_w = driverMPCController(v_ref, v, mpc_drv_state_w, p, 1.0);
      mpc_drv_state_w = mpc_out_w.new_state;
      demand = mpc_out_w.pedal_pos > 0 ? mpc_out_w.pedal_pos : -mpc_out_w.brake_demand;
      // [A17] ROC 제한 — MPC 경로 1Hz 기준
      if ((p.app_roc_limit || 0) > 0) {
        const roc_max = (p.app_roc_limit / 100);
        demand = Math.max(prev_demand_w - roc_max, Math.min(prev_demand_w + roc_max, demand));
      }
      prev_demand_w = demand;
    } else {
      // PID/PI: 내부 루프 0.1s에서 계산 — 모드 분류용 부호 추정만 산출
      demand = (v_ref - v) > 0.1 ? 0.06 : (v_ref - v) < -0.1 ? -0.06 : 0;
    }

    // ── [A21-DC7] 주행 모드 분류 — v_sim + demand 기반 (드라이빙 모드 감응)
    //    기존: v_ref/a_ref 기준 → 모드 무관 고정값
    //    변경: 실제 차속(v) + 요구 입력(demand) → ECO/SPORT 차이 반영
    if      (v * 3.6 < 0.5)    t_stop   += 1.0;
    else if (demand >  0.05)   t_accel  += 1.0;
    else if (demand < -0.05)   t_decel  += 1.0;
    else                       t_cruise += 1.0;

    // ══ 내부 물리 루프 (dt_sim = 0.1s) ══
    for (let k = 0; k < n_inner; k++) {
      t_wltp += dt_sim;

      // [M4-Fix] Catmull-Rom 보간 v_ref_k → PID/PI 드라이버 0.1s 갱신
      //   MPC: 외부 1Hz demand 유지 (계산 비용)
      //   PID: dt_sim=0.1s, v_ref_k 목표 → 가속도 연속 추종
      //   PI : 레거시 경로도 0.1s 갱신, 적분 항도 dt_sim 기준 누산
      if (!p.driver_mpc_enable) {
        const _cr_a  = k / n_inner;
        const _cr_a2 = _cr_a * _cr_a, _cr_a3 = _cr_a2 * _cr_a;
        const v_ref_k = Math.max(0, 0.5 * (
          2 * _cr_v1 +
          (_cr_v2 - _cr_v0) * _cr_a +
          (2*_cr_v0 - 5*_cr_v1 + 4*_cr_v2 - _cr_v3) * _cr_a2 +
          (-_cr_v0 + 3*_cr_v1 - 3*_cr_v2 + _cr_v3) * _cr_a3
        ));
        if (p.driver_pid_enable !== false && drv_state_w) {
          // PID 드라이버 — 0.1s 스텝, 보간 속도 목표
          const pid_k    = driverPIDController(v_ref_k, v, drv_state_w, p, dt_sim);
          drv_state_w    = pid_k.new_state;
          demand         = pid_k.pedal_pos > 0 ? pid_k.pedal_pos : -pid_k.brake_demand;
        } else {
          // [A17] 레거시 PI — 0.1s 갱신, look-ahead는 다음 서브스텝 보간값 사용
          const _cr_an   = Math.min((k + 1) / n_inner, 1);
          const _cr_an2  = _cr_an * _cr_an, _cr_an3 = _cr_an2 * _cr_an;
          const v_ref_kn = Math.max(0, 0.5 * (
            2 * _cr_v1 +
            (_cr_v2 - _cr_v0) * _cr_an +
            (2*_cr_v0 - 5*_cr_v1 + 4*_cr_v2 - _cr_v3) * _cr_an2 +
            (-_cr_v0 + 3*_cr_v1 - 3*_cr_v2 + _cr_v3) * _cr_an3
          ));
          const v_err_k      = v_ref_k - v;
          track_err_i        = Math.max(-5, Math.min(5, track_err_i + v_err_k * dt_sim));
          const lookahead_ff_k = (v_ref_kn - v_ref_k) * (p.wltp_lookahead_s || 0);
          const _w_dm        = (p.drive_mode || 'NORMAL').toLowerCase();
          const _w_suffix    = _w_dm === 'off_road' ? 'offroad' : _w_dm;
          const _w_kp        = p[`gs_kp_${_w_suffix}`] || p.wltp_tracking_kp || 3.0;
          const _w_ki        = p[`gs_ki_${_w_suffix}`] || p.wltp_tracking_ki || 0.1;
          demand             = _w_kp * v_err_k + _w_ki * track_err_i + lookahead_ff_k;
        }
        // [A17] ROC 제한 — 0.1s 스텝 기준 (roc_limit [%/s] × dt_sim)
        if ((p.app_roc_limit || 0) > 0) {
          const roc_max_k = (p.app_roc_limit / 100) * dt_sim;
          demand = Math.max(prev_demand_w - roc_max_k, Math.min(prev_demand_w + roc_max_k, demand));
        }
        prev_demand_w = demand;
      }

      // [R-10-02] GAP-10: 다단 기어 (WLTP 내부 루프)
      let gear_ratio_w = p.gear_ratio || 9.5;
      let torque_scale_w = 1.0;
      if (p.multi_gear_enable && gear_state_w) {
        const gearOut_w = getActiveGear(v, p.T_max_motor * Math.max(0, demand), soc, gear_state_w, p, dt_sim, t_wltp);
        gear_ratio_w   = gearOut_w.gear_ratio_eff;
        torque_scale_w = gearOut_w.torque_scale;
        gear_state_w   = gearOut_w.gear_state_new;
      }
      const omega_m = Math.max(0.5, v / p.r_wheel * gear_ratio_w);   // [rad/s]
      const ph      = phases[phase];

      if (demand >= 0) {
        // ━━ 구동 모드 ━━
        // [BugFix-A04-01] raw throttle 직접 사용 → driveModeThrottle(v_kmh) 경유로 수정
        const throttle_raw = Math.min(1.0, demand);
        const throttle = driveModeThrottle(throttle_raw, p, v * 3.6);
        const T_mot = motorTorqueSpeed(omega_m, throttle, p,
                        thState.T_motor, thState.T_inv, thState.T_batt) * torque_scale_w;
        // 구동력 & 차량 가속도
        let F_drv = T_mot * gear_ratio_w * (p.eta_driveline || 0.97) / p.r_wheel;
        F_drv = Math.min(F_drv, mu_eff * p.m * g * Math.cos(grade_rad));
        const res = resistanceForcesV3(v, p, grade_rad, T_amb, T_tire_sim, thState.T_motor); // [A25-Fix5]
        const a_vehicle = (F_drv - res.F_total) / m_eff_wltp; // [A43-Fix-4] p.m → m_eff_wltp
        const lpm   = lambda_pm_T(thState.T_motor, p);
        const Iq_req = T_mot > 0
          ? Math.min(T_mot / (1.5 * (p.poles / 2) * lpm), p.I_max_rms) : 0;

        // 에너지 체인: P_batt_chem [kW] = 배터리 방전 전력 (drive+aux 합산)
        const chain    = computeEnergyChain(v, omega_m, T_mot, 0, Iq_req, soc, p, grade_rad, thState);
        const P_batt_W = Math.max(0, chain.P_batt_chem * 1000); // [W]
        const dE       = P_batt_W * dt_sim;                      // [J]

        // [A24-FIX-A] 3구간 가중평균 mid_chain 누산
        //   각 대표 시각(300/900/1500s) 부근 첫 구동 포인트를 누산 → 최종 평균
        if (_mc_cnt < 3 && _MC_TARGETS.some(function(tgt) {
              return t_s >= tgt && t_s < tgt + 10 && v > 5 / 3.6; })) {
          if (_mc_acc === null) {
            _mc_acc = { ...chain };
          } else {
            // P_* 필드 합산 (문자열/비숫자 필드는 첫 값 유지)
            Object.keys(_mc_acc).forEach(function(k) {
              if (typeof _mc_acc[k] === 'number') _mc_acc[k] += (chain[k] || 0);
            });
          }
          _mc_cnt++;
        }

        // SOC 업데이트 (방전: +P)
        soc = socUpdate(soc, P_batt_W, dt_sim, p);

        // [Parity-W1] 2RC 배터리 모델 스텝 — V_terminal 추적
        let batt2rc_result_w = null;
        if (battery_2rc_w) {
          const I_batt_est_w = P_batt_W / Math.max(1, batt2rc_state_w);
          batt2rc_result_w = battery2RCModelWithSEI(battery_2rc_w, I_batt_est_w, dt_sim, p, null);
          if (batt2rc_result_w?.V_terminal) {
            batt2rc_state_w = batt2rc_state_w * 0.9 + batt2rc_result_w.V_terminal * 0.1;
          }
        }

        // [Parity-W2] Kalman SOC 업데이트 (EKF/UKF/Linear)
        if (kf_state_w && p.kalman_enable !== false) {
          const V_meas_w    = batt2rc_result_w?.V_terminal
            ?? batteryOCV_smart(soc, p, thState.T_batt);
          const I_batt_kf_w = P_batt_W / Math.max(1, p.V_dc || 400);
          const soc_meas_w  = batteryOCV_smart(soc, p, thState.T_batt);
          kf_state_w   = kalmanSOCUpdate(kf_state_w, V_meas_w, I_batt_kf_w, soc, soc_meas_w, p);
          soc_kalman_w = kf_state_w.soc_est !== undefined ? kf_state_w.soc_est : soc;
          if (Math.abs(soc_kalman_w - soc) < 0.15) soc = soc_kalman_w;
        }

        // 에너지 누산
        E_drive_J    += dE;
        ph.E_drive_J += dE;
        E_aux_drive_J += (chain.P_aux || 0) * 1000 * dt_sim; // [A44-Fix-3] 구동 구간 HVAC 실적산

        // 열 발생량 누산 [W·s → J]
        // [A25-Fix7] HVAC 전류 → 배터리 내부저항 발열 환류
        // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
        const _hvac4 = hvacDetailedPower(T_amb, v, soc, p); // [A25-Fix4] 현재 soc
        p._hvac_prev_mode = _hvac4.new_hvac_mode;
        const P_aux_drv = _hvac4.P;
        const I_hvac_w  = P_aux_drv / Math.max(1, p.V_dc || 400);
        const Q_hvac_batt_w = I_hvac_w * I_hvac_w * R_int_T(soc, thState.T_batt, p);
        Q_b_acc += ((chain.Q_batt || 0) + Q_hvac_batt_w) * dt_sim;
        Q_i_acc += (chain.Q_inv   || 0) * dt_sim;
        Q_m_acc += (chain.Q_motor || 0) * dt_sim;

        // [Parity-W4] RLS µ 추정 업데이트 — 구동 모드
        {
          const F_normal_w = p.m * g * Math.cos(grade_rad);
          rls_state_w = rlsMuUpdate(rls_state_w, F_drv, F_normal_w, a_prev_w, p.m, p);
          a_prev_w = a_vehicle;
        }
        // [P5] 조향 피드백 모델 — simWLTP 연결 (simLaunch/simReaccel 패리티)
        if (_steeringFeedbackModel !== null) {
          const steerOut_w = _steeringFeedbackModel(
            steer_state_w, 0, 0, Math.max(v, 0.5), p, dt_sim
          );
          steer_state_w = { delta_sw: steerOut_w.delta_sw, delta_sw_dot: steerOut_w.delta_sw_dot, T_driver: steer_state_w.T_driver };
          steer_state_w._last = steerOut_w;
        }

        v += a_vehicle * dt_sim;

      } else {
        // ━━ 회생/제동 모드 ━━
        // [A17] BPP 데드존 + 감지 임계값 적용
        const bpp_dz  = Math.max((p.bpp_deadzone_pct || 0), (p.brake_detect_threshold || 0)) / 100;
        const demand_eff = Math.max(0, -demand - bpp_dz); // 데드존 이하는 코스트
        // [A43-Fix-5] ✅ simWLTP 회생 강도를 드라이브 모드에 연동
        //   수정 전: wltp_regen_aggressiveness 고정 0.8 → 모드별 차이 미반영
        //   수정 후: driveModeRegenStrength(p) × aggressiveness → ECO/SPORT 전비 차이 실제 반영
        const regen_mode_k = driveModeRegenStrength(p); // ECO:0.5, NORMAL:0.7, SPORT:1.0
        const regen_aggr   = (p.wltp_regen_aggressiveness || 0.8) * regen_mode_k;
        const brake_demand = p.regen_calib_enable
          ? Math.max(regenMapDemand(v, 0, p), Math.min(1.0, demand_eff * regen_aggr))
          : Math.min(1.0, demand_eff * regen_aggr);
        const regen_raw_wl = regenTorqueEnhanced(omega_m, brake_demand, p,
                               soc, thState.T_batt, thState.T_motor, thState.T_inv);
        // [Fix-RB-1] S-커브 블렌딩 — WLTP 저속 구간 회생/마찰 비율 보정
        const blend_wl = regenFrictionBlend(regen_raw_wl.T_regen, regen_raw_wl.T_friction, v, soc, omega_m, p, dt_sim);
        const regen = { ...regen_raw_wl, T_regen: blend_wl.T_regen, T_friction: blend_wl.T_friction };
        // [Fix-RB-2] 저크 제한 T_regen_prev 갱신
        p._regen_T_prev = blend_wl.T_regen;

        if (regen.T_regen > 0 && v > 0.5) {
          const lpm_r     = lambda_pm_T(thState.T_motor, p);
          const Iq_regen  = regen.T_regen / Math.max(0.01, 1.5 * (p.poles / 2) * lpm_r);
          const regenChain = computeRegenChain(omega_m, regen.T_regen, 0, Iq_regen,
                               soc, p, thState.T_batt, thState.T_motor);

          // P_batt_charge [kW] = 배터리로 들어오는 순 충전 전력
          const P_chg_W = Math.max(0, (regenChain.P_batt_charge || 0) * 1000); // [W]
          const dE_regen = P_chg_W * dt_sim; // [J]

          // SOC 업데이트 (충전: -P)
          soc = socUpdate(soc, -P_chg_W, dt_sim, p);

          E_regen_J    += dE_regen;
          ph.E_regen_J += dE_regen;

          Q_b_acc += (regenChain.Q_batt_regen  || 0) * dt_sim;
          Q_i_acc += (regenChain.Q_inv_regen   || 0) * dt_sim;
          Q_m_acc += (regenChain.Q_motor_regen || 0) * dt_sim;
        }

        // *** 회생 구간에서는 computeEnergyChain을 쓰지 않으므로
        //     보조기기 소비를 별도로 SOC에서 차감 ***
        // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
        const _hvac5 = hvacDetailedPower(T_amb, v, soc, p); // [A25-Fix4] 현재 soc 전달
        p._hvac_prev_mode = _hvac5.new_hvac_mode;
        const P_aux_W  = _hvac5.P;
        const dE_aux   = P_aux_W * dt_sim;            // [J]
        // [A25-Fix7] HVAC 전류 → 배터리 내부저항 발열 환류 (회생 구간도 동일)
        const I_hvac_regen = P_aux_W / Math.max(1, p.V_dc || 400);
        const Q_hvac_batt_regen = I_hvac_regen * I_hvac_regen * R_int_T(soc, thState.T_batt, p);
        Q_b_acc += Q_hvac_batt_regen * dt_sim;
        soc            = socUpdate(soc, P_aux_W, dt_sim, p);
        E_aux_regen_J += dE_aux;

        // 제동 감속
        const F_regen = regen.T_regen * gear_ratio_w / p.r_wheel;
        const F_fric  = (regen.T_friction || 0) * gear_ratio_w / p.r_wheel;
        const res     = resistanceForcesV3(v, p, grade_rad, T_amb, T_tire_sim, thState.T_motor); // [A25-Fix5]
        const a_vehicle = -(F_regen + F_fric + res.F_total) / m_eff_wltp; // [A43-Fix-4] p.m → m_eff_wltp
        // [Parity-W4] RLS µ 추정 업데이트 — 회생/제동 모드
        {
          const F_normal_w = p.m * g * Math.cos(grade_rad);
          rls_state_w = rlsMuUpdate(rls_state_w, -F_regen, F_normal_w, a_prev_w, p.m, p);
          a_prev_w = a_vehicle;
        }
        // [P5] 조향 피드백 모델 — 회생 구간 (직진 가정: Fy=0, alpha=0)
        if (_steeringFeedbackModel !== null) {
          const steerOut_w = _steeringFeedbackModel(
            steer_state_w, 0, 0, Math.max(v, 0.5), p, dt_sim
          );
          steer_state_w = { delta_sw: steerOut_w.delta_sw, delta_sw_dot: steerOut_w.delta_sw_dot, T_driver: steer_state_w.T_driver };
          steer_state_w._last = steerOut_w;
        }
        v += a_vehicle * dt_sim;
      }

      v = Math.max(0, v);

      // 공통 거리 누산
      d_m      += v * dt_sim;
      ph.d_m   += v * dt_sim;
      ph.t_s   += dt_sim;

      // ── 열 모델 (0.5s마다) ──
      th_accum += dt_sim;
      if (th_accum >= 0.5) {
        // [A25-Fix2] coolantFlowControl → phi_flow → thermalStep 연동
        cool_state_w = coolantFlowControl(cool_state_w, thState.T_cool, p, th_accum);
        // [P1] 기어박스 열 모델 — simLaunch 패턴 이식
        const omega_m_th_w = Math.max(0.5, v / p.r_wheel * (gear_ratio_w || p.gear_ratio || 9.5));
        const gear_loss_w  = gearLossTempEnhanced(omega_m_th_w, T_gear_w, thState.T_motor, p);
        T_gear_w = gearThermalStep(T_gear_w, gear_loss_w.P_loss, v, p, th_accum);
        thState = thermalStep(thState,
          Q_b_acc / th_accum,
          Q_i_acc / th_accum,
          Q_m_acc / th_accum,
          v, p, th_accum, cool_state_w.phi_flow);
        // [Parity-W3] 3D 열 분포 업데이트 (thermal_3d_enable 게이팅)
        if (p.thermal_3d_enable && thermal_3d_w) {
          const Q_total_3d_w = Q_b_acc + Q_i_acc + Q_m_acc;
          thermal_3d_w = update3DThermalStep(thermal_3d_w, Q_total_3d_w, cool_state_w.phi_flow, thState.T_cool, th_accum, p);
        }
        Q_b_acc = 0; Q_i_acc = 0; Q_m_acc = 0; th_accum = 0;
      }
      // [A25-Fix5] 간이 타이어 온도 갱신: 구름 마찰열 - 대류 냉각
      {
        const _F_roll_w = (p.m * g * (p.Crr || 0.0095));
        const Q_tire_heat_w = _F_roll_w * v * (p.tire_heat_fraction || 0.015);
        const h_conv_w = 10 * (1 + Math.min(v / 30, 2));
        const Q_tire_cool_w = h_conv_w * 0.05 * Math.max(0, T_tire_sim - T_amb);
        T_tire_sim = Math.max(T_amb, T_tire_sim + (Q_tire_heat_w - Q_tire_cool_w) / (p.C_th_tire || 8000) * dt_sim);
      }
      // [P4] SOH + Rainflow 업데이트 — simLaunch 패리티
      if (soh_state_w && p.soh_enable) {
        const batt_snap_w = { SOC: soc, T_cell: thState.T_batt };
        const I_batt_w    = Math.abs(P_batt_W) / Math.max(1, p.V_dc || 400); // [B-1 Fix] P_batt_W 기반 실전류
        soh_state_w = rf_state_w
          ? updateSOHWithRainflow(soh_state_w, rf_state_w, batt_snap_w, I_batt_w, dt_sim, p)
          : updateSOH(soh_state_w, batt_snap_w, I_batt_w, dt_sim, p);
      }
    } // inner loop end

    // SOC 하한 보호
    soc = Math.max(p.wltp_soc_usable_min || 0.05, soc);

    // ── 5초마다 로그 ──
    if (step % 5 === 0) {
      // 순소비 에너지 = 구동 방전 - 회생 충전 + 회생구간 보조기기
      const E_net_J = E_drive_J - E_regen_J + E_aux_regen_J;
      const e_cum   = d_m > 50
        ? (E_net_J / 3_600_000) / (d_m / 1000) * 100 : 0;

      data.push({
        t:      t_s,
        phase,
        v_ref:  +(profile[step].v).toFixed(1),
        v_sim:  +(v * 3.6).toFixed(1),
        soc:    +(soc * 100).toFixed(2),
        kalman_soc: +(soc_kalman_w * 100).toFixed(2),                                   // [Parity-W2]
        T_batt:  +thState.T_batt.toFixed(1),
        T_motor: +thState.T_motor.toFixed(1),
        T_inv:   +thState.T_inv.toFixed(1),
        T_cool:  +thState.T_cool.toFixed(1),
        hotspot_T: thermal_3d_w ? +thermal_3d_w.T_max.toFixed(1) : +thState.T_batt.toFixed(1), // [Parity-W3]
        T_delta_3d: thermal_3d_w ? +thermal_3d_w.T_delta.toFixed(2) : 0,               // [Parity-W3]
        V_terminal: batt2rc_state_w ? +batt2rc_state_w.toFixed(2) : (p.V_dc || 400),   // [Parity-W1]
        mu_rls: +rls_state_w.mu_est.toFixed(3),                                         // [Parity-W4]
        d_cum:         +(d_m / 1000).toFixed(3),
        E_cum_kWh:     +(E_drive_J / 3_600_000).toFixed(4),
        E_net_cum_kWh: +(E_net_J   / 3_600_000).toFixed(4),
        E_regen_cum:   +(E_regen_J / 3_600_000).toFixed(4),
        kWh_100km_cum: +e_cum.toFixed(2),
        derate_batt:  Math.min(100, +(P_dis_max_T(thState.T_batt, p) / (p.P_dis_ref || 150000) * 100).toFixed(0)),
        derate_inv:   Math.min(100, +(I_max_T(thState.T_inv,   p) / (p.I_max_rms  || 400)    * 100).toFixed(0)),
        derate_motor: Math.min(100, +(T_max_motor_T(thState.T_motor, p) / (p.T_max_motor || 400) * 100).toFixed(0)),
        driver_mpc_active: !!(p.driver_mpc_enable && mpc_drv_state_w),                  // [Parity-W5]
        driver_pedal: p.driver_mpc_enable && mpc_drv_state_w
          ? +mpc_drv_state_w.pedal_prev.toFixed(3) : null,                               // [Parity-W5]
        T_gear:  +T_gear_w.toFixed(1),                                                   // [P1]
        rainflow_damage: rf_state_w ? +(rf_state_w.total_damage || 0).toFixed(5) : 0,   // [P4]
        rainflow_cycles: rf_state_w ? +(rf_state_w.cycle_count_rf || 0).toFixed(1) : 0, // [P4]
        steer_T_feedback:  steer_state_w._last ? +steer_state_w._last.T_feedback.toFixed(3)       : 0, // [P5]
        steer_SAT:         steer_state_w._last ? +steer_state_w._last.SAT.toFixed(3)              : 0, // [P5]
        steer_angle_wheel: steer_state_w._last ? +steer_state_w._last.steer_angle_wheel.toFixed(4): 0, // [P5]
        rainflow_cycles: rf_state_w ? +(rf_state_w.cycle_count_rf || 0).toFixed(1) : 0, // [P4]
      });
    }
  } // outer step loop end

  // ══ 최종 결과 계산 ══════════════════════════════════════════
  const d_km = d_m / 1000;

  // 총 에너지 [kWh]
  const E_drive_kWh    = E_drive_J    / 3_600_000;  // 구동 방전 (aux 포함)
  const E_regen_kWh    = E_regen_J    / 3_600_000;  // 회생 충전
  const E_aux_regen_kWh = E_aux_regen_J / 3_600_000; // 회생구간 aux

  // 순 소비 에너지 [kWh] = 구동방전 - 회생충전 + 회생구간aux
  const E_net_kWh = E_drive_kWh - E_regen_kWh + E_aux_regen_kWh;

  // WLTP 전비 [kWh/100km]
  const e_wltp = d_km > 0.5 ? +(E_net_kWh / d_km * 100).toFixed(2) : 0;

  // CO₂ 환산 [gCO₂/km]
  const co2_gkm = +(e_wltp * (p.wltp_ef_grid || GRID_EF_DEFAULT_SIM) / 100).toFixed(1); // [BUG-2-FIX][P-3-Fix]

  // 항속거리: 실사용 가능 SOC 범위 기준
  const soc_range = (p.wltp_soc_usable_max || 0.90) - (p.wltp_soc_usable_min || 0.10);
  const range_km  = e_wltp > 0
    ? +((p.battery_energy * soc_range) / e_wltp * 100).toFixed(0) : 0;

  // [P-10-Fix] 회생 KPI 명칭 분리
  // eta_regen_pct  : E_regen / E_drive × 100  — 구동에너지 대비 회생 비율 (사이클 단위)
  // eta_regen_total: E_regen / E_KE    × 100  — 운동에너지 대비 회수율 (simBraking 단위)
  // 두 값을 같은 레이블로 혼용하면 P-10 오류 발생.
  // WLTP/사이클 컨텍스트에서는 regen_ratio_pct 별칭을 UI 레이블에 사용할 것.
  const eta_regen_pct   = E_drive_kWh > 0
    ? +(E_regen_kWh / E_drive_kWh * 100).toFixed(1) : 0;
  const regen_ratio_pct = eta_regen_pct;   // [P-10-Fix] 명시적 별칭: E_regen/E_drive 기반

  // Phase별 요약
  const phaseSummary = Object.entries(phases).map(([name, ph]) => {
    const E_net_ph_kWh = (ph.E_drive_J - ph.E_regen_J) / 3_600_000;
    const d_ph_km      = ph.d_m / 1000;
    return {
      phase:    name,
      d_km:     +d_ph_km.toFixed(3),
      t_s:      +ph.t_s.toFixed(1),
      E_kWh:    +E_net_ph_kWh.toFixed(4),
      e_kWh100: d_ph_km > 0.05 ? +(E_net_ph_kWh / d_ph_km * 100).toFixed(2) : 0,
      avg_v:    ph.t_s > 0 ? +(ph.d_m / ph.t_s * 3.6).toFixed(1) : 0,
      eta_regen_pct: ph.E_drive_J > 0
        ? +(ph.E_regen_J / ph.E_drive_J * 100).toFixed(1) : 0,
    };
  });

  // 주행 모드 비율
  const t_total_s = t_stop + t_accel + t_cruise + t_decel || 1;
  const maneuverSplit = {
    stop:   +(t_stop   / t_total_s * 100).toFixed(1),
    accel:  +(t_accel  / t_total_s * 100).toFixed(1),
    cruise: +(t_cruise / t_total_s * 100).toFixed(1),
    decel:  +(t_decel  / t_total_s * 100).toFixed(1),
  };

  // 열 통계
  const thermalStats = {
    T_motor_max:          data.length ? Math.max(...data.map(d => d.T_motor)) : T_amb,
    T_batt_max:           data.length ? Math.max(...data.map(d => d.T_batt))  : T_amb,
    T_inv_max:            data.length ? Math.max(...data.map(d => d.T_inv))   : T_amb,
    derate_events_batt:   data.filter(d => d.derate_batt  < 100).length,
    derate_events_inv:    data.filter(d => d.derate_inv   < 100).length,
    derate_events_motor:  data.filter(d => d.derate_motor < 100).length,
  };

  // [A24-FIX-A] 3구간 평균 mid_chain 확정
  if (_mc_acc && _mc_cnt > 0) {
    mid_chain = {};
    Object.keys(_mc_acc).forEach(function(k) {
      mid_chain[k] = typeof _mc_acc[k] === 'number'
        ? +(_mc_acc[k] / _mc_cnt).toFixed(4)
        : _mc_acc[k];
    });
  }
  // fallback: 구동 구간이 전혀 없었던 경우 null 유지 (EnergyTab이 기본값으로 처리)

  return {
    // 시계열 데이터
    data,
    // 거리/에너지
    d_km: +d_km.toFixed(3),
    E_drive_kWh:  +E_drive_kWh.toFixed(4),
    E_regen_kWh:  +E_regen_kWh.toFixed(4),
    E_aux_kWh:    +((E_aux_drive_J + E_aux_regen_J) / 3_600_000).toFixed(4), // [A44-Fix-3] 단일점 추정 → 실적산
    E_net_kWh:    +E_net_kWh.toFixed(4),
    // 핵심 지표
    e_wltp, consumption_kWh_100km: e_wltp, // [v6-fix5] 별칭
    range_km, overall_efficiency: eta_regen_pct, // 별칭
    co2_gkm,
    eta_regen_pct,          // E_regen/E_drive [%] — 사이클 회생 비율 (구동에너지 기준)
    regen_ratio_pct,        // [P-10-Fix] eta_regen_pct 동일값, UI 레이블용 명시적 별칭
    soc_final:        +(soc * 100).toFixed(2),
    soc_kalman_final: +(soc_kalman_w * 100).toFixed(2),                                 // [Parity-W2]
    // [Parity-W1] 최종 배터리 단자 전압
    V_terminal_final: batt2rc_state_w ? +batt2rc_state_w.toFixed(2) : (p.V_dc || 400),
    // [Parity-W3] 3D 열해석 최종 핫스팟
    hotspot_T:   thermal_3d_w ? +thermal_3d_w.T_max.toFixed(1)   : +thState.T_batt.toFixed(1),
    T_delta_3d:  thermal_3d_w ? +thermal_3d_w.T_delta.toFixed(2) : 0,
    // [Parity-W4] RLS µ 최종 추정값
    mu_rls_final: +rls_state_w.mu_est.toFixed(3),
    // [P4] Rainflow SOH 최종값
    rainflow_damage: rf_state_w ? +(rf_state_w.total_damage || 0).toFixed(5) : 0,
    rainflow_cycles: rf_state_w ? +(rf_state_w.cycle_count_rf || 0).toFixed(1) : 0,
    soh_final_w: soh_state_w ? +(soh_state_w.soh * 100).toFixed(2) : null,
    // [V41] RDE 오염물질 배출 체인
    rdeEmissions: calcRDEEmissions(phaseSummary, p, p.cycle_type || 'WLTP'),
    // 상세 분석
    phaseSummary,
    maneuverSplit,
    thermalStats,
    thFinal: { ...thState },
    T_amb_used: T_amb,
    mid_chain,  // WLTP 중간 지점 에너지 체인 스냅샷 (ECV용)
  };
}

// WLTP Temperature sensitivity: run at multiple T_amb

// ── simWLTPTempSensitivity ──
/**
 * 주변 온도 [-10, 0, 10, 25, 40 °C]별 WLTP 사이클을 시뮬레이션하고 결과를 반환한다.
 *
 * @param {object} params  차량 파라미터
 * @returns {{ T_amb, e_wltp, range_km, co2_gkm, eta_regen_pct, T_batt_max, T_motor_max, E_aux_kWh }[]}
 */
function simWLTPTempSensitivity(params) {
  const temps = [-10, 0, 10, 25, 40];
  return temps.map(T => {
    const r = simDrivingCycle(params, T);
    if (!r) return { T_amb: T, e_wltp: 0, range_km: 0, co2_gkm: 0, eta_regen_pct: 0, T_batt_max: 0, T_motor_max: 0, E_aux_kWh: 0 };
    return {
      T_amb: T,
      e_wltp: r.e_wltp,
      range_km: r.range_km,
      co2_gkm: r.co2_gkm,
      eta_regen_pct: r.eta_regen_pct,
      T_batt_max: r.thermalStats.T_batt_max,
      T_motor_max: r.thermalStats.T_motor_max,
      E_aux_kWh: r.E_aux_kWh,
    };
  });
}

// ════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════
// [R-04 + R-14] Monte Carlo 개선판 — onProgress 완전 연동
// WebWorker 미지원 환경 대응: setTimeout chunk 방식 (메인 스레드 비블로킹)
// ════════════════════════════════════════════════════════════
/**
 * simMonteCarloAsync(params, options) → Promise<{results, stats, histogram}>
 *
 * [R-14 개선사항]
 *   1. onProgress(ratio, partialStats) — 진행율 + 중간 통계 동시 전달
 *   2. 경량 proxy 시뮬(lightweight=true) ↔ 전체 simWLTP(lightweight=false) 선택
 *   3. 사이클별 불확실도 파라미터 분리 (WLTP vs RDE vs JC08 vs CLTC)
 *   4. WebWorker 미지원 환경 자동 감지 → setTimeout chunk 폴백 보장
 *   5. onProgress 콜백에 { ratio, mean, sigma, done } 전달
 *
 * @param {Object} params     차량 파라미터
 * @param {Object} [options]
 *   @param {number}   options.nRuns       실행 횟수 (기본 300)
 *   @param {number}   options.chunkSize   청크 크기 (기본 25, 경량 50)
 *   @param {Function} options.onProgress  (progress: {ratio,mean,sigma,done}) → void
 *   @param {boolean}  options.lightweight proxy 계산 사용 여부 (기본 false)
 *   @param {string}   options.cycle_type  사이클 타입 (기본 params.cycle_type||'WLTP')
 */

// ── simThermalStress ──
function simThermalStress(params, cycles, v_target_kmh) {
  const dt = 0.01, dt_th = 0.05;
  const grade_rad = 0;
  const mu_eff = ROAD_MU[params.road_condition] || params.mu;
  let thState = { T_batt: params.T_amb, T_inv: params.T_amb, T_motor: params.T_amb, T_cool: params.T_amb };
  let soc = params.soc_init, t_global = 0;
  const data = [];
  const v_tgt = v_target_kmh / 3.6;

  for (let cyc = 0; cyc < cycles; cyc++) {
    let v = 0.1, t_cyc = 0, t_launch = 0;
    // Accelerate
    while (v < v_tgt && t_cyc < 15) {
      const omega_m = v / params.r_wheel * params.gear_ratio;
      const T_mot = motorTorqueSpeed(omega_m, 1.0, params, thState.T_motor, thState.T_inv, thState.T_batt);
      let F_drv = T_mot * params.gear_ratio * params.eta_driveline / params.r_wheel;
      F_drv = Math.min(F_drv, mu_eff * params.m * g);
      const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
      const a = (F_drv - res.F_total) / params.m;
      const Iq = T_mot / (1.5 * (params.poles / 2) * params.lambda_pm);
      const motor = computeMotorLosses(omega_m, T_mot, 0, Iq, params, thState.T_motor);
      const I_rms = Math.sqrt(Iq * Iq);
      const inv = computeInverterLosses(I_rms, thState.T_inv, params);
      const P_dc = T_mot * omega_m + motor.P_total + inv.P_total;
      const Ri = R_int_T(soc, thState.T_batt, params);
      const Q_batt = Math.pow(P_dc / params.V_dc, 2) * Ri;
      thState = thermalStep(thState, Q_batt, inv.P_total, motor.P_total, v, params, dt);
      soc = socUpdate(soc, P_dc + Q_batt, dt, params);
      v += a * dt; if (v < 0) v = 0; t_cyc += dt; t_global += dt;
    }
    t_launch = t_cyc; // capture acceleration time before coast
    // Coast back to ~20 km/h
    while (v > 20 / 3.6 && t_cyc < 40) {
      const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
      const a = -res.F_total / params.m;
      thState = thermalStep(thState, 0, 0, 0, v, params, dt);
      v += a * dt; if (v < 0) v = 0; t_cyc += dt; t_global += dt;
    }
    data.push({
      cycle: cyc + 1, t_global: +t_global.toFixed(1),
      T_batt: +thState.T_batt.toFixed(1), T_inv: +thState.T_inv.toFixed(1),
      T_motor: +thState.T_motor.toFixed(1), T_cool: +thState.T_cool.toFixed(1),
      soc: +(soc * 100).toFixed(1),
      launch_time: +t_launch.toFixed(3),
      derate_batt: +(P_dis_max_T(thState.T_batt, params) / params.P_dis_ref * 100).toFixed(0),
      derate_inv: +(I_max_T(thState.T_inv, params) / params.I_max_rms * 100).toFixed(0),
      derate_motor: +(T_max_motor_T(thState.T_motor, params) / params.T_max_motor * 100).toFixed(0),
    });
  }
  return data;
}

// ── _initDrivabilitySim ── [Fix-DRY] drivability 함수 공통 초기화 헬퍼
/**
 * Drivability 시뮬레이션 공통 초기 상태를 생성한다.
 * VehiclePhysicsState 초기화, SOH 프로파일 주입, dt/dt_th/grade_rad 반환.
 *
 * @param {object} params      차량 파라미터
 * @param {number} v_init_kmh  초기 속도 [km/h]
 * @returns {{ state, dt: 0.005, dt_th: 0.05, grade_rad: 0 }}
 */
// simDrivabilityTipIn / TipOut / Braking 의 공통 패턴 추출
// - VehiclePhysicsState 초기화
// - SOH 프로파일 주입
// - 표준 시뮬 상수(dt, dt_th, grade_rad) 반환
function _initDrivabilitySim(params, v_init_kmh) {
  const state = initVehiclePhysicsState(params, v_init_kmh / 3.6);
  const soh_profile = getSOHProfile(params);
  if (state.soh_state) state.soh_state.profile = soh_profile;
  return {
    state,
    dt:       0.005,
    dt_th:    0.05,
    grade_rad: 0,
  };
}

// ── simDrivabilityTipIn ──
/**
 * 팁인(스로틀 스텝 입력) 주행성 시뮬레이션을 실행한다 (5초).
 * t=0.5 s에 throttle_step 인가. 1차 토크 필터(torque_filter_tau) 적용 가능.
 * 저크 RMS·피크·등급(Excellent/Good/Fair/Poor) 산출.
 *
 * @param {object} params            차량 파라미터
 * @param {number} v_init_kmh        초기 속도 [km/h]
 * @param {number} throttle_step     스로틀 스텝 크기 [0~1]
 * @param {number} [torque_filter_tau=0]  1차 필터 시상수 [s]
 * @returns {{ data, jerk_analysis }}
 */
function simDrivabilityTipIn(params, v_init_kmh, throttle_step, torque_filter_tau) {
  const tau_filter = torque_filter_tau || 0;
  const { state: stateInit, dt, dt_th, grade_rad } = _initDrivabilitySim(params, v_init_kmh);
  const sim_time   = 5.0;

  let state = stateInit;

  const data = [];
  let T_mot_filtered = 0;  // 필터링된 토크 상태

  while (state.t < sim_time) {
    // 스텝 입력 (t=0.5s 이후 throttle 적용)
    const throttle_cmd = state.t >= 0.5 ? throttle_step : 0.0;

    // 토크 필터 적용 (1차 저역 통과)
    if (tau_filter > 0) {
      const omega_m = state.v / params.r_wheel * params.gear_ratio;
      const T_raw   = motorTorqueSpeed(omega_m, driveModeThrottle(throttle_cmd, params),
                                       params, state.thState.T_motor, state.thState.T_inv, state.thState.T_batt);
      const alpha_f = dt / (tau_filter + dt);
      T_mot_filtered = T_mot_filtered + alpha_f * (T_raw - T_mot_filtered);
    }

    const inputs = {
      throttle:      throttle_cmd,
      brake_demand:  0,
      grade_rad,
      // [Fix-DV1] mode_throttle 클램프 누락 수정:
      //   수정 전: T_mot_filtered / T_max_motor — 클램프 없음
      //             필터 초기 과도 응답(transient)에서 T_mot_filtered > T_max_motor 가능
      //             → mode_throttle > 1.0 → stepVehiclePhysics 내부 throttle 오버라이드 오동작
      //             → 실제 모터 출력이 최대값 이상으로 증폭되는 비물리적 가속 발생
      //   수정 후: Math.min(1, Math.max(0, ...)) 으로 [0,1] 강제 클램프
      mode_throttle: tau_filter > 0
        ? Math.min(1, Math.max(0, T_mot_filtered / Math.max(params.T_max_motor || 300, 1))) // [Fix-DV1]
        : undefined,
    };

    state = stepVehiclePhysics(state, inputs, params, dt, dt_th);

    if (Math.round(state.t / dt) % 10 === 0) {  // 50ms마다 로깅
      data.push({
        t:             +state.t.toFixed(3),
        v_kmh:         +(state.v * 3.6).toFixed(2),
        a:             +state.a.toFixed(3),
        throttle_eff:  +driveModeThrottle(throttle_cmd, params).toFixed(3),
        T_mot:         +(state._T_mot || 0).toFixed(1),
        T_mot_filtered: tau_filter > 0 ? +T_mot_filtered.toFixed(1) : +(state._T_mot || 0).toFixed(1),
        P_elec_kW:     +(state._P_elec / 1000).toFixed(2),
        soc:           +(state.soc * 100).toFixed(2),
        T_motor_temp:  +state.thState.T_motor.toFixed(1),
      });
    }
  }

  const jerk_analysis = computeDrivabilityJerk(data, 0.05);
  return { data, jerk_analysis };
}

/**
 * simDrivabilityTipOut(params, v_init_kmh, throttle_init, torque_filter_tau)
 * 팁아웃(급감속/코스팅 전환) 시나리오
 */

// ── simDrivabilityTipOut ──
function simDrivabilityTipOut(params, v_init_kmh, throttle_init, torque_filter_tau) {
  const tau_filter = torque_filter_tau || 0;
  const { state: stateInit, dt, dt_th, grade_rad } = _initDrivabilitySim(params, v_init_kmh);
  const sim_time   = 5.0;

  let state = stateInit;

  // [Fix-WARMUP] T_mot_filtered 초기값 수정:
  //   수정 전: T_mot_filtered = 0 — 필터 과도응답으로 워밍업(0~0.5s) 구간 가속도 비물리적
  //             (0→정상상태 토크로 점진 상승 → 실제 팁아웃 전 저크 오염)
  //   수정 후: 초기 속도·throttle 조건의 정상상태 모터 토크로 시드
  //             → 워밍업 구간이 실제 정속 주행 상태를 재현, 팁아웃 저크만 순수 측정
  let T_mot_filtered = 0;
  if (tau_filter > 0) {
    const omega_m_init = (v_init_kmh / 3.6) / Math.max(params.r_wheel || 0.3, 0.01)
                       * (params.gear_ratio || 9.5);
    const T_mode_init  = driveModeThrottle(throttle_init, params);
    // 초기 온도 25°C 근사 (initVehiclePhysicsState 기본값과 동일)
    T_mot_filtered = motorTorqueSpeed(
      omega_m_init, T_mode_init, params, 25, 25, params.V_dc || 400
    );
  }

  const data = [];

  while (state.t < sim_time) {
    // t < 0.5s: 초기 throttle, t >= 0.5s: 0으로 급감
    const throttle_cmd = state.t < 0.5 ? throttle_init : 0.0;

    if (tau_filter > 0) {
      const omega_m = state.v / params.r_wheel * params.gear_ratio;
      const T_raw   = motorTorqueSpeed(omega_m, driveModeThrottle(throttle_cmd, params),
                                       params, state.thState.T_motor, state.thState.T_inv, state.thState.T_batt);
      const alpha_f = dt / (tau_filter + dt);
      T_mot_filtered = T_mot_filtered + alpha_f * (T_raw - T_mot_filtered);
    }

    const inputs = {
      throttle:      throttle_cmd,
      brake_demand:  0,
      grade_rad,
      mode_throttle: tau_filter > 0
        ? Math.min(1, Math.max(0, T_mot_filtered / Math.max(params.T_max_motor || 300, 1))) // [Fix-DV1]
        : undefined,
    };

    state = stepVehiclePhysics(state, inputs, params, dt, dt_th);

    if (Math.round(state.t / dt) % 10 === 0) {
      data.push({
        t:             +state.t.toFixed(3),
        v_kmh:         +(state.v * 3.6).toFixed(2),
        a:             +state.a.toFixed(3),
        throttle_eff:  +driveModeThrottle(throttle_cmd, params).toFixed(3),
        T_mot:         +(state._T_mot || 0).toFixed(1),
        T_mot_filtered: tau_filter > 0 ? +T_mot_filtered.toFixed(1) : +(state._T_mot || 0).toFixed(1),
        is_regen:      state._is_regen || false,
        P_regen_kW:    state._regen_result ? +(state._regen_result.P_regen / 1000).toFixed(2) : 0,
        soc:           +(state.soc * 100).toFixed(2),
      });
    }
  }

  const jerk_analysis = computeDrivabilityJerk(data, 0.05);
  return { data, jerk_analysis };
}

/**
 * simDrivabilityBraking(params, v_init_kmh, brake_demand, torque_filter_tau)
 * 유압 브레이크 지연 + 회생제동 협조에 따른 저크 시나리오
 */

// ── simDrivabilityBraking ──
function simDrivabilityBraking(params, v_init_kmh, brake_demand, torque_filter_tau) {
  // [Fix-DRY-Braking] 공통 초기화는 _initDrivabilitySim, 브레이킹 전용 dt는 개별 오버라이드
  const { state: stateInit, grade_rad } = _initDrivabilitySim(params, v_init_kmh);
  const dt        = 0.02;   // 브레이킹 시뮬 전용 타임스텝 (TipIn/Out 0.005s와 상이)
  const dt_th     = 0.2;
  const tau_filter = torque_filter_tau || 0;

  let state      = stateInit;
  let brakeState = initBrakeSystemState(params);
  const data     = [];

  while (state.v > 0.3 && state.t < 30) {
    // 제동 요청 (t=0.5s 이후 제동)
    const brake_cmd   = state.t >= 0.5 ? brake_demand : 0;
    // 회생제동 분리 (regen 가능 영역)
    const regen_ratio = params.regen_max_ratio || 0.6;
    const p_regen_cmd = Math.min(brake_cmd * regen_ratio, state.soc < 0.95 ? brake_cmd * regen_ratio : 0);

    // 유압 브레이크 지연 모델 적용
    const brakeOut  = brakeSystemDynamics(brakeState, brake_cmd, p_regen_cmd, params, dt);
    brakeState      = brakeOut.state;

    const inputs = {
      throttle:      0,
      // [Fix-DV2] 제동력 정규화 오류 수정:
      //   수정 전: brakeOut.F_brake_total / (params.m * 9.81 * (params.mu_brake || 0.9))
      //             params.mu_brake = 내부 브레이크 마찰계수(캘리퍼-패드 간) ≠ 노면마찰계수
      //             params.mu_brake는 보통 0.3~0.45 (주철 디스크+유기 패드)
      //             → params.mu_brake || 0.9 fallback이 0.9(노면 µ 수준)로 물리 혼용
      //             → wet(params.mu=0.6)에서도 0.9 고정 사용 → 정규화값 과소 → 실제 제동보다 약한 입력
      //   수정 후: 정규화 기준 = params.m * g (순수 무게 기반 → brake_demand = a/g [0~µ])
      //             이 방식이 stepVehiclePhysics 내부 brake_demand 해석과 일관성 있음
      //             최대 제동 = params.mu * g → 최대 brake_demand ≈ params.mu (≤ 1) 이 되어 정상 범위
      brake_demand:  Math.min(1, brakeOut.F_brake_total / Math.max(params.m * 9.81, 1)), // [Fix-DV2]
      grade_rad,
    };

    state = stepVehiclePhysics(state, inputs, params, dt, dt_th);

    if (Math.round(state.t / dt) % 5 === 0) {
      data.push({
        t:             +state.t.toFixed(3),
        v_kmh:         +(state.v * 3.6).toFixed(2),
        a:             +state.a.toFixed(3),
        F_hyd:         +brakeOut.F_hyd.toFixed(1),
        F_regen:       +brakeOut.F_regen.toFixed(1),
        p_hyd_actual:  +brakeOut.p_hyd_actual.toFixed(3),
        p_regen_actual: +brakeOut.p_regen_actual.toFixed(3),
        jerk_brake:    +brakeOut.jerk_brake.toFixed(3),
        blend_ratio:   +brakeOut.blend_ratio.toFixed(3),
        soc:           +(state.soc * 100).toFixed(2),
      });
    }
  }

  // [Fix-BRK-DT] 제동 저크 dt 불일치 수정:
  //   수정 전: computeDrivabilityJerk(data, dt) — dt=0.02s (내부 스텝 간격)
  //             로그 조건 % 5 === 0 → 실제 샘플 간격 = 0.02 × 5 = 0.10s
  //             → jerk = Δa / 0.02 = 실제값의 5배 과대 산출
  //   수정 후: dt_log = dt * LOG_STEP = 0.10s → jerk 계산 정확
  const LOG_STEP = 5;
  const dt_log   = dt * LOG_STEP;  // 0.10s — 실제 샘플 간격
  const jerk_analysis = computeDrivabilityJerk(data, dt_log);
  return { data, jerk_analysis, brakeState };
}

/**
 * runDrivabilityAnalysis(params, options)
 * 전체 승차감 분석 통합 실행
 * @param {Object} params   차량 파라미터
 * @param {Object} options  { v_init_kmh, throttle_step, tau_filter_values, brake_demand }
 */

// ── simEmergencyBraking ──
function simEmergencyBraking(params, v_init_kmh, grade_deg) {
  // 1) ABS ON 긴급 제동
  const withABS = simBraking({ ...params, abs_enable: true }, v_init_kmh, 1.0, grade_deg);
  // 2) ABS OFF 긴급 제동 (잠금)
  const withoutABS = simBraking({ ...params, abs_enable: false }, v_init_kmh, 1.0, grade_deg);
  // 3) 페이드 후 긴급 제동 (디스크 온도 300°C에서 시작)
  const afterFade = simBraking({ ...params, abs_enable: true, T_brake_init: 350 }, v_init_kmh, 1.0, grade_deg);
  // 4) 반복 제동 시나리오 (5회 연속)
  const repeatBrake = [];
  let T_disc_carry = params.T_brake_init || 25;
  for (let rep = 1; rep <= 5; rep++) {
    const r = simBraking({ ...params, T_brake_init: T_disc_carry }, v_init_kmh, 1.0, grade_deg);
    T_disc_carry = r.T_disc_final;
    repeatBrake.push({
      rep, distance: r.distance, time: r.time,
      T_disc_final: r.T_disc_final,
      fade_factor: r.fade_factor_final,
      eta_regen: r.eta_regen_total,
    });
  }
  return { withABS, withoutABS, afterFade, repeatBrake };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// §BODE  PI 제어기 보드 선도 계산 (Bode Plot for PI loops)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
/**
 * Compute Bode data for a PI current controller (d or q axis)
 *
 * Plant (continuous):  G_plant(s) = 1/(L·s + R)
 * Controller (PI):     C(s) = Kp + Ki/s
 * Computation+PWM delay: e^(-s·1.5·Ts)  [1.5-샘플 지연 모델]
 * Open-loop:          L(s) = C(s)·G_plant(s)·e^(-s·1.5·Ts)
 * Closed-loop (CL):   T(s) = L/(1+L)
 *
 * ─── BUG FIX (v3.2) ───────────────────────────────────────
 * 이전 버전: f_max = 20 kHz → wc = Kp/L ≈ 26.5 kHz 가 범위 밖
 *            → 0 dB 교차점 미감지 → PM/BW = N/A
 * 수정:      f_max = 5×wc_est/2π 로 동적 결정 (최소 50 kHz)
 *            + 300 포인트로 해상도 향상
 */

// ── simSkidPad ──
function simSkidPad(params, R_pad_m) {
  const m   = params.m || 1850;
  const R   = R_pad_m || 30;  // 기본 30m 반경
  const L   = params.wheelbase || 2.87;
  const lf  = params.a_cg || 1.43;
  const lr  = L - lf;
  const Caf = params.cornering_stiffness_f || 80000;
  const Car = params.cornering_stiffness_r || 85000;
  const mu  = ROAD_MU[params.road_condition] || params.mu || 1.0;

  // 속도 스위프
  const results = [];
  for (let v_kmh = 10; v_kmh <= 200; v_kmh += 2) {
    const v = v_kmh / 3.6;
    const ay_g = v * v / (R * g);         // 횡가속도 (g)
    const Fy_req = m * v * v / R;          // 총 횡력 요구 [N]
    const Fy_f_req = Fy_req * lr / L;     // 전륜 횡력
    const Fy_r_req = Fy_req * lf / L;     // 후륜 횡력
    // Fz 분배 (정적)
    const Fz_f = m * g * lr / L;
    const Fz_r = m * g * lf / L;
    // Pacejka 최대 횡력 (B·C·D·E 모델)
    const B = params.pacejka_B || 10;
    const C = params.pacejka_C || 1.9;
    const D = params.pacejka_D || 1.0;
    const Fy_max_f = D * Fz_f * mu * Math.sin(C * Math.atan(B * 0.2));
    const Fy_max_r = D * Fz_r * mu * Math.sin(C * Math.atan(B * 0.2));
    // 슬립각 추정 (소각도 근사 선형 → 비선형 포화)
    const alpha_f_lin = Fy_f_req / Caf;
    const alpha_r_lin = Fy_r_req / Car;
    // 언더스티어 구배 Ku
    const Ku = m / L * (lf / Car - lr / Caf);
    const delta_ack = L / R;  // 아커만 조향각 [rad]
    const delta_us  = delta_ack + Ku * ay_g;  // 실제 조향각 (US 포함)
    const us_deg    = (delta_us - delta_ack) * 180 / Math.PI;
    // 포화 여부
    const f_saturated = Fy_f_req > Fy_max_f;
    const r_saturated = Fy_r_req > Fy_max_r;
    const limit_g     = Math.min(Fy_max_f, Fy_max_r) / m / g;  // 극한 횡G 근사
    results.push({
      v_kmh: +v_kmh.toFixed(0),
      ay_g:  +ay_g.toFixed(3),
      Fy_req: +Fy_req.toFixed(0),
      Fy_f:  +Fy_f_req.toFixed(0),
      Fy_r:  +Fy_r_req.toFixed(0),
      alpha_f_deg: +(alpha_f_lin * 180 / Math.PI).toFixed(2),
      alpha_r_deg: +(alpha_r_lin * 180 / Math.PI).toFixed(2),
      delta_us_deg: +us_deg.toFixed(3),
      yaw_rate_dps: +(v / R * 180 / Math.PI).toFixed(2),
      f_saturated, r_saturated,
      limit_g: +limit_g.toFixed(3),
      feasible: !f_saturated && !r_saturated,
    });
  }
  // 최대 가능 속도 (타이어 한계 초과 전 마지막)
  const maxFeasible = results.filter(r => r.feasible);
  const v_max_mps   = maxFeasible.length ? maxFeasible[maxFeasible.length - 1].v_kmh : 0;
  const ay_max_g    = maxFeasible.length ? maxFeasible[maxFeasible.length - 1].ay_g : 0;
  const Ku_val      = m / L * (lf / Car - lr / Caf);
  const handling    = Math.abs(Ku_val) < 0.001 ? "Neutral Steer" :
                      Ku_val > 0 ? "Understeer" : "Oversteer";

  return { results, v_max_kmh: +v_max_mps.toFixed(0), ay_max_g: +ay_max_g.toFixed(3),
           Ku: +Ku_val.toFixed(4), handling, R_pad_m: R };
}

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-04] 제동 에너지 회수 최적화
// ══════════════════════════════════════════════════════════════════════════════
/**
 * regenEnergyOptimizer(omega_m, v, soc, T_batt, T_motor, params, brake_demand)
 * 동적 최적 회생 토크 산출
 *   목적함수: max E_regen = η_regen·T_regen·ω / (w_E·E_friction + w_J·ΔT²)
 *   제약: T_regen ≤ T_max_regen, SOC ≤ soc_max, T_batt ≤ T_limit
 */

// ── simFullSpecCycle ──
function simFullSpecCycle(rows, initConds, params) {
  if (!rows || rows.length < 2) return null;

  const p = { ...params };
  // Apply initial conditions
  let soc = (initConds.soc_pct != null ? initConds.soc_pct / 100 : (p.soc_init || 0.9));
  let T_batt = initConds.batt_temp_C != null ? initConds.batt_temp_C : (p.T_amb || 25);
  let v = (initConds.speed_kmh != null ? initConds.speed_kmh : 0) / 3.6;

  // ★ [SST 경고] ROAD_MU_MAP — zero-import 제약으로 인한 인라인 복사본
  // [Fix-ROAD_MU_MAP] 중복 선언 제거 → 파일 상단 ROAD_MU 단일 참조 사용
  const m_eq = effectiveMass(p);
  const dt = rows.length > 1 ? (rows[1].t - rows[0].t) : 0.01;

  const data = [];
  let E_batt_Wh = 0, E_regen_Wh = 0, dist_m = 0;
  let kf_state     = initKalmanByType(soc, p);
  let soc_kalman   = soc;
  // [Parity-FSC1] 2RC 배터리 모델
  let battery_2rc_fsc   = p.battery_model === '2RC' ? init2RCBatteryState(p) : null;
  let batt2rc_st_fsc    = p.V_dc || 400;
  // [Parity-FSC2] 4-노드 열관리 (thermalStep 교체)
  let thState_fsc       = { T_batt, T_inv: T_batt, T_motor: T_batt, T_cool: T_batt };
  let cool_state_fsc    = initCoolantFlowState(p);
  let Q_b_acc_fsc = 0, Q_i_acc_fsc = 0, Q_m_acc_fsc = 0, th_acc_fsc = 0;
  // [Parity-FSC3] 3D 열 분포
  let thermal_3d_fsc    = p.thermal_3d_enable ? init3DThermalState(p) : null;
  // [Parity-FSC4] RLS µ 추정
  let rls_state_fsc     = initRLSMuState(p);
  let a_prev_fsc        = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const T_amb = row.T_amb;
    const grade_rad = row.grade_deg * Math.PI / 180;
    const mu_eff = Math.min(row.road_mu, ROAD_MU[p.road_condition] || p.mu || 1.0);

    // Drive mode from cycle
    const modeParams = { ...p, drive_mode: row.drive_mode };
    const aps = row.aps / 100;
    const bps = row.bps / 100;

    // Resistance
    const Crr_eff = CrrWithPressure(v, T_batt, p);
    const rho_eff = p.rho * Math.exp(-p.altitude / 8500) || 1.2;
    const F_air   = 0.5 * rho_eff * p.CdA * v * v;
    const F_roll  = m_eq * 9.81 * Crr_eff * Math.cos(grade_rad);
    const F_grade = m_eq * 9.81 * Math.sin(grade_rad);

    // [Bug2-Fix] SteerAngle 횡력 → 종방향 스크럽 저항 및 마찰원 한계 감소
    // Ackermann 모델: 요레이트 ω = v·tan(δ)/L, 횡가속도 a_y = v²·tan(δ)/L
    const steer_rad   = (row.steer_deg || 0) * Math.PI / 180;
    const L_wb        = (p.l_f || 1.4) + (p.l_r || 1.475);  // 휠베이스 (m)
    const a_y         = v > 0.5 ? v * v * Math.tan(steer_rad) / L_wb : 0;  // 횡가속도 (m/s²)
    // 코너링 스크럽 드래그: 타이어 횡력이 종방향 저항을 증가시킴 (경험계수 0.05 * Crr)
    const F_scrub     = m_eq * Math.abs(a_y) * (Crr_eff * 0.5);
    // 마찰원(Friction Ellipse): 횡방향 마찰 소모분만큼 종방향 한계 감소
    const mu_lat_frac = Math.min(1.0, Math.abs(a_y) / Math.max(mu_eff * 9.81, 0.1));
    const mu_lon_avail = mu_eff * Math.sqrt(Math.max(0, 1 - mu_lat_frac * mu_lat_frac));

    const F_res   = F_air + F_roll + F_grade + F_scrub;

    // Drive demand
    let F_drv = 0, F_brk = 0;
    if (bps > 0.01) {
      // [v8-fix6] 브레이크 페달 입력: 회생 + 마찰 블렌딩 (원페달 여부 무관)
      const v_kmh = v * 3.6;
      const regen_demand = regenMapDemand(v, bps, p);
      const F_regen_max = Math.min(p.P_max_regen / Math.max(v, 0.5), mu_lon_avail * m_eq * 9.81) * (p.eta_regen_motor || 0.92);
      F_brk = -(regen_demand * F_regen_max + (1 - regen_demand) * bps * m_eq * 9.81 * 0.8);
    } else if (aps > 0.01) {
      // [v8-fix6] 가속 페달 입력: 구동력 계산 (마찰원 한계 적용)
      const throttle = driveModeThrottle(aps, modeParams);
      const omega_m = v * (p.gear_ratio || 9.5) / (p.r_wheel || 0.33);
      const T_motor_max_spd = Math.min(p.T_max_motor, (p.P_max_wheel || 220000) / Math.max(omega_m, 1));
      const T_motor = throttle * T_motor_max_spd;
      const T_wheel = T_motor * (p.gear_ratio || 9.5) * (p.eta_driveline || 0.95);
      F_drv = Math.min(T_wheel / (p.r_wheel || 0.33), mu_lon_avail * m_eq * 9.81);
    } else if (p.one_pedal_enable) {
      // [v8-fix6] 원페달 코스트: 페달 OFF → 자동 회생 (이전 코드에서 도달 불가능했던 분기)
      const regen = regenMapDemand(v, 0, p);
      const F_regen_max = Math.min(p.P_max_regen / Math.max(v, 0.5), mu_eff * m_eq * 9.81) * (p.eta_regen_motor || 0.92);
      F_brk = -regen * F_regen_max;
    }

    const F_net = F_drv + F_brk - F_res;
    const a = F_net / m_eq;
    v = Math.max(0, v + a * dt);
    dist_m += v * dt;

    // Power
    const P_mech = (F_drv + F_brk) * v;
    const P_elec = P_mech > 0 ? P_mech / (p.eta_motor_inv || 0.91) : P_mech * (p.eta_regen_motor || 0.92);
    // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
    const _hvac6 = hvacDetailedPower(T_amb, v, soc, p);
    p._hvac_prev_mode = _hvac6.new_hvac_mode;
    const P_aux  = _hvac6.P;
    const P_batt = P_elec + P_aux;

    // [Parity-FSC1] 2RC 배터리 모델 스텝
    const I_batt = P_batt / Math.max(batt2rc_st_fsc, 200);
    let batt2rc_result_fsc = null;
    if (battery_2rc_fsc) {
      batt2rc_result_fsc = battery2RCModelWithSEI(battery_2rc_fsc, I_batt, dt, p, null);
      if (batt2rc_result_fsc?.V_terminal)
        batt2rc_st_fsc = batt2rc_st_fsc * 0.9 + batt2rc_result_fsc.V_terminal * 0.1;
    }

    // SOC — socUpdate 연결 (기존 직접 dSOC 대체)
    soc = socUpdate(soc, Math.abs(P_batt) * Math.sign(P_batt > 0 ? 1 : -1), dt, p);
    soc = Math.max(0, Math.min(1, soc));

    // [Parity-Kalman] EKF/UKF SOC 추정
    if (kf_state && p.kalman_enable !== false) {
      const V_m = batt2rc_result_fsc?.V_terminal ?? batteryOCV_smart(soc, p, thState_fsc.T_batt);
      const I_kf = P_batt / Math.max(1, p.V_dc || 400);
      kf_state   = kalmanSOCUpdate(kf_state, V_m, I_kf, soc, batteryOCV_smart(soc, p, thState_fsc.T_batt), p);
      soc_kalman = kf_state.soc_est !== undefined ? kf_state.soc_est : soc;
      if (Math.abs(soc_kalman - soc) < 0.15) soc = soc_kalman;
    }

    E_batt_Wh += Math.abs(P_batt) * dt / 3600;
    if (P_batt < 0) E_regen_Wh += Math.abs(P_batt) * dt / 3600;

    // [Parity-FSC2] 열 발생량 누산 → 0.5s마다 thermalStep
    const R_int_fsc   = R_int_T(soc, thState_fsc.T_batt, p);
    const Q_batt_fsc  = I_batt * I_batt * R_int_fsc;
    const omega_m_fsc = v * (p.gear_ratio || 9.5) / (p.r_wheel || 0.33);
    const Q_motor_fsc = (p.k_m0 || 2) + (p.k_m1 || 0.005) * omega_m_fsc;
    Q_b_acc_fsc += Q_batt_fsc * dt;
    Q_m_acc_fsc += Q_motor_fsc * dt;
    th_acc_fsc  += dt;
    if (th_acc_fsc >= 0.5) {
      cool_state_fsc = coolantFlowControl(cool_state_fsc, thState_fsc.T_cool, p, th_acc_fsc);
      thState_fsc = thermalStep(thState_fsc,
        Q_b_acc_fsc / th_acc_fsc, Q_i_acc_fsc / th_acc_fsc, Q_m_acc_fsc / th_acc_fsc,
        v, p, th_acc_fsc, cool_state_fsc.phi_flow);
      // [Parity-FSC3] 3D 열 분포
      if (p.thermal_3d_enable && thermal_3d_fsc) {
        const Q_tot_fsc = Q_b_acc_fsc + Q_i_acc_fsc + Q_m_acc_fsc;
        thermal_3d_fsc = update3DThermalStep(thermal_3d_fsc, Q_tot_fsc,
          cool_state_fsc.phi_flow, thState_fsc.T_cool, th_acc_fsc, p);
      }
      Q_b_acc_fsc = Q_i_acc_fsc = Q_m_acc_fsc = th_acc_fsc = 0;
    }
    // T_batt 동기화 (기존 코드와 호환성 유지)
    T_batt = thState_fsc.T_batt;

    // [Parity-FSC4] RLS µ 추정
    const F_normal_fsc = m_eq * 9.81 * Math.cos((row.grade_deg || 0) * Math.PI / 180);
    if (v > 1) {
      rls_state_fsc = rlsMuUpdate(rls_state_fsc, F_drv + F_brk, F_normal_fsc,
        a_prev_fsc, m_eq, p);
    }
    a_prev_fsc = a;

    if (i % 10 === 0) { // store every 100ms
      data.push({
        t: +row.t.toFixed(2),
        v_kmh: +(v * 3.6).toFixed(2),
        aps: row.aps, bps: row.bps,
        a_ms2: +a.toFixed(3),
        soc_pct: +(soc * 100).toFixed(2),
        P_kW: +(P_batt / 1000).toFixed(2),
        P_mech_kW: +(P_mech / 1000).toFixed(2),
        P_aux_kW: +(P_aux / 1000).toFixed(3),
        // [v8-fix8] 저항 분해·구동력·효율 추가
        F_drv: +F_drv.toFixed(1),
        F_brk: +F_brk.toFixed(1),
        F_air: +F_air.toFixed(1),
        F_roll: +F_roll.toFixed(1),
        F_grade: +F_grade.toFixed(1),
        F_res: +F_res.toFixed(1),
        grade: row.grade_deg,
        T_amb: row.T_amb,
        T_batt: +T_batt.toFixed(2),   // [FixB→Parity-FSC2] 4-노드 thermalStep 연동
        T_motor: +thState_fsc.T_motor.toFixed(1),
        T_inv:   +thState_fsc.T_inv.toFixed(1),
        T_cool:  +thState_fsc.T_cool.toFixed(1),
        hotspot_T:  thermal_3d_fsc ? +thermal_3d_fsc.T_max.toFixed(1) : +T_batt.toFixed(1), // [Parity-FSC3]
        T_delta_3d: thermal_3d_fsc ? +thermal_3d_fsc.T_delta.toFixed(2) : 0,
        V_terminal: +batt2rc_st_fsc.toFixed(2),                                               // [Parity-FSC1]
        kalman_soc: +(soc_kalman * 100).toFixed(2),                                           // [Kalman]
        mu_est:     +rls_state_fsc.mu_est.toFixed(4),                                         // [Parity-FSC4]
        road_mu: row.road_mu,
        dist_m: +dist_m.toFixed(1),
        drive_mode: row.drive_mode || p.drive_mode || "NORMAL",
        // [Bug2-Fix] 조향·횡방향 물리량 출력
        steer_deg: +(row.steer_deg || 0).toFixed(2),
        a_y:       +a_y.toFixed(3),
        F_scrub:   +F_scrub.toFixed(1),
        mu_lon:    +mu_lon_avail.toFixed(4),
      });
    }
  }

  const dist_km = dist_m / 1000;
  return {
    data,
    summary: {
      dist_km: +dist_km.toFixed(3),
      t_total_s: rows[rows.length-1].t,
      E_batt_kWh: +(E_batt_Wh / 1000).toFixed(4),
      E_regen_kWh: +(E_regen_Wh / 1000).toFixed(4),
      regen_pct: dist_km > 0 ? +(E_regen_Wh / E_batt_Wh * 100).toFixed(1) : 0,
      consumption_kWh100: dist_km > 0 ? +((E_batt_Wh / 1000) / dist_km * 100).toFixed(2) : 0,
      soc_end_pct: +(soc * 100).toFixed(1),
      soc_kalman_final: +(soc_kalman * 100).toFixed(2),                                   // [Kalman]
      soc_delta_pct: +((soc - (initConds.soc_pct != null ? initConds.soc_pct/100 : 0.9)) * 100).toFixed(1),
      v_max_kmh: +Math.max(...data.map(d => d.v_kmh)).toFixed(1),
      v_avg_kmh: +(data.reduce((s, d) => s + d.v_kmh, 0) / data.length).toFixed(1),
      T_batt_max:  data.length ? +Math.max(...data.map(d => d.T_batt)).toFixed(1)   : T_batt, // [Parity-FSC2]
      T_motor_max: data.length ? +Math.max(...data.map(d => d.T_motor ?? 0)).toFixed(1) : 0,
      hotspot_T_max: thermal_3d_fsc ? +thermal_3d_fsc.T_max.toFixed(1) : +T_batt.toFixed(1), // [Parity-FSC3]
      V_terminal_final: +batt2rc_st_fsc.toFixed(2),                                        // [Parity-FSC1]
    }
  };
}

// ============================================================
// [A36-PERF] runCoreSim / runHeavySim — 2단계 우선순위 분리
//   Phase-1 Core  (~300ms): 도로부하·발진·재가속·코스팅·제동·정상효율·에너지소비 → 즉시 표시
//   Phase-2 Heavy (~700ms): WLTP·온도민감도·열스트레스·민감도분석 → 백그라운드 완성
// ============================================================

// ── runDrivabilityAnalysis ──
/**
 * 팁인·팁아웃·제동 주행성 분석을 일괄 실행한다.
 * 팁인: tau_filter_values 배열(기본 [0, 0.05, 0.1, 0.2]s) 각각 비교.
 * 팁아웃: tau_tipout [Fix-TAU-FIX] 옵션 반영. 제동: simDrivabilityBraking.
 *
 * @param {object} params    차량 파라미터
 * @param {object} [options]  { v_init_kmh, throttle_step, tau_filter_values, brake_demand, tau_tipout }
 * @returns {{ tip_in, tip_out, braking, best_tau, summary }}
 */
function runDrivabilityAnalysis(params, options = {}) {
  const {
    v_init_kmh        = 60,
    throttle_step     = 0.8,
    tau_filter_values = [0, 0.05, 0.1, 0.2],  // 비교할 필터 시상수들
    brake_demand      = 0.6,
    // [Fix-TAU-FIX] 팁아웃 τ 하드코딩 수정:
    //   수정 전: simDrivabilityTipOut(..., 0.1) — UI 슬라이더 값 무시
    //   수정 후: tau_tipout 옵션으로 외부 주입 → DrivabilityTabContent에서 filterTau 전달
    tau_tipout        = 0.1,
  } = options;

  // 팁인: 여러 필터 시상수 비교
  const tip_in_results = tau_filter_values.map(tau => ({
    tau,
    label: tau === 0 ? 'No Filter' : `τ=${tau}s`,
    ...simDrivabilityTipIn(params, v_init_kmh, throttle_step, tau),
  }));

  // 팁아웃 — [Fix-TAU-FIX] tau_tipout 옵션 반영
  const tip_out_result = simDrivabilityTipOut(params, v_init_kmh, throttle_step, tau_tipout);

  // 제동 협조 저크
  const brake_result = simDrivabilityBraking(params, v_init_kmh, brake_demand, 0);

  // 종합 저크 통계
  const best_tau = tip_in_results.reduce((best, cur) =>
    cur.jerk_analysis.jerk_rms < best.jerk_analysis.jerk_rms ? cur : best
  );

  return {
    tip_in:    tip_in_results,
    tip_out:   tip_out_result,
    braking:   brake_result,
    best_tau:  best_tau.tau,
    summary: {
      tip_in_jerk_rms_no_filter:  tip_in_results[0]?.jerk_analysis?.jerk_rms || 0,
      tip_in_jerk_rms_best:       best_tau.jerk_analysis?.jerk_rms || 0,
      tip_out_jerk_rms:           tip_out_result.jerk_analysis?.jerk_rms || 0,
      brake_jerk_rms:             brake_result.jerk_analysis?.jerk_rms || 0,
      overall_grade:              best_tau.jerk_analysis?.jerk_grade || 'N/A',
    },
  };
}

// ── proxyRun ──
/**
 * 사이클 대표 속도 기반 정상 상태 근사로 에너지 소비·항속 거리를 빠르게 추정한다.
 * Monte Carlo/LHS 샘플별 정밀 시뮬레이션 대신 사용되는 경량 프록시.
 * [ENV-FIX-03] p.rho 적용(T_amb·altitude 반영).
 *
 * @param {object} p           차량 파라미터
 * @param {string} cycle_type  사이클 유형
 * @returns {{ e_wltp: number, range_km: number }}
 */
function proxyRun(p, cycle_type) {
  const v_avg_ms = (
    cycle_type === 'JC08'        ? 24.4 :
    cycle_type === 'CLTC'        ? 28.96 :
    cycle_type.startsWith('RDE_URBAN')    ? 20 :
    cycle_type.startsWith('RDE_RURAL')    ? 67 :
    cycle_type.startsWith('RDE_MOTORWAY') ? 100 :
    cycle_type.startsWith('RDE')          ? 42 :
    cycle_type === 'EPA_HWY'     ? 77.7 :
    cycle_type === 'EPA_CITY'    ? 34.1 :
    46.5  // WLTP
  ) / 3.6;
  // [ENV-FIX-03] rho=1.225 하드코딩 → p.rho 사용 (T_amb·altitude 반영)
  const F_aero = 0.5 * (p.rho || 1.225) * p.CdA * v_avg_ms * v_avg_ms;
  const F_roll = p.Crr * p.m * 9.81;
  const P_wheel = (F_aero + F_roll) * v_avg_ms;
  const eta = (p.eta_driveline || 0.95) * (p.eta_motor_inv || 0.91) * 0.97;
  const P_elec = P_wheel / eta + (p.aux_power || 1500);
  const kWh_100km = P_elec / 1000 / (v_avg_ms * 3.6) * 100;
  const _soc_rng2 = (p.wltp_soc_usable_max || 0.90) - (p.wltp_soc_usable_min || 0.10); // [B-2 Fix]
  const range_km  = p.battery_energy > 0 ? p.battery_energy * _soc_rng2 / kWh_100km * 100 : 0;
  return { e_wltp: +kWh_100km.toFixed(2), range_km: +range_km.toFixed(0) };
}

// ── runEnhancedMonteCarlo ──
/**
 * 향상된 Monte Carlo 시뮬레이션을 실행한다 (LHS 또는 단순 MC).
 * 파라미터: m ±8%, CdA ±10%, Crr ±15%, eta_driveline ±2%, T_amb ±10°C.
 * proxyRun 경량 추정으로 n_runs회 샘플링 → 통계(mean, std, histogram) 산출.
 *
 * @param {object} params   차량 파라미터 (monte_carlo_runs, sensitivity_method 포함)
 * @param {number} [n_runs] 샘플 수 (기본: params.monte_carlo_runs || 2000)
 * @returns {{ results, stats, histogram }}
 */
function runEnhancedMonteCarlo(params, n_runs) {
  const n = n_runs || params.monte_carlo_runs || 2000;
  const method = params.sensitivity_method || 'lhs';
  // Parameter ranges (±3σ for normal → use uniform ±10%)
  const param_ranges = {
    m:           [params.m * 0.92,            params.m * 1.08],
    CdA:         [params.CdA * 0.90,          params.CdA * 1.10],
    Crr:         [params.Crr * 0.85,          params.Crr * 1.15],
    eta_driveline:[params.eta_driveline*0.97, params.eta_driveline*1.01],
    T_amb:       [(params.T_amb || 25) - 10,  (params.T_amb || 25) + 10],
  };
  let samples;
  if (method === 'lhs') {
    samples = latinHypercubeSample(n, param_ranges);
  } else {
    // Simple Monte Carlo
    samples = Array.from({ length: n }, () => {
      const s = {};
      for (const [k,[lo,hi]] of Object.entries(param_ranges)) {
        s[k] = lo + Math.random() * (hi - lo);
      }
      return s;
    });
  }
  // Run quick energy calc for each sample (lightweight proxy)
  const results = samples.map(s => {
    const p = { ...params, ...s };
    // Proxy: WLTP consumption estimate (simplified road load)
    const v_avg = 46.5 / 3.6; // WLTP avg speed m/s
    // [ENV-FIX-03] p.rho 사용 (T_amb/altitude 반영된 값)
    const F_road = 0.5 * (p.rho || 1.225) * p.CdA * v_avg * v_avg + p.Crr * p.m * 9.81;
    const P_wheel = F_road * v_avg;
    const P_elec = P_wheel / (p.eta_driveline * 0.95 * 0.97); // motor+inv
    const kWh_100km = P_elec / 1000 / (v_avg * 3.6) * 100;
    // 0-100 proxy
    const P_max = p.P_max_wheel || 250000;
    const t100 = p.m * 100 / 3.6 / (P_max / (100 / 3.6)) * 0.85;
    return { kWh_100km: +kWh_100km.toFixed(2), t100: +t100.toFixed(2), params_used: s };
  });

  const kWh_vals = results.map(r => r.kWh_100km);
  const mean_kWh = kWh_vals.reduce((s,v)=>s+v,0)/n;
  const std_kWh  = Math.sqrt(kWh_vals.reduce((s,v)=>s+(v-mean_kWh)**2,0)/n);
  const sorted = [...kWh_vals].sort((a,b)=>a-b);
  const p5  = sorted[Math.floor(n*0.05)];
  const p95 = sorted[Math.floor(n*0.95)];

  // Sobol / tornado: contribution per param (variance-based)
  const sensitivity = {};
  for (const key of Object.keys(param_ranges)) {
    const base_vals = results.map(r => r.params_used[key]);
    const corr = correlationCoeff(base_vals, kWh_vals);
    sensitivity[key] = { pearson_r: +corr.toFixed(3), impact_pct: +(corr*corr*100).toFixed(1) };
  }

  // Pass/Fail
  const target_kWh = params.pf_wltp_max_kWh || 30;
  const pass_count = kWh_vals.filter(v => v <= target_kWh).length;

  return {
    n, method, mean_kWh: +mean_kWh.toFixed(2), std_kWh: +std_kWh.toFixed(2),
    p5: +p5.toFixed(2), p95: +p95.toFixed(2),
    pass_rate: +((pass_count/n)*100).toFixed(1),
    sensitivity,
    histogram: (()=>{ const lo=Math.min(...kWh_vals),hi=Math.max(...kWh_vals),w=(hi-lo)/20||1,c=Array(20).fill(0); for(const v of kWh_vals) c[Math.min(19,Math.floor((v-lo)/w))]++; return c.map((n,k)=>({x:+(lo+(k+0.5)*w).toFixed(2),count:n})); })(),
    samples: results.slice(0, 200), // return first 200 for chart
  };
}

// ── simMonteCarloAsyncWorker ──
/**
 * Web Worker 가용 시 Worker로, 불가 시 simMonteCarloAsync(setTimeout 청크)로 폴백하는 Monte Carlo 실행기.
 * Worker 오류 시도 simMonteCarloAsync로 자동 폴백 — 크래시 없음.
 *
 * @param {object} params      차량 파라미터
 * @param {object} [options]   { nRuns, onProgress, chunkSize, cycle_type }
 * @returns {Promise<{ results, stats, histogram, worker_used }>}
 */
function simMonteCarloAsyncWorker(params, options = {}) {
  const nRuns      = options.nRuns      ?? 300;
  const onProgress = options.onProgress ?? null;
  const chunkSize  = options.chunkSize  ?? 40;
  const cycle_type = options.cycle_type ?? (params.cycle_type || 'WLTP');
  const RDE_EXTRA  = cycle_type.startsWith('RDE') ? { grade_deg: 0.5, mu: 0.05 } : {};
  const uncertain  = {
    CdA: 0.03, Crr: 0.05, m: 0.02, eta_motor_inv: 0.015,
    R_batt: 0.10, aux_power: 0.15, T_amb: 0.08, ...RDE_EXTRA,
  };

  // Worker 시도
  const canWorker = typeof Worker !== 'undefined';
  if (canWorker) {
    return new Promise((resolve, reject) => {
      let worker;
      try {
        worker = new Worker(
          new URL('../../workers/montecarlo.worker.js', import.meta.url)
        );
        worker.onmessage = (e) => {
          const msg = e.data;
          if (msg.type === 'progress') {
            if (onProgress) onProgress({ ratio: msg.ratio, mean: msg.mean, sigma: msg.sigma,
                                          n_done: msg.n_done, done: false });
          } else if (msg.type === 'done') {
            worker.terminate();
            if (onProgress) onProgress({ ratio: 1.0, mean: msg.stats.mean, sigma: msg.stats.sigma,
                                          n_done: msg.stats.n, done: true });
            resolve({ results: msg.results, stats: { ...msg.stats, cycle_type },
                      histogram: msg.histogram, worker_used: true });
          }
        };
        worker.onerror = (err) => {
          worker.terminate();
          // 폴백 → simMonteCarloAsync
          simMonteCarloAsync(params, options).then(resolve).catch(reject);
        };
        worker.postMessage({ params, nRuns, chunkSize, uncertain, cycle_type });
      } catch (_) {
        if (worker) { try { worker.terminate(); } catch(_2) {} }
        simMonteCarloAsync(params, options).then(resolve).catch(reject);
      }
    });
  }
  // Worker 미지원 → 기존 setTimeout 폴백
  return simMonteCarloAsync(params, options);
}

// ══════════════════════════════════════════════════════════════════════════
// [R-16-02] Sobol 민감도 지수 완전 구현 (Saltelli 2010 추정량)
// ══════════════════════════════════════════════════════════════════════════

/**
 * vanDerCorput(n, base) — van der Corput 저차원 불일치 수열
 */

// ── sensitivityAnalysis ──
// [Fix-Sens-B1] drive_mode PI 게인 적용: ECO/SPORT 모드에서 실계산과 일치하도록
//               _applyDriveModeGainsInline(params) 후 시뮬레이션 실행
function sensitivityAnalysis(params, grade_deg) {
  // [B-1] raw params 대신 drive_mode 게인이 적용된 p를 기저 계산에 사용
  const p = _applyDriveModeGainsInline(params);

  const base      = simLaunch(p, 1.0, 100, grade_deg)   || { time_s: 999, dist_m: 999, E_batt_Wh: 0, eta_cycle: 0, time: 999 };
  const baseCoast = simCoast(p, 100, grade_deg)          || { time_s: 999, dist_m: 999, time: 999 };
  const baseBrake = simBraking(p, 100, 0.8, grade_deg)  || { eta_regen_total: 0, dist_m: 999 };
  // [v6-fix3] base 결과 null-safe 보정
  if (!base.time)      base.time      = base.time_s || 999;
  if (!baseCoast.time) baseCoast.time = baseCoast.time_s || 999;

  // [A-1] factors 배열 확장: 4개 → 8개 파라미터 / delta ±10% 통일
  //        eta_regen_motor는 물리 한계(효율 1 근접)로 ±5% 적용
  //        battery_energy는 simCoast와 무관(SOC 소비 없음) → skipCoast:true 후 A-3에서 별도 처리
  const factors = [
    { name: "CdA",      key: "CdA",              delta: 0.10 },
    { name: "Crr",      key: "Crr",              delta: 0.10 },
    { name: "Mass",     key: "m",                delta: 0.10 },
    { name: "μ",        key: "mu",               delta: 0.10 },
    { name: "P_max",    key: "P_max_wheel",      delta: 0.10 },
    { name: "GR",       key: "gear_ratio",       delta: 0.10 },
    { name: "η_regen",  key: "eta_regen_motor",  delta: 0.05 }, // ±5%: 물리 한계 근접
    { name: "E_batt",   key: "battery_energy",   delta: 0.10, skipCoast: true }, // A-3에서 S_range 별도 계산
  ];

  const centralDiff = [];
  const results = []; // UI가 기대하는 형식

  factors.forEach((f) => {
    // [B-1] 각 perturbation에도 drive_mode 게인 적용된 p를 기반으로 사용
    const p_base = p[f.key];
    if (p_base == null) return; // 파라미터가 없으면 스킵

    const dp = p_base * f.delta;
    const p_hi = { ...p, [f.key]: p_base + dp };
    const p_lo = { ...p, [f.key]: p_base - dp };

    if (f.key === "mu") {
      p_hi.road_condition = "dry";
      p_lo.road_condition = "dry";
    }

    const _lhi = simLaunch(p_hi, 1.0, 100, grade_deg);
    const _llo = simLaunch(p_lo, 1.0, 100, grade_deg);
    const _bhi = simBraking(p_hi, 100, 0.8, grade_deg);
    const _blo = simBraking(p_lo, 100, 0.8, grade_deg);
    // [v6-fix3] null-safe fallback
    const l_hi = _lhi || { time_s:999, time:999, E_batt_Wh:0, eta_cycle:0 };
    const l_lo = _llo || { time_s:999, time:999, E_batt_Wh:0, eta_cycle:0 };
    const b_hi = _bhi || { eta_regen_total:0 };
    const b_lo = _blo || { eta_regen_total:0 };
    [l_hi, l_lo].forEach(r => { if (!r.time) r.time = r.time_s || 999; });

    // [A-2] S_coast → S_range: 항속거리 대리지표를 time → dist_m으로 교체
    //        dist_m이 길수록 항속거리가 길다는 방향성과 일치 (S 부호 동일 유지)
    //        battery_energy는 simCoast와 물리적으로 무관 → skipCoast 인자는 아래 A-3에서 처리
    let S_range = 0;
    if (!f.skipCoast) {
      const _chi = simCoast(p_hi, 100, grade_deg);
      const _clo = simCoast(p_lo, 100, grade_deg);
      const c_hi = _chi || { dist_m: 0, time_s:999, time:999 };
      const c_lo = _clo || { dist_m: 0, time_s:999, time:999 };
      [c_hi, c_lo].forEach(r => { if (!r.time) r.time = r.time_s || 999; });

      S_range = (baseCoast.dist_m > 0)
        ? (p_base / baseCoast.dist_m) * (c_hi.dist_m - c_lo.dist_m) / (2 * dp)
        : 0;

      // UI results: coast 관련 행은 dist_m 기반으로 갱신
      results.push({ param: f.name, variation: "0%",
        value: p_base.toFixed(4), launch_time: base.time.toFixed(3), coast_dist_m: baseCoast.dist_m?.toFixed(1) ?? "—" });
      results.push({ param: f.name, variation: `-${(f.delta*100).toFixed(0)}%`,
        value: (p_base - dp).toFixed(4), launch_time: l_lo.time.toFixed(3), coast_dist_m: c_lo.dist_m?.toFixed(1) ?? "—" });
      results.push({ param: f.name, variation: `+${(f.delta*100).toFixed(0)}%`,
        value: (p_base + dp).toFixed(4), launch_time: l_hi.time.toFixed(3), coast_dist_m: c_hi.dist_m?.toFixed(1) ?? "—" });
    } else {
      // skipCoast 인자 (battery_energy): S_range는 A-3에서 별도 계산하므로 일단 0
      results.push({ param: f.name, variation: "0%",
        value: p_base.toFixed(4), launch_time: base.time.toFixed(3), coast_dist_m: "—" });
      results.push({ param: f.name, variation: `-${(f.delta*100).toFixed(0)}%`,
        value: (p_base - dp).toFixed(4), launch_time: l_lo.time.toFixed(3), coast_dist_m: "—" });
      results.push({ param: f.name, variation: `+${(f.delta*100).toFixed(0)}%`,
        value: (p_base + dp).toFixed(4), launch_time: l_hi.time.toFixed(3), coast_dist_m: "—" });
    }

    // [Fix-Sens-C1] 부호 수정: 발진 시간은 "낮을수록 좋음" KPI
    //   파라미터 증가 → 발진 시간 감소 → S_launch > 0 (개선)이어야 함
    //   hi↔lo 교환으로 부호 수정 — Tornado 색상(초록=개선) 및 KH 설명과 일치
    const S_launch = base.time > 0
      ? (p_base / base.time) * (l_lo.time - l_hi.time) / (2 * dp)
      : 0;
    const S_regen = baseBrake.eta_regen_total > 0
      ? (p_base / baseBrake.eta_regen_total) * ((b_hi.eta_regen_total || 0) - (b_lo.eta_regen_total || 0)) / (2 * dp)
      : 0;

    centralDiff.push({
      param:  f.name,
      key:    f.key,
      p_base: +p_base.toFixed(4),
      dT_launch_hi:  +(l_hi.time - base.time).toFixed(3),
      dT_launch_lo:  +(l_lo.time - base.time).toFixed(3),
      S_launch:  +S_launch.toFixed(4),
      S_range:   +S_range.toFixed(4),   // [A-2] S_coast 필드명 → S_range (UI 매핑 단순화)
      S_regen:   +S_regen.toFixed(4),
      E_launch_hi:   l_hi.E_batt_Wh,
      E_launch_lo:   l_lo.E_batt_Wh,
      eta_regen_hi:  b_hi.eta_regen_total,
      eta_regen_lo:  b_lo.eta_regen_total,
    });
  });

  // [A-3] battery_energy 전용 S_range: simCoast 경로로는 계산 불가 → simWLTP 경로 분기
  //        이론값: S_range(E_batt) ≈ 0.95~1.0 (항속거리는 배터리 용량에 거의 선형 비례)
  //        수정 전 fallback: 0.58 (실제보다 크게 과소평가)
  const ebattFactor = factors.find(f => f.key === 'battery_energy');
  if (ebattFactor) {
    const E_base = p[ebattFactor.key];
    if (E_base > 0) {
      const dp_e   = E_base * 0.10;
      const p_hi_e = { ...p, battery_energy: E_base + dp_e };
      const p_lo_e = { ...p, battery_energy: E_base - dp_e };

      // 경량 WLTP 프록시: e_wltp(소비효율)를 고정하고 range_km만 비례 계산
      // → simWLTP 1회 호출(~15ms)로 e_wltp 기준값을 구한 뒤 선형 외삽
      const wltp_base = simWLTP(p);
      const e_wltp = wltp_base?.e_wltp || 0;
      if (e_wltp > 0) {
        const soc_range = p.wltp_soc_usable_max != null ? p.wltp_soc_usable_max - (p.wltp_soc_usable_min || 0.05) : 0.80;
        const r_hi    = (E_base + dp_e) * soc_range / e_wltp * 100;
        const r_lo    = (E_base - dp_e) * soc_range / e_wltp * 100;
        const r_base  = wltp_base.range_km || 0;
        const S_range_ebatt = r_base > 0
          ? (E_base / r_base) * (r_hi - r_lo) / (2 * dp_e)
          : 0;
        // centralDiff에서 battery_energy 항목 업데이트
        const ebattEntry = centralDiff.find(d => d.key === 'battery_energy');
        if (ebattEntry) ebattEntry.S_range = +S_range_ebatt.toFixed(4);
      }
    }
  }

  return {
    base_launch:    +base.time.toFixed(3),
    base_coast:     +(baseCoast.time || 0).toFixed(2),
    base_coast_dist: +(baseCoast.dist_m || 0).toFixed(1),  // [A-2] dist_m 기반 기저값 추가
    base_E_launch:  base.E_batt_Wh,
    base_eta_cycle: base.eta_cycle,
    base_regen_eta: baseBrake.eta_regen_total,
    centralDiff,
    results // UI가 사용하는 형식
  };
}

// ============================================================
// UI Components
// ============================================================
// Vehicle Presets
// ============================================================





// ============================================================
// Extended Vehicle Presets — Kia / Hyundai / Tesla (Advance59)
// ─────────────────────────────────────────────────────────────
// 공통 메모
//   • 800V 차량: V_dc=680(피크 800), I_max_rms 낮춤
//   • 400V 차량: V_dc=400, I_max_rms 높음
//   • CdA = Cd × 전면적(A), Crr 공칭값
//   • l_f/l_r = 무게중심까지 거리 (wheel_base × 전방하중비)
// ─────────────────────────────────────────────────────────────

// ─── Tesla Model Y Long Range AWD (2024) ────────────────────
// 공식 제원: m=2003 kg, WLTP 533 km, 75 kWh, Cd=0.227, A≈2.59

// ─── Tesla Model S Plaid (2024) ──────────────────────────────
// 공식 제원: m=2162 kg, WLTP ~637 km, 95 kWh(usable), Cd=0.208, A≈2.36

// ─── Tesla Model X Long Range AWD (2024) ────────────────────
// 공식 제원: m=2487 kg, WLTP ~560 km, 95 kWh(usable), Cd=0.250, A≈2.85

// ─── Hyundai Ioniq 5 Long Range AWD (E-GMP 800V, 2023) ───────
// 공식 제원: m=2100 kg, WLTP 481 km, 77.4 kWh, Cd=0.288, A≈2.46

// ─── Hyundai Ioniq 5 Long Range RWD (E-GMP 800V, 2023) ───────
// 공식 제원: m=1985 kg, WLTP 507 km, 77.4 kWh, 후륜단독 160kW

// ─── Hyundai Ioniq 5 N (E-GMP 800V, 2024) ───────────────────
// 공식 제원: m=2235 kg, WLTP 448 km (추정), 84 kWh, 478kW(부스트 608kW)

// ─── Kia EV6 Long Range AWD (E-GMP 800V, 2023) ───────────────
// 공식 제원: m=2010 kg, WLTP 506 km, 77.4 kWh, 239kW/605Nm AWD

// ─── Kia EV6 GT (E-GMP 800V, 2023) ─────────────────────────
// 공식 제원: m=2155 kg, WLTP 424 km, 77.4 kWh, 430kW/740Nm AWD

// ─── Kia EV9 Long Range AWD (800V, 2024) ────────────────────
// 공식 제원: m=2597 kg, WLTP 505 km, 99.8 kWh, 283kW/600Nm AWD

// ─── Kia EV3 Long Range RWD (400V, 2024) ────────────────────
// 공식 제원: m=1685 kg, WLTP 605 km, 81.4 kWh, 150kW/310Nm RWD

// ─── Kia EV6 Long Range RWD (E-GMP 800V, 2023) ───────────────
// 공식 제원: m=1870 kg, WLTP 528 km, 77.4 kWh, 168kW/350Nm RWD

// ============================================================
// Comparison Simulation Runner
// ============================================================
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// §CSV_TEMPLATE  Full-Spec 드라이빙 사이클 CSV 분석기
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
/**
 * Template column spec (10 ms sampling):
 * Col 0: Time_s           [s]     - 시간 (0.01 s steps)
 * Col 1: APS_pct          (%)     - 가속 페달 (0–100)
 * Col 2: BPS_pct          (%)     - 브레이크 페달 (0–100)
 * Col 3: DriveMode        [-]     - ECO=0 / NORMAL=1 / SPORT=2
 * Col 4: SteerAngle_deg   [deg]   - 횡방향 조향각
 * Col 5: T_amb_C          (°C)    - 외기온
 * Col 6: RoadMu           [-]     - 노면 마찰계수
 * Col 7: Grade_deg        [deg]   - 구배 (+상향)
 *
 * Header row 0 (name): Time_s, APS_pct, BPS_pct, DriveMode, SteerAngle_deg, T_amb_C, RoadMu, Grade_deg
 * Header row 1 (unit): [s], (%), (%), [ECO=0/NOR=1/SPT=2], [deg], (°C), [-], [deg]
 * Row 2+: numeric data
 *
 * Initial conditions (separate section at bottom, prefixed with #):
 *   # init_soc_pct, 90
 *   # init_batt_temp_C, 25
 *   # init_speed_kmh, 0
 */

/** Generate CSV template content (string) */

// ── generateCycleCSVTemplate ──
function generateCycleCSVTemplate() {
  // [Bug1/2-Fix] 템플릿 업데이트:
  //   • RoadMu → 업로드 시 params.mu에 자동 반영됨 (Bug1-Fix)
  //   • SteerAngle_deg → 코너링 스크럽 드래그 + 마찰원 한계 계산에 실제 사용됨 (Bug2-Fix)
  //   • 슬라럼·경사 구간 추가로 두 컬럼의 영향 확인 가능
  const header1 = "Time_s,APS_pct,BPS_pct,DriveMode,SteerAngle_deg,T_amb_C,RoadMu,Grade_deg";
  const header2 = "[s],(%),(%),[ECO=0/NORMAL=1/SPORT=2],[deg],(°C),[-],[deg]";
  const rows = [];

  // ── 구간 1 (0–2s): 정차 대기 ──────────────────────────────────────────
  for (let t = 0; t <= 2.0; t += 0.1)
    rows.push(`${t.toFixed(2)},0.0,0.0,1,0.0,25.0,1.00,0.0`);

  // ── 구간 2 (2–7s): 가속 (직진) ─────────────────────────────────────────
  for (let t = 2.1; t <= 7.0; t += 0.1) {
    const aps = Math.min(55, (t - 2) * 11).toFixed(1);
    rows.push(`${t.toFixed(2)},${aps},0.0,1,0.0,25.0,1.00,0.0`);
  }

  // ── 구간 3 (7–13s): 정속 + 슬라럼 코너링 (±10°) ──────────────────────
  // SteerAngle이 코너링 스크럽 드래그·마찰원에 미치는 영향 확인용
  for (let t = 7.1; t <= 13.0; t += 0.1) {
    const steer = (10 * Math.sin((t - 7) * Math.PI)).toFixed(2);
    rows.push(`${t.toFixed(2)},20.0,0.0,1,${steer},25.0,1.00,0.0`);
  }

  // ── 구간 4 (13–18s): 오르막 + 직진 (+3°) ──────────────────────────────
  // Grade_deg → 중력 경사 저항, RoadMu 그대로 1.0
  for (let t = 13.1; t <= 18.0; t += 0.1)
    rows.push(`${t.toFixed(2)},30.0,0.0,1,0.0,25.0,1.00,3.0`);

  // ── 구간 5 (18–22s): 습윤 노면 제동 (RoadMu=0.7) ─────────────────────
  // RoadMu → 업로드 후 params.mu에 자동 반영 (Bug1-Fix) 확인용
  for (let t = 18.1; t <= 22.0; t += 0.1) {
    const bps = Math.min(50, (t - 18) * 12.5).toFixed(1);
    rows.push(`${t.toFixed(2)},0.0,${bps},1,0.0,20.0,0.70,-1.5`);
  }

  // ── 구간 6 (22–25s): 정차 ─────────────────────────────────────────────
  for (let t = 22.1; t <= 25.0; t += 0.1)
    rows.push(`${t.toFixed(2)},0.0,0.0,1,0.0,20.0,0.70,0.0`);

  const initSection = [
    "",
    "# ===== Initial Conditions =====",
    "# init_soc_pct,90",
    "# init_batt_temp_C,25",
    "# init_speed_kmh,0",
    "",
    "# ===== Column Description =====",
    "# Time_s         : 시간 [s] — 0.01 s (10 ms) 샘플링 권장",
    "# APS_pct        : 가속 페달 [0–100 %]",
    "# BPS_pct        : 브레이크 페달 [0–100 %] — 원패달 모드 시 무시됨",
    "# DriveMode      : 주행 모드 (0=ECO, 1=NORMAL, 2=SPORT)",
    "# SteerAngle_deg : 조향각 [deg] — ★ Ackermann 모델로 코너링 스크럽 드래그 및",
    "#                  마찰원(Friction Ellipse) 종방향 한계 감소에 실제 반영됨",
    "#                  (a_y = v²·tan(δ)/L_wb, F_scrub = m·|a_y|·Crr·0.5)",
    "# T_amb_C        : 외기온 (°C) — HVAC 및 배터리 열관리 입력",
    "# RoadMu         : 노면 마찰계수 (건조=1.0, 습윤=0.7, 적설=0.4, 빙판=0.3)",
    "#                  ★ CSV 업로드 시 평균값이 params.mu에 자동 반영됨",
    "# Grade_deg      : 구배 [deg] (양수=상향, 음수=하향) — 중력 경사 저항 직접 반영",
  ].join("\n");
  return [header1, header2, ...rows, initSection].join("\n");
}

/** Parse full-spec driving cycle CSV → structured object */

// ── parseFullSpecCycleCSV ──
function parseFullSpecCycleCSV(text) {
  // [R-03] 열 이름 기반 유연 파싱 + 상세 검증 메시지
  const lines = text.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  const initConds = { soc_pct: null, batt_temp_C: null, speed_kmh: null };
  const dataRows = [];
  const errors = [];
  let headerParsed = false;
  let unitRowSkipped = false;
  let colNames = [];

  // 열 이름 정규화: 공백/특수문자 제거, 소문자화
  const normCol = s => s.toLowerCase().replace(/[^a-z0-9_]/g, "").replace(/^col[a-h]_?/, "");

  // 열 이름 → 값 추출기 (순서 독립적)
  const colIdx = {};  // name → index
  const getCol = (row, aliases, def = NaN) => {
    for (const a of aliases) {
      if (colIdx[a] !== undefined && row[colIdx[a]] !== undefined) {
        const v = parseFloat(row[colIdx[a]]);
        if (!isNaN(v)) return v;
      }
    }
    // position fallback: aliases에 숫자 index도 허용
    return def;
  };

  const MODE_MAP = { 0: "ECO", 1: "NORMAL", 2: "SPORT" };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    // 초기 조건 주석 파싱
    if (line.startsWith("#")) {
      const inner = line.slice(1).trim();
      const m = inner.match(/^(init_soc_pct|init_batt_temp_c|init_batt_temp_C|init_speed_kmh)\s*[,=]\s*([\d.\-]+)/i);
      if (m) {
        const key = m[1].toLowerCase();
        if (key === "init_soc_pct")     initConds.soc_pct = parseFloat(m[2]);
        if (key.startsWith("init_batt")) initConds.batt_temp_C = parseFloat(m[2]);
        if (key === "init_speed_kmh")   initConds.speed_kmh = parseFloat(m[2]);
      }
      continue;
    }
    const sep = line.includes("\t") ? "\t" : ",";
    const cols = line.split(sep).map(c => c.trim());
    if (!headerParsed) {
      // 헤더 행 파싱: 열 이름 → 인덱스 맵
      colNames = cols.map(c => normCol(c));
      colNames.forEach((name, i) => { colIdx[name] = i; });
      headerParsed = true;
      continue;
    }
    // 단위 행 스킵 ([s], (%) 등으로 시작)
    if (!unitRowSkipped && (cols[0].startsWith("[") || cols[0] === "")) {
      unitRowSkipped = true; continue;
    }
    // 데이터 행
    const num = cols.map(c => parseFloat(c));
    // time은 필수
    const t_val = !isNaN(num[colIdx['times'] ?? colIdx['time_s'] ?? colIdx['t'] ?? 0]) 
      ? num[colIdx['times'] ?? colIdx['time_s'] ?? colIdx['t'] ?? 0]
      : num[0];
    if (isNaN(t_val)) { errors.push(`Line ${li+1}: time 값 없음 — 스킵`); continue; }
    
    const aps_i = colIdx['apspct'] ?? colIdx['aps'] ?? colIdx['aps_pct'] ?? 1;
    const bps_i = colIdx['bpspct'] ?? colIdx['bps'] ?? colIdx['bps_pct'] ?? 2;
    const mode_i = colIdx['drivemode'] ?? colIdx['drive_mode'] ?? 3;
    const steer_i = colIdx['steerangledeg'] ?? colIdx['steerangle_deg'] ?? colIdx['steer_deg'] ?? 4; // [FixA] 'steerangle_deg' 별칭 추가 (normCol('SteerAngle_deg')의 실제 출력)
    const tamb_i = colIdx['t_ambc'] ?? colIdx['t_amb_c'] ?? colIdx['t_amb'] ?? 5;
    const mu_i = colIdx['roadmu'] ?? colIdx['road_mu'] ?? 6;
    const grade_i = colIdx['grade_deg'] ?? colIdx['gradedeg'] ?? 7;

    const modeIdx = Math.round(!isNaN(num[mode_i]) ? num[mode_i] : 1);
    dataRows.push({
      t:           t_val,
      aps:         Math.min(100, Math.max(0, isNaN(num[aps_i]) ? 0 : num[aps_i])),
      bps:         Math.min(100, Math.max(0, isNaN(num[bps_i]) ? 0 : num[bps_i])),
      drive_mode:  MODE_MAP[modeIdx] || "NORMAL",
      steer_deg:   isNaN(num[steer_i]) ? 0 : num[steer_i],
      T_amb:       isNaN(num[tamb_i]) ? 25 : num[tamb_i],
      road_mu:     isNaN(num[mu_i])   ? 1.0 : Math.max(0.1, Math.min(1.5, num[mu_i])),
      grade_deg:   isNaN(num[grade_i]) ? 0 : Math.max(-20, Math.min(20, num[grade_i])),
    });
  }

  if (dataRows.length < 2) return { ok: false, error: `데이터 행 부족 (${dataRows.length}행). 헤더와 최소 2개 이상의 데이터가 필요합니다.`, errors };

  // 시계열 연속성 검사
  let dt_issues = 0;
  for (let i = 1; i < dataRows.length; i++) {
    const dt = dataRows[i].t - dataRows[i-1].t;
    if (dt <= 0 || dt > 10) dt_issues++;
  }
  if (dt_issues > dataRows.length * 0.1) {
    errors.push(`⚠️ ${dt_issues}개 행에서 시간 간격 이상 (역전 또는 10s 초과) 감지`);
  }

  const t_total = dataRows[dataRows.length-1].t - dataRows[0].t;
  const dt_avg = t_total / (dataRows.length - 1);
  const max_aps = Math.max(...dataRows.map(d => d.aps));
  const max_bps = Math.max(...dataRows.map(d => d.bps));
  const avg_mu  = dataRows.reduce((s, d) => s + d.road_mu, 0) / dataRows.length;
  const grades  = dataRows.map(d => d.grade_deg);
  const min_grade = Math.min(...grades), max_grade = Math.max(...grades);
  const t_amb_avg = dataRows.reduce((s, d) => s + d.T_amb, 0) / dataRows.length;

  return {
    ok: true,
    rows: dataRows,
    meta: {
      n: dataRows.length,
      t_total_s: +t_total.toFixed(2),
      dt_avg_ms: +(dt_avg * 1000).toFixed(1),
      max_aps, max_bps,
      avg_mu: +avg_mu.toFixed(3),
      min_grade: +min_grade.toFixed(2), max_grade: +max_grade.toFixed(2),
      t_amb_avg: +t_amb_avg.toFixed(1),
      col_map: Object.fromEntries(Object.entries(colIdx).slice(0, 12)), // 디버그용
    },
    initConds,
    errors,
  };
}

/**
 * Simulate parsed full-spec cycle using vehicle params
 * Uses Euler integration, 10 ms step assumed
 */

// ── runCoreSim ──
function runCoreSim(params, reacV0 = 60, reacV1 = 100, launchThrottle = 1.0, reaccelThrottle = null) {
  const grade = params.grade_deg;
  const launchThr  = Math.max(0.05, Math.min(1.0, launchThrottle));
  const reaccelThr = Math.max(0.05, Math.min(1.0, reaccelThrottle != null ? reaccelThrottle : launchThrottle));
  const roadload   = simRoadLoad(params, [-5, 0, 5]);
  const launch100  = simLaunch(params, launchThr,       100,  grade);
  const launch300  = simLaunch(params, launchThr,       300,  grade);
  const launch150  = simLaunch(params, launchThr * 0.5, 300,  grade);
  const reaccel100 = simReaccel(params, reacV0, reacV1, reaccelThr,       grade);
  const reaccel50  = simReaccel(params, reacV0, reacV1, reaccelThr * 0.5, grade);
  const coast = simCoast(params, 100, grade);
  const coastHiCdA = simCoast({ ...params, CdA: params.CdA * 1.2 }, 100, grade);
  const coastLoCdA = simCoast({ ...params, CdA: params.CdA * 0.8 }, 100, grade);
  const brake100 = simBraking(params, 100, 1.0, grade);
  const brake50  = simBraking(params, 100, 0.5, grade);
  const brakeCold   = simBraking({ ...params, T_amb: -10, batt_temp: -10 }, 100, 1.0, grade);
  const brakeHiSOC  = simBraking({ ...params, soc_init: 0.95 }, 100, 1.0, grade);
  const emergency   = simEmergencyBraking(params, 100, grade);
  const steadyEff   = generateSteadyStateEfficiency(params);
  const condComp    = generateConditionComparison(params);
  const energyCons  = generateEnergyConsumption(params);
  return {
    roadload, launch100, launch300, launch150, reaccel100, reaccel50,
    coast, coastHiCdA, coastLoCdA,
    brake100, brake50, brakeCold, brakeHiSOC, emergency,
    steadyEff, condComp, energyCons,
    // Placeholders for heavy results (will be filled by runHeavySim)
    thermalStress: null, sens: null,
    wltp: null, wltpTempSens: [], csvCycleResult: null,
    passFailReport: null, sohProfile: null,
    _coreOnly: true,  // flag: heavy 결과 아직 없음
  };
}

// ── runHeavySim ──
/**
 * 무거운(heavy) 시뮬레이션 세트를 실행한다: 열 스트레스, 민감도, 주행 사이클, 온도 민감도, CSV 사이클.
 * runCoreSim 결과를 coreResult로 수신해 전체 결과를 병합 후 합격·불합격 보고서 생성.
 *
 * @param {object} params            차량 파라미터
 * @param {number} [reacV0=60]       재가속 시작 속도 [km/h]
 * @param {number} [reacV1=100]      재가속 목표 속도 [km/h]
 * @param {number} [launchThrottle=1.0]  발진 스로틀
 * @param {object} [coreResult=null] runCoreSim 결과 (없으면 heavy만 반환)
 * @returns {object}  전체 시뮬레이션 결과 (_coreOnly: false)
 */
function runHeavySim(params, reacV0 = 60, reacV1 = 100, launchThrottle = 1.0, coreResult = null) {
  const grade = params.grade_deg;
  const thermalStress = simThermalStress(params, 10, 300);
  const sens = sensitivityAnalysis(params, grade);
  const cycle_type = params.cycle_type || 'WLTP';
  // ── 드라이브 모드 게인 주행 사이클 연결 ──────────────────────────────
  // simWLTP / simDrivingCycle 진입부에서도 적용되지만, runHeavySim 레벨에서도
  // 명시적으로 적용해 결과 메타(_sim_dm_mode 등)를 wltp 반환값에 보존.
  const paramsForCycle = _applyDriveModeGainsInline(params);
  const wltp = cycle_type === 'WLTP' ? simWLTP(paramsForCycle) : simDrivingCycle(paramsForCycle);
  const wltpTempSens = simWLTPTempSensitivity(paramsForCycle);

  let csvCycleResult = null;
  if (params.driving_cycle_mode === "csv_full" && params.driving_cycle_data_full
      && Array.isArray(params.driving_cycle_data_full) && params.driving_cycle_data_full.length > 1) {
    try {
      const initConds = {
        soc_pct: params.csv_init_soc ?? 90,
        batt_temp_C: params.csv_init_batt_temp ?? params.T_amb ?? 25,
        speed_kmh: params.csv_init_speed ?? 0,
      };
      csvCycleResult = simFullSpecCycle(params.driving_cycle_data_full, initConds, params);
    } catch (e) {
      console.warn("[v8] CSV cycle simulation failed:", e);
    }
  }

  const allResults = { ...(coreResult || {}), thermalStress, sens, wltp, wltpTempSens, csvCycleResult };
  const passFailReport = generatePassFailReport(allResults, params);
  const sohProfileUsed = getSOHProfile(params);

  return {
    ...allResults,
    drive_mode: params.drive_mode || 'NORMAL',
    one_pedal: params.one_pedal_enable || false,
    cycle_type,
    passFailReport, sohProfile: sohProfileUsed,
    v7_meta: {
      IRI: params.IRI || 2.5,
      tire_pressure: params.tire_pressure_actual || params.tire_pressure_nominal || 2.4,
      gear_loss_temp_enable: params.gear_loss_temp_enable || false,
      regen_blend_enable: params.regen_blend_enable || true,
      rls_mu_enable: params.rls_mu_enable || true,
      road_roughness_enable: params.road_roughness_enable || false,  // [Fix-RE-01] v7_meta 누락 수정
      hvac_mode: params.hvac_mode || 'auto',
    },
    _coreOnly: false,
  };
}

// ── runFullSim ──
/**
 * 전체 시뮬레이션 세트를 실행한다: launch, reaccel, coast, braking, skidpad, drivability,
 * 도로부하, 효율 맵, 에너지 소비, 비교분석, 주행 사이클, 열 스트레스, 민감도.
 *
 * @param {object} params               차량 파라미터
 * @param {number} [reacV0=60]          재가속 시작 속도 [km/h]
 * @param {number} [reacV1=100]         재가속 목표 속도 [km/h]
 * @param {number} [launchThrottle=1.0] 발진 스로틀
 * @param {number|null} [reaccelThrottle=null]  재가속 스로틀 (null이면 launchThrottle 사용)
 * @returns {object}  전체 시뮬레이션 결과 객체
 */
function runFullSim(params, reacV0 = 60, reacV1 = 100, launchThrottle = 1.0, reaccelThrottle = null) {
  const grade = params.grade_deg;
  const launchThr  = Math.max(0.05, Math.min(1.0, launchThrottle));
  const reaccelThr = Math.max(0.05, Math.min(1.0, reaccelThrottle != null ? reaccelThrottle : launchThrottle));
  const roadload   = simRoadLoad(params, [-5, 0, 5]);
  const launch100  = simLaunch(params, launchThr,       100,  grade);
  const launch300  = simLaunch(params, launchThr,       300,  grade);
  const launch150  = simLaunch(params, launchThr * 0.5, 300,  grade); // [A25-Thr] 비교용 절반 개도
  const reaccel100 = simReaccel(params, reacV0, reacV1, reaccelThr,       grade);
  const reaccel50  = simReaccel(params, reacV0, reacV1, reaccelThr * 0.5, grade);
  const coast = simCoast(params, 100, grade);
  const coastHiCdA = simCoast({ ...params, CdA: params.CdA * 1.2 }, 100, grade);
  const coastLoCdA = simCoast({ ...params, CdA: params.CdA * 0.8 }, 100, grade);
  const brake100 = simBraking(params, 100, 1.0, grade);
  const brake50 = simBraking(params, 100, 0.5, grade);
  const brakeCold = simBraking({ ...params, T_amb: -10, batt_temp: -10 }, 100, 1.0, grade);
  const brakeHiSOC = simBraking({ ...params, soc_init: 0.95 }, 100, 1.0, grade);
  const emergency = simEmergencyBraking(params, 100, grade);
  const steadyEff = generateSteadyStateEfficiency(params);
  const condComp = generateConditionComparison(params);
  const energyCons = generateEnergyConsumption(params);
  const thermalStress = simThermalStress(params, 10, 300);
  const sens = sensitivityAnalysis(params, grade);
  // ── NEW v3: Driving Cycle (WLTP / FTP-75 / EPA) ──
  const cycle_type = params.cycle_type || 'WLTP';
  // ── 드라이브 모드 게인 주행 사이클 연결 ──────────────────────────────
  const paramsForCycle = _applyDriveModeGainsInline(params);
  const wltp = cycle_type === 'WLTP' ? simWLTP(paramsForCycle) : simDrivingCycle(paramsForCycle);
  const wltpTempSens = simWLTPTempSensitivity(paramsForCycle);

  // ── [v8-fix1] CSV Full-Spec Driving Cycle 연결 ──
  let csvCycleResult = null;
  if (params.driving_cycle_mode === "csv_full" && params.driving_cycle_data_full
      && Array.isArray(params.driving_cycle_data_full) && params.driving_cycle_data_full.length > 1) {
    try {
      const initConds = {
        soc_pct: params.csv_init_soc ?? 90,
        batt_temp_C: params.csv_init_batt_temp ?? params.T_amb ?? 25,
        speed_kmh: params.csv_init_speed ?? 0,
      };
      csvCycleResult = simFullSpecCycle(params.driving_cycle_data_full, initConds, params);
    } catch (e) {
      console.warn("[v8] CSV cycle simulation failed:", e);
      csvCycleResult = null;
    }
  }

  // [v7] Pass/Fail 검증 리포트
  const allResults = {
    roadload, launch100, launch300, launch150, reaccel100, reaccel50,
    coast, coastHiCdA, coastLoCdA,
    brake100, brake50, brakeCold, brakeHiSOC, emergency,
    steadyEff, condComp, energyCons, thermalStress, sens,
    wltp, wltpTempSens,
    csvCycleResult,   // [v8-fix1] CSV 사이클 결과 포함
  };
  const passFailReport = generatePassFailReport(allResults, params);
  // [v7] SOH 프로파일 요약
  const sohProfileUsed = getSOHProfile(params);

  return {
    ...allResults,
    drive_mode: params.drive_mode || 'NORMAL',
    one_pedal: params.one_pedal_enable || false,
    cycle_type,
    // [v7] 신규 필드
    passFailReport,
    sohProfile: sohProfileUsed,
    v7_meta: {
      IRI: params.IRI || 2.5,
      tire_pressure: params.tire_pressure_actual || params.tire_pressure_nominal || 2.4,
      gear_loss_temp_enable: params.gear_loss_temp_enable || false,
      regen_blend_enable: params.regen_blend_enable || true,
      rls_mu_enable: params.rls_mu_enable || true,
      road_roughness_enable: params.road_roughness_enable || false,  // [Fix-RE-01] v7_meta 누락 수정
      hvac_mode: params.hvac_mode || 'auto',
    },
  };
}

// ── runComparison ──
/**
 * 두 차량 파라미터 세트에 대해 runFullSim을 각각 실행하고 비교 결과를 반환한다.
 *
 * @param {object} paramsA  차량 A 파라미터 (shortName 포함)
 * @param {object} paramsB  차량 B 파라미터 (shortName 포함)
 * @returns {{ a, b, mergedLaunch, mergedCoast, mergedBrake, mergedEff, mergedCons, summary, radar }}
 */
function runComparison(paramsA, paramsB, reacV0 = 60, reacV1 = 100, launchThrottle = 1.0, reaccelThrottle = null) {
  const a = runFullSim(paramsA, reacV0, reacV1, launchThrottle, reaccelThrottle);
  const b = runFullSim(paramsB, reacV0, reacV1, launchThrottle, reaccelThrottle);
  return _buildCompMerged(a, b, paramsA, paramsB);
}

// ── runCoreComparison ──
/**
 * 두 차량에 대해 runCoreSim만 실행하고 비교 결과를 반환한다 (fast, ~600 ms).
 *
 * @param {object} paramsA  차량 A 파라미터
 * @param {object} paramsB  차량 B 파라미터
 * @returns {{ a, b, mergedLaunch, mergedCoast, mergedBrake, mergedEff, mergedCons, summary, radar }}
 */
function runCoreComparison(paramsA, paramsB, reacV0 = 60, reacV1 = 100, launchThrottle = 1.0, reaccelThrottle = null) {
  const a = runCoreSim(paramsA, reacV0, reacV1, launchThrottle, reaccelThrottle);
  const b = runCoreSim(paramsB, reacV0, reacV1, launchThrottle, reaccelThrottle);
  return _buildCompMerged(a, b, paramsA, paramsB);
}

// ── _buildCompMerged ──
/**
 * 두 시뮬레이션 결과를 비교 형식으로 병합한다.
 * launch/coast/brake 시계열 mergeTimeSeries, 효율·소비 mergeByKey, summary, radar 포함.
 *
 * @param {object} a       차량 A 시뮬레이션 결과
 * @param {object} b       차량 B 시뮬레이션 결과
 * @param {object} paramsA 차량 A 파라미터 (shortName)
 * @param {object} paramsB 차량 B 파라미터 (shortName)
 * @returns {{ a, b, mergedLaunch, mergedCoast, mergedBrake, mergedEff, mergedCons, summary, radar }}
 */
function _buildCompMerged(a, b, paramsA, paramsB) {
  const mergedLaunch = mergeTimeSeries(a.launch300.data, b.launch300.data, "t", paramsA.shortName, paramsB.shortName);
  const mergedCoast  = mergeTimeSeries(a.coast.data, b.coast.data, "t", paramsA.shortName, paramsB.shortName);
  const mergedBrake  = mergeTimeSeries(a.brake100.data, b.brake100.data, "t", paramsA.shortName, paramsB.shortName);
  const mergedEff    = mergeByKey(a.steadyEff, b.steadyEff, "v_kmh", paramsA.shortName, paramsB.shortName);
  const mergedCons   = mergeByKey(a.energyCons, b.energyCons, "v_kmh", paramsA.shortName, paramsB.shortName);
  const summary = buildSummary(paramsA, paramsB, a, b);
  const radar   = buildRadar(paramsA, paramsB, a, b);
  return { a, b, mergedLaunch, mergedCoast, mergedBrake, mergedEff, mergedCons, summary, radar };
}

// [A36-PERF] Core-only comparison (fast, ~600ms for both vehicles)

// ── mergeByKey ──
/**
 * key 기준으로 두 데이터 배열을 외부 조인하여 nameA_{col}/nameB_{col} 열로 병합한다.
 *
 * @param {object[]} dataA  차량 A 데이터
 * @param {object[]} dataB  차량 B 데이터
 * @param {string}   key    조인 키 (예: 'v_kmh')
 * @param {string}   nameA  차량 A 이름 (열 접두사)
 * @param {string}   nameB  차량 B 이름 (열 접두사)
 * @returns {object[]}  병합된 오름차순 정렬 배열
 */
function mergeByKey(dataA, dataB, key, nameA, nameB) {
  const map = new Map();
  dataA.forEach(d => { map.set(d[key], { [key]: d[key] }); const m = map.get(d[key]); Object.keys(d).forEach(k => { if (k !== key) m[`${nameA}_${k}`] = d[k]; }); });
  dataB.forEach(d => { if (!map.has(d[key])) map.set(d[key], { [key]: d[key] }); const m = map.get(d[key]); Object.keys(d).forEach(k => { if (k !== key) m[`${nameB}_${k}`] = d[k]; }); });
  return Array.from(map.values()).sort((a, b) => a[key] - b[key]);
}

// ── mergeTimeSeries ──
/**
 * timeKey 기준으로 두 시계열 데이터 배열을 외부 조인하여 병합한다.
 * 시각값은 .toFixed(2) 스트링 키로 정렬 후 오름차순 반환.
 *
 * @param {object[]} dataA    차량 A 시계열
 * @param {object[]} dataB    차량 B 시계열
 * @param {string}   timeKey  시각 키 (예: 't')
 * @param {string}   nameA    차량 A 이름 (열 접두사)
 * @param {string}   nameB    차량 B 이름 (열 접두사)
 * @returns {object[]}  병합된 오름차순 시계열
 */
function mergeTimeSeries(dataA, dataB, timeKey, nameA, nameB) {
  const map = new Map();
  dataA.forEach(d => {
    const k = d[timeKey].toFixed(2);
    if (!map.has(k)) map.set(k, { [timeKey]: d[timeKey] });
    const m = map.get(k);
    Object.keys(d).forEach(key => { if (key !== timeKey) m[`${nameA}_${key}`] = d[key]; });
  });
  dataB.forEach(d => {
    const k = d[timeKey].toFixed(2);
    if (!map.has(k)) map.set(k, { [timeKey]: d[timeKey] });
    const m = map.get(k);
    Object.keys(d).forEach(key => { if (key !== timeKey) m[`${nameB}_${key}`] = d[key]; });
  });
  return Array.from(map.values()).sort((a, b) => a[timeKey] - b[timeKey]);
}

// ── buildSummary ──
/**
 * 두 차량 비교 요약 테이블(rows)을 생성한다.
 * 항목: 0→100 km/h, 재가속, 코스팅, 제동 거리, 회생 효율, kWh/100 km @60/80/100/120, 항속 거리, WLTP 전비.
 *
 * @param {object} pA  차량 A 파라미터
 * @param {object} pB  차량 B 파라미터
 * @param {object} rA  차량 A 시뮬레이션 결과
 * @param {object} rB  차량 B 시뮬레이션 결과
 * @returns {object[]}  { metric, unitLabel, a, b, lower? }[] 비교 행 배열
 */
function buildSummary(pA, pB, rA, rB) {
  const rows = [
    { metric: "0→100 km/h", unitLabel: "s", a: rA.launch100.time, b: rB.launch100.time, lower: true },
    { metric: "60→100 km/h", unitLabel: "s", a: rA.reaccel100.time, b: rB.reaccel100.time, lower: true },
    { metric: "Coast 100→0", unitLabel: "s", a: rA.coast.time, b: rB.coast.time },
    { metric: "Coast distance", unitLabel: "m", a: rA.coast.distance, b: rB.coast.distance },
    { metric: "Brake 100→0", unitLabel: "m", a: rA.brake100.distance, b: rB.brake100.distance, lower: true },
    { metric: "η_regen (100%)", metricEn: "Regen Efficiency (100%)", unitLabel: "%", a: rA.brake100.eta_regen_total, b: rB.brake100.eta_regen_total },
    { metric: "회생 에너지", metricEn: "Regen Energy", unitLabel: "Wh", a: rA.brake100.E_regen_Wh, b: rB.brake100.E_regen_Wh },
  ];
  // Append kWh/100km at key speeds
  [60, 80, 100, 120].forEach(v => {
    const eA = rA.energyCons.find(d => d.v_kmh === v);
    const eB = rB.energyCons.find(d => d.v_kmh === v);
    if (eA && eB) rows.push({ metric: `kWh/100km @${v}`, unitLabel: "kWh", a: eA.kWh_100km, b: eB.kWh_100km, lower: true });
  });
  // Range estimate
  [60, 100, 120].forEach(v => {
    const eA = rA.energyCons.find(d => d.v_kmh === v);
    const eB = rB.energyCons.find(d => d.v_kmh === v);
    if (eA && eB) rows.push({ metric: `Range @${v}km/h`, unitLabel: "km", a: eA.range_km, b: eB.range_km });
  });
  // WLTP results
  if (rA.wltp && rB.wltp) {
    rows.push({ metric: "WLTP 전비", metricEn: "WLTP Efficiency", unitLabel: "kWh/100km", a: rA.wltp.e_wltp, b: rB.wltp.e_wltp, lower: true });
    rows.push({ metric: "WLTP 항속거리", unitLabel: "km", a: rA.wltp.range_km, b: rB.wltp.range_km });
    rows.push({ metric: "WLTP CO₂", unitLabel: "g/km", a: rA.wltp.co2_gkm, b: rB.wltp.co2_gkm, lower: true });
    rows.push({ metric: "WLTP 회생 비율", unitLabel: "%", a: rA.wltp.eta_regen_pct, b: rB.wltp.eta_regen_pct });
    rows.push({ metric: "WLTP 모터 최고온도", metricEn: "WLTP Max Motor Temp.", unitLabel: "°C", a: rA.wltp.thermalStats?.T_motor_max?.toFixed(1), b: rB.wltp.thermalStats?.T_motor_max?.toFixed(1), lower: true });
    rows.push({ metric: "WLTP 배터리 최고온도", metricEn: "WLTP Max Batt. Temp.", unitLabel: "°C", a: rA.wltp.thermalStats?.T_batt_max?.toFixed(1), b: rB.wltp.thermalStats?.T_batt_max?.toFixed(1), lower: true });
  }
  return rows;
}

// ── buildHist ──
/**
 * 값 배열로 히스토그램 bins개를 생성한다.
 *
 * @param {number[]} vals  데이터 값 배열
 * @param {number}   bins  구간 수
 * @returns {{ x: number, count: number }[]}  구간 중심값 및 빈도
 */
function buildHist(vals, bins) {
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const w = (hi - lo) / bins || 0.1;
  return Array.from({length: bins}, (_, b) => {
    const l = lo + b*w, r = l + w;
    return { x: +((l+r)/2).toFixed(2), count: vals.filter(v => v >= l && v < r).length };
  });
}

/**
 * simMonteCarloAsyncWorker(params, options)
 * Web Worker 우선 실행 → 미지원 환경은 setTimeout 폴백 보장
 */

// ── buildRadar ──
function buildRadar(pA, pB, rA, rB) {
  // ── [A46] 상대 정규화 헬퍼 ──────────────────────────────────
  // lower=true: 낮을수록 좋음 → 낮은 값이 100%, 높은 값은 비례
  // lower=false: 높을수록 좋음 → 높은 값이 100%, 낮은 값은 비례
  const relNorm = (vA, vB, lower) => {
    if (lower) {
      // 낮을수록 좋음: best = min(vA, vB) = 100%
      const best = Math.min(vA, vB);
      const worst = Math.max(vA, vB);
      if (worst === 0) return { a: 100, b: 100 };
      return {
        a: best > 0 ? Math.min(100, (best / Math.max(vA, 0.001)) * 100) : 100,
        b: best > 0 ? Math.min(100, (best / Math.max(vB, 0.001)) * 100) : 100,
      };
    } else {
      // 높을수록 좋음: best = max(vA, vB) = 100%
      const best = Math.max(vA, vB, 0.001);
      return {
        a: Math.min(100, (vA / best) * 100),
        b: Math.min(100, (vB / best) * 100),
      };
    }
  };

  // ── [1] 0→100 가속 시간 ─────────────────────────────────────
  const rawT_A = (rA.launch100 && rA.launch100.time < 90) ? rA.launch100.time : 15;
  const rawT_B = (rB.launch100 && rB.launch100.time < 90) ? rB.launch100.time : 15;
  const accel = relNorm(rawT_A, rawT_B, true); // 낮을수록 좋음

  // ── [2] WLTP 전비 (kWh/100km) ───────────────────────────────
  const getWltpE = function(r, p) {
    if (r.wltp && r.wltp.e_wltp > 5 && r.wltp.e_wltp < 50) return r.wltp.e_wltp;
    if (r.steadyEff && r.steadyEff.length > 0) {
      let pt70 = r.steadyEff.find(function(d) { return d.v_kmh === 70; })
              || r.steadyEff.find(function(d) { return d.v_kmh === 80; });
      if (pt70 && pt70.kWh_100km > 5) return +(pt70.kWh_100km * 1.15).toFixed(2);
    }
    if (r.energyCons && r.energyCons.length > 0) {
      let ec70 = r.energyCons.find(function(d) { return d.v_kmh === 70; })
              || r.energyCons.find(function(d) { return d.v_kmh === 80; });
      if (ec70 && ec70.kWh_100km > 5) return +(ec70.kWh_100km * 1.15).toFixed(2);
    }
    return 18;
  };
  const wA_e = getWltpE(rA, pA);
  const wB_e = getWltpE(rB, pB);
  const eff = relNorm(wA_e, wB_e, true); // 낮을수록 좋음

  // ── [3] WLTP 항속거리 (km) ──────────────────────────────────
  const getRange = function(r, p, wE) {
    if (r.wltp && r.wltp.range_km > 50 && r.wltp.range_km < 1500) return r.wltp.range_km;
    const _soc_rng3 = (p.wltp_soc_usable_max || 0.90) - (p.wltp_soc_usable_min || 0.10); // [B-2 Fix]
    return Math.round((p.battery_energy || 60) * _soc_rng3 / wE * 100);
  };
  const wA_range = getRange(rA, pA, wA_e);
  const wB_range = getRange(rB, pB, wB_e);
  const range = relNorm(wA_range, wB_range, false); // 높을수록 좋음

  // ── [4] 최대 출력 (kW) ──────────────────────────────────────
  const pMaxA = (pA.P_max_wheel || 100000) / 1000;
  const pMaxB = (pB.P_max_wheel || 100000) / 1000;
  const power = relNorm(pMaxA, pMaxB, false); // 높을수록 좋음

  // ── [5] 회생 회수율 (%) ─────────────────────────────────────
  const getRegen = function(r) {
    if (r.wltp && r.wltp.eta_regen_pct > 0) return r.wltp.eta_regen_pct;
    if (r.brake100 && r.brake100.eta_regen_total > 0) return r.brake100.eta_regen_total;
    return 20;
  };
  const wA_regen = getRegen(rA);
  const wB_regen = getRegen(rB);
  const regen = relNorm(wA_regen, wB_regen, false); // 높을수록 좋음

  // ── [6] 열관리 마진 (%) ─────────────────────────────────────
  const T_ds_A = pA.T_batt_derate_start || 45;
  const T_ds_B = pB.T_batt_derate_start || 45;
  const T_amb_A = pA.T_amb || 25;
  const T_amb_B = pB.T_amb || 25;

  const getTbattMax = function(r, p) {
    let T_amb = p.T_amb || 25;
    if (r.wltp && r.wltp.thermalStats && r.wltp.thermalStats.T_batt_max != null) {
      let sim_T = r.wltp.thermalStats.T_batt_max;
      if (sim_T >= T_amb && sim_T <= 65) return sim_T;
    }
    if (r.wltp && r.wltp.thFinal && r.wltp.thFinal.T_batt != null) {
      let wfT = r.wltp.thFinal.T_batt;
      if (wfT >= T_amb && wfT <= 65) return wfT;
    }
    if (r.launch100 && r.launch100.thFinal && r.launch100.thFinal.T_batt != null) {
      let th_T = r.launch100.thFinal.T_batt;
      if (th_T >= T_amb && th_T <= T_amb + 35) return th_T;
    }
    let C_rate_avg = 0.15;
    let V_pack = p.V_dc || 400;
    let E_kWh = p.battery_energy || 60;
    let n_s = p.bms_cell_count_s || 96;
    let n_p = p.bms_cell_count_p || 4;
    let R_cell = (p.R0_base || 0.020) + (p.R1_base || 0.015);
    let R_pack = R_cell * n_s / n_p;
    let I_pack = (E_kWh * 1000 * C_rate_avg) / V_pack;
    let P_heat_pack = I_pack * I_pack * R_pack;
    let dT_ss = P_heat_pack * (p.R_th_bc || 0.55) * 0.4;
    return T_amb + Math.max(2, Math.min(12, dT_ss));
  };

  const T_max_A = getTbattMax(rA, pA);
  const T_max_B = getTbattMax(rB, pB);
  const thermalMarginA_pct = Math.max(0, Math.min(100, (T_ds_A - T_max_A) / Math.max(1, T_ds_A - T_amb_A) * 100));
  const thermalMarginB_pct = Math.max(0, Math.min(100, (T_ds_B - T_max_B) / Math.max(1, T_ds_B - T_amb_B) * 100));
  const T_headroom_A = T_ds_A - T_max_A;
  const T_headroom_B = T_ds_B - T_max_B;
  // 절대 헤드룸(°C) 기반 상대 정규화: 헤드룸 큰 쪽 = 100%
  // 헤드룸이 음수(초과) 가능 → 0 floor 적용 후 상대 비교
  const thA_clamp = Math.max(0, T_headroom_A);
  const thB_clamp = Math.max(0, T_headroom_B);
  const thermal = relNorm(thA_clamp, thB_clamp, false); // 높을수록 좋음

  // ── [7] SOH 유지율 (%) ──────────────────────────────────────
  const estSOH = function(p, wE, rangeKm, r) {
    if (p.soh_enable && r && r.launch100 && r.launch100.soh_state && r.launch100.soh_state.soh != null) {
      let sim_soh = r.launch100.soh_state.soh * 100;
      if (sim_soh >= 50 && sim_soh <= 100) return sim_soh;
    }
    if (p.soh_enable && r && r.sohProfile && r.sohProfile.soh_100k != null) {
      let prof_soh = r.sohProfile.soh_100k;
      if (prof_soh >= 50 && prof_soh <= 100) return prof_soh;
    }
    let T_C     = p.T_amb || 25;
    let batt_kWh = p.battery_energy || 60;
    let V_pack  = p.V_dc || 400;
    let n_p     = p.bms_cell_count_p || 4;
    let wE_safe = Math.max(8, wE || 18);
    let range_s = Math.max(100, rangeKm || 350);
    let avgDOD = 0.65;
    let n_cycles = 100000 * (wE_safe / 100) / (batt_kWh * avgDOD);
    let Ah_nom   = batt_kWh * 1000 / V_pack;
    let Ah_thrput = n_cycles * avgDOD * Ah_nom;
    let k_cyc    = 0.00020;
    let Q_cyc    = k_cyc * Math.pow(Math.max(1, Ah_thrput), 0.55);
    let t_days   = (100000 / range_s) * (365 / 300);
    let k_cal    = 0.00131;
    let T_factor = Math.pow(2, (T_C - 25) / 10);
    let Q_cal    = k_cal * T_factor * Math.sqrt(Math.max(1, t_days));
    let soh = (1 - Q_cal - Q_cyc) * 100;
    return Math.max(70, Math.min(100, soh));
  };
  let sohA = estSOH(pA, wA_e, wA_range, rA);
  let sohB = estSOH(pB, wB_e, wB_range, rB);
  const soh = relNorm(sohA, sohB, false); // 높을수록 좋음

  // ── [8] 종합 효율 (%) ───────────────────────────────────────
  const getEtaOpt = function(r) {
    if (r.steadyEff && r.steadyEff.length > 0) {
      let pts = r.steadyEff.filter(function(d) {
        return d.v_kmh >= 60 && d.v_kmh <= 100 && (d.eta_total || 0) > 50;
      });
      if (pts.length > 0) {
        let avg = pts.reduce(function(s, d) { return s + d.eta_total; }, 0) / pts.length;
        return +avg.toFixed(1);
      }
      let best = r.steadyEff.reduce(function(m, d) {
        return (d.eta_total || 0) > (m.eta_total || 0) ? d : m;
      }, { eta_total: 0 });
      if (best.eta_total > 50) return best.eta_total;
    }
    return 85;
  };
  let etaOptA = getEtaOpt(rA);
  let etaOptB = getEtaOpt(rB);
  const effScore = relNorm(etaOptA, etaOptB, false); // 높을수록 좋음

  // [Advance29-WARN06] SOH 실측 여부 플래그
  const sohA_isSim = !!(pA.soh_enable && rA && (
    (rA.launch100 && rA.launch100.soh_state && rA.launch100.soh_state.soh != null) ||
    (rA.sohProfile && rA.sohProfile.soh_100k != null)
  ));
  const sohB_isSim = !!(pB.soh_enable && rB && (
    (rB.launch100 && rB.launch100.soh_state && rB.launch100.soh_state.soh != null) ||
    (rB.sohProfile && rB.sohProfile.soh_100k != null)
  ));

  // ── [A46] dims 배열 — 상대 정규화 점수 사용 ─────────────────
  // a, b: 0~100 (best of A/B = 100%, 상대 비율)
  let dims = [
    { dim: "0→100 가속",  dimEn: "0→100 Accel",    a: accel.a,     b: accel.b,     unit: "s",         lower: true,  raw_a: +rawT_A.toFixed(1),             raw_b: +rawT_B.toFixed(1) },
    { dim: "WLTP 전비",   dimEn: "WLTP Efficiency", a: eff.a,       b: eff.b,       unit: "kWh/100km", lower: true,  raw_a: +wA_e.toFixed(1),               raw_b: +wB_e.toFixed(1) },
    { dim: "항속거리",    dimEn: "Range",            a: range.a,     b: range.b,     unit: "km",        lower: false, raw_a: Math.round(wA_range),           raw_b: Math.round(wB_range) },
    { dim: "최대 출력",   dimEn: "Max Power",        a: power.a,     b: power.b,     unit: "kW",        lower: false, raw_a: Math.round(pMaxA),              raw_b: Math.round(pMaxB) },
    { dim: "회생 회수율", dimEn: "Regen Rate",       a: regen.a,     b: regen.b,     unit: "%",         lower: false, raw_a: +wA_regen.toFixed(1),           raw_b: +wB_regen.toFixed(1) },
    { dim: "열관리 마진", dimEn: "Thermal Margin",   a: thermal.a,   b: thermal.b,   unit: "%",         lower: false, raw_a: +thermalMarginA_pct.toFixed(1), raw_b: +thermalMarginB_pct.toFixed(1) },
    { dim: "SOH 유지율",  dimEn: "SOH Retention",    a: soh.a,       b: soh.b,       unit: "%",         lower: false, raw_a: +sohA.toFixed(1),               raw_b: +sohB.toFixed(1),
      note_a: sohA_isSim ? "sim" : "est", note_b: sohB_isSim ? "sim" : "est" },
    { dim: "종합 효율",   dimEn: "Total Efficiency",  a: effScore.a,  b: effScore.b,  unit: "%",         lower: false, raw_a: +etaOptA.toFixed(1),            raw_b: +etaOptB.toFixed(1) },
  ];
  return dims;
}

// ============================================================
// [Disp11] Compare — Simulation Helpers
// ============================================================
// [Advance29-BUG06] thr 인자 추가: 현재 rlThrottle을 반영한 민감도 계산

// ── calcRDEEmissions ──
/**
 * RDE 구간별 비배기(non-exhaust) PM 및 간접 NOx/CO₂ 배출량을 산출한다.
 * 브레이크 PM: EMEP 기준 ~9 mg/km × friction_ratio.
 * 타이어 PM: UN GTR19 기반 속도 의존 ~7.5~15 mg/km.
 * NOx·CO₂: 전력망 grid_nox/co2_g_kwh [g/kWh] × 소비 에너지.
 * EU 2030 브레이크 PM ≤7 mg/km, UN GTR19 타이어 PM ≤15.3 mg/km 기준 포함.
 *
 * @param {object[]} phaseSummary  구간별 { phase, d_km, E_kWh, avg_v, eta_regen_pct }
 * @param {object}   params        grid_nox_g_kwh, grid_co2_g_kwh
 * @param {string}   cycle_type    사이클 유형 (RDE 여부 판별)
 * @returns {{ phaseEmissions, total, limits, is_rde, cycle_type }|null}
 */
function calcRDEEmissions(phaseSummary, params, cycle_type) {
  if (!phaseSummary || phaseSummary.length === 0) return null;

  const grid_nox_g_kwh  = params.grid_nox_g_kwh  || 1.2;   // 전력망 NOx [g/kWh] — 한국 평균
  const grid_co2_g_kwh  = params.grid_co2_g_kwh  || 450;   // 전력망 CO₂ [g/kWh]

  const phaseEmissions = phaseSummary.map(ph => {
    const d_km      = ph.d_km      || 0;
    const e_kWh     = Math.max(0, ph.E_kWh || 0);          // 소비 에너지 [kWh]
    const v_avg     = ph.avg_v     || 0;                    // 평균 속도 [km/h]
    const regen_pct = ph.eta_regen_pct || ph.regen_pct || 0;

    // ── PM 비배기 (Non-Exhaust) 배출 ──────────────────────────
    // ① 브레이크 마모: 회생제동으로 마찰제동 비율(friction_ratio)만큼 PM 감소
    //    EMEP 기준치: 재래식 브레이크 ~9 mg/km
    const friction_ratio   = Math.max(0, 1 - regen_pct / 100);
    const pm_brake_mg_km   = +(friction_ratio * 9.0).toFixed(2);

    // ② 타이어 마모: 속도·하중 의존, UN GTR19 기준 ~11 mg/km@저속, ~15 mg/km@고속
    const pm_tire_mg_km    = +(7.5 + (v_avg / 120) * 7.0).toFixed(2);

    // ③ 도로 재부유: 고속에서 증가하는 경험 공식
    const pm_road_mg_km    = +(3.0 * Math.sqrt(Math.max(v_avg, 5) / 50)).toFixed(2);

    const pm_total_mg_km   = +(pm_brake_mg_km + pm_tire_mg_km + pm_road_mg_km).toFixed(2);

    // ── NOx 간접 배출 (전력망 발전원 기반) ────────────────────
    const nox_indirect_mg_km = d_km > 0.01
      ? +((e_kWh / d_km) * grid_nox_g_kwh * 1000).toFixed(1) : 0;

    // ── CO₂ 간접 배출 ─────────────────────────────────────────
    const co2_indirect_g_km = d_km > 0.01
      ? +((e_kWh / d_km) * grid_co2_g_kwh).toFixed(1) : 0;

    return {
      phase: ph.phase,
      d_km, v_avg, e_kWh, regen_pct,
      pm_brake_mg_km, pm_tire_mg_km, pm_road_mg_km, pm_total_mg_km,
      nox_indirect_mg_km, co2_indirect_g_km,
    };
  });

  // 거리 가중평균 합산
  const total_d = phaseEmissions.reduce((s, p) => s + p.d_km, 0) || 1;
  const wAvg = key => +(phaseEmissions.reduce((s, p) => s + p[key] * p.d_km, 0) / total_d).toFixed(2);

  // EU 규제 참조 (2030+ 제안): 총 비배기 PM ≤ 7 mg/km (브레이크), 타이어 UN GTR19 ≤ 15.3 mg/km
  return {
    phaseEmissions,
    total: {
      pm_brake_mg_km:      wAvg('pm_brake_mg_km'),
      pm_tire_mg_km:       wAvg('pm_tire_mg_km'),
      pm_road_mg_km:       wAvg('pm_road_mg_km'),
      pm_total_mg_km:      wAvg('pm_total_mg_km'),
      nox_indirect_mg_km:  wAvg('nox_indirect_mg_km'),
      co2_indirect_g_km:   wAvg('co2_indirect_g_km'),
    },
    limits: {
      pm_brake_eu: 7.0,   // mg/km — EU 2030 제안 비배기 PM 한계
      pm_tire_un:  15.3,  // mg/km — UN GTR19 타이어 마모 한계
    },
    is_rde: !!(cycle_type && cycle_type.startsWith('RDE')),
    cycle_type,
  };
}

// ──────────────────────────────────────────────────────────────
// 도심/고속도로 복합 최적화 (도심:고속=55:45 EPA 복합, 한국 복합 60:40)
// ──────────────────────────────────────────────────────────────



// Phase tag for FTP-75

// ── correlationCoeff ──
/**
 * 두 수열의 피어슨 상관계수를 계산한다.
 *
 * @param {number[]} x  데이터 배열 x
 * @param {number[]} y  데이터 배열 y (x와 같은 길이)
 * @returns {number}  상관계수 [-1, 1]
 */
function correlationCoeff(x, y) {
  const n = x.length;
  const mx = x.reduce((s,v)=>s+v,0)/n;
  const my = y.reduce((s,v)=>s+v,0)/n;
  let num=0, dx2=0, dy2=0;
  for (let i=0;i<n;i++) {
    const ex=x[i]-mx, ey=y[i]-my;
    num+=ex*ey; dx2+=ex*ex; dy2+=ey*ey;
  }
  return num / Math.sqrt(Math.max(dx2*dy2, 1e-12));
}

// ══════════════════════════════════════════════════════════════════════════
// [R-16-01] Monte Carlo Web Worker 완전 마이그레이션
// ══════════════════════════════════════════════════════════════════════════

// [Refactor] Worker 코드 → workers/montecarlo.worker.js 로 분리

// ── sensLC_compare ──
/**
 * 주요 파라미터(m, CdA, Crr, η, T_max, P_max) ±r% 변동 시 발진·코스팅 시간 변화량을 분석한다.
 * [Advance29-BUG06] thr 인자: UI rlThrottle 값을 스로틀로 반영.
 *
 * @param {object}  p    차량 파라미터
 * @param {number}  [thr=1.0]  스로틀 개도
 * @returns {{ param, paramEn, launch_hi, launch_lo, coast_hi, coast_lo }[]}
 */
function sensLC_compare(p, thr) {
  let _thr = (thr !== undefined ? Math.max(0.05, Math.min(1.0, thr)) : 1.0);
  let bL = simLaunch(p, _thr, 100, 0), bC = simCoast(p, 100, 0);
  let facts = [
    { key: "m",              label: "질량 (m)",          labelEn: "Mass (m)",            r: 0.1 },
    { key: "CdA",            label: "공력계수 (CdA)",    labelEn: "Drag Coeff. (CdA)",  r: 0.2 },
    { key: "Crr",            label: "구름저항 (Crr)",    labelEn: "Roll Resist. (Crr)", r: 0.2 },
    { key: "eta_driveline",  label: "구동효율 (η)",      labelEn: "Drivetrain Eff. (η)", r: 0.05 },
    { key: "T_max_motor",    label: "모터토크 (T_max)",  labelEn: "Motor Torque (T_max)", r: 0.15 },
    { key: "P_max_wheel",    label: "최대출력 (P_max)",  labelEn: "Max Power (P_max)",  r: 0.15 },
  ];
  return facts.map(function (f) {
    let pH = Object.assign({}, p); pH[f.key] = p[f.key] * (1 + f.r);
    let pL = Object.assign({}, p); pL[f.key] = p[f.key] * (1 - f.r);
    let lH = simLaunch(pH, _thr, 100, 0), lL = simLaunch(pL, _thr, 100, 0); // [Advance29-BUG06] 1.0 고정 → _thr 적용
    let cH = simCoast(pH, 100, 0), cL = simCoast(pL, 100, 0);
    return {
      param: f.label,
      paramEn: f.labelEn,
      launch_hi: +((lH.time || 0) - (bL.time || 0)).toFixed(2),
      launch_lo: +((lL.time || 0) - (bL.time || 0)).toFixed(2),
      coast_hi: +((cH.time || 0) - (bC.time || 0)).toFixed(1),
      coast_lo: +((cL.time || 0) - (bC.time || 0)).toFixed(1),
    };
  });
}

// ── sensHeat_compare ──
/**
 * CdA ± [12,8,4,0,-4,-8,-12]%, Crr ± 동일 범위 조합에서 WLTP 전비 변화율을 계산한다.
 *
 * @param {object} p  차량 파라미터
 * @returns {{ data: { CdA_pct, Crr_pct, delta_e }[], baseE: number }}
 */
function sensHeat_compare(p) {
  let steps = [-12, -8, -4, 0, 4, 8, 12];
  let baseR = simWLTP(p); let baseE = baseR.e_wltp || 15; let data = [];
  steps.forEach(function (dc) {
    steps.forEach(function (dr) {
      let mod = Object.assign({}, p, { CdA: p.CdA * (1 + dc / 100), Crr: p.Crr * (1 + dr / 100) });
      let e = simWLTP(mod).e_wltp || baseE;
      data.push({ CdA_pct: dc, Crr_pct: dr, delta_e: +(((e - baseE) / baseE) * 100).toFixed(2) });
    });
  });
  return { data: data, baseE: baseE };
}

// ── steadyAt_cmp ──
/**
 * 정속 vk [km/h]에서 도로 저항력·전기 소비 전력·Wh/km을 산출한다 (비교 경량 계산).
 * [Advance29-WARN04] CrrDynamic DI 적용. [CMP-FIX-06] Wh/km = P_elec/(v·3.6).
 *
 * @param {object} p   차량 파라미터
 * @param {number} vk  속도 [km/h]
 * @returns {{ v_kmh, Wh_km, P_road_kW, P_elec_kW }}
 */
function steadyAt_cmp(p, vk) {
  let v = vk / 3.6, th = (p.grade_deg || 0) * Math.PI / 180;
  let Fa = 0.5 * p.rho * p.CdA * v * v;
  // [Advance29-WARN04] 고정 Crr → CrrDynamic(v, T_tire, p) 적용 — 속도·온도 의존 동적 Crr
  let T_tire = p.T_amb || 25; // tire ≈ ambient (정적 계산 시 합리적 근사)
  let Crr_eff = (_CrrDynamic !== null) ? _CrrDynamic(v, T_tire, p) : p.Crr;
  let Fr = p.m * 9.81 * Crr_eff * Math.cos(th);
  let Fg = p.m * 9.81 * Math.sin(th);
  let Ft = Fa + Fr + Fg, Pr = Ft * v;
  let Pe = Pr / (p.eta_driveline * (p.eta_motor_inv || 0.91)) + (p.aux_power || 1500);
  // [CMP-FIX-06] Wh_km 단위 수정: Pe[W]/v[m/s] = J/m → Wh/km = J/m / 3.6
  // 기존: Pe/(v*1000)*1000 = Pe/v (3.6배 과대) → 수정: Pe/(v*3.6)
  let wh = v > 0.01 ? Pe / (v * 3.6) : 0;
  return { v_kmh: vk, Wh_km: +wh.toFixed(1), P_road_kW: +(Pr / 1000).toFixed(1), P_elec_kW: +(Pe / 1000).toFixed(1) };
}

// ── vCross_cmp ──
/**
 * 공기저항 = 구름저항이 되는 교차 속도(크로스오버 속도)를 탐색한다.
 *
 * @param {object} p  차량 파라미터 (rho, CdA, m, Crr)
 * @returns {number}  교차 속도 [km/h] (없으면 0)
 */
function vCross_cmp(p) {
  for (let v = 10; v <= 200; v++) {
    let vm = v / 3.6;
    if (0.5 * p.rho * p.CdA * vm * vm >= p.m * 9.81 * p.Crr) return v;
  }
  return 0;
}



// ══════════════════════════════════════════════════════════════════════════════
// §  DOE / BAYESIAN OPTIMIZATION ENGINE  [Refactoring_06 신규]
// ══════════════════════════════════════════════════════════════════════════════
// [DOE-1]  2^k 완전 요인 설계 (Full Factorial)
// [DOE-2]  Gaussian Process 서로게이트 + EI 기반 Bayesian 최적화
// [DOE-3]  신뢰 구간 자동 계산 (t-분포 / Bootstrap)
// [DOE-4]  통계 보고서 CSV 자동 출력
// ══════════════════════════════════════════════════════════════════════════════

/* ── [DOE-1] 2^k 완전 요인 설계 ─────────────────────────────────────────── */

// ── estVmax_cmp ──
/**
 * 구동력 ≥ 도로 저항력 조건으로 최고 속도를 역방향 탐색한다 (250→0 km/h).
 *
 * @param {object} p  차량 파라미터
 * @returns {number}  추정 최고 속도 [km/h]
 */
function estVmax_cmp(p) {
  for (let v = 250; v > 0; v--) {
    let vm = v / 3.6, wm = (vm / p.r_wheel) * p.gear_ratio;
    if (wm < 0.1) wm = 0.1;
    let Pp = Math.min(p.P_max_wheel, p.P_max_battery * p.eta_driveline);
    let Tw = Math.min(p.T_max_motor, Pp / wm) * p.gear_ratio * p.eta_driveline;
    let Fd = Math.min(Tw / p.r_wheel, p.mu * p.m * 9.81);
    let Ft = 0.5 * p.rho * p.CdA * vm * vm + p.m * 9.81 * p.Crr;
    if (Fd >= Ft) return v;
  }
  return 0;
}

// ── findVopt_cmp ──
/**
 * Wh/km 최소 지점을 탐색하여 최적 경제 속도를 반환한다 (30~200 km/h).
 * [CMP-FIX-07] 탐색 상한 130→200 km/h 확장 (고효율 차량 대응).
 *
 * @param {object} p  차량 파라미터
 * @returns {number}  최적 경제 속도 [km/h]
 */
function findVopt_cmp(p) {
  let b = 60, be = 999;
  // [CMP-FIX-07] 탐색 상한 130→200 km/h: Tesla 등 고효율 차량의 최적 경제속도가 130을 초과할 수 있음
  for (let v = 30; v <= 200; v++) { let c = steadyAt_cmp(p, v); if (c.Wh_km < be) { be = c.Wh_km; b = v; } }
  return b;
}

// ── latinHypercubeSample ──
/**
 * 라틴 하이퍼큐브 샘플링(LHS)으로 n × k 파라미터 샘플 배열을 생성한다.
 * 각 파라미터는 n구간으로 분할 후 무작위 순열로 셔플하여 공간 균등성 보장.
 *
 * @param {number} n              샘플 수
 * @param {Object.<string,[number,number]>} params_ranges  { key: [lo, hi] }
 * @returns {object[]}  n개 샘플 객체 배열
 */
function latinHypercubeSample(n, params_ranges) {
  // LHS: n samples, k parameters
  const keys = Object.keys(params_ranges);
  const k = keys.length;
  const samples = Array.from({ length: n }, (_, i) => ({}));
  for (const key of keys) {
    const [lo, hi] = params_ranges[key];
    // Permuted intervals
    const perm = Array.from({ length: n }, (_, i) => i).sort(() => Math.random() - 0.5);
    for (let i = 0; i < n; i++) {
      const u = (perm[i] + Math.random()) / n;
      samples[i][key] = lo + u * (hi - lo);
    }
  }
  return samples;
}

// [R-06-H deleted] sobolSensitivityIndex

// ── generatePassFailReport ──
/**
 * 시뮬레이션 결과에 대해 항목별 합격/불합격/경고 판정 보고서를 생성한다.
 * 판정 항목: 0→100 km/h, WLTP 전비, 회생 효율, 모터·배터리 최고 온도, 브레이크 거리 등.
 * [PF-FIX-01] T_motor/batt_max: wltp.thermalStats 우선, fallback launch100 전체 스캔.
 * [PF-FIX-02] toFixed 안전 적용 (typeof 기반).
 *
 * @param {object} results  runFullSim / runHeavySim 반환값
 * @param {object} params   차량 파라미터
 * @returns {object[]}  { label, val, unit, min, max, status, reason, hint, cat }[]
 */
function generatePassFailReport(results, params) {
  const r = results;
  const WARN  = '⚠️ WARN';
  const PASS  = '✅ PASS';
  const FAIL  = '❌ FAIL';

  // [PF-FIX-02] val 포맷: +val.toFixed 는 Function → NaN → 항상 falsy → 반올림 미적용 버그
  //   수정: typeof val === 'number' 로 판단하여 toFixed(2) 정확히 적용
  function check(val, min, max, label, unit, labelEn, cat, hint, hintEn) {
    if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
      return { label, labelEn: labelEn||label, val: '–', unit, min, max, cat: cat||'기타', status: WARN, reason: '데이터 없음', reasonEn: 'No data', hint: hint||'', hintEn: hintEn||'' };
    }
    const ok = (min === null || val >= min) && (max === null || val <= max);
    // [PF-FIX-04] reason 텍스트: 숫자-단위 사이 공백 추가 (예: "20 s 미만" → 가독성 개선)
    const reason = !ok
      ? (val < min ? `하한 ${min} ${unit} 미만` : `상한 ${max} ${unit} 초과`)
      : '정상 범위';
    const reasonEn = !ok
      ? (val < min ? `Below lower limit ${min} ${unit}` : `Exceeds upper limit ${max} ${unit}`)
      : 'Normal range';
    // [PF-FIX-02] typeof 기반 정확한 타입 판단
    return { label, labelEn: labelEn||label, val: typeof val === 'number' ? +val.toFixed(2) : val, unit, min, max, cat: cat||'기타', status: ok ? PASS : FAIL, reason, reasonEn, hint: hint||'', hintEn: hintEn||'' };
  }

  const pf = params;
  const launch_t   = r.launch100?.time;
  const wltp_kwh   = r.wltp?.e_wltp;
  const regen_pct  = r.wltp?.eta_regen_pct;

  // [PF-FIX-01] T_motor_max/T_batt_max: 잘못된 소스 → 올바른 소스로 수정
  //   수정 전: r.launch100?.thFinal?.T_motor/T_batt — 30초 launch 시뮬레이션 종료 시점 온도
  //            thFinal은 시뮬레이션 마지막 스텝의 열 상태값이므로 최고 온도가 아닐 수 있음
  //            launch 30초(7~9 s 가속 + 이후 감속) 동안의 열 부하는 WLTP 1800초보다 훨씬 낮음
  //   수정 후: r.wltp?.thermalStats?.T_motor_max / T_batt_max 우선 사용
  //            (WLTP 전 구간에서 실시간 maxT_motor/maxT_batt 추적 — 실제 최고 온도)
  //            WLTP 없을 때 fallback: launch100 데이터 전체 스캔 최대값 → thFinal 순
  const T_motor_max = r.wltp?.thermalStats?.T_motor_max
    ?? (r.launch100?.data ? Math.max(...r.launch100.data.map(function(d){ return d.T_motor_temp||0; })) : null)
    ?? r.launch100?.thFinal?.T_motor;
  const T_batt_max  = r.wltp?.thermalStats?.T_batt_max
    ?? (r.launch100?.data ? Math.max(...r.launch100.data.map(function(d){ return d.T_batt||0; })) : null)
    ?? r.launch100?.thFinal?.T_batt;

  // [A68-06-Fix-PF2] eta_at_target = v_target 도달 시점 효율 (정확한 0→100 구간)
  //   eta_cycle은 30초 전체 누적이라 0→100 구간 효율을 과소/과대 추정 가능
  //   [FIX-ETA] 항목명을 실제 소스(발진 구간 효율)와 일치하도록 수정
  //     수정 전: '사이클 효율' — WLTP 사이클 효율(85~92%)로 오해 유발
  //     수정 후: '발진 파워트레인 효율' — 풀 부하 급가속(60~75%) 수준으로 물리적 타당
  const eta_total   = r.launch100?.eta_at_target ?? r.launch100?.eta_cycle;

  // [FIX-REACCEL] 하한 0.5s 제거 → null (물리 불가능: 최고 성능 EV도 60→100 재가속 최소 3~4s)
  //   하한이 존재할 물리적 근거 없음 — 상한(pf_reaccel_max)만 의미 있음
  const reaccel_t   = r.reaccel100?.time;

  // [A68-06-Fix-PF1] centralDiff[0]은 CdA가 아닐 수 있음 → key 탐색으로 수정
  // [FIX-CDA] S_launch → S_range: CdA는 저속 발진 구간에서 영향 미미(F_aero ∝ v²)
  //   S_launch 기준 CdA 민감도는 항상 낮아 사실상 항상 PASS → 의미 없음
  //   S_range(항속거리 민감도) 기준이 물리적으로 올바른 CdA 검증 지표
  const sens_cda    = r.sens?.centralDiff?.find(function(d){ return d.key==='CdA'; })?.S_range;

  // [FIX-COAST] coast 시간(100→0 400~800s) → coast 주행거리로 교체
  //   실제 EV 100 km/h 관성 주행 종단 시간: 400~800s → 상한 120s는 물리 불가능, 항상 FAIL
  //   dist_m은 공기저항·구름저항 성능의 유효 대리 지표 (낮을수록 손실 큼)
  const coast_dist  = r.coast?.dist_m;

  // [ADD] 누락 항목: 제동 거리 / 회생 제동 비율 / 항속거리 / 발진 거리
  const stop_dist   = r.brake100?.stopping_distance_m;
  const brake_regen = r.brake100?.eta_regen_total;   // % of KE recovered
  const range_km    = r.wltp?.range_km;
  const launch_dist = r.launch100?.dist_at_target;

  const items = [
    check(launch_t,    pf.pf_launch100_min||2,      pf.pf_launch100_max||8,     '0→100 km/h 시간',         's',         '0→100 km/h Time',              '성능',   'pf_launch100_max 상향 또는 T_max_motor·P_max_wheel 증가', 'Increase pf_launch100_max or raise T_max_motor / P_max_wheel'),
    // [FIX-REACCEL] 하한 null — 재가속이 빠를수록 좋으므로 하한 기준 불필요
    check(reaccel_t,   null,                         pf.pf_reaccel_max||5.0,     '60→100 km/h 재가속',      's',         '60→100 km/h Re-accel',         '성능',   'pf_reaccel_max 상향 또는 P_max_wheel·gear_ratio 조정', 'Increase pf_reaccel_max or tune P_max_wheel / gear_ratio'),
    // [FIX-COAST] 시간 체크 제거 → 주행거리 체크 (단위: m, 낮으면 손실 큼)
    check(coast_dist,  pf.pf_coast_dist_min||2000,   null,                       'coast 주행거리 (100→0)',   'm',         'Coast Distance (100→0)',        '성능',   'Crr·CdA 감소 또는 pf_coast_dist_min 하향 조정', 'Reduce Crr / CdA or lower pf_coast_dist_min'),
    check(wltp_kwh,    pf.pf_wltp_min_kWh||8,       pf.pf_wltp_max_kWh||30,     'WLTP 전비',               'kWh/100km', 'WLTP Efficiency',               '에너지', 'CdA·Crr 감소, eta_driveline 개선 또는 pf_wltp_max_kWh 재검토', 'Reduce CdA/Crr, improve eta_driveline or review pf_wltp_max_kWh'),
    check(regen_pct,   pf.pf_regen_min_pct||10,     pf.pf_regen_max_pct||60,    '회생 에너지 비율',        '%',         'Regen Energy Ratio',            '에너지', 'P_max_regen·regen_blend 증가 또는 pf_regen_min_pct 검토', 'Increase P_max_regen / regen_blend or review pf_regen_min_pct'),
    // [FIX-ETA] 항목명 수정: '사이클 효율' → '발진 파워트레인 효율' (소스 = launch 구간)
    check(eta_total,   pf.pf_eta_total_min||60,      100,                        '발진 파워트레인 효율',    '%',         'Launch Powertrain Efficiency',  '에너지', 'K_cu·K_fe·K_inv 보정 또는 eta_driveline 증가', 'Calibrate K_cu/K_fe/K_inv or increase eta_driveline'),
    check(T_motor_max, null,                         pf.pf_T_motor_max||120,     '모터 최고 온도 (WLTP)',   '°C',        'Motor Max Temp (WLTP)',         '열관리', 'R_th_mc 감소, UA_rad 증가 또는 pf_T_motor_max 상향 조정', 'Reduce R_th_mc, increase UA_rad or raise pf_T_motor_max'),
    check(T_batt_max,  null,                         pf.pf_T_batt_max||45,       '배터리 최고 온도 (WLTP)', '°C',        'Battery Max Temp (WLTP)',       '열관리', 'R_th_bc 감소, UA_rad 증가 또는 pf_T_batt_max 상향 조정', 'Reduce R_th_bc, increase UA_rad or raise pf_T_batt_max'),
    // [FIX-CDA] S_launch → S_range (CdA는 고속·항속거리에 영향, 발진 저속 구간에선 거의 무관)
    check(sens_cda,    null,                         pf.pf_sens_cda_max||0.5,    'CdA 민감도 지수',         '',          'CdA Sensitivity Index',         '민감도', 'CdA 수치 점검 또는 pf_sens_cda_max 상향 조정', 'Review CdA value or raise pf_sens_cda_max'),
    // [ADD-BRAKE] EuroNCAP 기준: 100→0 제동 거리 < 45 m
    check(stop_dist,   null,                         pf.pf_stop_dist_max||45,    '100→0 제동 거리',         'm',         'Braking Distance 100→0',       '제동',   'brake_force_max 증가 또는 pf_stop_dist_max 상향 조정', 'Increase brake_force_max or raise pf_stop_dist_max'),
    // [ADD-BRAKE-REGEN] EV 특성: 회생 제동 비율 > 40 %
    check(brake_regen, pf.pf_brake_regen_min||40,   null,                        '회생 제동 비율',          '%',         'Regen Braking Ratio',           '제동',   'P_max_regen·regen_blend 증가 또는 pf_brake_regen_min 하향', 'Increase P_max_regen / regen_blend or lower pf_brake_regen_min'),
    // [ADD-RANGE] 항속거리: 사용자 목표값 기준 (pf_range_min 미설정 시 100 km)
    check(range_km,    pf.pf_range_min||100,         null,                        'WLTP 항속거리',           'km',        'WLTP Range',                   '에너지', 'battery_energy 증가, CdA·Crr 감소 또는 pf_range_min 하향', 'Increase battery_energy, reduce CdA/Crr or lower pf_range_min'),
    // [ADD-LAUNCH-DIST] 발진 거리 < 350 m (공간 효율 — 급가속 시 이동 거리)
    check(launch_dist, null,                         pf.pf_launch_dist_max||350, '발진 거리 (0→100)',       'm',         'Launch Distance (0→100)',      '성능',   'τ_torque 감소 또는 pf_launch_dist_max 상향 조정', 'Reduce τ_torque or raise pf_launch_dist_max'),
  ];

  const pass_cnt = items.filter(i => i.status === PASS).length;
  const fail_cnt = items.filter(i => i.status === FAIL).length;
  const warn_cnt = items.filter(i => i.status === WARN).length;
  const overall  = fail_cnt === 0 ? (warn_cnt === 0 ? PASS : WARN) : FAIL;

  return {
    items,
    summary: { total: items.length, pass: pass_cnt, fail: fail_cnt, warn: warn_cnt },
    overall,
    timestamp: new Date().toISOString(),
    vehicle: params.shortName || 'Custom',
  };
}

// ── verifySSTConstants ─────────────────────────────────────────────────────
/**
 * [Fix-Issue2] SST 인라인 복사본 불일치 런타임 감지
 *
 * simulation.js는 zero-import 설계이므로 ROAD_MU·DRIVE_MODE_GAIN_TABLE을
 * 직접 import할 수 없어 인라인 복사본을 유지한다. 이 함수를 앱 초기화 시
 * 호출하면 복사본과 원본 SST 값을 비교해 불일치를 조기에 발견할 수 있다.
 *
 * 사용 (ev-sim_v2_0.jsx 또는 main.jsx 초기화 블록):
 *   import { ROAD_MU } from '../constants/physics_consts.js';
 *   import { DRIVE_MODE_GAIN_TABLE } from '../core/control/drive_mode_config.js';
 *   import { verifySSTConstants } from '../core/physics/simulation.js';
 *   verifySSTConstants(ROAD_MU, DRIVE_MODE_GAIN_TABLE);
 *
 * @param {object} ext_ROAD_MU           physics_consts.js 의 ROAD_MU 원본
 * @param {object} [ext_DRIVE_GAIN_TABLE] drive_mode_config.js 의 테이블 원본 (선택)
 * @returns {{ ok: boolean, errors: string[] }}
 */
function verifySSTConstants(ext_ROAD_MU, ext_DRIVE_GAIN_TABLE, ext_stubs) {
  const errors = [];

  // ① ROAD_MU 비교
  if (ext_ROAD_MU && typeof ext_ROAD_MU === 'object') {
    // ① ROAD_MU 비교: simulation.js 내 인라인 ROAD_MU 값과 외부 원본 비교
    const muKeys = ['dry', 'wet', 'snow', 'ice'];
    for (const k of muKeys) {
      if (ROAD_MU[k] !== ext_ROAD_MU[k]) {
        errors.push(
          `[SST] ROAD_MU.${k} 불일치: simulation.js=${ROAD_MU[k]}, physics_consts.js=${ext_ROAD_MU[k]}`
        );
      }
    }
    // [Fix-SST] ROAD_MU_MAP은 제거됨 — 중복 ROAD_MU_MAP_CHECK 블록 삭제
    // (ROAD_MU 단일 참조로 통합됐으므로 인라인 복사본과의 비교는 불필요)
  }

  // ② DRIVE_MODE_GAIN_TABLE 비교 (선택)
  if (ext_DRIVE_GAIN_TABLE && typeof ext_DRIVE_GAIN_TABLE === 'object') {
    const modes = Object.keys(DRIVE_MODE_GAIN_TABLE_SIM);
    for (const mode of modes) {
      const simRow = DRIVE_MODE_GAIN_TABLE_SIM[mode];
      const extRow = ext_DRIVE_GAIN_TABLE[mode];
      if (!extRow) {
        errors.push(`[SST] DRIVE_MODE_GAIN_TABLE 모드 누락: ${mode}`);
        continue;
      }
      for (const key of Object.keys(simRow)) {
        if (simRow[key] !== extRow[key]) {
          errors.push(
            `[SST] DRIVE_MODE_GAIN_TABLE.${mode}.${key}: sim=${simRow[key]}, sst=${extRow[key]}`
          );
        }
      }
    }
  }

  // ③ [Fix-StubVerify] init2RCBatteryState / initRLSMuState 스텁 반환형 검증
  //    _shared_stubs.js 원본 함수를 ext_stubs로 전달받아 더미 params로 호출,
  //    반환 객체의 키 집합이 인라인 스텁과 동일한지 비교.
  //
  //    사용 (초기화 블록):
  //      import { init2RCBatteryState, initRLSMuState } from './_shared_stubs.js';
  //      verifySSTConstants(ROAD_MU, DRIVE_MODE_GAIN_TABLE,
  //        { init2RCBatteryState, initRLSMuState });
  if (ext_stubs && typeof ext_stubs === 'object') {
    const dummyParams = { soc_init: 0.8, T_amb: 25, mu: 1.0, rls_P_init: 1.0 };

    if (typeof ext_stubs.init2RCBatteryState === 'function') {
      const extKeys = Object.keys(ext_stubs.init2RCBatteryState(dummyParams)).sort().join(',');
      const simKeys = Object.keys(init2RCBatteryState(dummyParams)).sort().join(',');
      if (extKeys !== simKeys) {
        errors.push(
          `[SST] init2RCBatteryState 반환 키 불일치:\n` +
          `  simulation.js 스텁: [${simKeys}]\n` +
          `  _shared_stubs.js:   [${extKeys}]`
        );
      }
    }

    if (typeof ext_stubs.initRLSMuState === 'function') {
      const extKeys = Object.keys(ext_stubs.initRLSMuState(dummyParams)).sort().join(',');
      const simKeys = Object.keys(initRLSMuState(dummyParams)).sort().join(',');
      if (extKeys !== simKeys) {
        errors.push(
          `[SST] initRLSMuState 반환 키 불일치:\n` +
          `  simulation.js 스텁: [${simKeys}]\n` +
          `  _shared_stubs.js:   [${extKeys}]`
        );
      }
    }
  }

  if (errors.length > 0) {
    console.error(
      '[simulation.js] SST 불일치 발견 — 인라인 복사본을 SST 원본과 동기화하세요.\n' +
      errors.join('\n')
    );
  }

  return { ok: errors.length === 0, errors };
}

// ── [Fix-DI-warn] DI 미호출 런타임 경고 ──────────────────────────────────────
// 모듈 평가 완료 후 다음 태스크에서 미주입 상태를 감지해 경고한다.
// setTimeout(0) 사용 이유: 앱 초기화 코드(main.jsx)가 import 직후 동기적으로
// setSSTConstants() / setPhysicsFunctions()를 호출할 시간을 보장하기 위함.
if (typeof setTimeout !== 'undefined') {
  setTimeout(() => {
    if (!_sstInjected) {
      console.warn(
        '[simulation.js] setSSTConstants() 미호출 — ROAD_MU / G_GRAVITY / DRIVE_MODE_GAIN_TABLE이\n' +
        '인라인 폴백값으로 동작 중입니다. 앱 초기화 시 다음을 호출하세요:\n' +
        '  import { ROAD_MU, G_GRAVITY } from "constants/physics_consts.js";\n' +
        '  import { DRIVE_MODE_GAIN_TABLE } from "core/control/drive_mode_config.js";\n' +
        '  setSSTConstants(ROAD_MU, G_GRAVITY, DRIVE_MODE_GAIN_TABLE);'
      );
    }
    if (!_physFnInjected) {
      const missing = [
        _motorFOCStep           ? null : 'motorFOCStep',
        _sensorlessObserver     ? null : 'sensorlessObserver',
        _initFOCState           ? null : 'initFOCState',
        _steeringFeedbackModel  ? null : 'steeringFeedbackModel',
        _initBetaEKF            ? null : 'initBetaEKF',
        _ekfUpdateBeta          ? null : 'ekfUpdateBeta',
        _initVDControlState     ? null : 'initVDControlState',
        _vdControlStep          ? null : 'vdControlStep',
        _computeVerticalLoad_v4 ? null : 'computeVerticalLoad_v4',
        _CrrDynamic             ? null : 'CrrDynamic',
      ].filter(Boolean);
      console.warn(
        '[simulation.js] setPhysicsFunctions() 미호출 — 아래 함수들이 인라인 근사값 폴백으로 동작 중입니다:\n' +
        '  ' + missing.join(', ') + '\n' +
        '앱 초기화 시 setPhysicsFunctions({ motorFOCStep, sensorlessObserver, ... })를 호출하세요.'
      );
    }
  }, 0);
}

// ╚══════════════════════════════════════════════════════════════════╝
// END OF v7 물리 / 제어 모듈
// ╚══════════════════════════════════════════════════════════════════╝

// ── [RevB] simRDE — 실도로 주행 (RDE, Real Driving Emissions) 사이클 ────────
// EU Regulation 2017/1151 Annex IIIA 기반 도심/교외/고속 3구간 통계적 생성
// WLTP 대비 실도로 동력 요구 반영 (온도·경사·급가감속 포함)
//
// 입력:  params — 차량 파라미터 (simWLTP와 동일 스키마)
//        options — { urban_km:16, rural_km:16, motorway_km:16,
//                    T_amb:15, grade_urban:0.5, grade_rural:1.0,
//                    v_motorway:120 }
// 반환:  { e_rde_kWh100, phases:{urban,rural,motorway}, data[], rde_pass }
export function simRDE(params, options = {}) {
  const {
    urban_km      = 16,     // 도심 구간 [km] (EU 기준 ≥16km)
    rural_km      = 16,     // 교외 구간 [km]
    motorway_km   = 16,     // 고속 구간 [km]
    T_amb         = 15,     // 환경 온도 [°C] (RDE: -7~35°C)
    grade_urban   = 0.5,    // 도심 평균 경사 [%]
    grade_rural   = 1.0,    // 교외 평균 경사 [%]
    grade_motorway= 0.5,    // 고속 평균 경사 [%]
    v_motorway    = 120,    // 고속 구간 평균 속도 [km/h]
    rde_cf        = 1.5,    // RDE 허용 배율 (Conformity Factor)
  } = options;

  const g = 9.80665;
  const dt = 1.0;  // 1Hz 시뮬레이션

  // RDE 3구간 속도 프로파일 생성 (통계적 근사)
  // 도심: 0~60 km/h, 교외: 60~90, 고속: 90~145
  function makePhaseProfile(v_avg_kmh, dist_km, grade_pct) {
    const v_avg = v_avg_kmh / 3.6;
    const t_total = (dist_km * 1000) / v_avg;  // 예상 시간 [s]
    const grade_rad = Math.atan(grade_pct / 100);
    const profile = [];
    let v = 0, d = 0;
    for (let t = 0; d < dist_km * 1000 && t < t_total * 2; t++) {
      // 간단한 속도 변동 모델: 평균 주변 정규 노이즈 + 정차 확률
      const stop_prob = v_avg_kmh < 50 ? 0.04 : 0.01;  // 도심 정차 확률
      let v_target = v_avg * (1 + 0.3 * (Math.random() - 0.5));
      if (Math.random() < stop_prob) v_target = 0;
      v_target = Math.max(0, Math.min(v_target, (v_avg_kmh <= 60 ? 60 : v_avg_kmh <= 90 ? 90 : 145) / 3.6));
      const a = Math.max(-3.5, Math.min(2.5, (v_target - v) / dt));
      v = Math.max(0, v + a * dt);
      d += v * dt;
      profile.push({ t, v_ms: v, v_kmh: +(v * 3.6).toFixed(1), grade_rad, d_m: d });
    }
    return profile;
  }

  const urban_profile    = makePhaseProfile(30, urban_km, grade_urban);
  const rural_profile    = makePhaseProfile(70, rural_km, grade_rural);
  const motorway_profile = makePhaseProfile(v_motorway, motorway_km, grade_motorway);

  const mu_eff   = ROAD_MU[params.road_condition] || params.mu || 0.8;
  const m_eff    = params.m * 1.04;  // 회전관성 보정
  let thState    = { T_batt: T_amb, T_inv: T_amb, T_motor: T_amb, T_cool: T_amb };
  let soc        = params.soc_init;

  function runPhase(profile) {
    let E_J = 0, d_m = 0, n_stop = 0, v_max = 0;
    for (const pt of profile) {
      const v = pt.v_ms;
      if (v < 0.5) { n_stop++; continue; }
      v_max = Math.max(v_max, v * 3.6);
      const omega_m = v / params.r_wheel * params.gear_ratio;
      const F_res   = m_eff * g * (params.Cr || 0.012) + 0.5 * (params.rho_air || 1.225) * (params.Cd || 0.28) * (params.A_front || 2.35) * v * v + m_eff * g * Math.sin(pt.grade_rad);
      const T_mot   = motorTorqueSpeed(omega_m, 0.8, params, thState.T_motor, thState.T_inv, thState.T_batt);
      const Iq      = T_mot / (1.5 * (params.poles / 2) * params.lambda_pm);
      const motor   = computeMotorLosses(omega_m, T_mot, 0, Iq, params, thState.T_motor);
      const inv     = computeInverterLosses(Math.abs(Iq), thState.T_inv, params);
      const P_mech  = Math.max(0, F_res * v);
      const P_dc    = P_mech + motor.P_total + inv.P_total;
      E_J          += P_dc * dt;
      soc           = socUpdate(soc, P_dc, dt, params);
      d_m          += v * dt;
    }
    return { E_kWh: E_J / 3.6e6, d_km: d_m / 1000, n_stop, v_max_kmh: +v_max.toFixed(1) };
  }

  const urban    = runPhase(urban_profile);
  const rural    = runPhase(rural_profile);
  const motorway = runPhase(motorway_profile);

  const total_E_kWh = urban.E_kWh + rural.E_kWh + motorway.E_kWh;
  const total_d_km  = urban.d_km  + rural.d_km  + motorway.d_km;
  const e_rde       = total_d_km > 0 ? +(total_E_kWh / total_d_km * 100).toFixed(2) : 0;

  // WLTP 대비 RDE 보정 계수 (실도로 실효 전비 비율)
  const e_wltp_ref = params._wltp_e100 || e_rde * 0.88;  // WLTP 참조 전비
  const rde_cf_actual = e_rde / Math.max(e_wltp_ref, 1);

  return {
    e_rde_kWh100:  e_rde,
    e_wltp_ref:    +e_wltp_ref.toFixed(2),
    rde_cf_actual: +rde_cf_actual.toFixed(3),
    rde_pass:      rde_cf_actual <= rde_cf,
    soc_final:     +soc.toFixed(3),
    phases: { urban, rural, motorway },
    total_d_km:    +total_d_km.toFixed(1),
    total_E_kWh:   +total_E_kWh.toFixed(3),
    T_amb,
  };
}


// ── [RevC-C03] simRDEMonteCarlo — RDE CF 통계 검증 ──────────────────────
// EU Regulation 2017/1151 §3.2 준수 확인
// N회 RDE 시뮬레이션으로 CF(Conformity Factor) 분포 산출
// CF = e_rde / e_wltp_ref — EU 기준 CF ≤ 1.5 (전기차 적합성)
//
// 입력:
//   params      — 차량 파라미터
//   options     — { N:1000, cf_limit:1.5, rde_opts:{urban_km,rural_km,motorway_km} }
//   onProgress  — (done, total) => void (선택적 진행률 콜백)
// 반환:
//   { cf_dist, cf_mean, cf_p10, cf_p50, cf_p90, cf_p95, pass_rate,
//     compliant, e_rde_mean, e_wltp_ref, N }
export function simRDEMonteCarlo(params, options = {}, onProgress = null) {
  const {
    N           = 1000,
    cf_limit    = 1.5,
    rde_opts    = { urban_km: 16, rural_km: 16, motorway_km: 16 },
    T_amb_range = [-7, 35],    // EU RDE 허용 환경 온도 범위 [°C]
    seed_mode   = 'random',    // 'random' | 'quasi' (준난수)
  } = options;

  const results = [];

  for (let i = 0; i < N; i++) {
    // 환경 변수 무작위 샘플링 (균일 분포)
    const T_amb   = T_amb_range[0] + Math.random() * (T_amb_range[1] - T_amb_range[0]);
    const d_var   = 0.85 + Math.random() * 0.30;  // 구간 거리 ±15% 변동
    const grade_u = Math.random() * 1.5;           // 도심 경사 0~1.5%
    const grade_r = 0.5 + Math.random() * 2.0;    // 교외 경사 0.5~2.5%
    const v_mwy   = 100 + Math.random() * 50;     // 고속 속도 100~150 km/h

    try {
      const r = simRDE(params, {
        ...rde_opts,
        urban_km:     rde_opts.urban_km    * d_var,
        rural_km:     rde_opts.rural_km    * d_var,
        motorway_km:  rde_opts.motorway_km * d_var,
        T_amb,
        grade_urban:    grade_u,
        grade_rural:    grade_r,
        v_motorway:     v_mwy,
      });

      results.push({
        e_rde:   r.e_rde_kWh100,
        cf:      r.rde_cf_actual,
        T_amb:   +T_amb.toFixed(1),
        pass:    r.rde_cf_actual <= cf_limit,
      });
    } catch (_) {
      // 개별 시뮬 실패 시 스킵 (통계에 불포함)
    }

    if (onProgress && i % 50 === 0) onProgress(i, N);
  }

  if (results.length === 0) return { error: 'All simulations failed', N };

  // 통계 계산
  const cfs   = results.map(r => r.cf).sort((a, b) => a - b);
  const e_rdes = results.map(r => r.e_rde);
  const n      = cfs.length;
  const pct    = (p) => cfs[Math.min(n - 1, Math.floor(p * n))];
  const mean   = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const std    = (arr) => {
    const m = mean(arr);
    return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length);
  };

  const pass_count = results.filter(r => r.pass).length;

  return {
    N:             n,
    cf_mean:       +mean(cfs).toFixed(4),
    cf_std:        +std(cfs).toFixed(4),
    cf_p05:        +pct(0.05).toFixed(4),
    cf_p10:        +pct(0.10).toFixed(4),
    cf_p25:        +pct(0.25).toFixed(4),
    cf_p50:        +pct(0.50).toFixed(4),
    cf_p75:        +pct(0.75).toFixed(4),
    cf_p90:        +pct(0.90).toFixed(4),
    cf_p95:        +pct(0.95).toFixed(4),
    cf_max:        +cfs[n - 1].toFixed(4),
    cf_min:        +cfs[0].toFixed(4),
    cf_limit,
    pass_count,
    pass_rate:     +(pass_count / n * 100).toFixed(2),
    compliant:     pct(0.95) <= cf_limit,   // P95 기준 적합 판정
    e_rde_mean:    +mean(e_rdes).toFixed(2),
    e_rde_std:     +std(e_rdes).toFixed(2),
    cf_distribution: cfs,   // 원시 분포 (히스토그램 렌더링용)
  };
}

// ── generateNEDCProfile ── (NEDC 레거시 사이클, ECE 15 + EUDC)
/**
 * NEDC (New European Driving Cycle) 프로파일 생성
 *
 * ■ 사이클 구성 (ECE Regulation 101, Annex 9)
 *   ECE 15 (도심) × 4회 반복 (0 ~ 780 s):
 *     각 195s — 정지→저속→가속→정속→감속 패턴
 *     최고 속도: 50 km/h
 *   EUDC (교외) 1회 (780 ~ 1180 s):
 *     최고 속도: 120 km/h
 *   총 시간: 1180 s, 거리: 11.022 km, 평균 속도: 33.6 km/h
 *
 * NEDC는 EU에서 2017년 WLTP로 대체되었으나,
 * 레거시 차량 비교·인증 재현 시뮬레이션을 위해 구현한다.
 *
 * 참고: UN/ECE/TRANS/WP.29/GRPE; EU Regulation 715/2007 Annex I
 */
function generateNEDCProfile() {
  const profile = [];
  let t = 0;

  /** 선형 보간으로 키포인트 사이를 1 Hz 분해능으로 채움 */
  function addKeypoints(kpts) {
    for (let i = 0; i < kpts.length - 1; i++) {
      const [t0, v0] = kpts[i];
      const [t1, v1] = kpts[i + 1];
      const steps = Math.round(t1 - t0);
      for (let s = 0; s < steps; s++) {
        profile.push({ t: t++, v: v0 + (v1 - v0) * (s / steps), phase: t <= 780 ? 'ECE15' : 'EUDC' });
      }
    }
  }

  // ── ECE 15 단위 사이클 키포인트 (0 기준, 총 195s) ──────────────
  // 출처: ECE Regulation No. 83 Annex 4a, Table A4a/1
  const ece15 = [
    [0,0],[11,0],[15,15],[23,15],[28,32],[49,32],[54,10],[56,0],
    [58,0],[60,0],[69,35],[74,35],[79,50],[97,50],[100,35],[110,35],[119,0],
    [121,0],[128,0],[133,35],[144,35],[150,50],[163,50],[166,35],[176,35],[183,0],
    [185,0],[195,0]
  ];

  // ECE 15 × 4 반복 (780 s)
  for (let rep = 0; rep < 4; rep++) {
    const t_offset = rep * 195;
    const kpts = ece15.map(([ts, v]) => [ts + t_offset, v]);
    addKeypoints(kpts);
  }

  // ── EUDC (교외) 키포인트 (780 ~ 1180s) ──────────────────────────
  const eudc = [
    [780,0],[782,0],[794,70],[814,70],[818,50],[828,50],[832,70],[849,70],
    [853,50],[862,50],[866,100],[886,100],[904,120],[935,120],[940,80],
    [955,80],[963,120],[978,120],[983,80],[995,80],[1001,120],[1005,0],
    [1180,0]
  ];
  addKeypoints(eudc);

  // phase 태그 정정
  return profile.map((d, i) => ({
    ...d,
    phase: i < 780 ? 'ECE15' : 'EUDC',
    v: Math.max(0, d.v),
  }));
}

// ── generateRDEProfileStochastic ── (RDE 구간 비율 확률 변동)
/**
 * UN GTR 규정 최소 비율을 준수하면서 구간 비율을 랜덤하게 변동
 *
 * 기존 generateRDEProfile: 도심(900s)/농촌(600s)/고속(600s) 고정
 * 이 함수: 총 2100s 유지하면서 비율을 확률적으로 변동
 *   - 도심:  ≥ 29% (최소 609s)  → 샘플 범위 29~45%
 *   - 농촌:  ≥ 23% (최소 483s)  → 샘플 범위 23~36%
 *   - 고속:  ≥ 23% (최소 483s)  → 잔여 = 전체 - 도심 - 농촌
 *
 * @param {number} [seed]      - 재현성 시드 (기본: Date.now())
 * @param {string} [mode]      - 'full' | 'urban' | 'rural' | 'motorway'
 * @returns {Array}            - 1Hz 속도 프로파일 (phase 포함)
 */
function generateRDEProfileStochastic(seed, mode = 'full') {
  let s = seed ?? Date.now();
  function lcg() { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; }

  if (mode !== 'full') return generateRDEProfile(mode); // 단일 구간은 기존 함수 위임

  const T_TOTAL = 2100;
  // 비율 샘플링 (UN GTR 최소 준수)
  const p_urban    = 0.29 + lcg() * 0.16; // 29~45%
  const p_rural_raw = 0.23 + lcg() * 0.13; // 23~36%
  const p_rural    = Math.min(p_rural_raw, 1 - p_urban - 0.23);
  const p_motorway = Math.max(0.23, 1 - p_urban - p_rural);

  const t_urban    = Math.round(T_TOTAL * p_urban);
  const t_rural    = Math.round(T_TOTAL * p_rural);
  const t_motorway = T_TOTAL - t_urban - t_rural;

  const profile = [];
  let t_cur = 0;

  function makeAndBuild(v_lo, v_hi, t_seg, phase) {
    let v_cur = 0;
    let elapsed = 0;
    while (elapsed < t_seg) {
      const v_tgt  = v_lo + lcg() * (v_hi - v_lo);
      const idle_t = lcg() < 0.20 ? Math.floor(10 + lcg() * 20) : 0;
      const accel_t = Math.max(5, Math.floor(5 + lcg() * 15));
      const cruise_t = Math.floor(15 + lcg() * 35);
      const decel_t = Math.max(4, Math.floor(4 + lcg() * 10));
      const seg_t   = idle_t + accel_t + cruise_t + decel_t;
      if (elapsed + seg_t > t_seg * 1.1) break;

      for (let i = 0; i < idle_t && elapsed < t_seg; i++, elapsed++)
        profile.push({ t: t_cur++, v: 0, phase });
      for (let i = 0; i < accel_t && elapsed < t_seg; i++, elapsed++) {
        v_cur += (v_tgt - v_cur) / Math.max(1, accel_t - i);
        profile.push({ t: t_cur++, v: Math.max(0, v_cur), phase });
      }
      v_cur = v_tgt;
      for (let i = 0; i < cruise_t && elapsed < t_seg; i++, elapsed++)
        profile.push({ t: t_cur++, v: v_tgt * (0.96 + lcg() * 0.08), phase });
      for (let i = 0; i < decel_t && elapsed < t_seg; i++, elapsed++) {
        v_cur = Math.max(0, v_cur * (1 - (i + 1) / decel_t));
        profile.push({ t: t_cur++, v: v_cur, phase });
      }
      v_cur = 0;
      elapsed += seg_t - (idle_t + accel_t + cruise_t + decel_t); // 정렬
    }
  }

  makeAndBuild(0,  60,  t_urban,    'Urban');
  makeAndBuild(60, 90,  t_rural,    'Rural');
  makeAndBuild(90, 130, t_motorway, 'Motorway');

  return profile;
}

// ── sampleDriverAggressiveness ── (드라이버 공격성 분포 샘플링)
/**
 * 드라이버 공격성 파라미터 모집단 샘플링
 *
 * ■ 기존 모델의 한계
 *   `wltp_regen_aggressiveness` 단일 고정값 → 동일 시뮬레이션이 반복 재현만 가능.
 *   실제 도로에는 다양한 운전 스타일이 공존 (Beta 분포로 모델링).
 *
 * ■ Beta 분포 파라미터화
 *   α_drv, β_drv (기본: 2.0, 5.0) → 우편향, 대부분 평균 이하 공격성
 *   (α=2, β=5 → 평균 E[X]=0.286, 분위 P50≈0.25, P90≈0.57)
 *   SPORT 운전자 시나리오: α=5, β=2 → 좌편향 (공격적)
 *
 *   Beta 샘플: Johnk 방법 (지수분포 기반 변환)
 *     X_a = -ln(U1) / α,  X_b = -ln(U2) / β  → B = X_a / (X_a + X_b)
 *
 * @param {object} [options]
 *   alpha       - Beta α (기본 2.0)
 *   beta        - Beta β (기본 5.0)
 *   n           - 샘플 수 (기본 1)
 *   seed        - 시드 (기본 랜덤)
 * @returns {number|number[]} 공격성 값 ∈ (0,1), n=1이면 스칼라
 */
function sampleDriverAggressiveness({ alpha = 2.0, beta = 5.0, n = 1, seed } = {}) {
  let s = seed ?? Date.now();
  function lcg()  { s = (s * 1664525 + 1013904223) & 0xffffffff; return Math.max(1e-15, (s >>> 0) / 0xffffffff); }
  // Johnk's method for Beta(α,β) via Gamma sampling
  function betaSample() {
    // Gamma(α,1) ≈ -ln(U)^(1/α) via envelope for small α (Ahrens-Dieter 단순화)
    // 범용 근사: Johnk — U^(1/α) / (U^(1/α) + V^(1/β))
    const u = lcg(), v = lcg();
    const xa = Math.pow(u, 1 / alpha);
    const xb = Math.pow(v, 1 / beta);
    return xa / (xa + xb);
  }
  const samples = Array.from({ length: n }, betaSample);
  return n === 1 ? samples[0] : samples;
}

export {
  setDriveModeThrottle,
  generateFTP75Profile,
  generateNEDCProfile,             // ✅ RevB: NEDC 레거시 사이클
  generateRDEProfileStochastic,    // ✅ RevB: RDE 확률적 구간 비율
  sampleDriverAggressiveness,      // ✅ RevB: 드라이버 공격성 분포 샘플러
  generateWLTPProfile,
  generateJC08Profile,
  generateCLTCProfile,
  generateRDEProfile,
  generateEPA_CITY_Profile,
  generateEPA_HWY_Profile,
  getCycleProfile,
  getWLTPProfile,
  wltpPhaseTag,
  jc08PhaseTag,
  cltcPhaseTag,
  ftp75PhaseTag,
  rdePhaseTag,
  genericPhaseTag,
  initDriverPIDState,
  driverPIDController,
  initDriverMPCState,
  driverMPCController,
  init2RCBatteryState,
  initRLSMuState,
  simMonteCarloAsync,
  simLaunch,
  simCoast,
  simReaccel,
  simBraking,
  simDrivingCycle,
  simWLTP,
  simWLTPTempSensitivity,
  simThermalStress,
  simDrivabilityTipIn,
  simDrivabilityTipOut,
  simDrivabilityBraking,
  simEmergencyBraking,
  simSkidPad,
  simFullSpecCycle,
  runDrivabilityAnalysis,
  proxyRun,
  sensitivityAnalysis,
  generateCycleCSVTemplate,
  parseFullSpecCycleCSV,
  runCoreSim,
  runHeavySim,
  runFullSim,
  runComparison,
  runCoreComparison,
  _buildCompMerged,
  mergeByKey,
  mergeTimeSeries,
  buildSummary,
  buildHist,
  buildRadar,
  calcRDEEmissions,
  correlationCoeff,
  sensLC_compare,
  sensHeat_compare,
  steadyAt_cmp,
  vCross_cmp,
  estVmax_cmp,
  findVopt_cmp,
  latinHypercubeSample,
  generatePassFailReport,
  computeDrivabilityJerk,
  verifySSTConstants,  // [Fix-Issue2] SST 인라인 복사본 불일치 감지
  setSSTConstants,           // [SST-DI]    SST 원본 상수 주입 (zero-import 설계 부채 해소)
  injectWLTPOfficialProfile, // [Fix-③]     WLTP 공식 1Hz 프로파일 DI 주입
  setPhysicsFunctions, // [Fix-typeof-DI] 외부 물리함수 DI 주입 (motor/dynamics/vehicle)
  runEnhancedMonteCarlo,
  simMonteCarloAsyncWorker,
};